import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { resolvePublicAssetBackgroundUrl, resolvePublicAssetUrl } from './utils/resolvePublicAssetUrl';
import { getCountryFromCellId, getCellIdFromCountry, getAllCountries } from './worldMap/countryToCellMapping';
import {
  localizeBuildingDescription,
  localizeBuildingName,
  localizeCountryName,
  localizeFactionName,
  localizeMissionType,
  localizePurpose
} from './utils/warEffortLocalization';
import { getNeighbourCountries, getNeighbourCellIds } from './worldMap/countryNeighbours';
import { WorldMap } from './worldMap';
import { preloadWarEffortMapPaintImages } from './worldMap/mapPaintImages';
import { CountryHoverProvider, WarEffortCountryHoverTooltip } from './worldMap/countryHoverContext';
import WarEffortAttackHoverStatus from './worldMap/WarEffortAttackHoverStatus';

// Parse last action message for a country name so we can highlight on map when server doesn't send lastPlayerActionCellId
function getCellIdFromLastActionMessage(message) {
  if (!message || typeof message !== 'string') return null;
  const countries = getAllCountries().sort((a, b) => b.length - a.length);
  for (const country of countries) {
    if (message.includes(country)) return getCellIdFromCountry(country);
  }
  return null;
}
import { API_URL } from './config/apiConfig';
import { getFactionBoxBackground, getFactionIconPath } from './utils/factionHudDisplay';
import { publishWarEffortMissionStatus } from './utils/warEffortWarningEvents';
import {
  filterCommandCenterCellsByOwner,
  mergeCommandCenterBoostFields
} from './utils/warEffortCommandCenterMap';
import {
  addDismissedWarEffortMissionId,
  isWarEffortMissionDismissed,
} from './utils/warEffortDismissedMissions';
import { buildOrderDetailLangQuery, appendLangQueryToUrl } from './utils/buildOrderDetailLangQuery';
import CustomDropdown from './CustomDropdown';
import { playSoundEffect } from './hooks/useSoundEffects';
import { useBattleStatTooltip } from './hooks/useBattleStatTooltip';
import { getEffectiveSoundOutputVolume } from './hooks/soundEffectsManager';
import { createPortal } from 'react-dom';
import OrderDesign from './OrderDesign';
import OrderSidebarStyleCard from './components/OrderSidebarStyleCard';
import './components/OrderSidebarStyleCard.css';
import { OrderCardBubbles } from './components/OrderCardBubbles';
import './StrategyPreferenceRewardModal.css';
import './WarEffortFortification.css';
import WarEffortBotLog from './WarEffortBotLog';
import AchievementUnlockedToast from './notifications/AchievementUnlockedToast';
import { usePendingAchievementToasts } from './hooks/usePendingAchievementToasts';
import AttackStrategyStatisticsPanel from './components/AttackStrategyStatisticsPanel';
import { getActiveAttackStrategyOrders } from './utils/attackStrategyStatistics';
import { enrichHallUnitsWithLocalizedNames, enrichOrdersWithLocalizedNames } from './utils/enrichOrdersWithLocalizedNames';
import MenuPaintedButton from './components/MenuPaintedButton';
import {
  WarEffortEliteDefenseDialog,
  WarEffortEliteDefenseWaitingOverlay,
} from './components/WarEffortEliteDefenseDialog';
import {
  MENU_PAINTED_SURFACE_FILTER,
  WAR_EFFORT_FACTION_BUTTON_PATH,
  WAR_EFFORT_LEAVE_MISSION_BUTTON_PATH,
  WAR_EFFORT_PRIMARY_BUTTON_PATH,
  WAR_EFFORT_REFRESH_BUTTON_PATH,
  WAR_EFFORT_ADD_BOT_BUTTON_PATH,
} from './config/welcomeAssets';

const EMPTY_CELL_IDS = [];

/** Attack strategy modal row — same card style as Strategy sidebar / battle S: modal. */
function AttackStrategySlotRow({
  order,
  orderId,
  occurrence,
  isDeactivated,
  costLabel,
  onToggle,
  setTooltipRect,
  hallStars = 0,
  canSell = false,
  sellEnergy = null,
  sellBusy = false,
  onSell = null,
}) {
  const { t } = useTranslation();
  const cost = order.cost ?? order.intel_cost ?? 0;
  return (
    <div
      className={`war-effort-attack-strategy-entry${isDeactivated ? ' war-effort-attack-strategy-entry--inactive' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        onToggle(orderId, occurrence);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          playSoundEffect('/sounds/click.mp3');
          onToggle(orderId, occurrence);
        }
      }}
      onMouseEnter={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const showAbove = typeof window !== 'undefined' && rect.bottom + 400 > window.innerHeight;
        setTooltipRect({ order, orderId, occurrence, top: rect.top, bottom: rect.bottom, left: rect.left, showAbove });
      }}
      onMouseLeave={() => setTooltipRect(null)}
    >
      <OrderSidebarStyleCard
        order={order}
        displayName={order.name}
        displayCost={cost}
        costLabel={costLabel}
        className="order-sidebar-style-card--battle-modal-list"
        beforeCost={
          hallStars > 0 ? (
            <span className="war-effort-hall-stars" aria-label={`${hallStars} hall ascension`}>
              {'★'.repeat(hallStars)}
            </span>
          ) : null
        }
      />
      {canSell && sellEnergy != null && typeof onSell === 'function' && (
        <button
          type="button"
          className="war-effort-attack-strategy-sell"
          disabled={sellBusy}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (sellBusy) return;
            playSoundEffect('/sounds/click.mp3');
            onSell(orderId, occurrence);
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {t('warEffort.sellForEnergy', { energy: sellEnergy })}
        </button>
      )}
    </div>
  );
}

function getAttackStrategyUnitSellEnergy(order, unitSellPrices) {
  if (!unitSellPrices) return null;
  const rarity = String(order.rarity || '').trim().toLowerCase();
  if (!rarity) return null;
  const energy = unitSellPrices[rarity];
  if (energy == null) return null;
  return energy;
}

const HALL_RESOURCE_ICONS = {
  stone: '/images/resource_stone.png',
  biomass: '/images/resource_biomass.png',
  food_rations: '/images/resource_foodrations.png',
  erray: '/images/resource_erray.png',
  lavender: '/images/resource_lavender.png'
};

const TRADE_FORUM_RESOURCE_ORDER = ['lavender', 'food_rations', 'erray', 'stone', 'biomass'];
const TRADE_FORUM_MARKET_MAX = 20000;
const TRADE_FORUM_AMOUNT_STEP = 100;
const TRADE_FORUM_AUTO_DEFAULTS = {
  sellEnergyBelow: 500,
  sellResourceOver: 1000,
  sellStockAbove: 19000,
  sellAmount: 1000,
  sellResources: [...TRADE_FORUM_RESOURCE_ORDER],
  buyEnergyAbove: 800,
  buyResourceUnder: 1000,
  buyStockBelow: 20000,
  buyAmount: 1000,
  buyResources: [...TRADE_FORUM_RESOURCE_ORDER]
};

function emptyTradeForumAutoDraft() {
  return {
    sellEnergyBelow: String(TRADE_FORUM_AUTO_DEFAULTS.sellEnergyBelow),
    sellResourceOver: String(TRADE_FORUM_AUTO_DEFAULTS.sellResourceOver),
    sellStockAbove: String(TRADE_FORUM_AUTO_DEFAULTS.sellStockAbove),
    sellAmount: String(TRADE_FORUM_AUTO_DEFAULTS.sellAmount),
    sellResources: [...TRADE_FORUM_AUTO_DEFAULTS.sellResources],
    buyEnergyAbove: String(TRADE_FORUM_AUTO_DEFAULTS.buyEnergyAbove),
    buyResourceUnder: String(TRADE_FORUM_AUTO_DEFAULTS.buyResourceUnder),
    buyStockBelow: String(TRADE_FORUM_AUTO_DEFAULTS.buyStockBelow),
    buyAmount: String(TRADE_FORUM_AUTO_DEFAULTS.buyAmount),
    buyResources: [...TRADE_FORUM_AUTO_DEFAULTS.buyResources]
  };
}

function tradeForumAutomationHasSavedRules(automation) {
  if (!automation) return false;
  if (automation.sellEnergyBelow != null || automation.sellResourceOver != null || automation.sellStockAbove != null || automation.sellAmount != null) return true;
  if (automation.buyEnergyAbove != null || automation.buyResourceUnder != null || automation.buyStockBelow != null || automation.buyAmount != null) return true;
  if (Array.isArray(automation.sellResources) && automation.sellResources.length > 0) return true;
  if (Array.isArray(automation.buyResources) && automation.buyResources.length > 0) return true;
  return false;
}

function tradeForumAutoDraftFromAutomation(automation) {
  if (!tradeForumAutomationHasSavedRules(automation)) {
    return emptyTradeForumAutoDraft();
  }
  return {
    sellEnergyBelow: automation.sellEnergyBelow ?? '',
    sellResourceOver: automation.sellResourceOver ?? '',
    sellStockAbove: automation.sellStockAbove ?? '',
    sellAmount: automation.sellAmount ?? '',
    sellResources: Array.isArray(automation.sellResources) ? automation.sellResources : [],
    buyEnergyAbove: automation.buyEnergyAbove ?? '',
    buyResourceUnder: automation.buyResourceUnder ?? '',
    buyStockBelow: automation.buyStockBelow ?? '',
    buyAmount: automation.buyAmount ?? '',
    buyResources: Array.isArray(automation.buyResources) ? automation.buyResources : []
  };
}

function tradeForumNudgeAmount(current, delta, minValue) {
  const n = parseInt(current, 10);
  const base = Number.isInteger(n) ? n : (delta > 0 ? 0 : minValue);
  return String(Math.min(TRADE_FORUM_MARKET_MAX, Math.max(minValue, base + delta)));
}

function TradeForumAmountStepper({ value, minValue, onChange, ariaLabel }) {
  const { t } = useTranslation();
  const n = parseInt(value, 10);
  const numeric = Number.isInteger(n) ? n : null;
  return (
    <div className="war-effort-trade-forum-stepper">
      <button
        type="button"
        className="war-effort-trade-forum-step"
        disabled={numeric == null ? minValue > 0 : numeric <= minValue}
        aria-label={t('warEffort.tradeForum.decrease', { label: ariaLabel })}
        onClick={() => onChange(tradeForumNudgeAmount(value, -TRADE_FORUM_AMOUNT_STEP, minValue))}
      >
        −
      </button>
      <input
        className="war-effort-trade-forum-amount"
        type="number"
        min={minValue}
        step="1"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
      <button
        type="button"
        className="war-effort-trade-forum-step"
        disabled={numeric != null && numeric >= TRADE_FORUM_MARKET_MAX}
        aria-label={t('warEffort.tradeForum.increase', { label: ariaLabel })}
        onClick={() => onChange(tradeForumNudgeAmount(value, TRADE_FORUM_AMOUNT_STEP, minValue))}
      >
        +
      </button>
    </div>
  );
}

function tradeForumRateForStock(stock) {
  if (stock > 19000) return 0.8;
  if (stock > 10000) return 1.2;
  if (stock < 1000) return 2;
  if (stock < 5000) return 1.6;
  return 1.4;
}

function tradeForumClampBuyAmount(requested, marketStock, stored, storageMax, energy) {
  const room = Math.max(0, storageMax - stored);
  let amount = Math.min(requested, room, marketStock);
  if (amount <= 0) return 0;
  while (amount > 0) {
    const rate = tradeForumRateForStock(marketStock - amount);
    if (Math.round(amount * rate) <= energy) return amount;
    amount -= 1;
  }
  return 0;
}

function tradeForumClampSellAmount(requested, marketStock, stored, energy, energyCap) {
  const marketRoom = Math.max(0, TRADE_FORUM_MARKET_MAX - marketStock);
  let amount = Math.min(requested, stored, marketRoom);
  if (amount <= 0) return 0;
  const energyRoom = Math.max(0, energyCap - energy);
  while (amount > 0) {
    const rate = tradeForumRateForStock(marketStock + amount);
    if (Math.round(amount / rate) <= energyRoom) return amount;
    amount -= 1;
  }
  return 0;
}

function tradeForumBuyPreview(row, stored, storageMax, energy, requested) {
  if (!Number.isInteger(requested) || requested < 1) {
    return { qty: 0, reasonKey: 'enterPositiveAmount' };
  }
  if (storageMax <= 0) return { qty: 0, reasonKey: 'placeMatchingBuilding' };
  if (row.stock <= 0) return { qty: 0, reasonKey: 'marketNoStock' };
  if (stored >= storageMax) return { qty: 0, reasonKey: 'storageFull' };
  const qty = tradeForumClampBuyAmount(requested, row.stock, stored, storageMax, energy);
  if (qty <= 0) return { qty: 0, reasonKey: 'notEnoughEnergy' };
  return { qty, reasonKey: null };
}

function tradeForumSellPreview(row, stored, energy, energyCap, requested) {
  if (!Number.isInteger(requested) || requested < 1) {
    return { qty: 0, reasonKey: 'enterPositiveAmount' };
  }
  if (row.stock >= TRADE_FORUM_MARKET_MAX) return { qty: 0, reasonKey: 'marketNotBuying' };
  if (stored <= 0) return { qty: 0, reasonKey: 'noStoredToSell' };
  if (energy >= energyCap) return { qty: 0, reasonKey: 'energyAtCapacity' };
  const qty = tradeForumClampSellAmount(requested, row.stock, stored, energy, energyCap);
  if (qty <= 0) return { qty: 0, reasonKey: 'energyAtCapacity' };
  return { qty, reasonKey: null };
}

function TradeForumMapPanel({
  tradeForum,
  tradeForumError,
  tradeForumAmounts,
  tradeForumBusyKey,
  tradeForumAutoDraft,
  tradeForumAutoSaving,
  onAmountChange,
  onTrade,
  onAutoFieldChange,
  onToggleAutoResource,
  onActivateAutomation,
  onDeactivateAutomation
}) {
  const { t } = useTranslation();
  const { showStatTooltip, hideStatTooltip, statTooltipPortal } = useBattleStatTooltip();
  const resourceLabel = (resource) => t(`warEffort.resourceNames.${resource}`);
  const reasonText = (key) => (key ? t(`warEffort.tradeForum.reasons.${key}`) : null);
  const draft = tradeForumAutoDraft || emptyTradeForumAutoDraft();
  if (tradeForum && (!tradeForum.storage || !tradeForum.storageMax || !Array.isArray(tradeForum.market))) {
    throw new Error('Trade Forum: payload is incomplete');
  }
  if (tradeForum && typeof tradeForum.trumpManeuverBlocked !== 'boolean') {
    throw new Error('Trade Forum: payload is missing trumpManeuverBlocked');
  }
  const trumpBlocked = Boolean(tradeForum && tradeForum.trumpManeuverBlocked);
  return (
    <div
      className="war-effort-map-chrome--trade-forum"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {statTooltipPortal}
      <div className="war-effort-trade-forum-panel">
        <div className="war-effort-trade-forum-title">{localizeBuildingName('trade', t)}</div>
        {!tradeForum ? (
          <div className="war-effort-trade-forum-status">{tradeForumError || t('warEffort.tradeForum.loadingMarket')}</div>
        ) : (
          <>
            <div className="war-effort-trade-forum-energy">
              {t('warEffort.tradeForum.energyCap', { energy: tradeForum.energy, energyCap: tradeForum.energyCap })}
            </div>
            {tradeForum.trumpManeuverEndsAt && (
              <div className="war-effort-trade-forum-status">
                {trumpBlocked
                  ? t('warEffort.tradeForum.trumpLockedWatch')
                  : t('warEffort.tradeForum.trumpActiveOpponentsCannot')}
              </div>
            )}
            {TRADE_FORUM_RESOURCE_ORDER.map((resource) => {
              const row = tradeForum.market.find((item) => item.resource === resource);
              if (!row) {
                throw new Error(`Trade Forum: market is missing ${resource}`);
              }
              const stored = tradeForum.storage[resource];
              const storageMax = tradeForum.storageMax[resource];
              if (stored == null) {
                throw new Error(`Trade Forum: storage is missing ${resource}`);
              }
              if (storageMax == null) {
                throw new Error(`Trade Forum: storageMax is missing ${resource}`);
              }
              const amountRaw = tradeForumAmounts[resource];
              const amount = parseInt(amountRaw, 10);
              const buyPreview = tradeForumBuyPreview(row, stored, storageMax, tradeForum.energy, amount);
              const sellPreview = tradeForumSellPreview(row, stored, tradeForum.energy, tradeForum.energyCap, amount);
              const buyBusy = tradeForumBusyKey === `${resource}-buy`;
              const sellBusy = tradeForumBusyKey === `${resource}-sell`;
              const buyReason = reasonText(buyPreview.reasonKey);
              const sellReason = reasonText(sellPreview.reasonKey);
              const clampNotes = [
                buyPreview.qty > 0 && buyPreview.qty < amount ? t('warEffort.tradeForum.willBuy', { qty: buyPreview.qty }) : null,
                sellPreview.qty > 0 && sellPreview.qty < amount ? t('warEffort.tradeForum.willSell', { qty: sellPreview.qty }) : null
              ].filter(Boolean);
              const blockNotes = [
                buyReason && sellReason && buyReason === sellReason
                  ? buyReason
                  : null,
                buyReason && buyReason !== sellReason ? t('warEffort.tradeForum.buyPrefix', { reason: buyReason }) : null,
                sellReason && buyReason !== sellReason ? t('warEffort.tradeForum.sellPrefix', { reason: sellReason }) : null
              ].filter(Boolean);
              const label = resourceLabel(resource);
              return (
                <div key={resource} className="war-effort-trade-forum-row">
                  <div className="war-effort-trade-forum-row-main">
                    <img src={HALL_RESOURCE_ICONS[resource]} alt={label} />
                    <div className="war-effort-trade-forum-row-copy">
                      <div className="war-effort-trade-forum-resource">{label}</div>
                      <div className="war-effort-trade-forum-meta">
                        {t('warEffort.tradeForum.storedMarketRate', {
                          stored,
                          storageMax,
                          stock: row.stock,
                          rate: row.rate,
                          buyRate: row.buyRate,
                          sellRate: row.sellRate
                        })}
                      </div>
                    </div>
                    <TradeForumAmountStepper
                      value={amountRaw ?? ''}
                      minValue={1}
                      ariaLabel={t('warEffort.tradeForum.amountAria', { resource: label })}
                      onChange={(value) => onAmountChange(resource, value)}
                    />
                  </div>
                  <div className="war-effort-trade-forum-actions">
                    <button
                      type="button"
                      className="war-effort-trade-forum-btn"
                      disabled={Boolean(buyPreview.reasonKey) || buyBusy || trumpBlocked}
                      title={trumpBlocked ? t('warEffort.tradeForum.trumpTradingLocked') : (buyReason || (buyPreview.qty < amount ? t('warEffort.tradeForum.buyQty', { qty: buyPreview.qty }) : t('warEffort.tradeForum.buyResource', { resource: label })))}
                      onClick={() => onTrade(resource, 'buy')}
                    >
                      {buyBusy ? t('warEffort.tradeForum.buying') : t('warEffort.tradeForum.buy')}
                    </button>
                    <button
                      type="button"
                      className="war-effort-trade-forum-btn"
                      disabled={Boolean(sellPreview.reasonKey) || sellBusy || trumpBlocked}
                      title={trumpBlocked ? t('warEffort.tradeForum.trumpTradingLocked') : (sellReason || (sellPreview.qty < amount ? t('warEffort.tradeForum.sellQty', { qty: sellPreview.qty }) : t('warEffort.tradeForum.sellResource', { resource: label })))}
                      onClick={() => onTrade(resource, 'sell')}
                    >
                      {sellBusy ? t('warEffort.tradeForum.selling') : t('warEffort.tradeForum.sell')}
                    </button>
                  </div>
                  {clampNotes.length > 0 && (
                    <div className="war-effort-trade-forum-reason war-effort-trade-forum-reason--note">
                      {clampNotes.join(' · ')}
                    </div>
                  )}
                  {blockNotes.length > 0 && (
                    <div className="war-effort-trade-forum-reason">
                      {blockNotes.join(' · ')}
                    </div>
                  )}
                </div>
              );
            })}
            {tradeForum.level >= 2 && (
              <div className="war-effort-trade-forum-auto">
                <div className="war-effort-trade-forum-auto-title">{t('warEffort.tradeForum.automation')}</div>
                <div className="war-effort-trade-forum-sell-units-hint">{t('warEffort.tradeForum.sellUnitsHint')}</div>
                <div className="war-effort-trade-forum-auto-grid">
                  <div className="war-effort-trade-forum-auto-col">
                    <div className="war-effort-trade-forum-auto-heading">{t('warEffort.tradeForum.sellWhenEnergyLow')}</div>
                    <label>
                      {t('warEffort.tradeForum.energyBelow')}
                      <TradeForumAmountStepper
                        value={draft.sellEnergyBelow}
                        minValue={0}
                        ariaLabel={t('warEffort.tradeForum.sellEnergyBelow')}
                        onChange={(value) => onAutoFieldChange('sellEnergyBelow', value)}
                      />
                    </label>
                    <label>
                      {t('warEffort.tradeForum.resourceOver')}
                      <TradeForumAmountStepper
                        value={draft.sellResourceOver}
                        minValue={0}
                        ariaLabel={t('warEffort.tradeForum.sellResourceOver')}
                        onChange={(value) => onAutoFieldChange('sellResourceOver', value)}
                      />
                    </label>
                    <label>
                      {t('warEffort.tradeForum.marketStockAbove')}
                      <TradeForumAmountStepper
                        value={draft.sellStockAbove}
                        minValue={0}
                        ariaLabel={t('warEffort.tradeForum.sellMarketStockAbove')}
                        onChange={(value) => onAutoFieldChange('sellStockAbove', value)}
                      />
                    </label>
                    <label>
                      {t('warEffort.tradeForum.sellAmount')}
                      <TradeForumAmountStepper
                        value={draft.sellAmount}
                        minValue={1}
                        ariaLabel={t('warEffort.tradeForum.sellAmount')}
                        onChange={(value) => onAutoFieldChange('sellAmount', value)}
                      />
                    </label>
                    <div className="war-effort-trade-forum-picks" role="group" aria-label={t('warEffort.tradeForum.sellResources')}>
                      {TRADE_FORUM_RESOURCE_ORDER.map((resource) => {
                        const active = draft.sellResources.includes(resource);
                        const label = resourceLabel(resource);
                        return (
                          <button
                            key={`sell-${resource}`}
                            type="button"
                            className={`war-effort-trade-forum-pick${active ? ' war-effort-trade-forum-pick--active' : ''}`}
                            aria-pressed={active}
                            aria-label={label}
                            onMouseEnter={(event) => showStatTooltip(event, label, { placement: 'above' })}
                            onMouseLeave={hideStatTooltip}
                            onClick={() => {
                              hideStatTooltip();
                              onToggleAutoResource('sellResources', resource);
                            }}
                          >
                            <img src={HALL_RESOURCE_ICONS[resource]} alt="" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="war-effort-trade-forum-auto-col">
                    <div className="war-effort-trade-forum-auto-heading">{t('warEffort.tradeForum.buyWhenEnergyHigh')}</div>
                    <label>
                      {t('warEffort.tradeForum.energyAbove')}
                      <TradeForumAmountStepper
                        value={draft.buyEnergyAbove}
                        minValue={0}
                        ariaLabel={t('warEffort.tradeForum.buyEnergyAbove')}
                        onChange={(value) => onAutoFieldChange('buyEnergyAbove', value)}
                      />
                    </label>
                    <label>
                      {t('warEffort.tradeForum.resourceUnder')}
                      <TradeForumAmountStepper
                        value={draft.buyResourceUnder}
                        minValue={0}
                        ariaLabel={t('warEffort.tradeForum.buyResourceUnder')}
                        onChange={(value) => onAutoFieldChange('buyResourceUnder', value)}
                      />
                    </label>
                    <label>
                      {t('warEffort.tradeForum.marketStockBelow')}
                      <TradeForumAmountStepper
                        value={draft.buyStockBelow}
                        minValue={0}
                        ariaLabel={t('warEffort.tradeForum.buyMarketStockBelow')}
                        onChange={(value) => onAutoFieldChange('buyStockBelow', value)}
                      />
                    </label>
                    <label>
                      {t('warEffort.tradeForum.buyAmount')}
                      <TradeForumAmountStepper
                        value={draft.buyAmount}
                        minValue={1}
                        ariaLabel={t('warEffort.tradeForum.buyAmount')}
                        onChange={(value) => onAutoFieldChange('buyAmount', value)}
                      />
                    </label>
                    <div className="war-effort-trade-forum-picks" role="group" aria-label={t('warEffort.tradeForum.buyResources')}>
                      {TRADE_FORUM_RESOURCE_ORDER.map((resource) => {
                        const active = draft.buyResources.includes(resource);
                        const label = resourceLabel(resource);
                        return (
                          <button
                            key={`buy-${resource}`}
                            type="button"
                            className={`war-effort-trade-forum-pick${active ? ' war-effort-trade-forum-pick--active' : ''}`}
                            aria-pressed={active}
                            aria-label={label}
                            onMouseEnter={(event) => showStatTooltip(event, label, { placement: 'above' })}
                            onMouseLeave={hideStatTooltip}
                            onClick={() => {
                              hideStatTooltip();
                              onToggleAutoResource('buyResources', resource);
                            }}
                          >
                            <img src={HALL_RESOURCE_ICONS[resource]} alt="" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="war-effort-trade-forum-actions">
                  <button
                    type="button"
                    className="war-effort-trade-forum-btn"
                    disabled={tradeForumAutoSaving || (tradeForum.automation && tradeForum.automation.active === true)}
                    onClick={onActivateAutomation}
                  >
                    {tradeForumAutoSaving ? t('warEffort.tradeForum.saving') : t('warEffort.tradeForum.activate')}
                  </button>
                  <button
                    type="button"
                    className="war-effort-trade-forum-btn"
                    disabled={tradeForumAutoSaving || !tradeForum.automation || tradeForum.automation.active !== true}
                    onClick={onDeactivateAutomation}
                  >
                    {t('warEffort.tradeForum.deactivate')}
                  </button>
                </div>
                {tradeForum.automation && tradeForum.automation.active === true && (
                  <div className="war-effort-trade-forum-reason war-effort-trade-forum-reason--note">
                    {t('warEffort.tradeForum.automationActive')}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {tradeForumError && tradeForum && (
          <div className="war-effort-trade-forum-status war-effort-trade-forum-status--error">{tradeForumError}</div>
        )}
      </div>
    </div>
  );
}

function hallRowOccurrence(row) {
  if (!row || row.occurrence == null || Number.isNaN(Number(row.occurrence))) {
    throw new Error('Hall of Ascension: occurrence is required');
  }
  return Number(row.occurrence);
}

function hallAscensionLevelForOrder(hallAscensions, orderId, occurrence) {
  if (!Array.isArray(hallAscensions) || orderId == null) return 0;
  if (occurrence == null || Number.isNaN(Number(occurrence))) {
    throw new Error('hallAscensionLevelForOrder: occurrence is required');
  }
  return hallAscensions.filter((row) =>
    Number(row.order_id) === Number(orderId) && hallRowOccurrence(row) === Number(occurrence)
  ).length;
}

function hallCopyIsSelected(unit, selectedOrderId, selectedOccurrence) {
  if (!unit) return false;
  return Number(unit.orderId) === Number(selectedOrderId)
    && Number(unit.occurrence) === Number(selectedOccurrence);
}

function hallAscensionBonusText(row, t) {
  if (!row) {
    throw new Error('Hall of Ascension: missing ascension bonus');
  }
  if (typeof t !== 'function') {
    throw new Error('Hall of Ascension: t is required');
  }
  if (row.kind === 'stats') {
    if (row.stat_bonus !== 'attack' && row.stat_bonus !== 'health') {
      throw new Error(`Hall of Ascension: invalid stat bonus ${row.stat_bonus}`);
    }
    const stat = t(row.stat_bonus === 'attack' ? 'warEffort.statAttack' : 'warEffort.statHealth');
    return t('warEffort.plusOneStat', { stat });
  }
  if (row.kind === 'ability') {
    if (!row.ability_name) {
      throw new Error('Hall of Ascension: missing ability name');
    }
    if (row.ability_name === 'Fell') {
      return t('warEffort.hallFellBonus');
    }
    if (row.ability_name === 'Raid') {
      return t('warEffort.hallRaidBonus');
    }
    return row.ability_name;
  }
  throw new Error(`Hall of Ascension: invalid bonus kind ${row.kind}`);
}

function hallRowsForOrder(hallAscensions, orderId, occurrence) {
  if (!Array.isArray(hallAscensions) || orderId == null) return [];
  if (occurrence == null || Number.isNaN(Number(occurrence))) {
    throw new Error('hallRowsForOrder: occurrence is required');
  }
  return hallAscensions
    .filter((row) => Number(row.order_id) === Number(orderId) && hallRowOccurrence(row) === Number(occurrence))
    .sort((a, b) => a.ascension_level - b.ascension_level);
}

function previewHallAscendedOrder(order, rows) {
  const next = {
    ...order,
    abilities: Array.isArray(order.abilities) ? [...order.abilities] : []
  };
  const highlights = { attack: false, health: false, abilities: [] };
  const grantedNames = [];
  for (const row of rows) {
    if (row.kind === 'stats') {
      if (row.stat_bonus === 'attack') {
        next.attack_rating = (next.attack_rating || 0) + 1;
        highlights.attack = true;
      } else if (row.stat_bonus === 'health') {
        next.health = (next.health || 0) + 1;
        highlights.health = true;
      }
    } else if (row.kind === 'ability' && row.ability_name) {
      if (!next.abilities.includes(row.ability_name)) {
        next.abilities.push(row.ability_name);
      }
      highlights.abilities.push(row.ability_name);
      grantedNames.push(row.ability_name);
    }
  }
  next.instanceAbilities = [...next.abilities];
  next.hasInstanceModifications = true;
  next.maxHealth = next.health;
  next.originalHealth = next.health;
  const baseDescription = order.description ? String(order.description) : '';
  const extraNames = grantedNames.filter((name) => !baseDescription.toLowerCase().includes(String(name).toLowerCase()));
  if (extraNames.length > 0) {
    next.description = baseDescription ? `${baseDescription} ${extraNames.join(' ')}` : extraNames.join(' ');
  }
  return { order: next, highlights };
}

function hallTooltipDisplayOrder(unit) {
  if (!unit) {
    throw new Error('hallTooltipDisplayOrder: unit is required');
  }
  const abilities = Array.isArray(unit.previewAbilities) ? [...unit.previewAbilities] : [];
  const grantedAbilities = Array.isArray(unit.highlights && unit.highlights.abilities)
    ? unit.highlights.abilities
    : [];
  const baseDescription = unit.order && unit.order.description ? String(unit.order.description) : '';
  const extraNames = grantedAbilities.filter((name) => {
    if (!name) {
      throw new Error('hallTooltipDisplayOrder: granted ability name is required');
    }
    return !baseDescription.toLowerCase().includes(String(name).toLowerCase());
  });
  return {
    ...(unit.order || {}),
    id: unit.orderId,
    name: unit.name,
    type: unit.type,
    rarity: unit.rarity,
    faction_name: unit.factionName,
    attack_rating: unit.previewAttack,
    health: unit.previewHealth,
    maxHealth: unit.previewHealth,
    originalHealth: unit.previewHealth,
    abilities,
    instanceAbilities: abilities,
    hasInstanceModifications: true,
    description: extraNames.length > 0
      ? (baseDescription ? `${baseDescription} ${extraNames.join(' ')}` : extraNames.join(' '))
      : baseDescription
  };
}

function HallResourceCostLine({ cost }) {
  if (!cost) return null;
  const parts = Object.entries(cost).filter(([, amount]) => amount > 0);
  if (parts.length === 0) return null;
  return (
    <span className="war-effort-hall-cost-line">
      {parts.map(([resource, amount]) => (
        <span key={resource} className="war-effort-hall-cost-item">
          <img src={HALL_RESOURCE_ICONS[resource]} alt={resource} />
          {amount}
        </span>
      ))}
    </span>
  );
}

// Trading Hub effect_type (snake_case) -> translation key under warEffort.tradingHub.shop
const SHOP_EFFECT_TYPE_TO_KEY = {
  future_knowledge: 'futureKnowledge',
  movement_voucher: 'movementVoucher',
  structural_exposure: 'structuralExposure',
  sneaky_peeky: 'sneakyPeeky',
  peak_generation: 'peakGeneration',
  infrastructure_subsidy: 'infrastructureSubsidy',
  sabotage: 'sabotage',
  conflict_cooldown: 'conflictCooldown',
  cease_fire_buyout: 'ceaseFireBuyout',
  speedpass: 'speedpass',
  domestic_turmoil: 'domesticTurmoil',
  tectonic_reconcilement: 'tectonicReconcilement',
  strategic_crippling: 'strategicCrippling',
  trump_maneuver: 'trumpManeuver',
  way_of_the_old_gods: 'wayOfTheOldGods',
  territory_ownership: 'territoryOwnership',
  command_center_production: 'commandCenterProduction'
};

// effect_type -> image filename (matches server catalog in /images/)
const SHOP_EFFECT_TYPE_TO_IMAGE = {
  future_knowledge: '1_future_knowledge_injection.jpg',
  movement_voucher: '4_movement_voucher.jpg',
  structural_exposure: '2_structural_exposure.jpg',
  sneaky_peeky: '5_sneaky_peeky.jpg',
  peak_generation: '10_generation_surge.jpg',
  infrastructure_subsidy: '8_discount.jpg',
  sabotage: '9_sabotage.jpg',
  conflict_cooldown: '3_cease_fire.jpg',
  cease_fire_buyout: '7_cease_fire_buyout.jpg',
  speedpass: '6_speedpass.jpg',
  domestic_turmoil: '11_turmoil.jpg',
  tectonic_reconcilement: '12_Reconcilement.jpg',
  strategic_crippling: '13_Crippling.jpg',
  trump_maneuver: '14_trump.jpg',
  way_of_the_old_gods: '15_old_gods.jpg',
  territory_ownership: 'world_button.png',
  command_center_production: 'command.png'
};

function readTerritoryBonuses(raw) {
  if (raw == null) return null;
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('readTerritoryBonuses: expected an object');
  }
  const readCount = (value, name) => {
    const n = Number(value);
    if (!Number.isInteger(n) || n < 0) {
      throw new Error(`readTerritoryBonuses: ${name} must be a non-negative integer`);
    }
    return n;
  };
  if (typeof raw.ownsSriLanka !== 'boolean') {
    throw new Error('readTerritoryBonuses: ownsSriLanka must be a boolean');
  }
  return {
    ownedCountryCount: readCount(raw.ownedCountryCount, 'ownedCountryCount'),
    commandCenterFortifiedCount: readCount(raw.commandCenterFortifiedCount, 'commandCenterFortifiedCount'),
    ownershipEnergyPercent: readCount(raw.ownershipEnergyPercent, 'ownershipEnergyPercent'),
    commandCenterResourcePercent: readCount(raw.commandCenterResourcePercent, 'commandCenterResourcePercent'),
    commandCenterEnergyPercent: readCount(raw.commandCenterEnergyPercent, 'commandCenterEnergyPercent'),
    ownsSriLanka: raw.ownsSriLanka,
    energyProductionPerMinute: readCount(raw.energyProductionPerMinute, 'energyProductionPerMinute')
  };
}

let ENERGY_PRODUCTION_BASE_PER_MINUTE = 10;
let ENERGY_PRODUCTION_POWERPLANT_PER_MINUTE = 8;
let ENERGY_PRODUCTION_POWERPLANT_UPGRADED_PER_MINUTE = 16;
let ENERGY_PRODUCTION_SRI_LANKA_PER_MINUTE = 20;

function computeEnergyProductionPerMinute(placedBuildings, territoryBonuses, activeShopEffects) {
  if (!territoryBonuses) {
    throw new Error('computeEnergyProductionPerMinute: territoryBonuses is required');
  }
  if (!Array.isArray(placedBuildings)) {
    throw new Error('computeEnergyProductionPerMinute: placedBuildings must be an array');
  }
  let plantEnergy = 0;
  for (const item of placedBuildings) {
    const id = item.building?.id;
    if (id === 'powerplant') plantEnergy += ENERGY_PRODUCTION_POWERPLANT_PER_MINUTE;
    else if (id === 'powerplant_upgraded') plantEnergy += ENERGY_PRODUCTION_POWERPLANT_UPGRADED_PER_MINUTE;
  }
  const sriLankaBonus = territoryBonuses.ownsSriLanka ? ENERGY_PRODUCTION_SRI_LANKA_PER_MINUTE : 0;
  const baseGrant = ENERGY_PRODUCTION_BASE_PER_MINUTE + plantEnergy + sriLankaBonus;
  const fraction = (territoryBonuses.ownershipEnergyPercent + territoryBonuses.commandCenterEnergyPercent) / 100;
  let total = Math.round(baseGrant * (1 + fraction));
  if (activeShopEffects != null && !Array.isArray(activeShopEffects)) {
    throw new Error('computeEnergyProductionPerMinute: activeShopEffects must be an array');
  }
  const peakActive = Array.isArray(activeShopEffects) && activeShopEffects.some(
    (eff) => eff.effectType === 'peak_generation' && Number(eff.remainingTicks) > 0
  );
  if (peakActive) total *= 2;
  return total;
}

function WarEffortEnergyBar({ energyCap, energyFillPercent, energyAmount, productionPerMinute, t }) {
  const [hovered, setHovered] = useState(false);
  const barRef = useRef(null);
  const rect = hovered && barRef.current ? barRef.current.getBoundingClientRect() : null;
  return (
    <div
      ref={barRef}
      className="war-effort-energy-bar war-effort-map-chrome--energy"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="war-effort-map-chrome--energy-label" style={{ fontSize: '10px', color: '#4ECDC4', marginTop: '0', fontWeight: 'bold', letterSpacing: '0.5px' }}>{t('warEffort.energy')}</span>
      <span className="war-effort-map-chrome--energy-cap" style={{ fontSize: '11px', color: '#aaa', marginTop: '4px', fontWeight: 'bold' }}>{energyCap}</span>
      <div
        className="war-effort-energy-cylinder war-effort-map-chrome--energy-cylinder"
      >
        <div
          style={{
            width: '100%',
            height: `${energyFillPercent}%`,
            minHeight: energyAmount > 0 ? '4px' : 0,
            backgroundColor: energyAmount > 0 ? '#4ECDC4' : 'transparent',
            borderRadius: '0 0 10px 10px',
            transition: 'height 0.3s ease'
          }}
        />
      </div>
      <span className="war-effort-map-chrome--energy-zero" style={{ fontSize: '11px', color: '#aaa', marginBottom: '8px', fontWeight: 'bold' }}>0</span>
      <span className="war-effort-map-chrome--energy-amount" style={{ fontSize: '12px', color: '#4ECDC4', fontWeight: 'bold', marginBottom: '0' }}>
        {energyAmount}
      </span>
      {hovered && productionPerMinute != null && rect && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            position: 'fixed',
            left: `${rect.right + 12}px`,
            top: `${rect.top + rect.height / 2}px`,
            transform: 'translateY(-50%)',
            width: 220,
            maxWidth: 'min(220px, calc(100vw - 80px))',
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            border: '2px solid rgba(78, 205, 196, 0.6)',
            borderRadius: '8px',
            padding: '12px 16px',
            zIndex: 10001,
            pointerEvents: 'none',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.8)',
            color: 'white',
            fontSize: '14px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: 1.5
          }}
        >
          <div style={{ fontWeight: 'bold', color: '#4ECDC4', marginBottom: '6px', fontSize: '15px' }}>
            {t('warEffort.energyProductionTitle', 'Energy production')}
          </div>
          <div style={{ color: '#E0E0E0' }}>
            {t('warEffort.energyProductionPerMinute', {
              amount: productionPerMinute,
              defaultValue: '{{amount}} energy per minute'
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

const THUMBNAIL_SIZE = 44;

function ActiveShopEffectsThumbnails({ effects, effectTypeToKey, effectTypeToImage, t, title }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const thumbRefs = useRef([]);

  const handleMouseEnter = (idx, e) => {
    setHoveredIndex(idx);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ x: rect.left, y: rect.top });
  };

  const handleMouseLeave = () => setHoveredIndex(null);

  const panelTitle = title || t('warEffort.tradingHub.activeEffects', 'Active shop effects');

  return (
    <div className="war-effort-map-chrome--shop-effects">
      <div className="war-effort-map-chrome--shop-effects-panel">
        <div style={{ fontWeight: 'bold', color: '#4ECDC4', fontSize: '13px' }}>
          {panelTitle}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
          {effects.map((eff, idx) => {
            const image = effectTypeToImage[eff.effectType] || 'shop_energy.jpg';
            const imgSrc = `/images/${image}`;
            return (
              <div
                key={idx}
                ref={el => { thumbRefs.current[idx] = el; }}
                onMouseEnter={(e) => handleMouseEnter(idx, e)}
                onMouseLeave={handleMouseLeave}
                style={{
                  width: THUMBNAIL_SIZE,
                  height: THUMBNAIL_SIZE,
                  borderRadius: 6,
                  overflow: 'hidden',
                  border: '2px solid rgba(255,255,255,0.25)',
                  cursor: 'default',
                  flexShrink: 0
                }}
              >
                <img
                  src={imgSrc}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.src = '/images/shop_energy.jpg'; }}
                />
              </div>
            );
          })}
        </div>
      </div>
      {hoveredIndex != null && effects[hoveredIndex] && (() => {
        const eff = effects[hoveredIndex];
        const key = effectTypeToKey[eff.effectType] || eff.effectType;
        const name = eff.name || t(`warEffort.tradingHub.shop.${key}`, eff.effectType);
        const descKey = `${key}Desc`;
        const description = eff.description || t(`warEffort.tradingHub.shop.${descKey}`, '');
        const hasExpiry = eff.expiresAt != null;
        const hasTicks = eff.remainingTicks != null && eff.remainingTicks > 0;
        const rect = thumbRefs.current[hoveredIndex]?.getBoundingClientRect();
        const gap = 12;
        const tooltipWidth = 340;
        const rightEdge = rect ? rect.left - gap : window.innerWidth - tooltipPos.x - gap;
        const topY = rect ? rect.top + rect.height / 2 : tooltipPos.y + THUMBNAIL_SIZE / 2;
        return (
          <div
            style={{
              position: 'fixed',
              right: `${window.innerWidth - rightEdge}px`,
              top: topY,
              transform: 'translateY(-50%)',
              width: tooltipWidth,
              maxWidth: 'min(340px, calc(100vw - 120px))',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              border: '2px solid rgba(78, 205, 196, 0.6)',
              borderRadius: '8px',
              padding: '14px 18px',
              zIndex: 10001,
              pointerEvents: 'none',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.8)',
              color: 'white',
              fontSize: '14px',
              fontFamily: 'Arial, sans-serif',
              lineHeight: 1.5
            }}
          >
            <div style={{ fontWeight: 'bold', color: '#4ECDC4', marginBottom: '8px', fontSize: '15px' }}>{name}</div>
            {description && <div style={{ color: '#E0E0E0', lineHeight: 1.5, marginBottom: hasExpiry || hasTicks ? '8px' : 0 }}>{description}</div>}
            {hasExpiry && (
              <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                {t('warEffort.tradingHub.expiresAt', 'expires')} {new Date(eff.expiresAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            {hasTicks && !hasExpiry && (
              <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                {eff.remainingTicks} {t('warEffort.tradingHub.remainingTicks', 'ticks')}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

const FORTIFICATION_TYPES = [
  { type: 'health_boost', name: 'Health Boost', cost: 280, description: 'All defending units have +1 health', unitId: 227 },
  { type: 'attack_boost', name: 'Attack Boost', cost: 320, description: 'All defending units have +1 attack', unitId: 77 },
  { type: 'intel_boost', name: 'Intel Boost', cost: 200, description: 'Defender has +1 intel', unitId: 237 },
  { type: 'order_boost', name: 'Order Boost', cost: 200, description: 'Defender has +1 order', unitId: 103 },
  { type: 'disorientation', name: 'Disorientation', cost: 380, description: 'Attacker cannot play hand orders on their first 2 turns', unitId: 59 },
  { type: 'defend_twice', name: 'Defend Twice', cost: 420, description: 'Defender can defend twice', unitId: 187 },
  { type: 'instakill', name: 'Instakill', cost: 500, description: 'Defender can instakill a unit once', unitId: 60 },
  { type: 'deploy_random', name: 'Deploy Random', cost: 500, description: 'Defender can deploy a random unit costing max intel once', unitId: 279 },
  { type: 'ability_relay_drone', name: 'Ability Relay Drone', cost: 400, description: 'Defender starts with ability relay drone', unitId: 399 },
  { type: 'elite_force', name: 'Elite Force', cost: 500, description: 'Country is defended by you using your Attack Strategy.', unitId: 417 }
];

// Same card size as AfterBattle victory reward screen (390px) so orders are full size
const REWARD_CARD_SIZE = 390;

// Render a single reward card (order, billy_bucks, resource, duplicator, building, emote) for mission-over screen
function WarEffortMissionOverRewardCard({ reward, index, t }) {
  if (!reward || !reward.type) return null;
  const cardStyle = {
    width: REWARD_CARD_SIZE,
    height: REWARD_CARD_SIZE,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: 8,
    padding: 20,
    overflow: 'hidden',
    position: 'relative'
  };
  if (reward.type === 'order' && reward.data && reward.data.id) {
    const order = reward.data;
    const rarity = (order.rarity || 'common').toLowerCase();
    return (
      <div key={index} className={`reward-order-container rarity-${rarity}`} style={{ width: REWARD_CARD_SIZE, height: REWARD_CARD_SIZE, position: 'relative', margin: 0, padding: 0, flexShrink: 0 }}>
        <OrderDesign order={order} count={0} minimal={false} noMargin={true} />
        <OrderCardBubbles rarity={order?.rarity || reward.data?.rarity || 'common'} />
      </div>
    );
  }
  if (reward.type === 'billy_bucks') {
    return (
      <div key={index} style={cardStyle}>
        <img src="/images/billy_bucks.png" alt="Billy Bucks" style={{ width: 240, height: 240, marginBottom: 20, objectFit: 'contain' }} />
        <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: 10 }}>{reward.amount ?? 0}</div>
        <div style={{ fontSize: '1.2rem', color: '#ccc' }}>Billy Bucks</div>
      </div>
    );
  }
  if (reward.type === 'resource') {
    const resourceMap = { lavender: '/images/resource_lavender.png', biomass: '/images/resource_biomass.png', food_rations: '/images/resource_foodrations.png', stone: '/images/resource_stone.png', erray: '/images/resource_erray.png', energy: '/images/powerplant.png' };
    const resourceType = reward.resourceType || reward.data?.resourceType || '';
    const amount = reward.amount ?? reward.data?.amount ?? 0;
    const imgSrc = resourceMap[resourceType.toLowerCase()] || '/images/resource_biomass.png';
    const name = t ? (t(`warEffort.resourceNames.${resourceType.toLowerCase()}`) || resourceType || 'Resource') : (resourceType || 'Resource');
    return (
      <div key={index} style={cardStyle}>
        <img src={imgSrc} alt={name} style={{ width: 240, height: 240, marginBottom: 20, objectFit: 'contain' }} onError={(e) => { e.target.src = '/images/resource_biomass.png'; }} />
        <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: 10 }}>{amount}</div>
        <div style={{ fontSize: '1.2rem', color: '#ccc' }}>{name}</div>
      </div>
    );
  }
  if (reward.type === 'duplicator' && reward.data) {
    return (
      <div key={index} style={cardStyle}>
        <img src="/images/duplicator.jpg" alt="Duplicator" style={{ width: 240, height: 240, marginBottom: 20, objectFit: 'contain' }} />
        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: 10 }}>{reward.data.name}</div>
        <div style={{ fontSize: '0.9rem', color: '#ccc', textTransform: 'capitalize' }}>{reward.data.rarity} Duplicator</div>
      </div>
    );
  }
  if (reward.type === 'building' && reward.data) {
    const buildingImages = { powerplant: '/images/powerplant.png', barracks: '/images/barracks.png', training_grounds: '/images/training.png', armory: '/images/armory.png', drone_hangar: '/images/dronehangar.png', communications_hub: '/images/comhub.png', intelligence_agency: '/images/intel.png', bank: '/images/bank.png', research_center: '/images/research.png', hospital: '/images/hospital.png', hall: '/images/hall.png', hall_upgraded: '/images/hall_upgraded.png', airfield: '/images/airfield.png', depot: '/images/depot.png', trade: '/images/trade.png', trade_upgraded: '/images/trade_upgraded.png', edc: '/images/edc.png', edc_upgraded: '/images/edc_upgrade.png' };
    const bid = reward.data.building_id || '';
    const displayName = (reward.data.name || bid).replace(/_/g, ' ');
    const imgSrc = buildingImages[bid] || `/images/${bid}.png` || '/images/command.png';
    return (
      <div key={index} style={cardStyle}>
        <img src={imgSrc} alt={displayName} style={{ width: 240, height: 240, marginBottom: 20, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', textAlign: 'center', marginBottom: 10 }}>HQ Building</div>
        <div style={{ fontSize: '1rem', color: '#ccc' }}>{displayName}</div>
      </div>
    );
  }
  if (reward.type === 'emote') {
    return (
      <div key={index} style={cardStyle}>
        <img src="/images/emotes.jpg" alt="Emote" style={{ width: 240, height: 240, marginBottom: 20, objectFit: 'contain' }} />
        <div style={{ fontSize: '1.2rem', color: '#ccc' }}>{reward.data?.speechBubble || reward.data?.name || 'Emote'}</div>
      </div>
    );
  }
  return null;
}

// Redirect winner or loser to AfterBattle so they get the same experience as "Dev War Effort Rewards" / "Dev War Effort Rewards Loser" (message phase, flip cards).
function WarEffortRedirectToAfterBattle({ missionId, missionEndGameId, result, navigate }) {
  useEffect(() => {
    navigate('/after-battle', {
      state: {
        gameId: missionEndGameId,
        result: result || 'win',
        isWarEffort: true,
        gameState: { warEffortMissionId: missionId, warEffortBattleType: 'enemy_hq' },
        fromWarEffortMissionOver: true,
        missionIdForDismiss: missionId
      },
      replace: true
    });
  }, [missionId, missionEndGameId, result, navigate]);
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0a0a12', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
      Loading…
    </div>
  );
}

// Mission-over screen (win or loss). Shown when mission is completed.
// All winners see the same victory screen: wareffort_success.jpg + rewards (whether they won by conquest or opponents leaving).
// Leavers get no rewards (backend does not grant); same as PVE resign — show "no rewards" and do not call grant.
function WarEffortMissionOverScreen({ playerWon, missionId, missionEndGameId, onBack, API_URL, t }) {
  const { i18n } = useTranslation();
  const [missionEndRewards, setMissionEndRewards] = useState(null);
  const [noRewardsForUser, setNoRewardsForUser] = useState(false); // true when 404 or empty (e.g. leaver)
  useEffect(() => {
    if (!missionEndGameId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const langQuery = buildOrderDetailLangQuery(i18n.language);
    fetch(`${API_URL}/battle-rewards/${missionEndGameId}?${langQuery}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => {
        if (res.status === 404) {
          setNoRewardsForUser(true);
          return null;
        }
        return res.ok ? res.json() : null;
      })
      .then((data) => {
        if (data && Array.isArray(data.rewards) && data.rewards.length > 0) {
          setMissionEndRewards(data.rewards);
        } else if (data && (data.noRewards === true || !data.rewards || data.rewards.length === 0)) {
          setNoRewardsForUser(true);
        }
      })
      .catch(() => {});
  }, [missionEndGameId, API_URL, i18n.language]);

  const handleContinue = async () => {
    const token = localStorage.getItem('token');
    // Only call grant when we have rewards (leavers have no rewards row; same as PVE resign)
    if (token && missionEndGameId && missionEndRewards && missionEndRewards.length > 0) {
      try {
        await fetch(`${API_URL}/battle-rewards/${missionEndGameId}/grant`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        console.error('Failed to grant battle rewards:', e);
      }
    }
    if (missionId) {
      addDismissedWarEffortMissionId(missionId);
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/war-effort/mission/${missionId}/dismiss-completion`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        console.error('Failed to dismiss mission completion:', e);
      }
    }
    if (onBack) onBack();
  };

  return (
    <div
      className="war-effort-mission-over"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        background: playerWon
          ? 'url(/images/wareffort_success.jpg) center/cover no-repeat'
          : 'url(/images/wareffort_lose.jpg) center/cover no-repeat',
        color: '#fff',
        padding: 24,
        textAlign: 'center',
        overflowY: 'auto'
      }}
    >
      <h2 style={{ marginBottom: 16, fontSize: '1.75rem', position: 'relative', zIndex: 1 }}>
        {playerWon ? t('warEffort.missionWon', 'Mission won') : t('warEffort.missionLost', 'You lost this mission')}
      </h2>
      <p style={{ marginBottom: 16, maxWidth: 400, color: '#ccc', position: 'relative', zIndex: 1 }}>
        {playerWon
          ? t('warEffort.missionWonCompleted', 'You have completed this mission. You received your rewards.')
          : noRewardsForUser
            ? t('warEffort.missionLostNoRewards', 'You left the mission. No rewards were granted.')
            : t('warEffort.missionLostEnded', 'The mission has ended. You still received participation rewards.')}
      </p>
      {missionEndRewards && missionEndRewards.length > 0 && (
        <>
          <p style={{ marginBottom: 12, color: '#aaa', fontSize: '0.95rem', position: 'relative', zIndex: 1 }}>
            {t('warEffort.rewardsReceived', 'Rewards received')}
          </p>
          <div
            className="war-effort-mission-over-rewards-grid"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 40,
              justifyContent: 'center',
              marginBottom: 24,
              position: 'relative',
              zIndex: 1
            }}
          >
            {missionEndRewards.map((reward, index) => (
              <WarEffortMissionOverRewardCard key={index} reward={reward} index={index} t={t} />
            ))}
          </div>
        </>
      )}
      <button
        type="button"
        onClick={handleContinue}
        style={{
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: 'bold',
          color: '#fff',
          backgroundColor: '#4ECDC4',
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer',
          position: 'relative',
          zIndex: 1,
          marginTop: 5
        }}
      >
        {t('warEffort.continue', 'Continue')}
      </button>
    </div>
  );
}

// Purpose to faction mapping (must match backend)
const PURPOSE_TO_FACTION_MAPPING = {
  'Consume': ['Asrah', 'The Hollow', 'The Forsaken Pact'],
  'Eradicate': ['Exolot Dominion', 'The Corrupted Core', 'The Iron Alliance'],
  'Colonize': ['League of Shadows', 'The Conspiracy', 'The Shadow Consortium'],
  'Protect': ['Ra\'s Children', 'The Divine Syndicate', 'The Covenant'],
  'Survive': ['Remnants of Humanity', 'The Last Crusade', 'The Silent Accord']
};

// Faction name -> purpose (for deriving opponent Command Center stripe per cell in FFA/2v2)
const FACTION_TO_PURPOSE = (() => {
  const m = {};
  Object.entries(PURPOSE_TO_FACTION_MAPPING).forEach(([purpose, factions]) => {
    (factions || []).forEach((f) => { m[f] = purpose; });
  });
  return m;
})();

// Purpose -> HQ cell ID (first faction with HQ for that purpose)
const PURPOSE_TO_HQ_CELL_ID = {
  'Consume': 333,   // India (Asrah)
  'Eradicate': 254, // Peru (Exolot Dominion)
  'Colonize': 296,  // Switzerland (League of Shadows)
  'Protect': 413,   // Egypt (Ra's Children)
  'Survive': 213    // Canada (Remnants of Humanity)
};

// Reverse: HQ cell ID -> purpose (for resolving which opponent owns an HQ in FFA)
const HQ_CELL_ID_TO_PURPOSE = Object.fromEntries(
  Object.entries(PURPOSE_TO_HQ_CELL_ID).map(([p, id]) => [id, p])
);

// Purpose -> mission type label (for mission box display)
const PURPOSE_TO_MISSION_TYPE = {
  'Consume': 'Infect key officials',
  'Eradicate': 'Sabotage production',
  'Colonize': 'Infiltrate main communities',
  'Protect': 'Weaken enemy forces',
  'Survive': 'Raid targets'
};

// Purpose -> Primary faction name (for dev mode)
const DEV_PURPOSE_TO_FACTION = {
  'Consume': 'Asrah',
  'Eradicate': 'Exolot Dominion',
  'Colonize': 'League of Shadows',
  'Protect': "Ra's Children",
  'Survive': 'Remnants of Humanity'
};

// Purpose -> color for info box display (sandy yellow, olive green, etc.)
const PURPOSE_TO_COLOR = {
  'Protect': '#C4A35A',   // sandy yellow
  'Survive': '#6B8E23',   // olive green
  'Consume': '#DC143C',   // crimson red
  'Eradicate': '#800080', // purple
  'Colonize': '#4A4A4A'   // dark gray
};

// Command Center stripe colors per purpose (base + darker for map fill)
const PURPOSE_COMMAND_CENTER_STRIPE_COLORS = {
  Survive:   { base: '#556B2F', darker: '#3d4d21' },   // remnant green, dark green
  Protect:   { base: '#DAA520', darker: '#8B6914' },   // egypt yellow, dark yellow
  Colonize:  { base: '#1A1A1A', darker: '#0a0a0a' },  // shadow black, darkest black
  Consume:   { base: '#8B0000', darker: '#5c0000' },   // asrah red, dark red
  Eradicate: { base: '#4B0082', darker: '#2a004a' }    // exolot purple, dark purple
};

// The Sentients HQ (Madagascar) - trading hub, not attackable (must match backend SENTIENTS_HQ_CELL_ID)
const SENTIENTS_HQ_CELL_ID = 457;

function madagascarOffersSeenStorageKey(missionId, userId) {
  if (missionId == null || userId == null || userId === '') {
    throw new Error('madagascarOffersSeenStorageKey: missionId and userId are required');
  }
  return `coda.warEffort.madagascarOffersSeen.${missionId}.${userId}`;
}

function readSeenMadagascarOffersGeneration(missionId, userId) {
  const raw = localStorage.getItem(madagascarOffersSeenStorageKey(missionId, userId));
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    throw new Error('readSeenMadagascarOffersGeneration: stored generation is not a number');
  }
  return n;
}

function writeSeenMadagascarOffersGeneration(missionId, userId, generation) {
  if (generation == null || !Number.isFinite(Number(generation))) {
    throw new Error('writeSeenMadagascarOffersGeneration: generation is required');
  }
  localStorage.setItem(madagascarOffersSeenStorageKey(missionId, userId), String(generation));
}

// Country ownership bonuses (cell_id -> tooltip text; shown for whoever owns the country; must match backend)
const COUNTRY_BONUS_CELL_IDS = [224, 308, 387, 354]; // Cuba, Iceland, Sri Lanka, Taiwan
const ICELAND_CELL_ID = 308;
const COUNTRY_BONUS_CELL_BY_ID = {
  224: 'countryBonusCuba',
  308: 'countryBonusIceland',
  387: 'countryBonusSriLanka',
  354: 'countryBonusTaiwan'
};

function localizeCountryBonus(cellId, t) {
  if (typeof t !== 'function') {
    throw new Error('localizeCountryBonus: t is required');
  }
  const key = COUNTRY_BONUS_CELL_BY_ID[Number(cellId)];
  if (!key) return null;
  return t(`warEffort.${key}`);
}

// HQ building page: buildings available to build
// Why the image "lower border" was unknown: (1) HQ_BUILDINGS only has image URL, no width/height or aspectRatio.
// (2) With object-fit: contain the image is scaled to fit the box while preserving aspect ratio, so the actual
// painted image can end well above the box bottom (e.g. landscape images). (3) We never read the loaded image's
// naturalWidth/naturalHeight or measured the img element, so we had no data to compute where the image content ends.
// Fix: we now read naturalWidth/naturalHeight on img load and store aspect ratio per image src, then use it in
// layout so the progress bar sits just below the actual image (see buildingImageAspectRatio state and img onLoad).
const HQ_BUILDINGS = [
  { id: 'powerplant', name: 'Powerplant', image: '/images/powerplant.png', cost: 160, description: 'Level 1: 8 energy/min. Level 2: 16 energy/min.', maxCount: 5 },
  { id: 'powerplant_upgraded', name: 'Upgraded Powerplant', image: '/images/powerplant_upgraded.png', cost: 0, description: 'Level 1: 8 energy/min. Level 2: 16 energy/min.', maxCount: 5 },
  { id: 'eclipse_array', name: 'Eclipse Array', image: '/images/array.png', cost: 100, description: 'Level 1: Reveal neutral fortification types. Level 2: Also reveal opponent fortification types.', maxCount: 1 },
  { id: 'eclipse_array_upgraded', name: 'Upgraded Eclipse Array', image: '/images/array_upgraded.png', cost: 0, description: 'Level 1: Reveal neutral fortification types. Level 2: Also reveal opponent fortification types.', maxCount: 1 },
  { id: 'command_center', name: 'Command Center', image: '/images/command.png', cost: 260, description: 'When one of your countries is attacked, if you own 3+ neighbouring countries, all your units get +1 health.', maxCount: 1 },
  { id: 'depot', name: 'Depot', image: '/images/depot.png', cost: 400, description: 'Increase storage capacity per resource by 1000.', maxCount: 5 },
  { id: 'cattle_ranch', name: 'Cattle Farm', image: '/images/cattle.png', cost: 600, description: 'Produce 50 food rations per minute and provide 500 storage. Level 2: Provide 100 per minute and 1000 storage.', maxCount: 1 },
  { id: 'cattle_ranch_upgraded', name: 'Upgraded Cattle Farm', image: '/images/cattle_upgraded.png', cost: 0, description: 'Produce 50 food rations per minute and provide 500 storage. Level 2: Provide 100 per minute and 1000 storage.', maxCount: 1 },
  { id: 'quarry', name: 'Quarry', image: '/images/stone.png', cost: 600, description: 'Produce 50 stone per minute and provide 500 storage. Level 2: Provide 100 per minute and 1000 storage.', maxCount: 1 },
  { id: 'quarry_upgraded', name: 'Upgraded Quarry', image: '/images/stone_upgraded.png', cost: 0, description: 'Produce 50 stone per minute and provide 500 storage. Level 2: Provide 100 per minute and 1000 storage.', maxCount: 1 },
  { id: 'sewers', name: 'Sewers', image: '/images/sewers.png', cost: 600, description: 'Produce 50 biomass per minute and provide 500 storage. Level 2: Provide 100 per minute and 1000 storage.', maxCount: 1 },
  { id: 'sewers_upgraded', name: 'Upgraded Sewers', image: '/images/sewers_upgraded.png', cost: 0, description: 'Produce 50 biomass per minute and provide 500 storage. Level 2: Provide 100 per minute and 1000 storage.', maxCount: 1 },
  { id: 'portal', name: 'Portal', image: '/images/erray.png', cost: 800, description: 'Produce 50 erray per minute and provide 500 storage. Level 2: Provide 100 per minute and 1000 storage.', maxCount: 1 },
  { id: 'portal_upgraded', name: 'Upgraded Portal', image: '/images/erray_updated.png', cost: 0, description: 'Produce 50 erray per minute and provide 500 storage. Level 2: Provide 100 per minute and 1000 storage.', maxCount: 1 },
  { id: 'lavender_field', name: 'Lavender Farm', image: '/images/lavender.png', cost: 600, description: 'Produce 50 lavender per minute and provide 500 storage. Level 2: Provide 100 per minute and 1000 storage.', maxCount: 1 },
  { id: 'lavender_field_upgraded', name: 'Upgraded Lavender Farm', image: '/images/lavender_upgraded.png', cost: 0, description: 'Produce 50 lavender per minute and provide 500 storage. Level 2: Provide 100 per minute and 1000 storage.', maxCount: 1 },
  { id: 'training_grounds', name: 'Training Grounds', image: '/images/training.png', cost: 300, description: 'Level 1: Keep 2 of 3 orders from conquests and subversion. Level 2: Keep all 3. (Buildings like Barracks always give 1 choice.)', maxCount: 1 },
  { id: 'training_grounds_upgraded', name: 'Upgraded Training Grounds', image: '/images/training_upgraded.png', cost: 0, description: 'Level 1: Keep 2 of 3 orders from conquests and subversion. Level 2: Keep all 3. (Buildings like Barracks always give 1 choice.)', maxCount: 1 },
  { id: 'barracks', name: 'Barracks', image: '/images/barracks.png', cost: 160, description: 'Train new units to fight for you. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'barracks_upgraded', name: 'Upgraded Barracks', image: '/images/barracks_upgraded.png', cost: 0, description: 'Train new units to fight for you. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'armory', name: 'Armory', image: '/images/armory.png', cost: 130, description: 'Build new weapons for your troops. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'armory_upgraded', name: 'Upgraded Armory', image: '/images/armory_upgraded.png', cost: 0, description: 'Build new weapons for your troops. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'drone_hangar', name: 'Drone Hangar', image: '/images/dronehangar.png', cost: 100, description: 'Create drones to support you on the battlefield. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'drone_hangar_upgraded', name: 'Upgraded Drone Hangar', image: '/images/dronehangar_upgraded.png', cost: 0, description: 'Create drones to support you on the battlefield. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'communications_hub', name: 'Communications Hub', image: '/images/comhub.png', cost: 200, description: 'Call upon mighty stratagems to aid you in battle. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'communications_hub_upgraded', name: 'Upgraded Communications Hub', image: '/images/comhub_upgraded.png', cost: 0, description: 'Call upon mighty stratagems to aid you in battle. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'intelligence_agency', name: 'Intelligence Agency', image: '/images/intel.png', cost: 200, description: 'Show what countries your opponent is attacking.', maxCount: 1 },
  { id: 'bank', name: 'Omniforge', image: '/images/bank.png', cost: 200, description: 'Train a new engineer every tick.', maxCount: 1 },
  { id: 'research_center', name: 'Research Center', image: '/images/research.png', cost: 400, description: 'Earn an additional reward at the end of the mission.', maxCount: 1 },
  { id: 'hospital', name: 'Hospital', image: '/images/hospital.png', cost: 520, description: 'Level 1: HQ defenders +1 health. Level 2: HQ defenders +1 health and +1 attack.', maxCount: 1 },
  { id: 'hospital_upgraded', name: 'Upgraded Hospital', image: '/images/hospital_upgraded.png', cost: 0, description: 'Level 1: HQ defenders +1 health. Level 2: HQ defenders +1 health and +1 attack.', maxCount: 1 },
  { id: 'hall', name: 'Hall of Ascension', image: '/images/hall.png', cost: 800, description: 'Spend resources to ascend units in your strategy for the remainder of the mission. Level 2: Allow to ascend the same unit further.', maxCount: 1 },
  { id: 'hall_upgraded', name: 'Upgraded Hall of Ascension', image: '/images/hall_upgraded.png', cost: 0, description: 'Spend resources to ascend units in your strategy for the remainder of the mission. Level 2: Allow to ascend the same unit further.', maxCount: 1 },
  { id: 'airfield', name: 'Airfield', image: '/images/airfield.png', cost: 720, description: 'Reduce deployment costs by 20%.', maxCount: 1 },
  { id: 'trade', name: 'Trade Forum', image: '/images/trade.png', cost: 500, description: 'Sell resources for energy or sell energy for resources. Prices depend on the global market. Level 2: Automate trade to a certain degree. Sell orders from your attack strategy for energy.', maxCount: 1 },
  { id: 'trade_upgraded', name: 'Upgraded Trade Forum', image: '/images/trade_upgraded.png', cost: 0, description: 'Sell resources for energy or sell energy for resources. Prices depend on the global market. Level 2: Automate trade to a certain degree. Sell orders from your attack strategy for energy.', maxCount: 1 },
  { id: 'edc', name: 'Energy Distribution Center', image: '/images/edc.png', cost: 100, resourceCosts: { stone: 100, biomass: 100, erray: 100, lavender: 100, food_rations: 100 }, description: 'Level 1: Increase energy capacity by 500. Level 2: Increase energy capacity by another 500. Stacks with Cuba.', maxCount: 1 },
  { id: 'edc_upgraded', name: 'Upgraded Energy Distribution Center', image: '/images/edc_upgrade.png', cost: 0, description: 'Level 1: Increase energy capacity by 500. Level 2: Increase energy capacity by another 500. Stacks with Cuba.', maxCount: 1 }
];

// Sound for clicking each placed building (building id -> /sounds/<name>.mp3)
const BUILDING_CLICK_SOUNDS = {
  airfield: '/sounds/airfield.mp3',
  bank: '/sounds/bank.mp3',
  cattle_ranch: '/sounds/cattle.mp3',
  cattle_ranch_upgraded: '/sounds/cattle.mp3',
  command_center: '/sounds/command.mp3',
  depot: '/sounds/depot.mp3',
  eclipse_array: '/sounds/eclipse.mp3',
  eclipse_array_upgraded: '/sounds/eclipse.mp3',
  hospital: '/sounds/hospital.mp3',
  hospital_upgraded: '/sounds/hospital.mp3',
  hall: '/sounds/hall.mp3',
  hall_upgraded: '/sounds/hall.mp3',
  trade: '/sounds/forum.mp3',
  trade_upgraded: '/sounds/forum.mp3',
  edc: '/sounds/energy_distrib.mp3',
  edc_upgraded: '/sounds/energy_distrib.mp3',
  intelligence_agency: '/sounds/intel.mp3',
  lavender_field: '/sounds/lavender.mp3',
  lavender_field_upgraded: '/sounds/lavender.mp3',
  portal: '/sounds/portal.mp3',
  portal_upgraded: '/sounds/portal.mp3',
  powerplant: '/sounds/powerplant.mp3',
  powerplant_upgraded: '/sounds/powerplant.mp3',
  quarry: '/sounds/quarry.mp3',
  quarry_upgraded: '/sounds/quarry.mp3',
  research_center: '/sounds/research.mp3',
  sewers: '/sounds/sewers.mp3',
  sewers_upgraded: '/sounds/sewers.mp3',
  training_grounds: '/sounds/training.mp3',
  training_grounds_upgraded: '/sounds/training.mp3',
  barracks: '/sounds/barracks.mp3',
  barracks_upgraded: '/sounds/barracks.mp3',
  armory: '/sounds/armory.mp3',
  armory_upgraded: '/sounds/armory.mp3',
  drone_hangar: '/sounds/dronehangar.mp3',
  drone_hangar_upgraded: '/sounds/dronehangar.mp3',
  communications_hub: '/sounds/comhub.mp3',
  communications_hub_upgraded: '/sounds/comhub.mp3'
};

const BUILDING_UPGRADES = {
  powerplant: { upgradedId: 'powerplant_upgraded', cost: 200 },
  eclipse_array: { upgradedId: 'eclipse_array_upgraded', cost: 350 },
  training_grounds: { upgradedId: 'training_grounds_upgraded', cost: 800 },
  drone_hangar: { upgradedId: 'drone_hangar_upgraded', cost: 400 },
  communications_hub: { upgradedId: 'communications_hub_upgraded', cost: 600 },
  barracks: { upgradedId: 'barracks_upgraded', cost: 500 },
  armory: { upgradedId: 'armory_upgraded', cost: 450 },
  hospital: { upgradedId: 'hospital_upgraded', cost: 900 },
  hall: { upgradedId: 'hall_upgraded', cost: 900 },
  trade: { upgradedId: 'trade_upgraded', cost: 700 },
  edc: { upgradedId: 'edc_upgraded', cost: 250, resourceCosts: { stone: 250, biomass: 250, erray: 250, lavender: 250, food_rations: 250 } },
  quarry: { upgradedId: 'quarry_upgraded', cost: 900 },
  sewers: { upgradedId: 'sewers_upgraded', cost: 900 },
  cattle_ranch: { upgradedId: 'cattle_ranch_upgraded', cost: 900 },
  lavender_field: { upgradedId: 'lavender_field_upgraded', cost: 900 },
  portal: { upgradedId: 'portal_upgraded', cost: 1000 }
};

/**
 * Overlay display costs/rates from GET /war-effort/balance (config/warEffortBalance.json).
 * Mutates module-level tables so build/fortify/upgrade UI matches server authority.
 */
function applyWarEffortBalanceToUi(balance) {
  if (!balance || typeof balance !== 'object') {
    throw new Error('War Effort balance payload is required');
  }
  if (!balance.buildingPrices || typeof balance.buildingPrices !== 'object') {
    throw new Error('War Effort balance.buildingPrices is required');
  }
  if (!balance.buildingUpgradePrices || typeof balance.buildingUpgradePrices !== 'object') {
    throw new Error('War Effort balance.buildingUpgradePrices is required');
  }
  if (!balance.fortificationCosts || typeof balance.fortificationCosts !== 'object') {
    throw new Error('War Effort balance.fortificationCosts is required');
  }
  if (!balance.energyPerTick || typeof balance.energyPerTick !== 'object') {
    throw new Error('War Effort balance.energyPerTick is required');
  }
  if (balance.storeCapacity?.perDepot == null) {
    throw new Error('War Effort balance.storeCapacity.perDepot is required');
  }

  for (const building of HQ_BUILDINGS) {
    if (balance.buildingPrices[building.id] == null) {
      throw new Error(`War Effort balance missing buildingPrices.${building.id}`);
    }
    building.cost = balance.buildingPrices[building.id];
    if (balance.buildingResourcePrices?.[building.id]) {
      building.resourceCosts = { ...balance.buildingResourcePrices[building.id] };
    }
  }

  for (const [baseId, upgrade] of Object.entries(BUILDING_UPGRADES)) {
    if (balance.buildingUpgradePrices[baseId] == null) {
      throw new Error(`War Effort balance missing buildingUpgradePrices.${baseId}`);
    }
    upgrade.cost = balance.buildingUpgradePrices[baseId];
    if (balance.buildingUpgradeResourcePrices?.[baseId]) {
      upgrade.resourceCosts = { ...balance.buildingUpgradeResourcePrices[baseId] };
    }
  }

  for (const fort of FORTIFICATION_TYPES) {
    if (balance.fortificationCosts[fort.type] == null) {
      throw new Error(`War Effort balance missing fortificationCosts.${fort.type}`);
    }
    fort.cost = balance.fortificationCosts[fort.type];
  }

  ENERGY_PRODUCTION_BASE_PER_MINUTE = balance.energyPerTick.base;
  ENERGY_PRODUCTION_POWERPLANT_PER_MINUTE = balance.energyPerTick.powerplantL1;
  ENERGY_PRODUCTION_POWERPLANT_UPGRADED_PER_MINUTE = balance.energyPerTick.powerplantL2;
  ENERGY_PRODUCTION_SRI_LANKA_PER_MINUTE = balance.energyPerTick.sriLankaBonus;
  DEPOT_STORAGE_BONUS = balance.storeCapacity.perDepot;
}

/** Buildings that share a cap: base + upgraded count toward the same limit */
const BUILDING_CAP_GROUPS = {
  powerplant: ['powerplant', 'powerplant_upgraded'],
  eclipse_array: ['eclipse_array', 'eclipse_array_upgraded'],
  training_grounds: ['training_grounds', 'training_grounds_upgraded'],
  hospital: ['hospital', 'hospital_upgraded'],
  hall: ['hall', 'hall_upgraded'],
  trade: ['trade', 'trade_upgraded'],
  edc: ['edc', 'edc_upgraded'],
  barracks: ['barracks', 'barracks_upgraded'],
  armory: ['armory', 'armory_upgraded'],
  drone_hangar: ['drone_hangar', 'drone_hangar_upgraded'],
  communications_hub: ['communications_hub', 'communications_hub_upgraded'],
  quarry: ['quarry', 'quarry_upgraded'],
  sewers: ['sewers', 'sewers_upgraded'],
  cattle_ranch: ['cattle_ranch', 'cattle_ranch_upgraded'],
  lavender_field: ['lavender_field', 'lavender_field_upgraded'],
  portal: ['portal', 'portal_upgraded']
};

/** HQ build menu tabs (fixed order). Base building ids only (upgraded variants are not separate menu rows). */
const BUILD_MENU_TAB_KEYS = ['economy', 'orderGeneration', 'defensive', 'espionage'];

const BUILD_MENU_TAB_BASE_IDS = {
  economy: ['airfield', 'bank', 'cattle_ranch', 'depot', 'edc', 'lavender_field', 'portal', 'powerplant', 'quarry', 'research_center', 'sewers', 'trade'],
  orderGeneration: ['armory', 'barracks', 'communications_hub', 'drone_hangar', 'training_grounds'],
  defensive: ['command_center', 'hall', 'hospital'],
  espionage: ['eclipse_array', 'intelligence_agency']
};

(function validateBuildMenuTabCoverage() {
  const visibleMenuIds = new Set(
    HQ_BUILDINGS.filter((b) => !Object.values(BUILDING_UPGRADES).some((u) => u.upgradedId === b.id)).map((b) => b.id)
  );
  const categorized = new Set();
  for (const tabKey of BUILD_MENU_TAB_KEYS) {
    const ids = BUILD_MENU_TAB_BASE_IDS[tabKey];
    if (!ids || ids.length === 0) {
      throw new Error(`War Effort build menu: empty or missing id list for tab "${tabKey}"`);
    }
    for (const id of ids) {
      if (categorized.has(id)) {
        throw new Error(`War Effort build menu: building id "${id}" assigned to more than one tab`);
      }
      categorized.add(id);
    }
  }
  for (const id of visibleMenuIds) {
    if (!categorized.has(id)) {
      throw new Error(`War Effort build menu: no tab category for building id "${id}"`);
    }
  }
  for (const id of categorized) {
    if (!visibleMenuIds.has(id)) {
      throw new Error(`War Effort build menu: tab references unknown or non-menu building id "${id}"`);
    }
  }
})();

/** Inline SVGs for HQ build menu tabs (currentColor, matches tab label styling). */
function WarEffortBuildMenuTabIcon({ tabKey }) {
  const cls = 'war-effort-build-menu-tab__icon';
  const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round'
  };
  switch (tabKey) {
    case 'economy':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <line x1="12" y1="1" x2="12" y2="23" {...stroke} />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" {...stroke} />
        </svg>
      );
    case 'orderGeneration':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <path d="M12 5v2M9 3l1.5 2M15 3l-1.5 2" {...stroke} />
          <circle cx="12" cy="10" r="3" {...stroke} />
          <path d="M9 21v-5a3 3 0 0 1 6 0v5" {...stroke} />
          <path d="M15 12h5.5l1 2h-3l-1.5-2" {...stroke} />
          <path d="M20.5 14v4" {...stroke} />
        </svg>
      );
    case 'defensive':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" {...stroke} />
        </svg>
      );
    case 'espionage':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <ellipse cx="12" cy="6.5" rx="8" ry="2" {...stroke} />
          <path d="M5 6.5h14v3H5z" fill="currentColor" stroke="none" />
          <circle cx="9.5" cy="14" r="2" {...stroke} />
          <circle cx="14.5" cy="14" r="2" {...stroke} />
          <path d="M11.5 14h1" {...stroke} />
          <path d="M8.5 18.5c1.2 1.8 2.8 2.5 3.5 2.5s2.3-.7 3.5-2.5" {...stroke} />
        </svg>
      );
    default:
      throw new Error(`WarEffortBuildMenuTabIcon: unknown tabKey "${tabKey}"`);
  }
}

/** Inline SVGs for owned country / HQ modal action buttons (currentColor). */
function WarEffortOwnedOptionIcon({ action }) {
  const cls = 'war-effort-owned-option-btn__icon';
  const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round'
  };
  switch (action) {
    case 'fortify':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" {...stroke} />
        </svg>
      );
    case 'boredrive':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="12" r="4" {...stroke} />
          <line x1="12" y1="1" x2="12" y2="5" {...stroke} />
          <line x1="12" y1="19" x2="12" y2="23" {...stroke} />
          <line x1="4.22" y1="4.22" x2="7.05" y2="7.05" {...stroke} />
          <line x1="16.95" y1="16.95" x2="19.78" y2="19.78" {...stroke} />
          <line x1="1" y1="12" x2="5" y2="12" {...stroke} />
          <line x1="19" y1="12" x2="23" y2="12" {...stroke} />
          <line x1="4.22" y1="19.78" x2="7.05" y2="16.95" {...stroke} />
          <line x1="16.95" y1="7.05" x2="19.78" y2="4.22" {...stroke} />
        </svg>
      );
    case 'attack':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <path d="M4.5 4.5l15 15" {...stroke} />
          <path d="M4.5 4.5l2.5-2.5M4.5 4.5l-2.5 2.5" {...stroke} />
          <path d="M19.5 19.5l-2.5 2.5M19.5 19.5l2.5-2.5" {...stroke} />
          <path d="M19.5 4.5l-15 15" {...stroke} />
          <path d="M19.5 4.5l2.5 2.5M19.5 4.5l2.5-2.5" {...stroke} />
          <path d="M4.5 19.5l2.5-2.5M4.5 19.5l-2.5 2.5" {...stroke} />
        </svg>
      );
    case 'defenseStrategy':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <rect x="3" y="7" width="12" height="15" rx="1.5" {...stroke} />
          <rect x="9" y="2" width="12" height="15" rx="1.5" {...stroke} />
          <line x1="13" y1="6" x2="17" y2="6" {...stroke} />
          <line x1="13" y1="9" x2="17" y2="9" {...stroke} />
        </svg>
      );
    case 'showHq':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <path d="M5 4l5 5" {...stroke} />
          <path d="M10 9l-2 2v5h5l2-2" {...stroke} />
          <path d="M3 3l4 4" {...stroke} />
          <path d="M19 5l-5 5" {...stroke} />
          <circle cx="18.5" cy="5.5" r="2" {...stroke} />
          <path d="M14 10l-2 2v4.5h4.5l2-2" {...stroke} />
        </svg>
      );
    default:
      throw new Error(`WarEffortOwnedOptionIcon: unknown action "${action}"`);
  }
}

function WarEffortOwnedOptionLabel({ action, children }) {
  return (
    <span className="war-effort-owned-option-btn__label">
      <WarEffortOwnedOptionIcon action={action} />
      <span>{children}</span>
    </span>
  );
}

function getBuildingCapInfo(buildingId) {
  const baseId = buildingId.endsWith('_upgraded') ? buildingId.replace(/_upgraded$/, '') : buildingId;
  const groupIds = BUILDING_CAP_GROUPS[baseId] || [baseId];
  const baseBuilding = HQ_BUILDINGS.find((b) => b.id === baseId);
  const cap = baseBuilding?.maxCount ?? 999;
  return { groupIds, cap };
}

const BUILDINGS_WITH_PRODUCER_UI = ['barracks', 'barracks_upgraded', 'armory', 'armory_upgraded', 'drone_hangar', 'drone_hangar_upgraded', 'communications_hub', 'communications_hub_upgraded'];
const RESOURCE_STORAGE_BUILDING_IDS = [
  'lavender_field', 'lavender_field_upgraded',
  'cattle_ranch', 'cattle_ranch_upgraded',
  'quarry', 'quarry_upgraded',
  'sewers', 'sewers_upgraded',
  'portal', 'portal_upgraded'
];
const DEPOT_BUILDING_ID = 'depot';
let DEPOT_STORAGE_BONUS = 1000;
const HQ_RESOURCE_COST_ORDER = ['stone', 'biomass', 'erray', 'lavender', 'food_rations'];
const HQ_STORAGE_RESOURCE_BY_BUILDING_ID = {
  quarry: 'stone',
  quarry_upgraded: 'stone',
  sewers: 'biomass',
  sewers_upgraded: 'biomass',
  cattle_ranch: 'food_rations',
  cattle_ranch_upgraded: 'food_rations',
  lavender_field: 'lavender',
  lavender_field_upgraded: 'lavender',
  portal: 'erray',
  portal_upgraded: 'erray'
};

function getStoredHqResourcesFromPlacements(placedBuildings) {
  const storage = { stone: 0, biomass: 0, food_rations: 0, erray: 0, lavender: 0 };
  for (const p of placedBuildings || []) {
    const resource = HQ_STORAGE_RESOURCE_BY_BUILDING_ID[p.building?.id];
    if (!resource) continue;
    storage[resource] += typeof p.currentProduced === 'number' ? p.currentProduced : 0;
  }
  return storage;
}

function canAffordHqResourceCosts(resourceCosts, stored) {
  if (!resourceCosts) return true;
  for (const resource of HQ_RESOURCE_COST_ORDER) {
    const need = resourceCosts[resource] || 0;
    if (need > 0 && (stored[resource] || 0) < need) return false;
  }
  return true;
}

function BuildingHqCostLine({ energyCost, resourceCosts, energyAffordable, storedResources, energyDiscountLabel }) {
  const { t } = useTranslation();
  const resourceEntries = HQ_RESOURCE_COST_ORDER
    .map((resource) => ({ resource, need: resourceCosts?.[resource] || 0 }))
    .filter((entry) => entry.need > 0);
  return (
    <span className="war-effort-build-menu-card__cost-line">
      <span className={energyAffordable ? 'war-effort-build-menu-card__cost' : 'war-effort-build-menu-card__cost--insufficient'}>
        {t('warEffort.energyCostLabel', { cost: energyCost })}{energyDiscountLabel || ''}
      </span>
      {resourceEntries.map(({ resource, need }) => {
        const have = storedResources?.[resource] || 0;
        const ok = have >= need;
        return (
          <span
            key={resource}
            className={`war-effort-build-menu-card__cost-item${ok ? '' : ' war-effort-build-menu-card__cost-item--insufficient'}`}
          >
            <img src={HALL_RESOURCE_ICONS[resource]} alt={t(`warEffort.resourceNames.${resource}`)} />
            {need}
          </span>
        );
      })}
    </span>
  );
}

function countPlacedDepots(buildings) {
  return buildings.filter((p) => p.building?.id === DEPOT_BUILDING_ID).length;
}

function getStoragePerMinuteForBuildingId(buildingId, purpose) {
  if (!RESOURCE_STORAGE_BUILDING_IDS.includes(buildingId)) {
    throw new Error(`getStoragePerMinuteForBuildingId: ${buildingId} is not a resource storage building`);
  }
  if (purpose == null || String(purpose).trim() === '') {
    throw new Error('getStoragePerMinuteForBuildingId: purpose is required');
  }
  const isPortal = buildingId === 'portal' || buildingId === 'portal_upgraded';
  if (isPortal && String(purpose).trim().toLowerCase() === 'colonize') {
    return buildingId === 'portal_upgraded' ? 300 : 150;
  }
  return buildingId.endsWith('_upgraded') ? 250 : 100;
}

function getStorageMaxForBuildingId(buildingId, depotCount, purpose) {
  if (!RESOURCE_STORAGE_BUILDING_IDS.includes(buildingId)) {
    throw new Error(`getStorageMaxForBuildingId: ${buildingId} is not a resource storage building`);
  }
  if (depotCount == null) {
    throw new Error('getStorageMaxForBuildingId: depotCount is required');
  }
  if (purpose == null || String(purpose).trim() === '') {
    throw new Error('getStorageMaxForBuildingId: purpose is required');
  }
  const isPortal = buildingId === 'portal' || buildingId === 'portal_upgraded';
  let baseMax;
  if (isPortal && String(purpose).trim().toLowerCase() === 'colonize') {
    baseMax = buildingId === 'portal_upgraded' ? 1600 : 800;
  } else {
    baseMax = buildingId.endsWith('_upgraded') ? 1000 : 500;
  }
  return baseMax + depotCount * DEPOT_STORAGE_BONUS;
}

/**
 * Stable key for lastOpponentAction so we can compare by content, not reference.
 * Server sends an object each time (new reference); we only play sound when content actually changed.
 */
function getStableOpponentActionKey(data) {
  if (data == null) return null;
  if (typeof data === 'string') return data.trim() || null;
  if (data.type === 'subversion') {
    return `subversion:${data.username || ''}:${data.countryName || ''}:${data.countryCellId ?? ''}`;
  }
  if (data.type === 'attack') {
    return `attack:${data.username || ''}:${data.countryName || ''}:${data.countryCellId ?? ''}:${data.won === true}`;
  }
  return null;
}

function getLastPlayerActionDisplay(data, stateCellId, translate) {
  if (data == null) return null;
  if (typeof data === 'string') {
    throw new Error('getLastPlayerActionDisplay: lastPlayerAction must be a structured object');
  }
  const country = data.countryName ? localizeCountryName(data.countryName, translate) : '';
  if (data.type === 'subversion') {
    return {
      message: translate('warEffort.lastYouSubversion', { country }),
      cellId: data.cellId ?? stateCellId ?? null
    };
  }
  if (data.type === 'attack') {
    const won = data.won === true;
    const message = country
      ? translate(won ? 'warEffort.lastYouAttackedAndWon' : 'warEffort.lastYouAttackedAndLost', { country })
      : translate(won ? 'warEffort.lastYouAttackedAndWonNoCountry' : 'warEffort.lastYouAttackedAndLostNoCountry');
    return { message, cellId: data.cellId ?? stateCellId ?? null };
  }
  if (data.type === 'lostCountry') {
    const message = data.username
      ? translate('warEffort.lastYouLostCountryTo', { country, username: data.username })
      : translate('warEffort.lastYouLostCountry', { country });
    return { message, cellId: data.cellId ?? stateCellId ?? null };
  }
  throw new Error(`getLastPlayerActionDisplay: unknown type ${data.type}`);
}

function getLastOpponentActionDisplay(data, stateCellId, translate) {
  if (data == null) return null;
  if (typeof data === 'string') return { message: data, cellId: stateCellId ?? null };
  const country = data.countryName ? localizeCountryName(data.countryName, translate) : '';
  if (data.type === 'subversion') {
    return {
      message: translate('warEffort.lastOpponentSubversion', { username: data.username, country }),
      cellId: data.countryCellId ?? stateCellId ?? null
    };
  }
  if (data.type === 'attack') {
    const won = data.won === true;
    const message = country
      ? translate(won ? 'warEffort.lastOpponentAttackedAndWon' : 'warEffort.lastOpponentAttackedAndLost', { username: data.username, country })
      : translate(won ? 'warEffort.lastOpponentAttackedAndWonNoCountry' : 'warEffort.lastOpponentAttackedAndLostNoCountry', { username: data.username });
    return { message, cellId: data.countryCellId ?? stateCellId ?? null };
  }
  return null;
}

function WarEffortFortification({ mission, user, onBack, onFortificationComplete, onLogout, showBuildingPage, onBuildingPageChange }) {
  const { t, i18n: i18nInstance } = useTranslation();
  const displayCountry = (englishName) => localizeCountryName(englishName, t);
  const displayCountryForCell = (cellId) => localizeCountryName(getCountryFromCellId(cellId), t);
  const displayFaction = (englishName) => localizeFactionName(englishName, t);
  const navigate = useNavigate();
  const location = useLocation();
  const [hqBuildingCountryName, setHqBuildingCountryName] = useState('');
  const [showBuildMenu, setShowBuildMenu] = useState(false);
  const [buildMenuTab, setBuildMenuTab] = useState('economy');
  const [buildingForPlacement, setBuildingForPlacement] = useState(null);
  const [placementMouse, setPlacementMouse] = useState({ x: 0, y: 0 });
  const [placedBuildings, setPlacedBuildings] = useState([]);
  const [selectedPlacementId, setSelectedPlacementId] = useState(null);
  const [collectSparkPlacementId, setCollectSparkPlacementId] = useState(null);
  const [collectSparkAmount, setCollectSparkAmount] = useState(null);
  const [trainingResult, setTrainingResult] = useState(null);
  const [barracksOrdersModal, setBarracksOrdersModal] = useState({ show: false, placementId: null, buildingId: null, orders: [], loading: false });
  const [barracksOrdersModalSelectedIds, setBarracksOrdersModalSelectedIds] = useState([]);
  const [barracksEnrichedOrders, setBarracksEnrichedOrders] = useState([]);
  const [attackStrategyModal, setAttackStrategyModal] = useState({ show: false, loading: false, strategy: null, deactivatedSlots: [], hallAscensions: [], canSellUnits: false, unitSellPrices: null });
  const [attackStrategySellingKey, setAttackStrategySellingKey] = useState(null);
  const [attackStrategyTooltipRect, setAttackStrategyTooltipRect] = useState(null);
  const [hallAscensionModal, setHallAscensionModal] = useState({
    show: false,
    loading: false,
    hallLevel: 0,
    storage: null,
    units: [],
    selectedOrderId: null,
    selectedOccurrence: null,
    result: null,
    error: null
  });
  const [hallAscensionTooltip, setHallAscensionTooltip] = useState(null);
  const [tradeForum, setTradeForum] = useState(null);
  const [tradeForumAmounts, setTradeForumAmounts] = useState({});
  const [tradeForumError, setTradeForumError] = useState(null);
  const [tradeForumBusyKey, setTradeForumBusyKey] = useState(null);
  const [tradeForumAutoDraft, setTradeForumAutoDraft] = useState(null);
  const [tradeForumAutoSaving, setTradeForumAutoSaving] = useState(false);
  const [placementRefreshTrigger, setPlacementRefreshTrigger] = useState(0);
  const [powerplantFlashPlacements, setPowerplantFlashPlacements] = useState([]);
  const [unlockedBuildingIds, setUnlockedBuildingIds] = useState(['powerplant', 'barracks', 'training_grounds']);
  // Per image src: aspect ratio (width/height) from naturalWidth/naturalHeight on load; used to size producer image area so progress bar sits at true image bottom
  const [buildingImageAspectRatio, setBuildingImageAspectRatio] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedFortification, setSelectedFortification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attackDialog, setAttackDialog] = useState({ show: false, countryCellId: null, fortificationType: null, countryName: null, selectedStrategyId: null });
  const [enemyHqAttackInfo, setEnemyHqAttackInfo] = useState(null);
  const [attackEnemyHQDialog, setAttackEnemyHQDialog] = useState({ show: false, countryName: null, hqCellId: null, movementCost: null, selectedStrategyId: null, defenderUserId: null });
  const [attackOtherCountryDialog, setAttackOtherCountryDialog] = useState({ show: false, countryCellId: null, countryName: null, ownerFaction: null, selectedStrategyId: null, isOpponentUnfortified: false });
  const [ownedCountryChoice, setOwnedCountryChoice] = useState(null);
  const [tectonicBoredriveCost, setTectonicBoredriveCost] = useState(null);
  const [tectonicBoredriveLoading, setTectonicBoredriveLoading] = useState(false);
  const [tectonicBoredriveResult, setTectonicBoredriveResult] = useState(null);
  const [ownedHQChoice, setOwnedHQChoice] = useState(null);
  const [tradingHubOpen, setTradingHubOpen] = useState(false);
  const [tradingHubStatus, setTradingHubStatus] = useState(null);
  const [tradingHubLoading, setTradingHubLoading] = useState(false);
  const [tradingHubProtectCellId, setTradingHubProtectCellId] = useState(null);
  const [tradingHubSubvertCellId, setTradingHubSubvertCellId] = useState(null);
  const [shopSlotCellId, setShopSlotCellId] = useState({}); // { slotIndex: cellId } for shop items that need a target (Protect, Subvert, Structural Exposure, Speedpass)
  const [shopCountdownTick, setShopCountdownTick] = useState(() => Date.now()); // tick every 1s to update countdown display and refetch when slot reveal time reached
  const [protectedCellId, setProtectedCellId] = useState(null); // Trading Hub protected country (from mission-status)
  const [tradingHubBoosterOrders, setTradingHubBoosterOrders] = useState(null); // 3 orders shown after buying booster bundle
  const [gambleWonItem, setGambleWonItem] = useState(null); // { id, nameKey, descriptionKey, image } when player buys Gamble in Trading Hub
  const [achievementUnlocked, setAchievementUnlocked] = useState(null);
  const { current: pendingToast, dismissCurrent: dismissPendingToast } = usePendingAchievementToasts();
  const [energyCooldownDisplaySeconds, setEnergyCooldownDisplaySeconds] = useState(null); // live countdown for free energy (updated every second)
  const [reconquerCooldowns, setReconquerCooldowns] = useState([]); // [{ cell_id, remaining_seconds }] - countries we cannot reconquer yet
  const [cellsUnderAttack, setCellsUnderAttack] = useState([]); // Cell IDs with an active battle (crimson glow, block duplicate attack)
  const [pendingEliteDefense, setPendingEliteDefense] = useState(null);
  const [pendingEliteAttackWait, setPendingEliteAttackWait] = useState(null);
  const [eliteDefenseBusy, setEliteDefenseBusy] = useState(false);
  const [eliteDefenseActionError, setEliteDefenseActionError] = useState(null);
  const [trainingGroundsOrdersToKeep, setTrainingGroundsOrdersToKeep] = useState(1); // 1, 2, or 3 - from mission-status
  const [lastPlayerAction, setLastPlayerAction] = useState(null); // Last action for Your Purpose box (e.g. "You have just attacked Austria and won.")
  const [lastPlayerActionCellId, setLastPlayerActionCellId] = useState(null); // Cell ID for hover highlight on map (mission/last action)
  const [lastOpponentAction, setLastOpponentAction] = useState(null); // Opponent's last attack (country + outcome), same style as player; human or bot
  const [lastOpponentActionCellId, setLastOpponentActionCellId] = useState(null); // Cell ID for hover highlight on map
  const [opponentHqBuildingIds, setOpponentHqBuildingIds] = useState([]); // Opponent HQ building IDs for tooltip (1v1)
  const [opponentHqBuildingIdsByUser, setOpponentHqBuildingIdsByUser] = useState({}); // FFA/2v2: { [userId]: building_id[] } for tooltip
  const [viewerHasEclipseArrayUpgraded, setViewerHasEclipseArrayUpgraded] = useState(false); // Eclipse Array: show opponent fortification bonuses in tooltip only when true (from mission-status)
  const [viewerHasIntelligenceAgency, setViewerHasIntelligenceAgency] = useState(false); // Intelligence Agency: show opponent attack targets in info box (from mission-status)
  const [opponentAttackTargets, setOpponentAttackTargets] = useState([]); // [{ userId, username, countryCellId, countryName }] — only when viewer has Intelligence Agency
  const [activeShopEffects, setActiveShopEffects] = useState([]); // Trading Hub effects bought and still in effect: [{ effectType, expiresAt?, remainingTicks? }]
  const [territoryBonuses, setTerritoryBonuses] = useState(null); // Ownership / Command Center production bonuses from mission-status
  const [devBotEnergy, setDevBotEnergy] = useState(null); // Dev: bot energy (when mission has 1v1 or FFA bot)
  const [devBotCurrentAttack, setDevBotCurrentAttack] = useState(null); // Dev: { countryCellId, countryName } when bot is attacking
  const [devHasBot, setDevHasBot] = useState(false); // Dev: mission has a bot slot (so we show bot status block)
  const [fortifiedCountryUnderAttackWarning, setFortifiedCountryUnderAttackWarning] = useState(null); // { countryNames: string[] } - red box for 5s
  const fortifiedCountryWarningTimerRef = useRef(null);
  const [attackFromCountry, setAttackFromCountry] = useState(null);
  const [missionSubmissionHoverCellId, setMissionSubmissionHoverCellId] = useState(null);
  const [expandedInfoSlots, setExpandedInfoSlots] = useState([]); // Array of slot numbers (1-4) that are expanded; multiple can be open at once
  const [ceaseFireRemainingSeconds, setCeaseFireRemainingSeconds] = useState(0);
  const [opponentFortifiedCeaseFireRemainingSeconds, setOpponentFortifiedCeaseFireRemainingSeconds] = useState(0);
  const [conquestAttackerCeaseFireRemainingSeconds, setConquestAttackerCeaseFireRemainingSeconds] = useState(0);
  const [defenseStrategyDialog, setDefenseStrategyDialog] = useState({
    show: false,
    cellId: null,
    countryName: null,
    selectedStrategyId: null,
    useEverywhere: false,
    loading: false,
    saving: false
  });
  const [ownedCountryDefenseStrategyId, setOwnedCountryDefenseStrategyId] = useState(null);
  const [ownedCountryDefenseStrategyLoading, setOwnedCountryDefenseStrategyLoading] = useState(false);
  const [showMadagascarTradeHint, setShowMadagascarTradeHint] = useState(false);
  const [madagascarOffersGeneration, setMadagascarOffersGeneration] = useState(null);
  const [seenMadagascarOffersGeneration, setSeenMadagascarOffersGeneration] = useState(null);
  const [madagascarTradingBlocked, setMadagascarTradingBlocked] = useState(false);
  const madagascarHintHideTimerRef = useRef(null);
  const madagascarPopupGenerationRef = useRef(null);
  const madagascarAutoRestockTimerRef = useRef(null);
  const openTradingHubAfterMapRef = useRef(false);
  const openBuildMenuAfterBuildingPageRef = useRef(false);
  const [worldMapResetTrigger, setWorldMapResetTrigger] = useState(0);
  const errorAutoCloseTimerRef = useRef(null);
  const prevEnergyRef = useRef(null);
  const lastOpponentActionRef = useRef(null);
  const lastOpponentActionKeyRef = useRef(null);
  const tradingHubAmbienceRef = useRef(null);
  // Keep last displayable neutral_fortifications so map never flashes empty when state is briefly overwritten (e.g. on tick).
  const neutralFortificationsStableRef = useRef(null);
  const [, setBalanceRevision] = useState(0);
  // Auto-close red error messages (e.g. "You do not own this country") after 5 seconds
  useEffect(() => {
    if (error == null) return;
    if (errorAutoCloseTimerRef.current) clearTimeout(errorAutoCloseTimerRef.current);
    errorAutoCloseTimerRef.current = setTimeout(() => {
      setError(null);
      errorAutoCloseTimerRef.current = null;
    }, 5000);
    return () => {
      if (errorAutoCloseTimerRef.current) {
        clearTimeout(errorAutoCloseTimerRef.current);
        errorAutoCloseTimerRef.current = null;
      }
    };
  }, [error]);

  // Load balance config so UI costs/rates match config/warEffortBalance.json
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Not authenticated');
      return undefined;
    }
    let cancelled = false;
    fetch(`${API_URL}/war-effort/balance`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Failed to load War Effort balance (${res.status})`);
        }
        return res.json();
      })
      .then((balance) => {
        if (cancelled) return;
        applyWarEffortBalanceToUi(balance);
        setBalanceRevision((n) => n + 1);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('War Effort balance load failed:', err);
        setError(err.message || 'Failed to load War Effort balance');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Stop trading hub ambience when leaving the hub
  useEffect(() => {
    if (!tradingHubOpen && tradingHubAmbienceRef.current) {
      tradingHubAmbienceRef.current.pause();
      tradingHubAmbienceRef.current.currentTime = 0;
      tradingHubAmbienceRef.current = null;
    }
  }, [tradingHubOpen]);
  useEffect(() => {
    preloadWarEffortMapPaintImages();
  }, []);
  // When user clicks Trading from building page: switch to world map first, then open Trading Hub
  useEffect(() => {
    if (!showBuildingPage && openTradingHubAfterMapRef.current) {
      openTradingHubAfterMapRef.current = false;
      setTradingHubOpen(true);
    }
  }, [showBuildingPage]);
  // When user clicks Build from world map: switch to building page first, then open build menu
  useEffect(() => {
    if (showBuildingPage && openBuildMenuAfterBuildingPageRef.current) {
      openBuildMenuAfterBuildingPageRef.current = false;
      setShowBuildMenu(true);
    }
  }, [showBuildingPage]);
  useEffect(() => {
    if (showBuildMenu) {
      setBuildMenuTab('economy');
    }
  }, [showBuildMenu]);
  const buildMenuBuildingsForTab = useMemo(() => {
    const orderInTab = BUILD_MENU_TAB_BASE_IDS[buildMenuTab];
    if (!orderInTab) {
      throw new Error(`War Effort build menu: invalid tab "${buildMenuTab}"`);
    }
    const indexMap = new Map(orderInTab.map((id, i) => [id, i]));
    return [...HQ_BUILDINGS]
      .filter((b) => !Object.values(BUILDING_UPGRADES).some((u) => u.upgradedId === b.id))
      .filter((b) => indexMap.has(b.id))
      .sort((a, b) => indexMap.get(a.id) - indexMap.get(b.id));
  }, [buildMenuTab]);
  // Format initial mission data
  const formatMissionData = (missionData) => {
    if (!missionData) return null;
    const formatted = { ...missionData };
    // Handle PostgreSQL array format for fortified countries
    if (formatted.player1_fortified_countries && !Array.isArray(formatted.player1_fortified_countries)) {
      if (typeof formatted.player1_fortified_countries === 'string') {
        const cleaned = formatted.player1_fortified_countries.replace(/[{}]/g, '');
        formatted.player1_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
      } else {
        formatted.player1_fortified_countries = [];
      }
    }
    if (formatted.player2_fortified_countries && !Array.isArray(formatted.player2_fortified_countries)) {
      if (typeof formatted.player2_fortified_countries === 'string') {
        const cleaned = formatted.player2_fortified_countries.replace(/[{}]/g, '');
        formatted.player2_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
      } else {
        formatted.player2_fortified_countries = [];
      }
    }
    for (const key of ['player3_fortified_countries', 'player4_fortified_countries']) {
      if (formatted[key] && !Array.isArray(formatted[key])) {
        if (typeof formatted[key] === 'string') {
          const cleaned = formatted[key].replace(/[{}]/g, '');
          formatted[key] = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
        } else {
          formatted[key] = [];
        }
      }
    }
    return formatted;
  };
  
  const [missionData, setMissionData] = useState(() => formatMissionData(mission));

  // Get user ID from JWT token (declared before effects that depend on it)
  const userId = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwtDecode(token);
        return decoded.userId ? String(decoded.userId) : null;
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
    return localStorage.getItem('userId');
  }, []);

  const applyMadagascarOffersGeneration = useCallback((generation) => {
    if (generation == null) return;
    const n = Number(generation);
    if (!Number.isFinite(n)) {
      throw new Error('applyMadagascarOffersGeneration: generation must be a number');
    }
    setMadagascarOffersGeneration(n);
  }, []);

  // Helper: neutral_fortifications is "valid" for map display only if at least one cell has count 1–3 (same as neutralFortificationCountMap).
  const hasDisplayableNeutrals = (obj) => {
    if (!obj || typeof obj !== 'object') return false;
    return Object.values(obj).some((d) => (d && typeof d === 'object' && Number(d.count) >= 1 && Number(d.count) <= 3));
  };

  // Broadcast often sends count-only neutrals. Keep previously known types and apply incoming consecutive-wins.
  const mergeNeutralFortificationPayloads = (prev, incoming) => {
    if (!incoming || typeof incoming !== 'object') {
      throw new Error('mergeNeutralFortificationPayloads: incoming is required');
    }
    const merged = { ...incoming };
    if (!prev || typeof prev !== 'object') return merged;
    for (const cellId of Object.keys(incoming)) {
      const inc = incoming[cellId];
      const old = prev[cellId] || prev[String(cellId)] || prev[Number(cellId)];
      if (!inc || typeof inc !== 'object' || !old || typeof old !== 'object') continue;
      const incHasTypes = Array.isArray(inc.types) && inc.types.length > 0;
      const oldHasTypes = Array.isArray(old.types) && old.types.length > 0;
      merged[cellId] = {
        ...old,
        ...inc,
        count: inc.count != null ? inc.count : old.count,
        types: incHasTypes ? inc.types : (oldHasTypes ? old.types : inc.types),
        defend_twice_consecutive_wins: inc.defend_twice_consecutive_wins != null
          ? inc.defend_twice_consecutive_wins
          : old.defend_twice_consecutive_wins
      };
    }
    return merged;
  };

  // Sync mission prop into local state when parent updates. Never overwrite existing neutral_fortifications with empty or non-displayable prop (tick broadcast can send empty; backend returns 22 with count 1–3).
  // Command Center cell lists: keep previous only when the prop omits them (active-mission list). An explicit array, including empty, is authoritative after a takeover.
  useEffect(() => {
    if (mission) {
      const formatted = formatMissionData(mission);
      if (formatted) {
        setMissionData((prev) => {
          if (!prev) return formatted;
          const prevNeutrals = prev.neutral_fortifications;
          const propNeutrals = formatted.neutral_fortifications;
          const prevHasNeutrals = hasDisplayableNeutrals(prevNeutrals);
          const propHasNeutrals = hasDisplayableNeutrals(propNeutrals);
          let next = formatted;
          if (prevHasNeutrals && !propHasNeutrals) next = { ...next, neutral_fortifications: prevNeutrals };
          else if (prevHasNeutrals && propHasNeutrals) next = { ...next, neutral_fortifications: mergeNeutralFortificationPayloads(prevNeutrals, propNeutrals) };
          next = { ...next, ...mergeCommandCenterBoostFields(formatted, formatted.id === prev.id ? prev : null) };
          if (next.viewer_engineers_available == null && userId) {
            const uid = Number(userId);
            const slot = uid === Number(next.player1_id) ? 1 : uid === Number(next.player2_id) ? 2 : uid === Number(next.player3_id) ? 3 : uid === Number(next.player4_id) ? 4 : null;
            if (slot != null && next[`player${slot}_engineers_available`] != null) next.viewer_engineers_available = Number(next[`player${slot}_engineers_available`]);
            else if (prev.viewer_engineers_available != null) next.viewer_engineers_available = prev.viewer_engineers_available;
          }
          return next;
        });
      }
    }
  }, [mission, userId]);

  // On entering world map: fetch full mission (GET includes neutral_fortifications) so neutrals are visible immediately.
  // Cache-bust so we never get a cached response from before neutrals were assigned.
  const missionIdForFetch = mission?.id ?? missionData?.id;
  useEffect(() => {
    if (!missionIdForFetch) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    const url = `${API_URL}/war-effort/mission/${missionIdForFetch}?t=${Date.now()}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache', Pragma: 'no-cache' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const formatted = formatMissionData(data);
        if (formatted) {
          setMissionData((prev) => {
            const next = { ...formatted, ...mergeCommandCenterBoostFields(formatted, prev && prev.id === formatted.id ? prev : null) };
            if (next.viewer_engineers_available == null && userId) {
              const uid = Number(userId);
              const slot = uid === Number(next.player1_id) ? 1 : uid === Number(next.player2_id) ? 2 : uid === Number(next.player3_id) ? 3 : uid === Number(next.player4_id) ? 4 : null;
              if (slot != null && next[`player${slot}_engineers_available`] != null) next.viewer_engineers_available = Number(next[`player${slot}_engineers_available`]);
              else if (prev?.viewer_engineers_available != null) next.viewer_engineers_available = prev.viewer_engineers_available;
            }
            return next;
          });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [missionIdForFetch, userId]);

  // Fallback: if we have a mission but no neutral_fortifications yet, fetch and merge them (covers race where prop/initial fetch didn't have them)
  const hasNeutrals = missionData?.neutral_fortifications && typeof missionData.neutral_fortifications === 'object' && Object.keys(missionData.neutral_fortifications).length > 0;
  useEffect(() => {
    if (!missionData?.id || hasNeutrals) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const requestedId = missionData.id;
    let cancelled = false;
    const url = `${API_URL}/war-effort/mission/${requestedId}?t=${Date.now()}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache', Pragma: 'no-cache' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data || data.id !== requestedId) return;
        const neutrals = data.neutral_fortifications;
        if (neutrals && typeof neutrals === 'object' && Object.keys(neutrals).length > 0) {
          setMissionData((prev) => prev && prev.id === requestedId ? { ...prev, neutral_fortifications: neutrals } : prev);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [missionData?.id, hasNeutrals]);

  // Fetch Eclipse Array status once when mission loads so tooltip masking is correct before first poll
  useEffect(() => {
    if (!missionData?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/war-effort/mission-status/${missionData.id}?mapView=1`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) {
          if (typeof data.viewerHasEclipseArrayUpgraded === 'boolean') setViewerHasEclipseArrayUpgraded(data.viewerHasEclipseArrayUpgraded);
          if (typeof data.viewerHasIntelligenceAgency === 'boolean') setViewerHasIntelligenceAgency(data.viewerHasIntelligenceAgency);
          setOpponentAttackTargets(Array.isArray(data.opponentAttackTargets) ? data.opponentAttackTargets : []);
          if (Object.prototype.hasOwnProperty.call(data, 'territoryBonuses')) {
            setTerritoryBonuses(readTerritoryBonuses(data.territoryBonuses));
          }
        }
      })
      .catch(() => {});
  }, [missionData?.id]);

  const fetchLocalizedAttackStrategy = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('token is required to load attack strategy');
    }
    if (!missionData?.id) {
      throw new Error('missionId is required to load attack strategy');
    }
    const res = await fetch(
      `${API_URL}/war-effort/mission/${missionData.id}/attack-strategy?${buildOrderDetailLangQuery(i18nInstance.language)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.ok) {
      throw new Error('Failed to fetch attack strategy');
    }
    const data = await res.json();
    if (data.canSellUnits && (!data.unitSellPrices || typeof data.unitSellPrices !== 'object')) {
      throw new Error('Attack strategy: unitSellPrices is required when selling is enabled');
    }
    if (!data.strategy) {
      return {
        strategy: null,
        deactivatedSlots: data.deactivatedSlots || [],
        hallAscensions: data.hallAscensions || [],
        canSellUnits: Boolean(data.canSellUnits),
        unitSellPrices: data.unitSellPrices || null
      };
    }
    const orders = await enrichOrdersWithLocalizedNames(
      data.strategy.orders || [],
      i18nInstance.language,
      token
    );
    return {
      strategy: { ...data.strategy, orders },
      deactivatedSlots: data.deactivatedSlots || [],
      hallAscensions: data.hallAscensions || [],
      canSellUnits: Boolean(data.canSellUnits),
      unitSellPrices: data.unitSellPrices || null
    };
  }, [missionData?.id, i18nInstance.language]);

  const openAttackStrategyModal = useCallback(() => {
    setAttackStrategyModal({ show: true, loading: true, strategy: null, deactivatedSlots: [], hallAscensions: [], canSellUnits: false, unitSellPrices: null });
    fetchLocalizedAttackStrategy()
      .then((payload) => setAttackStrategyModal({ show: true, loading: false, ...payload }))
      .catch(() => setAttackStrategyModal({ show: true, loading: false, strategy: null, deactivatedSlots: [], hallAscensions: [], canSellUnits: false, unitSellPrices: null }));
  }, [fetchLocalizedAttackStrategy]);

  const fetchLocalizedHallAscension = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('token is required to load Hall of Ascension');
    }
    if (!missionData?.id) {
      throw new Error('missionId is required to load Hall of Ascension');
    }
    const res = await fetch(
      `${API_URL}/war-effort/mission/${missionData.id}/hall-ascension?${buildOrderDetailLangQuery(i18nInstance.language)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const body = await res.json();
    if (!res.ok) {
      throw new Error(body.error || 'Failed to load Hall of Ascension');
    }
    const units = await enrichHallUnitsWithLocalizedNames(
      body.units || [],
      i18nInstance.language,
      token
    );
    return {
      hallLevel: body.hallLevel,
      storage: body.storage,
      units
    };
  }, [missionData?.id, i18nInstance.language]);

  const handleAttackStrategyToggleSlot = useCallback((orderId, occurrence) => {
    const token = localStorage.getItem('token');
    if (!token || !missionData?.id) return;
    fetch(`${API_URL}/war-effort/mission/${missionData.id}/attack-strategy/toggle-slot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orderId, occurrence })
    })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('Failed to toggle')))
      .then(() => fetchLocalizedAttackStrategy())
      .then((payload) => setAttackStrategyModal((prev) => ({ ...prev, ...payload })))
      .catch((err) => console.error('Attack strategy toggle error:', err));
  }, [missionData?.id, fetchLocalizedAttackStrategy]);

  const handleAttackStrategySellUnit = useCallback((orderId, occurrence) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('token is required to sell an attack strategy unit');
    }
    if (!missionData?.id) {
      throw new Error('missionId is required to sell an attack strategy unit');
    }
    const sellingKey = `${orderId}:${occurrence}`;
    setAttackStrategySellingKey(sellingKey);
    fetch(`${API_URL}/war-effort/mission/${missionData.id}/attack-strategy/sell-unit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orderId, occurrence })
    })
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok) {
          throw new Error(body.error || 'Failed to sell unit');
        }
        return body;
      })
      .then((result) => {
        if (typeof result.energy === 'number') {
          setPlayerResource((prev) => (prev ? { ...prev, amount: result.energy } : prev));
        }
        return fetchLocalizedAttackStrategy();
      })
      .then((payload) => {
        setAttackStrategyModal((prev) => ({ ...prev, ...payload }));
        setAttackStrategyTooltipRect(null);
        setError(null);
      })
      .catch((err) => {
        console.error('Attack strategy sell error:', err);
        setError(err.message || t('warEffort.failedToSellUnit'));
      })
      .finally(() => setAttackStrategySellingKey(null));
  }, [missionData?.id, fetchLocalizedAttackStrategy, t]);
  const [controlledCountries, setControlledCountries] = useState([]);
  const [warEffortData, setWarEffortData] = useState(null);
  const [playerResource, setPlayerResource] = useState(null);
  // Play energy_increase.mp3 when energy goes up (tick, Trading Hub claim, etc.)
  useEffect(() => {
    const amount = playerResource?.amount;
    if (typeof amount !== 'number') return;
    const prev = prevEnergyRef.current;
    if (prev !== null && amount > prev) {
      playSoundEffect('/sounds/energy_increase.mp3');
    }
    prevEnergyRef.current = amount;
  }, [playerResource?.amount]);
  // Ref: stable key of the last opponent action we already played sound for (so we never repeat for same log message)
  const lastOpponentActionSoundPlayedRef = useRef(null);
  // Ref: have we run the opponent-action effect at least once (skip sound on first run = no sound on refresh/initial load)
  const opponentActionEffectHasRunRef = useRef(false);
  // Reset when mission changes so a new mission can play sound for its first opponent action
  useEffect(() => {
    lastOpponentActionRef.current = null;
    lastOpponentActionSoundPlayedRef.current = null;
    lastOpponentActionKeyRef.current = null;
    opponentActionEffectHasRunRef.current = false;
  }, [missionData?.id]);
  // Play opponent_news_update.mp3 exactly once per distinct action. Do not play on initial load or refresh (only when the message changes while the user is on the page).
  useEffect(() => {
    const key = getStableOpponentActionKey(lastOpponentAction);
    if (!key) {
      lastOpponentActionRef.current = null;
      return;
    }
    lastOpponentActionRef.current = lastOpponentAction;
    const prevKey = lastOpponentActionSoundPlayedRef.current;
    lastOpponentActionSoundPlayedRef.current = key;
    const isFirstRun = !opponentActionEffectHasRunRef.current;
    opponentActionEffectHasRunRef.current = true;
    if (!isFirstRun && prevKey !== key) {
      playSoundEffect('/sounds/opponent_news_update.mp3');
    }
  }, [lastOpponentAction]);
  const [buildingCostMultiplier, setBuildingCostMultiplier] = useState(1); // Taiwan: 0.8 = 20% off; Infrastructure Subsidy: *0.45
  const [hasInfrastructureSubsidy, setHasInfrastructureSubsidy] = useState(false); // Trading Hub: next build 55% off until used
  const countryHoverBindRef = useRef(null);
  const [opponentUsername, setOpponentUsername] = useState(null); // 1v1/2v2: single name
  const [opponentUsernames, setOpponentUsernames] = useState([]);  // FFA: list of opponent display names
  const [userStrategies, setUserStrategies] = useState([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState(null);
  
  // Dev mode state (only visible for player1)
  // Check if current user is player1 (same pattern as BattleOverview.js)
  const devUserId = typeof user === 'string' ? user : (user?.id || user?.username);
  const isDevUser = devUserId === 'player1' || devUserId === 1 || user?.username === 'player1';
  
  const [devModeActive, setDevModeActive] = useState(false);
  const [devSelectedPurpose, setDevSelectedPurpose] = useState(null);
  const [devShowFortifications, setDevShowFortifications] = useState(false);
  const [devCommandCenterCells, setDevCommandCenterCells] = useState({ byPurpose: {}, allCells: [] });
  const [devLoading, setDevLoading] = useState(false);
  const [devSimulateOpponentLoading, setDevSimulateOpponentLoading] = useState(false);
  const [devEndCeaseFireLoading, setDevEndCeaseFireLoading] = useState(false);
  const [devResetEnergyLoading, setDevResetEnergyLoading] = useState(false);
  // Pending changes in dev mode (cellId -> factionName or null)
  // These are NOT saved to DB until "Store as Default" is clicked
  const [devPendingOwners, setDevPendingOwners] = useState({});
  // Transfer energy to ally (2v2): modal open state and loading
  const [transferEnergyModalOpen, setTransferEnergyModalOpen] = useState(false);
  const [transferEnergyLoading, setTransferEnergyLoading] = useState(false);
  const [showNeutralFortifiedPopup, setShowNeutralFortifiedPopup] = useState(false);
  
  // Viewer slot (1–4): which of player1_id..player4_id is the current user. Used for FFA/2v2 fortified display.
  const viewerSlot = useMemo(() => {
    if (!userId || !missionData) return null;
    const uid = Number(userId);
    if (Number(missionData.player1_id) === uid) return 1;
    if (missionData.player2_id != null && Number(missionData.player2_id) === uid) return 2;
    if (missionData.player3_id != null && Number(missionData.player3_id) === uid) return 3;
    if (missionData.player4_id != null && Number(missionData.player4_id) === uid) return 4;
    return null;
  }, [userId, missionData?.player1_id, missionData?.player2_id, missionData?.player3_id, missionData?.player4_id]);

  // Verify that the current user is actually part of this mission
  const isUserInMission = useMemo(() => {
    if (!userId || !missionData) return false;
    const userIdNum = Number(userId);
    return [missionData.player1_id, missionData.player2_id, missionData.player3_id, missionData.player4_id]
      .filter(Boolean).some(id => Number(id) === userIdNum);
  }, [userId, missionData?.player1_id, missionData?.player2_id, missionData?.player3_id, missionData?.player4_id]);

  const hasTradeForum = useMemo(() => (
    placedBuildings.some((p) => p.building?.id === 'trade' || p.building?.id === 'trade_upgraded')
  ), [placedBuildings]);

  // Compare as numbers to handle type mismatches - memoize to prevent unnecessary recalculations
  const isPlayer1 = useMemo(() => {
    return userId ? Number(missionData?.player1_id) === Number(userId) : false;
  }, [userId, missionData?.player1_id]);

  // Viewer's purpose (for tooltip, HQ detection, etc.). FFA/2v2: use viewerSlot; 1v1: use isPlayer1.
  const viewerPurpose = useMemo(() => {
    const slot = viewerSlot;
    const mode = missionData?.mission_mode;
    if ((mode === 'ffa' || mode === '2v2') && slot >= 1 && slot <= 4) {
      return missionData?.[`player${slot}_purpose`] ?? null;
    }
    return isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
  }, [viewerSlot, isPlayer1, missionData?.mission_mode, missionData?.player1_purpose, missionData?.player2_purpose, missionData?.player3_purpose, missionData?.player4_purpose]);

  // Engineer count for UI: use viewer_engineers_available or derive from player{N}_engineers_available. Never invent a starting count so we only show 5 when it is really 5.
  const engineersForDisplay = useMemo(() => {
    if (!missionData) return undefined;
    const v = missionData.viewer_engineers_available;
    if (v != null && v !== '') return Number(v);
    if (viewerSlot != null) {
      const p = missionData[`player${viewerSlot}_engineers_available`];
      if (p != null && p !== '') return Number(p);
    }
    return undefined;
  }, [missionData?.viewer_engineers_available, missionData?.player1_engineers_available, missionData?.player2_engineers_available, missionData?.player3_engineers_available, missionData?.player4_engineers_available, viewerSlot]);

  // 2v2: show "Transfer Energy" when current user has an ally (other teammate)
  const showTransferEnergy = useMemo(() => {
    if (missionData?.mission_mode !== '2v2' || !userId) return false;
    const uid = Number(userId);
    const p1 = missionData.player1_id != null ? Number(missionData.player1_id) : null;
    const p2 = missionData.player2_id != null ? Number(missionData.player2_id) : null;
    const p3 = missionData.player3_id != null ? Number(missionData.player3_id) : null;
    const p4 = missionData.player4_id != null ? Number(missionData.player4_id) : null;
    return (uid === p1 && p2 != null) || (uid === p2 && p1 != null) || (uid === p3 && p4 != null) || (uid === p4 && p3 != null);
  }, [missionData?.mission_mode, missionData?.player1_id, missionData?.player2_id, missionData?.player3_id, missionData?.player4_id, userId]);
  
  // Calculate command center eligible cells based on current + pending owners
  const calculateDevCommandCenterCells = useCallback(() => {
    if (!devShowFortifications || !warEffortData?.cells) return { byPurpose: {}, allCells: [] };
    
    // Build merged owner map: base data + pending changes
    const mergedOwners = {};
    warEffortData.cells.forEach(cell => {
      const cellId = String(cell.cell_id);
      if (devPendingOwners.hasOwnProperty(cellId)) {
        mergedOwners[cellId] = devPendingOwners[cellId];
      } else {
        mergedOwners[cellId] = cell.owner_name || null;
      }
    });
    
    const commandCenterCells = {};
    const purposes = ['Consume', 'Eradicate', 'Colonize', 'Protect', 'Survive'];
    
    for (const purpose of purposes) {
      const factionName = DEV_PURPOSE_TO_FACTION[purpose];
      commandCenterCells[purpose] = [];
      
      for (const [cellIdStr, ownerFaction] of Object.entries(mergedOwners)) {
        if (ownerFaction !== factionName) continue;
        
        const cellId = Number(cellIdStr);
        const neighbourCellIds = getNeighbourCellIds(cellId);
        const ownedNeighbourCount = neighbourCellIds.filter(nId => 
          mergedOwners[String(nId)] === factionName
        ).length;
        
        if (ownedNeighbourCount >= 3) {
          commandCenterCells[purpose].push(cellId);
        }
      }
    }
    
    const allCells = [...new Set(Object.values(commandCenterCells).flat())];
    return { byPurpose: commandCenterCells, allCells };
  }, [devShowFortifications, warEffortData?.cells, devPendingOwners]);
  
  // Update command center cells when dependencies change
  useEffect(() => {
    if (devModeActive && devShowFortifications) {
      const result = calculateDevCommandCenterCells();
      setDevCommandCenterCells(result);
    }
  }, [devModeActive, devShowFortifications, calculateDevCommandCenterCells]);
  
  // Dev mode: set country purpose (frontend only - no DB write)
  const handleDevSetCountryPurpose = useCallback((cellId) => {
    if (!devModeActive) return;
    
    // Map purpose to faction name, 'clear' maps to null
    const factionName = devSelectedPurpose === 'clear' ? null : DEV_PURPOSE_TO_FACTION[devSelectedPurpose];
    
    // Update pending owners (frontend state only)
    setDevPendingOwners(prev => ({
      ...prev,
      [String(cellId)]: factionName
    }));
  }, [devModeActive, devSelectedPurpose]);
  
  // Dev mode: restore default (clear pending changes and refresh from DB)
  const handleDevRestoreDefault = useCallback(async () => {
    if (!devModeActive || devLoading) return;
    if (!window.confirm('Discard all pending changes and restore from stored default?')) return;
    try {
      setDevLoading(true);
      // Clear pending changes
      setDevPendingOwners({});
      // Refresh war effort data from the DB (which reflects war_effort_status)
      const token = localStorage.getItem('token');
      const statusResponse = await fetch(`${API_URL}/war-effort/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statusResponse.ok) {
        const data = await statusResponse.json();
        setWarEffortData(data);
      }
    } catch (err) {
      console.error('Error restoring default:', err);
    } finally {
      setDevLoading(false);
    }
  }, [devModeActive, devLoading]);
  
  // Dev mode: store as default (persist all pending changes to DB)
  const handleDevStoreAsDefault = useCallback(async () => {
    if (!devModeActive || devLoading) return;
    const pendingCount = Object.keys(devPendingOwners).length;
    if (pendingCount === 0) {
      alert('No pending changes to store.');
      return;
    }
    if (!window.confirm(`Store ${pendingCount} pending changes as the new default? This will affect all future missions.`)) return;
    try {
      setDevLoading(true);
      const token = localStorage.getItem('token');
      
      // Send all pending changes to backend
      const response = await fetch(`${API_URL}/war-effort/dev/store-pending-as-default`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pendingOwners: devPendingOwners })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Stored as default successfully');
        // Clear pending changes since they're now persisted
        setDevPendingOwners({});
        // Refresh war effort data
        const statusResponse = await fetch(`${API_URL}/war-effort/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          setWarEffortData(statusData);
        }
      } else {
        const errorData = await response.json();
        alert('Error: ' + (errorData.error || 'Failed to store as default'));
      }
    } catch (err) {
      console.error('Error storing as default:', err);
      alert('Error storing as default: ' + err.message);
    } finally {
      setDevLoading(false);
    }
  }, [devModeActive, devLoading, devPendingOwners]);

  // Dev mode: randomize (frontend only - no DB write)
  const handleDevRandomize = useCallback(() => {
    if (!devModeActive || !warEffortData?.cells) return;
    if (!window.confirm('Randomize country ownership across all purposes? (Changes are not saved until you click Store as Default)')) return;

    const allCellIds = warEffortData.cells.map(c => c.cell_id);
    const purposes = ['Consume', 'Eradicate', 'Colonize', 'Protect', 'Survive'];
    
    // Sort cells by neighbour count (ascending) to minimize command center clusters
    const cellNeighbourCounts = allCellIds.map(cellId => ({
      cellId,
      neighbourCount: getNeighbourCellIds(cellId).length
    }));
    
    // Shuffle cells randomly
    const shuffledCells = [...cellNeighbourCounts].sort(() => Math.random() - 0.5);
    
    // Assign in round-robin to distribute evenly
    const newPendingOwners = {};
    shuffledCells.forEach((cell, index) => {
      const purposeIndex = index % 5;
      const purpose = purposes[purposeIndex];
      const factionName = DEV_PURPOSE_TO_FACTION[purpose];
      newPendingOwners[String(cell.cellId)] = factionName;
    });
    
    // Replace all pending owners with randomized assignment
    setDevPendingOwners(newPendingOwners);
  }, [devModeActive, warEffortData?.cells]);

  // Dev mode: simulate opponent (join + fortify 3 random owned countries)
  const handleSimulateOpponent = useCallback(async () => {
    if (!devModeActive || devSimulateOpponentLoading || !missionData?.id || missionData?.player2_id) return;
    try {
      setDevSimulateOpponentLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/dev-simulate-opponent`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to simulate opponent');
      }
      const fullRes = await fetch(`${API_URL}/war-effort/mission/${missionData.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (fullRes.ok) {
        const fullMission = await fullRes.json();
        if (!Array.isArray(fullMission.viewer_command_center_boost_cell_ids)) {
          fullMission.viewer_command_center_boost_cell_ids = [];
        } else {
          fullMission.viewer_command_center_boost_cell_ids = fullMission.viewer_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
        }
        if (!Array.isArray(fullMission.opponent_command_center_boost_cell_ids)) {
          fullMission.opponent_command_center_boost_cell_ids = [];
        } else {
          fullMission.opponent_command_center_boost_cell_ids = fullMission.opponent_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
        }
        setMissionData(fullMission);
      }
    } catch (err) {
      console.error('Simulate opponent error:', err);
      alert(err.message || 'Failed to simulate opponent');
    } finally {
      setDevSimulateOpponentLoading(false);
    }
  }, [devModeActive, devSimulateOpponentLoading, missionData?.id, missionData?.player2_id]);

  // Debug logging - DISABLED to prevent infinite re-renders
  // Arrays/objects in dependencies cause infinite loops
  // Uncomment only for debugging specific issues
  /*
  useEffect(() => {
    console.log('🔍 User ID detection:', {
      user,
      userId,
      userType: typeof user,
      localStorageUserId: localStorage.getItem('userId'),
      missionPlayer1Id: missionData?.player1_id,
      missionPlayer2Id: missionData?.player2_id,
      isPlayer1,
      player1_fortified: missionData?.player1_fortified_countries,
      player2_fortified: missionData?.player2_fortified_countries
    });
  }, [userId, missionData?.player1_id, missionData?.player2_id, isPlayer1]);
  */
  
  // Get fortified countries array, ensuring it's an array and converting to numbers
  // Handle PostgreSQL array format (string like "{251,252,253}") or array
  const getFortifiedCountriesArray = (countries) => {
    if (!countries) return [];
    if (Array.isArray(countries)) {
      return countries.map(id => Number(id)).filter(id => !isNaN(id));
    }
    // Handle PostgreSQL array format (string like "{251,252,253}")
    if (typeof countries === 'string') {
      const cleaned = countries.replace(/[{}]/g, '');
      if (cleaned.trim() === '') return [];
      return cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id));
    }
    return [];
  };
  
  // Memoize to prevent recalculation on every render
  // Player's own fortified countries (for display and validation). Use viewerSlot for FFA/2v2 so my fortifications show as mine, not neutral.
  const playerFortifiedCountries = useMemo(() => {
    const mode = missionData?.mission_mode;
    const slot = viewerSlot;
    if ((mode === 'ffa' || mode === '2v2') && slot >= 1 && slot <= 4) {
      const key = `player${slot}_fortified_countries`;
      return getFortifiedCountriesArray(missionData?.[key]);
    }
    return getFortifiedCountriesArray(
      isPlayer1 ? missionData?.player1_fortified_countries : missionData?.player2_fortified_countries
    );
  }, [viewerSlot, isPlayer1, missionData?.mission_mode, missionData?.player1_fortified_countries, missionData?.player2_fortified_countries, missionData?.player3_fortified_countries, missionData?.player4_fortified_countries]);

  const isOwnedCellProtected = (cellId) => {
    if (protectedCellId == null || cellId == null) return false;
    return Number(cellId) === Number(protectedCellId);
  };

  // Opponent's fortified countries (for display). FFA: all other slots; 2v2: other team; 1v1: other player.
  const opponentFortifiedCountries = useMemo(() => {
    const mode = missionData?.mission_mode;
    const slot = viewerSlot;
    if (mode === 'ffa' && slot >= 1 && slot <= 4) {
      const merged = [];
      for (let s = 1; s <= 4; s++) {
        if (s === slot) continue;
        const key = `player${s}_fortified_countries`;
        merged.push(...getFortifiedCountriesArray(missionData?.[key]));
      }
      return [...new Set(merged)];
    }
    if (mode === '2v2' && slot >= 1 && slot <= 4) {
      const otherTeamSlots = slot <= 2 ? [3, 4] : [1, 2];
      const merged = [];
      for (const s of otherTeamSlots) {
        merged.push(...getFortifiedCountriesArray(missionData?.[`player${s}_fortified_countries`]));
      }
      return [...new Set(merged)];
    }
    return getFortifiedCountriesArray(
      isPlayer1 ? missionData?.player2_fortified_countries : missionData?.player1_fortified_countries
    );
  }, [viewerSlot, isPlayer1, missionData?.mission_mode, missionData?.player1_fortified_countries, missionData?.player2_fortified_countries, missionData?.player3_fortified_countries, missionData?.player4_fortified_countries]);

  // Fortification slots used (lifetime limit). Use viewerSlot for FFA/2v2.
  const playerFortificationSlotsUsed = useMemo(() => {
    const mode = missionData?.mission_mode;
    const slot = viewerSlot;
    if ((mode === 'ffa' || mode === '2v2') && slot >= 1 && slot <= 4) {
      const raw = missionData?.[`player${slot}_fortification_slots_used`];
      return raw != null ? Number(raw) : playerFortifiedCountries.length;
    }
    return (isPlayer1 ? missionData?.player1_fortification_slots_used : missionData?.player2_fortification_slots_used) ?? playerFortifiedCountries.length;
  }, [viewerSlot, isPlayer1, missionData?.mission_mode, missionData?.player1_fortification_slots_used, missionData?.player2_fortification_slots_used, missionData?.player3_fortification_slots_used, missionData?.player4_fortification_slots_used, playerFortifiedCountries.length]);
  
  // Cell IDs that get the Command Center fortified visual (own 3+ neighbours; used for tooltip)
  const commandCenterBoostCellIds = useMemo(() => {
    const fromApi = missionData?.viewer_command_center_boost_cell_ids;
    return Array.isArray(fromApi) ? fromApi.map((id) => Number(id)).filter((id) => !isNaN(id)) : [];
  }, [missionData?.viewer_command_center_boost_cell_ids]);
  // Opponent's Command Center boost cells (bot/opponent has building + owns 3+ neighbours) — show same fortified stripe
  const opponentCommandCenterBoostCellIds = useMemo(() => {
    const fromApi = missionData?.opponent_command_center_boost_cell_ids;
    return Array.isArray(fromApi) ? fromApi.map((id) => Number(id)).filter((id) => !isNaN(id)) : [];
  }, [missionData?.opponent_command_center_boost_cell_ids]);
  // Opponent action-fortified cells only (fortify country action) — faction stripe
  const opponentFortifiedCellsForMap = useMemo(() => {
    return opponentFortifiedCountries.map((id) => Number(id)).filter((id) => !isNaN(id));
  }, [opponentFortifiedCountries]);

  const territoryBonusEffects = useMemo(() => {
    if (!territoryBonuses) return [];
    const effects = [];
    if (territoryBonuses.ownershipEnergyPercent > 0) {
      effects.push({
        effectType: 'territory_ownership',
        name: t('warEffort.territoryBonuses.ownershipTitle', 'Territory control'),
        description: t('warEffort.territoryBonuses.ownershipDesc', {
          count: territoryBonuses.ownedCountryCount,
          percent: territoryBonuses.ownershipEnergyPercent,
          defaultValue: '{{count}} countries owned: +{{percent}}% energy production.'
        })
      });
    }
    if (territoryBonuses.commandCenterResourcePercent > 0 || territoryBonuses.commandCenterEnergyPercent > 0) {
      const descKey = territoryBonuses.commandCenterEnergyPercent > 0
        ? 'warEffort.territoryBonuses.commandCenterDescWithEnergy'
        : 'warEffort.territoryBonuses.commandCenterDesc';
      const defaultValue = territoryBonuses.commandCenterEnergyPercent > 0
        ? '{{count}} command center fortified countries: +{{resourcePercent}}% resource production, +{{energyPercent}}% energy production.'
        : '{{count}} command center fortified countries: +{{resourcePercent}}% resource production.';
      effects.push({
        effectType: 'command_center_production',
        name: t('warEffort.territoryBonuses.commandCenterTitle', 'Command Center network'),
        description: t(descKey, {
          count: territoryBonuses.commandCenterFortifiedCount,
          resourcePercent: territoryBonuses.commandCenterResourcePercent,
          energyPercent: territoryBonuses.commandCenterEnergyPercent,
          defaultValue
        })
      });
    }
    return effects;
  }, [territoryBonuses, t]);

  // Per-opponent list for FFA/2v2 mission box: each opponent gets main mission + up to 3 submissions (their fortified countries)
  const opponentsForBox = useMemo(() => {
    const mode = missionData?.mission_mode;
    const slot = viewerSlot;
    if (mode !== 'ffa' && mode !== '2v2' || slot < 1 || slot > 4) return [];
    const otherTeamSlots = mode === '2v2' ? (slot <= 2 ? [3, 4] : [1, 2]) : [1, 2, 3, 4].filter(s => s !== slot);
    const list = [];
    let nameIdx = 0;
    for (const s of otherTeamSlots) {
      const pid = missionData?.[`player${s}_id`];
      if (!pid) continue;
      const isBot = missionData?.[`player${s}_is_bot`] === true;
      const username = isBot ? 'Bot' : (Array.isArray(opponentUsernames) && opponentUsernames[nameIdx] != null ? opponentUsernames[nameIdx] : `Player ${pid}`);
      nameIdx++;
      const purpose = missionData?.[`player${s}_purpose`];
      const targetCountry = missionData?.[`player${s}_mission_target_country`] || missionData?.mission_target_country;
      const missionType = purpose ? PURPOSE_TO_MISSION_TYPE[purpose] : null;
      const missionName = (missionType && targetCountry) ? `${missionType} in ${targetCountry}` : null;
      const fortified = getFortifiedCountriesArray(missionData?.[`player${s}_fortified_countries`]).slice(0, 3);
      const hqCellId = purpose ? PURPOSE_TO_HQ_CELL_ID[purpose] : null;
      const hqCountry = hqCellId != null ? getCountryFromCellId(hqCellId) : null;
      const factionName = purpose ? DEV_PURPOSE_TO_FACTION[purpose] : null;
      list.push({ slot: s, userId: pid, username, purpose, missionName, targetCountry, fortifiedCountries: fortified, hqCellId, hqCountry, factionName });
    }
    return list;
  }, [missionData?.mission_mode, viewerSlot, missionData?.player1_id, missionData?.player2_id, missionData?.player3_id, missionData?.player4_id, missionData?.player1_is_bot, missionData?.player2_is_bot, missionData?.player3_is_bot, missionData?.player4_is_bot, missionData?.mission_target_country, missionData?.player1_purpose, missionData?.player2_purpose, missionData?.player3_purpose, missionData?.player4_purpose, missionData?.player1_mission_target_country, missionData?.player2_mission_target_country, missionData?.player3_mission_target_country, missionData?.player4_mission_target_country, missionData?.player1_fortified_countries, missionData?.player2_fortified_countries, missionData?.player3_fortified_countries, missionData?.player4_fortified_countries, opponentUsernames]);
  // Cells fortified by action (fortify country) - WorldMap shows these with faction stripe.
  // When a country has both action + command center fortification, we pass action-only so WorldMap prefers the action visual.
  const fortifiedCellsForMap = useMemo(() => playerFortifiedCountries, [playerFortifiedCountries]);

  // Tectonic Boredrive: cells where it was used (rupture indicator on map)
  const tectonicBoredriveCellsForMap = useMemo(() => {
    const raw = missionData?.tectonic_boredrive_cell_ids;
    if (!Array.isArray(raw)) return [];
    return raw.map((id) => Number(id)).filter((n) => !isNaN(n));
  }, [missionData?.tectonic_boredrive_cell_ids]);

  // Neutral fortifications: cellId -> count (1, 2, or 3) for map star display (blue/purple/golden).
  // Use stable ref when state is briefly non-displayable so fortifications never flash off (e.g. on tick).
  const neutralFortificationCountMap = useMemo(() => {
    const raw = missionData?.neutral_fortifications;
    const source = hasDisplayableNeutrals(raw) ? raw : (hasDisplayableNeutrals(neutralFortificationsStableRef.current) ? neutralFortificationsStableRef.current : null);
    if (hasDisplayableNeutrals(raw)) neutralFortificationsStableRef.current = raw;
    if (!source || typeof source !== 'object') return {};
    const map = {};
    Object.entries(source).forEach(([cellIdStr, data]) => {
      const cellId = Number(cellIdStr);
      const count = data?.count;
      if (!isNaN(cellId) && count >= 1 && count <= 3) {
        map[cellId] = count;
      }
    });
    return map;
  }, [missionData?.neutral_fortifications]);
  
  // Debug logging - DISABLED to prevent infinite re-renders
  // Arrays/objects in dependencies cause infinite loops
  // Uncomment only for debugging specific issues
  /*
  useEffect(() => {
    console.log('🔍 Fortified countries debug:', {
      isPlayer1,
      player1_id: missionData?.player1_id,
      player2_id: missionData?.player2_id,
      user_id: userId,
      playerFortifiedCountries: playerFortifiedCountries.length,
      missionDataExists: !!missionData
    });
  }, [userId, missionData?.player1_id, missionData?.player2_id, isPlayer1]);
  */
  
  // Get headquarters image path for a faction
  const getHeadquartersImage = (factionName) => {
    const imageMap = {
      'Remnants of Humanity': '/images/remnants_headquarter.jpg',
      'Exolot Dominion': '/images/exolot_landing.jpg',
      'League of Shadows': '/images/league_portal.jpg',
      "Ra's Children": '/images/ra_seat.jpg',
      'Asrah': '/images/asrah_ground.jpg',
      'The Sentients': '/images/sentient_serverfarm.jpg'
    };
    return imageMap[factionName] || null;
  };
  
  // Get cell data for tooltip (normalize types so owned countries match: API may return cell_id as number or string)
  const getCellData = (cellId) => {
    if (!warEffortData?.cells) return null;
    const id = Number(cellId);
    if (Number.isNaN(id)) return null;
    return warEffortData.cells.find(c => Number(c.cell_id) === id);
  };
  
  // Check if a cell is a headquarters (includes The Sentients HQ in Madagascar)
  const isHeadquarters = (cellId) => {
    const cellIdNum = Number(cellId);
    if (cellIdNum === SENTIENTS_HQ_CELL_ID) return true;
    const cell = getCellData(cellId);
    return cell?.is_headquarters === true || cell?.is_headquarters === 'true' || cell?.is_headquarters === 't';
  };
  
  // Handle country hover for tooltip - position so tooltip stays fully visible (e.g. Australia at map edge).
  // Updates the hover island only — must not setState on this component (map-page re-renders hitch the pointer).
  const handleCountryHover = useCallback((cellId, e) => {
    if (!cellId) return;

    const tooltipWidth = 282;   // 250px HQ image + 15px padding * 2 + 2px border
    const tooltipMaxHeight = Math.min(600, window.innerHeight - 48); // Tall HQ tooltips; cap by viewport
    const padding = 12;
    
    // Horizontal: prefer right of cursor, flip to left if would overflow right
    let x = e.clientX + padding;
    if (x + tooltipWidth > window.innerWidth - padding) {
      x = e.clientX - tooltipWidth - padding;
    }
    x = Math.max(padding, Math.min(window.innerWidth - tooltipWidth - padding, x));
    
    // Vertical: prefer below cursor, flip above if would overflow bottom
    let y = e.clientY + padding;
    if (y + tooltipMaxHeight > window.innerHeight - padding) {
      y = e.clientY - tooltipMaxHeight - padding;
    }
    y = Math.max(padding, Math.min(window.innerHeight - tooltipMaxHeight - padding, y));

    const bridge = countryHoverBindRef.current;
    if (!bridge || typeof bridge.show !== 'function') return;
    bridge.show(cellId, { x, y });
  }, []);
  
  // Handle country mouse leave
  const handleCountryLeave = useCallback(() => {
    if (!countryHoverBindRef.current) return;
    countryHoverBindRef.current.hide();
  }, []);
  
  // Set missionData from prop only when mission id changes; do not overwrite after fetch (fetched data has viewer_command_center_boost_cell_ids)
  useEffect(() => {
    if (!mission) return;
    setMissionData((prev) => {
      if (prev && prev.id === mission.id && Array.isArray(prev.viewer_command_center_boost_cell_ids)) return prev;
      const formattedMission = { ...mission };
      if (formattedMission.player1_fortified_countries && !Array.isArray(formattedMission.player1_fortified_countries)) {
        if (typeof formattedMission.player1_fortified_countries === 'string') {
          const cleaned = formattedMission.player1_fortified_countries.replace(/[{}]/g, '');
          formattedMission.player1_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
        } else {
          formattedMission.player1_fortified_countries = [];
        }
      }
      if (formattedMission.player2_fortified_countries && !Array.isArray(formattedMission.player2_fortified_countries)) {
        if (typeof formattedMission.player2_fortified_countries === 'string') {
          const cleaned = formattedMission.player2_fortified_countries.replace(/[{}]/g, '');
          formattedMission.player2_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
        } else {
          formattedMission.player2_fortified_countries = [];
        }
      }
      for (const key of ['player3_fortified_countries', 'player4_fortified_countries']) {
        if (formattedMission[key] && !Array.isArray(formattedMission[key])) {
          if (typeof formattedMission[key] === 'string') {
            const cleaned = formattedMission[key].replace(/[{}]/g, '');
            formattedMission[key] = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
          } else {
            formattedMission[key] = [];
          }
        }
      }
      if (formattedMission.viewer_engineers_available == null && userId) {
        const uid = Number(userId);
        const slot = uid === Number(formattedMission.player1_id) ? 1 : uid === Number(formattedMission.player2_id) ? 2 : uid === Number(formattedMission.player3_id) ? 3 : uid === Number(formattedMission.player4_id) ? 4 : null;
        if (slot != null && formattedMission[`player${slot}_engineers_available`] != null) formattedMission.viewer_engineers_available = Number(formattedMission[`player${slot}_engineers_available`]);
        else if (prev?.viewer_engineers_available != null) formattedMission.viewer_engineers_available = prev.viewer_engineers_available;
      }
      return formattedMission;
    });
  }, [mission?.id, userId]);
  
  // Refetch full mission data (Eclipse Array visibility, Command Center cells, etc.). Call after place/upgrade/delete HQ building.
  const refetchMission = useCallback(async () => {
    const missionId = mission?.id ?? missionData?.id;
    if (!missionId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/mission/${missionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.player1_fortified_countries && !Array.isArray(data.player1_fortified_countries)) {
        if (typeof data.player1_fortified_countries === 'string') {
          const cleaned = data.player1_fortified_countries.replace(/[{}]/g, '');
          data.player1_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
        } else {
          data.player1_fortified_countries = [];
        }
      }
      if (data.player2_fortified_countries && !Array.isArray(data.player2_fortified_countries)) {
        if (typeof data.player2_fortified_countries === 'string') {
          const cleaned = data.player2_fortified_countries.replace(/[{}]/g, '');
          data.player2_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
        } else {
          data.player2_fortified_countries = [];
        }
      }
      for (const key of ['player3_fortified_countries', 'player4_fortified_countries']) {
        if (data[key] && !Array.isArray(data[key])) {
          if (typeof data[key] === 'string') {
            const cleaned = data[key].replace(/[{}]/g, '');
            data[key] = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
          } else {
            data[key] = [];
          }
        }
      }
      if (!Array.isArray(data.viewer_command_center_boost_cell_ids)) {
        data.viewer_command_center_boost_cell_ids = [];
      } else {
        data.viewer_command_center_boost_cell_ids = data.viewer_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
      }
      if (!Array.isArray(data.opponent_command_center_boost_cell_ids)) {
        data.opponent_command_center_boost_cell_ids = [];
      } else {
        data.opponent_command_center_boost_cell_ids = data.opponent_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
      }
      if (data.viewer_engineers_available == null && userId) {
        const uid = Number(userId);
        const slot = uid === Number(data.player1_id) ? 1 : uid === Number(data.player2_id) ? 2 : uid === Number(data.player3_id) ? 3 : uid === Number(data.player4_id) ? 4 : null;
        if (slot != null && data[`player${slot}_engineers_available`] != null) data.viewer_engineers_available = Number(data[`player${slot}_engineers_available`]);
      }
      setMissionData(data);
    } catch (error) {
      console.error('Error refetching mission:', error);
    }
  }, [mission?.id, userId]);

  // Refresh mission data on mount to ensure we have the latest data
  useEffect(() => {
    refetchMission();
  }, [mission.id, refetchMission]);
  
  // Cease fire countdown: tick every second when mission has active cease fire
  useEffect(() => {
    const endsAt = missionData?.cease_fire_ends_at;
    if (!endsAt) {
      setCeaseFireRemainingSeconds(0);
      return;
    }
    const endMs = new Date(endsAt).getTime();
    const update = () => {
      const remaining = Math.max(0, Math.floor((endMs - Date.now()) / 1000));
      setCeaseFireRemainingSeconds(remaining);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [missionData?.cease_fire_ends_at]);

  // Opponent-fortified-only cease fire countdown (10 min after join; only blocks attacking opponent-fortified countries)
  useEffect(() => {
    const endsAt = missionData?.opponent_fortified_cease_fire_ends_at;
    if (!endsAt) {
      setOpponentFortifiedCeaseFireRemainingSeconds(0);
      return;
    }
    const endMs = new Date(endsAt).getTime();
    const update = () => {
      const remaining = Math.max(0, Math.floor((endMs - Date.now()) / 1000));
      setOpponentFortifiedCeaseFireRemainingSeconds(remaining);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [missionData?.opponent_fortified_cease_fire_ends_at]);

  // Conqueror-only: after taking an opponent's fortified country, cannot chain opponent fort or HQ (other attacks OK)
  useEffect(() => {
    const uid = Number(userId);
    const restrictedUid = missionData?.conquest_attacker_cease_fire_user_id != null
      ? Number(missionData.conquest_attacker_cease_fire_user_id)
      : null;
    const endsAt = missionData?.conquest_attacker_cease_fire_ends_at;
    if (!endsAt || restrictedUid == null || uid !== restrictedUid) {
      setConquestAttackerCeaseFireRemainingSeconds(0);
      return;
    }
    const endMs = new Date(endsAt).getTime();
    const update = () => {
      const remaining = Math.max(0, Math.floor((endMs - Date.now()) / 1000));
      setConquestAttackerCeaseFireRemainingSeconds(remaining);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [missionData?.conquest_attacker_cease_fire_ends_at, missionData?.conquest_attacker_cease_fire_user_id, userId]);
  
  const isCeaseFireActive = ceaseFireRemainingSeconds > 0;
  const isOpponentFortifiedCeaseFireActive = opponentFortifiedCeaseFireRemainingSeconds > 0;
  const isConquestAttackerCeaseFireActive = conquestAttackerCeaseFireRemainingSeconds > 0;
  const formatCeaseFireCountdown = (seconds) => {
    if (seconds <= 0) return '0:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  };

  // Dev mode: instantly end cease fire for this mission (defined after isCeaseFireActive)
  const handleEndCeaseFire = useCallback(async () => {
    if (!devModeActive || devEndCeaseFireLoading || !missionData?.id || (!isCeaseFireActive && !isOpponentFortifiedCeaseFireActive && !isConquestAttackerCeaseFireActive)) return;
    try {
      setDevEndCeaseFireLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/dev-end-cease-fire`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to end cease fire');
      }
      const fullRes = await fetch(`${API_URL}/war-effort/mission/${missionData.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (fullRes.ok) {
        const fullMission = await fullRes.json();
        if (!Array.isArray(fullMission.viewer_command_center_boost_cell_ids)) {
          fullMission.viewer_command_center_boost_cell_ids = [];
        } else {
          fullMission.viewer_command_center_boost_cell_ids = fullMission.viewer_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
        }
        if (!Array.isArray(fullMission.opponent_command_center_boost_cell_ids)) {
          fullMission.opponent_command_center_boost_cell_ids = [];
        } else {
          fullMission.opponent_command_center_boost_cell_ids = fullMission.opponent_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
        }
        setMissionData(fullMission);
      }
    } catch (err) {
      console.error('End cease fire error:', err);
      alert(err.message || 'Failed to end cease fire');
    } finally {
      setDevEndCeaseFireLoading(false);
    }
  }, [devModeActive, devEndCeaseFireLoading, missionData?.id, isCeaseFireActive, isOpponentFortifiedCeaseFireActive, isConquestAttackerCeaseFireActive]);

  // Dev mode: reset energy to 1000
  const handleDevResetEnergy = useCallback(async () => {
    if (!devModeActive || devResetEnergyLoading || !missionData?.id) return;
    try {
      setDevResetEnergyLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/dev-reset-energy`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to reset energy');
      }
      if (playerResource) {
        setPlayerResource((prev) => prev ? { ...prev, amount: 1000 } : null);
      }
    } catch (err) {
      console.error('Reset energy error:', err);
      alert(err.message || 'Failed to reset energy');
    } finally {
      setDevResetEnergyLoading(false);
    }
  }, [devModeActive, devResetEnergyLoading, missionData?.id]);

  // Dev mode: simulate "fortified country under attack" warning (red box for 5s)
  const handleDevSimulateUnderAttackWarning = useCallback(() => {
    if (!devModeActive) return;
    try {
      const audio = new Audio('/sounds/alarm_wareffort.mp3');
      audio.volume = 0.8;
      audio.play().catch(() => {});
    } catch (_) {}
    if (fortifiedCountryWarningTimerRef.current) {
      clearTimeout(fortifiedCountryWarningTimerRef.current);
      fortifiedCountryWarningTimerRef.current = null;
    }
    setFortifiedCountryUnderAttackWarning({ countryNames: ['Austria'] });
    fortifiedCountryWarningTimerRef.current = setTimeout(() => {
      setFortifiedCountryUnderAttackWarning(null);
      fortifiedCountryWarningTimerRef.current = null;
    }, 5000);
  }, [devModeActive]);

  const getFactionBackground = useCallback((faction) => {
    const factionColors = {
      'Remnants of Humanity': '#556B2F',
      'Exolot Dominion': '#4B0082',
      'League of Shadows': '#1A1A1A',
      'Ra\'s Children': '#F4A460',
      'Asrah': '#8B0000',
      'The Sentients': '#ADD8E6',
      'Combined': '#FFA500'
    };
    const factionFillImages = {
      'Asrah': '/images/red_paint.jpg',
      'League of Shadows': '/images/black_paint.jpg',
      'Remnants of Humanity': '/images/green_paint.jpg',
      "Ra's Children": '/images/yellow_paint.jpg',
      'The Sentients': '/images/blue_paint.jpg',
      'Exolot Dominion': '/images/purple_paint.jpg'
    };
    if (!faction) return '#808080';
    const color = factionColors[faction];
    if (!color) return '#808080';

    const hexToRgba = (hex, alpha = 1.0) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    let resolvedColor;
    if (color.includes('gradient')) {
      resolvedColor = color.replace(/#([0-9A-Fa-f]{6})/g, (match, hex) => {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, 1.0)`;
      });
    } else {
      resolvedColor = hexToRgba(color, 1.0);
    }

    const image = factionFillImages[faction];
    if (image) {
      return { color: resolvedColor, image };
    }
    return resolvedColor;
  }, []);

  const getFactionBackgroundCssColor = (faction) => {
    const bg = getFactionBackground(faction);
    if (typeof bg === 'object' && bg !== null) {
      if (!bg.color) {
        throw new Error(`getFactionBackground(${faction}): color is required`);
      }
      return bg.color;
    }
    return bg;
  };

  const viewerCommandCenterStripeColors = useMemo(() => {
    const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
    if (!playerPurpose) return null;
    return PURPOSE_COMMAND_CENTER_STRIPE_COLORS[playerPurpose] ?? null;
  }, [isPlayer1, missionData?.player1_purpose, missionData?.player2_purpose]);

  const opponentCommandCenterStripeColors = useMemo(() => {
    const opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
    if (!opponentPurpose) return null;
    return PURPOSE_COMMAND_CENTER_STRIPE_COLORS[opponentPurpose] ?? null;
  }, [isPlayer1, missionData?.player1_purpose, missionData?.player2_purpose]);

  // Faction names whose purposes are selected by at least one player (for map: show these in faction color; all others neutral).
  // Same as 1v1: Protect countries sandy yellow when any player has Protect; Consume countries red when any player has Consume. Only show neutral when no player has selected that country's purpose.
  // FFA: always include The Sentients so their owned countries show in Sentients color (not dark gray).
  const factionsInGame = useMemo(() => {
    const list = [];
    const addFactionsFor = (purpose) => {
      if (!purpose) return;
      const factions = PURPOSE_TO_FACTION_MAPPING[purpose];
      if (Array.isArray(factions)) list.push(...factions);
    };
    if (missionData?.player1_id) addFactionsFor(missionData.player1_purpose);
    if (missionData?.player2_id) addFactionsFor(missionData.player2_purpose);
    if (missionData?.player3_id) addFactionsFor(missionData.player3_purpose);
    if (missionData?.player4_id) addFactionsFor(missionData.player4_purpose);
    if (missionData?.mission_mode === 'ffa' && !list.includes('The Sentients')) {
      list.push('The Sentients');
    }
    return list;
  }, [missionData?.mission_mode, missionData?.player1_id, missionData?.player1_purpose, missionData?.player2_id, missionData?.player2_purpose, missionData?.player3_id, missionData?.player3_purpose, missionData?.player4_id, missionData?.player4_purpose]);
  
  // Create cell owners map from war effort data, merged with dev mode pending changes
  const cellOwnersMap = useMemo(() => {
    if (!warEffortData?.cells) return {};
    const map = {};
    warEffortData.cells.forEach(cell => {
      const cellIdStr = String(cell.cell_id);
      // Check if there's a pending change in dev mode
      if (devModeActive && devPendingOwners.hasOwnProperty(cellIdStr)) {
        // Use pending value (can be null to clear ownership)
        if (devPendingOwners[cellIdStr]) {
          map[cellIdStr] = devPendingOwners[cellIdStr];
        }
        // If null, don't add to map (cleared ownership)
      } else if (cell.owner_name) {
        map[cellIdStr] = cell.owner_name;
      }
    });
    return map;
  }, [warEffortData, devModeActive, devPendingOwners]);

  const viewerFactionNameSet = useMemo(() => {
    const factions = viewerPurpose ? PURPOSE_TO_FACTION_MAPPING[viewerPurpose] : null;
    return new Set(Array.isArray(factions) ? factions : []);
  }, [viewerPurpose]);

  const opponentFactionNameSet = useMemo(() => {
    const set = new Set();
    const add = (purpose) => {
      const factions = PURPOSE_TO_FACTION_MAPPING[purpose];
      if (Array.isArray(factions)) factions.forEach((f) => set.add(f));
    };
    const mode = missionData?.mission_mode;
    const slot = viewerSlot;
    if (mode === 'ffa' && slot >= 1 && slot <= 4) {
      for (let s = 1; s <= 4; s++) {
        if (s === slot) continue;
        if (missionData?.[`player${s}_id`]) add(missionData[`player${s}_purpose`]);
      }
    } else if (mode === '2v2' && slot >= 1 && slot <= 4) {
      const otherTeamSlots = slot <= 2 ? [3, 4] : [1, 2];
      for (const s of otherTeamSlots) {
        if (missionData?.[`player${s}_id`]) add(missionData[`player${s}_purpose`]);
      }
    } else {
      add(isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose);
    }
    return set;
  }, [isPlayer1, viewerSlot, missionData?.mission_mode, missionData?.player1_id, missionData?.player1_purpose, missionData?.player2_id, missionData?.player2_purpose, missionData?.player3_id, missionData?.player3_purpose, missionData?.player4_id, missionData?.player4_purpose]);

  const viewerHqCellIdForCcFilter = viewerPurpose ? PURPOSE_TO_HQ_CELL_ID[viewerPurpose] : null;
  const opponentHqCellIdsForCcFilter = useMemo(() => {
    const ids = [];
    opponentFactionNameSet.forEach((factionName) => {
      const purpose = FACTION_TO_PURPOSE[factionName];
      const hq = purpose ? PURPOSE_TO_HQ_CELL_ID[purpose] : null;
      if (hq != null) ids.push(hq);
    });
    return [...new Set(ids)];
  }, [opponentFactionNameSet]);

  // Drop Command Center stripes for countries the old owner no longer owns (takeover before CC lists refresh).
  const viewerCommandCenterBoostCellIdsOwned = useMemo(() => (
    filterCommandCenterCellsByOwner(
      commandCenterBoostCellIds,
      cellOwnersMap,
      viewerFactionNameSet,
      viewerHqCellIdForCcFilter != null ? [viewerHqCellIdForCcFilter] : []
    )
  ), [commandCenterBoostCellIds, cellOwnersMap, viewerFactionNameSet, viewerHqCellIdForCcFilter]);

  const opponentCommandCenterBoostCellIdsOwned = useMemo(() => (
    filterCommandCenterCellsByOwner(
      opponentCommandCenterBoostCellIds,
      cellOwnersMap,
      opponentFactionNameSet,
      opponentHqCellIdsForCcFilter
    )
  ), [opponentCommandCenterBoostCellIds, cellOwnersMap, opponentFactionNameSet, opponentHqCellIdsForCcFilter]);

  const commandCenterFortifiedCellsForMap = useMemo(() => {
    const actionSet = new Set((fortifiedCellsForMap || []).map((id) => Number(id)));
    return viewerCommandCenterBoostCellIdsOwned.filter((id) => !actionSet.has(Number(id)));
  }, [viewerCommandCenterBoostCellIdsOwned, fortifiedCellsForMap]);

  const opponentCommandCenterFortifiedCellsForMap = useMemo(() => {
    const actionSet = new Set(opponentFortifiedCellsForMap);
    return opponentCommandCenterBoostCellIdsOwned.filter((id) => !actionSet.has(id));
  }, [opponentCommandCenterBoostCellIdsOwned, opponentFortifiedCellsForMap]);

  // Per-cell opponent Command Center stripe colors (FFA/2v2: each cell colored by its owner's purpose so bot Survive = green, etc.)
  const opponentCommandCenterStripeByCellId = useMemo(() => {
    const cellIds = opponentCommandCenterBoostCellIdsOwned;
    if (!Array.isArray(cellIds) || cellIds.length === 0) return {};
    const byCell = {};
    for (const cellId of cellIds) {
      const owner = cellOwnersMap[String(cellId)];
      if (!owner) continue;
      const purpose = FACTION_TO_PURPOSE[owner];
      if (!purpose) continue;
      const colors = PURPOSE_COMMAND_CENTER_STRIPE_COLORS[purpose];
      if (colors) byCell[Number(cellId)] = colors;
    }
    return byCell;
  }, [opponentCommandCenterBoostCellIdsOwned, cellOwnersMap]);

  // Dev mode statistics: country counts and HQ info per purpose
  const devStatistics = useMemo(() => {
    if (!devModeActive) return null;
    
    const purposes = ['Consume', 'Eradicate', 'Colonize', 'Protect', 'Survive'];
    const stats = {};
    
    // Initialize stats for each purpose
    purposes.forEach(purpose => {
      const factionName = DEV_PURPOSE_TO_FACTION[purpose];
      const hqCellId = PURPOSE_TO_HQ_CELL_ID[purpose];
      stats[purpose] = {
        count: 0,
        factionName,
        hqCellId,
        hqCountryName: getCountryFromCellId(hqCellId),
        ownsHq: false
      };
    });
    
    // Count countries per purpose from merged cellOwnersMap
    Object.entries(cellOwnersMap).forEach(([cellIdStr, factionName]) => {
      const cellId = Number(cellIdStr);
      // Find which purpose this faction belongs to
      for (const purpose of purposes) {
        if (DEV_PURPOSE_TO_FACTION[purpose] === factionName) {
          stats[purpose].count++;
          // Check if this is the HQ
          if (cellId === PURPOSE_TO_HQ_CELL_ID[purpose]) {
            stats[purpose].ownsHq = true;
          }
          break;
        }
      }
    });
    
    // Calculate total
    const total = Object.values(stats).reduce((sum, s) => sum + s.count, 0);
    
    return { purposes: stats, total };
  }, [devModeActive, cellOwnersMap]);
  
  // Fetch controlled countries (faction-based) and war effort data
  // Use ref to track if we're already fetching to prevent infinite loops
  const fetchingRef = useRef(false);
  const fetchQueuedRef = useRef(false);
  
  const opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
  const opponentHqCellId = opponentPurpose ? PURPOSE_TO_HQ_CELL_ID[opponentPurpose] : null;

  // Resolve which opponent (defender) owns an HQ cell in FFA/2v2 (purpose -> slot -> player id)
  const getDefenderUserIdForHQCell = useCallback((mission, hqCellId) => {
    if (!mission || hqCellId == null) return null;
    const purpose = HQ_CELL_ID_TO_PURPOSE[hqCellId];
    if (!purpose) return null;
    for (let s = 1; s <= 4; s++) {
      const pid = mission[`player${s}_id`];
      const pPurpose = mission[`player${s}_purpose`];
      if (pid != null && pPurpose === purpose) return Number(pid);
    }
    return null;
  }, []);

  // Fetch enemy HQ attack info when in attack mode (1v1/2v2: single opponent; FFA: returns attackableHQs list)
  useEffect(() => {
    if (!missionData?.id || !attackFromCountry || !userId) {
      setEnemyHqAttackInfo(null);
      return;
    }
    const mode = missionData?.mission_mode;
    const hasOpponent = mode === 'ffa'
      ? [missionData.player1_id, missionData.player2_id, missionData.player3_id, missionData.player4_id].filter(Boolean).some(id => Number(id) !== Number(userId))
      : !!missionData?.player2_id;
    if (!hasOpponent) {
      setEnemyHqAttackInfo(null);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    const fromParam = attackFromCountry?.cellId != null ? `&fromCellId=${attackFromCountry.cellId}` : '';
    fetch(`${API_URL}/war-effort/enemy-hq-attack-info?missionId=${missionData.id}${fromParam}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!cancelled && data) setEnemyHqAttackInfo(data);
      })
      .catch(() => {
        if (!cancelled) setEnemyHqAttackInfo(null);
      });
    return () => { cancelled = true; };
  }, [missionData?.id, missionData?.mission_mode, missionData?.player1_id, missionData?.player2_id, missionData?.player3_id, missionData?.player4_id, attackFromCountry?.cellId, userId]);

  // Keep HQ dialog cost in sync when attack-info arrives/updates while the dialog is open
  useEffect(() => {
    if (!attackEnemyHQDialog.show || attackEnemyHQDialog.hqCellId == null || !enemyHqAttackInfo) return;
    let nextCost = null;
    if (missionData?.mission_mode === 'ffa' && Array.isArray(enemyHqAttackInfo.attackableHQs)) {
      const entry = enemyHqAttackInfo.attackableHQs.find((h) => Number(h.hqCellId) === Number(attackEnemyHQDialog.hqCellId));
      if (entry?.movementCost != null) nextCost = entry.movementCost;
    } else if (enemyHqAttackInfo.movementCost != null) {
      nextCost = enemyHqAttackInfo.movementCost;
    }
    if (nextCost == null || nextCost === attackEnemyHQDialog.movementCost) return;
    setAttackEnemyHQDialog((prev) => (prev.show ? { ...prev, movementCost: nextCost } : prev));
  }, [attackEnemyHQDialog.show, attackEnemyHQDialog.hqCellId, attackEnemyHQDialog.movementCost, enemyHqAttackInfo, missionData?.mission_mode]);

  const isAttackArrowTargetCell = useCallback((cellId) => {
    if (!attackFromCountry || cellId == null) return false;
    const hoveredNum = Number(cellId);
    if (hoveredNum === SENTIENTS_HQ_CELL_ID) return false;
    if (controlledCountries.includes(hoveredNum) || hoveredNum === attackFromCountry.cellId) return false;
    return true;
  }, [attackFromCountry, controlledCountries]);

  // Attacker fallback: if Elite Force / HQ join already started, enter even when WS start was missed.
  const tryEnterActiveWarEffortBattle = useCallback((activeBattle) => {
    if (!activeBattle?.gameId) return false;
    const path = window.location.pathname || '';
    if (path.includes(`/battle/${activeBattle.gameId}`)) return false;
    setPendingEliteDefense(null);
    setPendingEliteAttackWait(null);
    navigate(`/battle/${activeBattle.gameId}`, { state: { gameId: activeBattle.gameId } });
    return true;
  }, [navigate]);

  const applyElitePendingFromStatus = useCallback((statusData) => {
    if (!statusData) return;
    const active = statusData.activeWarEffortBattle;
    setPendingEliteAttackWait((prevWait) => {
      // Only force-enter when we were already on the attacker waiting overlay (or status still says waiting).
      if (active?.gameId && (prevWait != null || statusData.pendingEliteAttackWait)) {
        queueMicrotask(() => {
          tryEnterActiveWarEffortBattle(active);
        });
        return null;
      }
      return statusData.pendingEliteAttackWait || null;
    });
    setPendingEliteDefense(statusData.pendingEliteDefense || null);
  }, [tryEnterActiveWarEffortBattle]);
  
  const fetchControlledCountries = useCallback(async () => {
    if (!missionData?.id) {
      return;
    }
    if (fetchingRef.current) {
      fetchQueuedRef.current = true;
      return;
    }
    
    fetchingRef.current = true;
    
    try {
      do {
        fetchQueuedRef.current = false;
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWarEffortData(data);
        
        // Get player's purpose from mission data (needed for controlled countries).
        // FFA/2v2: use viewerSlot so player2/3/4 get their own purpose, not player2_purpose for everyone.
        let playerPurpose = null;
        if (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') {
          const slot = viewerSlot;
          if (slot >= 1 && slot <= 4) playerPurpose = missionData?.[`player${slot}_purpose`] ?? null;
        }
        if (playerPurpose == null) {
          playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
        }
        
        // If purpose is missing, try to get it from participant data
        if (!playerPurpose && data.participant && data.participant.purpose) {
          playerPurpose = data.participant.purpose;
        }
        
        if (playerPurpose) {
          const allowedFactions = PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [];
          const controlled = data.cells
            .filter(cell => {
              const matches = cell.owner_name && allowedFactions.includes(cell.owner_name);
              return matches;
            })
            .map(cell => Number(cell.cell_id));
          if (controlled.length === 0) {
            console.warn('⚠️ No controlled countries found! This means you cannot fortify any countries.');
          }
          setControlledCountries(controlled);
        } else {
          console.warn('⚠️ No purpose found for player');
          setError(t('warEffort.purposeNotSet'));
        }
      }
      
      // Also fetch mission status to get resource info
      const missionStatusResponse = await fetch(`${API_URL}/war-effort/mission-status/${missionData.id}?mapView=1`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (missionStatusResponse.ok) {
        const statusData = await missionStatusResponse.json();
        // FFA/2v2: use only viewerResource so we never show another slot's energy (e.g. player3 seeing player2's 140).
        const resource = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2')
          ? (statusData.viewerResource ?? null)
          : (statusData.viewerResource ?? (statusData.isPlayer1 ? statusData.player1Resource : statusData.player2Resource));
        if (resource) setPlayerResource(resource);
        if (typeof statusData.buildingCostMultiplier === 'number') {
          setBuildingCostMultiplier(statusData.buildingCostMultiplier);
        }
        setHasInfrastructureSubsidy(statusData.hasInfrastructureSubsidy === true);
        if (statusData.protectedCellId != null) {
          setProtectedCellId(statusData.protectedCellId);
        } else {
          setProtectedCellId(null);
        }
        setReconquerCooldowns(Array.isArray(statusData.reconquerCooldowns) ? statusData.reconquerCooldowns : []);
        setCellsUnderAttack(Array.isArray(statusData.cellsUnderAttack) ? statusData.cellsUnderAttack : []);
        setTrainingGroundsOrdersToKeep(typeof statusData.trainingGroundsOrdersToKeep === 'number' ? Math.min(3, Math.max(1, statusData.trainingGroundsOrdersToKeep)) : 1);
        setLastPlayerAction(statusData.lastPlayerAction ?? null);
        setLastPlayerActionCellId(statusData.lastPlayerActionCellId ?? null);
        const nextOppAction = statusData.lastOpponentAction ?? null;
        const nextOppKey = getStableOpponentActionKey(nextOppAction);
        if (nextOppKey !== lastOpponentActionKeyRef.current) {
          lastOpponentActionKeyRef.current = nextOppKey;
          setLastOpponentAction(nextOppAction);
        }
        setLastOpponentActionCellId(statusData.lastOpponentActionCellId ?? null);
        setOpponentHqBuildingIds(Array.isArray(statusData.opponentHqBuildingIds) ? statusData.opponentHqBuildingIds : []);
        const byUser = statusData.opponentHqBuildingIdsByUser;
        setOpponentHqBuildingIdsByUser(byUser && typeof byUser === 'object' && !Array.isArray(byUser)
          ? Object.fromEntries(Object.entries(byUser).map(([k, v]) => [String(k), Array.isArray(v) ? v : []]))
          : {});
        setViewerHasIntelligenceAgency(statusData.viewerHasIntelligenceAgency === true);
        setOpponentAttackTargets(Array.isArray(statusData.opponentAttackTargets) ? statusData.opponentAttackTargets : []);
        setActiveShopEffects(Array.isArray(statusData.activeEffects) ? statusData.activeEffects : []);
        if (Object.prototype.hasOwnProperty.call(statusData, 'territoryBonuses')) {
          setTerritoryBonuses(readTerritoryBonuses(statusData.territoryBonuses));
        }
        setDevHasBot(statusData.dev_has_bot === true);
        setDevBotEnergy(statusData.dev_bot_energy != null ? statusData.dev_bot_energy : null);
        setDevBotCurrentAttack(statusData.dev_bot_current_attack && typeof statusData.dev_bot_current_attack === 'object' ? statusData.dev_bot_current_attack : null);
        // Command Center: mission-status lists are authoritative, including empty (country taken over).
        setMissionData((prev) => {
          if (!prev?.id || prev.id !== missionData?.id) return prev;
          return { ...prev, ...mergeCommandCenterBoostFields(statusData, prev) };
        });
        if (Array.isArray(statusData.tectonic_boredrive_cell_ids) || statusData.viewer_engineers_available != null) {
          setMissionData((prev) => {
            if (!prev?.id || prev.id !== missionData?.id) return prev;
            const next = { ...prev };
            if (Array.isArray(statusData.tectonic_boredrive_cell_ids)) {
              next.tectonic_boredrive_cell_ids = statusData.tectonic_boredrive_cell_ids.map((id) => Number(id)).filter((n) => !isNaN(n));
            }
            if (statusData.viewer_engineers_available != null) {
              next.viewer_engineers_available = Number(statusData.viewer_engineers_available);
            }
            return next;
          });
        }
        publishWarEffortMissionStatus(missionData.id, statusData);
        applyElitePendingFromStatus(statusData);
        // Fortified/HQ/opponent warnings are shown globally by WarEffortWarnings.js (including alarm sound)
      }
      } while (fetchQueuedRef.current);
    } catch (error) {
      console.error('Error fetching controlled countries:', error);
    } finally {
      fetchingRef.current = false;
    }
    if (fetchQueuedRef.current) {
      fetchQueuedRef.current = false;
      fetchControlledCountries();
    }
  }, [missionData?.id, missionData?.mission_mode, missionData?.player1_purpose, missionData?.player2_purpose, missionData?.player3_purpose, missionData?.player4_purpose, isPlayer1, viewerSlot, applyElitePendingFromStatus]);

  // Listen for mission-update from WebSocket - refresh mission data and cell ownership (country changes)
  // When Trading Hub is open, refetch hub status so all players see the same slots and out-of-stock/countdown
  // Derive viewer_engineers_available from mission payload (broadcast has player{N}_engineers_available, not viewer-specific)
  useEffect(() => {
    const handler = (e) => {
      const m = e?.detail?.mission;
      if (!m || !missionData?.id || m.id !== missionData.id) return;
      setPlacementRefreshTrigger((t) => t + 1);
      setMissionData((prev) => {
        const formatted = { ...m };
        if (formatted.player1_fortified_countries && !Array.isArray(formatted.player1_fortified_countries)) {
          formatted.player1_fortified_countries = [];
        }
        if (formatted.player2_fortified_countries && !Array.isArray(formatted.player2_fortified_countries)) {
          formatted.player2_fortified_countries = [];
        }
        for (const key of ['player3_fortified_countries', 'player4_fortified_countries']) {
          if (formatted[key] && !Array.isArray(formatted[key])) {
            formatted[key] = [];
          }
        }
        // Broadcast has player1_engineers_available..player4_engineers_available; set viewer_engineers_available from viewer's slot so engineer counter and Tectonic Boredrive stay in sync. Only set when we have a real value (never invent a starting count).
        if (userId) {
          const uid = Number(userId);
          const slot = uid === Number(m.player1_id) ? 1 : uid === Number(m.player2_id) ? 2 : uid === Number(m.player3_id) ? 3 : uid === Number(m.player4_id) ? 4 : null;
          if (slot != null) {
            const raw = m[`player${slot}_engineers_available`];
            if (raw != null && raw !== '') formatted.viewer_engineers_available = Number(raw);
          }
        }
        // mission-update broadcast omits viewer-specific Command Center cells (undefined); keep prev.
        // An explicit array, including empty, is authoritative after a takeover.
        Object.assign(formatted, mergeCommandCenterBoostFields(formatted, prev));
        // mission-update broadcast sends neutral_fortifications (count-only). Only treat incoming as valid if at least one cell has count 1–3 (map display requires that). Otherwise preserve prev so fortifications stay visible on tick.
        const incomingNeutrals = formatted.neutral_fortifications;
        const hasValidIncomingNeutrals = hasDisplayableNeutrals(incomingNeutrals);
        if (!hasValidIncomingNeutrals && prev?.neutral_fortifications && hasDisplayableNeutrals(prev.neutral_fortifications)) {
          formatted.neutral_fortifications = prev.neutral_fortifications;
        } else if (hasValidIncomingNeutrals && prev?.neutral_fortifications && hasDisplayableNeutrals(prev.neutral_fortifications)) {
          formatted.neutral_fortifications = mergeNeutralFortificationPayloads(prev.neutral_fortifications, incomingNeutrals);
        }
        return formatted;
      });
      fetchControlledCountries();
      if (m.tradingHubOffersGeneration != null) {
        applyMadagascarOffersGeneration(m.tradingHubOffersGeneration);
      }
      // Refetch mission-status so lastPlayerAction/lastPlayerActionCellId update (e.g. "You have lost Chad to …") without waiting for the 25s poll
      const token = localStorage.getItem('token');
      fetch(`${API_URL}/war-effort/mission-status/${m.id}?mapView=1`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          if (data) {
            setLastPlayerAction(data.lastPlayerAction ?? null);
            if (data.lastPlayerActionCellId != null) setLastPlayerActionCellId(data.lastPlayerActionCellId);
            if (Object.prototype.hasOwnProperty.call(data, 'territoryBonuses')) {
              setTerritoryBonuses(readTerritoryBonuses(data.territoryBonuses));
            }
            setMissionData((prev) => {
              if (!prev?.id || prev.id !== m.id) return prev;
              return { ...prev, ...mergeCommandCenterBoostFields(data, prev) };
            });
            publishWarEffortMissionStatus(m.id, data);
            applyElitePendingFromStatus(data);
          }
        })
        .catch(() => {});
      if (tradingHubOpen && m.id) {
        const token = localStorage.getItem('token');
        fetch(`${API_URL}/war-effort/trading-hub/status?missionId=${m.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
          .then((res) => res.ok ? res.json() : null)
          .then((data) => {
            if (data) {
              const cooldownEndsAtMs = data.nextClaimAt ? new Date(data.nextClaimAt).getTime() : null;
              setTradingHubStatus({ ...data, cooldownEndsAtMs });
            }
          })
          .catch(() => {});
      }
    };
    window.addEventListener('war-effort-mission-update', handler);
    return () => window.removeEventListener('war-effort-mission-update', handler);
  }, [missionData?.id, userId, fetchControlledCountries, tradingHubOpen, applyMadagascarOffersGeneration, applyElitePendingFromStatus]);

  useEffect(() => {
    const enterBattle = (gameId, gameState) => {
      if (!gameId) return;
      setPendingEliteDefense(null);
      setPendingEliteAttackWait(null);
      navigate(`/battle/${gameId}`, { state: { gameState: gameState || undefined, gameId } });
    };
    const onOffer = (e) => {
      const pending = e?.detail?.pendingEliteDefense;
      if (!pending || Number(pending.missionId) !== Number(missionData?.id)) return;
      setEliteDefenseActionError(null);
      setPendingEliteDefense(pending);
    };
    const onWaiting = (e) => {
      const pending = e?.detail?.pendingEliteAttackWait;
      if (!pending || Number(pending.missionId) !== Number(missionData?.id)) return;
      setPendingEliteAttackWait(pending);
    };
    const onStarted = (e) => {
      const gameId = e?.detail?.gameId;
      const gameState = e?.detail?.gameState;
      const missionIdFromEvent = e?.detail?.warEffortMissionId ?? gameState?.warEffortMissionId;
      if (!gameId) return;
      if (missionIdFromEvent != null && Number(missionIdFromEvent) !== Number(missionData?.id)) return;
      enterBattle(gameId, gameState);
    };
    const onExpired = (e) => {
      const missionId = e?.detail?.missionId;
      if (Number(missionId) !== Number(missionData?.id)) return;
      setPendingEliteDefense(null);
    };
    window.addEventListener('war-effort-elite-defense-offer', onOffer);
    window.addEventListener('war-effort-elite-defense-waiting', onWaiting);
    window.addEventListener('war-effort-elite-defense-started', onStarted);
    window.addEventListener('war-effort-elite-defense-expired', onExpired);
    return () => {
      window.removeEventListener('war-effort-elite-defense-offer', onOffer);
      window.removeEventListener('war-effort-elite-defense-waiting', onWaiting);
      window.removeEventListener('war-effort-elite-defense-started', onStarted);
      window.removeEventListener('war-effort-elite-defense-expired', onExpired);
    };
  }, [missionData?.id, navigate]);

  // While attacker is waiting for defender join, poll often so a missed WS start still enters the battle.
  useEffect(() => {
    if (!pendingEliteAttackWait || !missionData?.id) return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    let cancelled = false;
    const pollFast = async () => {
      try {
        const res = await fetch(`${API_URL}/war-effort/mission-status/${missionData.id}?mapView=1`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) applyElitePendingFromStatus(data);
      } catch (_) {
        // ignore transient poll errors
      }
    };
    pollFast();
    const intervalId = setInterval(pollFast, 2000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [pendingEliteAttackWait, missionData?.id, applyElitePendingFromStatus]);

  const handleEliteDefenseAccept = async () => {
    if (!pendingEliteDefense || missionData?.id == null) {
      throw new Error('handleEliteDefenseAccept: pending elite defense is required');
    }
    try {
      setEliteDefenseActionError(null);
      setEliteDefenseBusy(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/elite-defense/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          countryCellId: pendingEliteDefense.countryCellId
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to accept defense');
      }
      if (!data.gameId || !data.gameState) {
        throw new Error('Accept defense: response is missing gameId or gameState');
      }
      setPendingEliteDefense(null);
      setPendingEliteAttackWait(null);
      navigate(`/battle/${data.gameId}`, { state: { gameState: data.gameState, gameId: data.gameId } });
    } catch (err) {
      console.error('Error accepting elite defense:', err);
      setEliteDefenseActionError(err.message);
      setError(err.message);
    } finally {
      setEliteDefenseBusy(false);
    }
  };

  const handleEliteDefenseRefuse = async () => {
    if (!pendingEliteDefense || missionData?.id == null) {
      throw new Error('handleEliteDefenseRefuse: pending elite defense is required');
    }
    try {
      setEliteDefenseActionError(null);
      setEliteDefenseBusy(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/elite-defense/refuse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          countryCellId: pendingEliteDefense.countryCellId
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to refuse defense');
      }
      setPendingEliteDefense(null);
    } catch (err) {
      console.error('Error refusing elite defense:', err);
      setEliteDefenseActionError(err.message);
      setError(err.message);
    } finally {
      setEliteDefenseBusy(false);
    }
  };
  
  // Sync protectedCellId from mission payload (GET mission includes it for 1v1/FFA/2v2 so tooltip shows on load)
  useEffect(() => {
    if (missionData?.protectedCellId != null) {
      setProtectedCellId(missionData.protectedCellId);
    }
  }, [missionData?.id, missionData?.protectedCellId]);

  // Fetch war effort data and mission status on mount / when mission changes (for tooltips, controlled countries, etc.)
  useEffect(() => {
    if (missionData?.id) {
      fetchControlledCountries();
    }
  }, [missionData?.id, missionData?.player1_purpose, missionData?.player2_purpose, isPlayer1, fetchControlledCountries]);

  // Poll mission status every 30s so energy bar updates soon after server grants energy (tick +10/min, Trading Hub, etc.)
  useEffect(() => {
    if (!missionData?.id) return;
    const token = localStorage.getItem('token');
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/war-effort/mission-status/${missionData.id}?mapView=1`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // FFA/2v2: use only viewerResource so we never show another slot's energy after poll/return from background.
          const resource = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2')
            ? (data.viewerResource ?? null)
            : (data.viewerResource ?? (data.isPlayer1 ? data.player1Resource : data.player2Resource));
          if (resource) setPlayerResource(resource);
          // When Trading Hub is open, refresh its energy so buy buttons update after an energy tick
          if (tradingHubOpen && resource?.amount != null) {
            setTradingHubStatus(prev => prev ? { ...prev, energyBalance: resource.amount } : null);
          }
          if (data.viewer_engineers_available != null) {
            setMissionData((prev) => prev?.id ? { ...prev, viewer_engineers_available: Number(data.viewer_engineers_available) } : prev);
          }
          if (typeof data.buildingCostMultiplier === 'number') {
            setBuildingCostMultiplier(data.buildingCostMultiplier);
          }
          setHasInfrastructureSubsidy(data.hasInfrastructureSubsidy === true);
          if (data.protectedCellId != null) setProtectedCellId(data.protectedCellId);
          else setProtectedCellId(null);
          if (Array.isArray(data.reconquerCooldowns)) setReconquerCooldowns(data.reconquerCooldowns);
          if (Array.isArray(data.cellsUnderAttack)) setCellsUnderAttack(data.cellsUnderAttack);
          if (typeof data.trainingGroundsOrdersToKeep === 'number') setTrainingGroundsOrdersToKeep(Math.min(3, Math.max(1, data.trainingGroundsOrdersToKeep)));
          setLastPlayerAction(data.lastPlayerAction ?? null);
          if (data.lastPlayerActionCellId != null) setLastPlayerActionCellId(data.lastPlayerActionCellId);
          if (data.tradingHubOffersGeneration != null) {
            applyMadagascarOffersGeneration(data.tradingHubOffersGeneration);
          }
          const nextOppAction = data.lastOpponentAction ?? null;
          const nextOppKey = getStableOpponentActionKey(nextOppAction);
          if (nextOppKey !== lastOpponentActionKeyRef.current) {
            lastOpponentActionKeyRef.current = nextOppKey;
            setLastOpponentAction(nextOppAction);
          }
          if (data.lastOpponentActionCellId != null) setLastOpponentActionCellId(data.lastOpponentActionCellId);
          if (Array.isArray(data.opponentHqBuildingIds)) setOpponentHqBuildingIds(data.opponentHqBuildingIds);
          const byUser = data.opponentHqBuildingIdsByUser;
          if (byUser && typeof byUser === 'object' && !Array.isArray(byUser)) {
            setOpponentHqBuildingIdsByUser(Object.fromEntries(Object.entries(byUser).map(([k, v]) => [String(k), Array.isArray(v) ? v : []])));
          }
          if (typeof data.viewerHasEclipseArrayUpgraded === 'boolean') setViewerHasEclipseArrayUpgraded(data.viewerHasEclipseArrayUpgraded);
          if (typeof data.viewerHasIntelligenceAgency === 'boolean') setViewerHasIntelligenceAgency(data.viewerHasIntelligenceAgency);
          setOpponentAttackTargets(Array.isArray(data.opponentAttackTargets) ? data.opponentAttackTargets : []);
          if (Array.isArray(data.activeEffects)) setActiveShopEffects(data.activeEffects);
          if (Object.prototype.hasOwnProperty.call(data, 'territoryBonuses')) {
            setTerritoryBonuses(readTerritoryBonuses(data.territoryBonuses));
          }
          setDevHasBot(data.dev_has_bot === true);
          setDevBotEnergy(data.dev_bot_energy != null ? data.dev_bot_energy : null);
          setDevBotCurrentAttack(data.dev_bot_current_attack && typeof data.dev_bot_current_attack === 'object' ? data.dev_bot_current_attack : null);
          // Command Center: mission-status lists are authoritative, including empty after a takeover.
          setMissionData((prev) => {
            if (!prev?.id) return prev;
            return { ...prev, ...mergeCommandCenterBoostFields(data, prev) };
          });
          // When mission completed (e.g. opponent left), mission-status now returns mission_end_game_id; merge so redirect to AfterBattle works. Preserve command center IDs (data.mission does not include them).
          if (data.mission?.status === 'completed' && data.mission?.mission_end_game_id) {
            setMissionData((prev) => {
              if (prev?.id !== data.mission?.id) return prev;
              const merged = { ...prev, ...data.mission };
              Object.assign(merged, mergeCommandCenterBoostFields(data.mission, prev));
              return merged;
            });
            if (onFortificationComplete) onFortificationComplete(data.mission);
          }
          publishWarEffortMissionStatus(missionData.id, data);
          applyElitePendingFromStatus(data);
          // Fortified/HQ/opponent warnings are shown globally by WarEffortWarnings.js
        }
      } catch (e) {
        // ignore
      }
    };
    poll();
    const intervalId = setInterval(poll, 25 * 1000); // 25s - balance between freshness and network load (WebSocket mission-update is primary)
    return () => {
      clearInterval(intervalId);
      if (fortifiedCountryWarningTimerRef.current) {
        clearTimeout(fortifiedCountryWarningTimerRef.current);
        fortifiedCountryWarningTimerRef.current = null;
      }
    };
  }, [missionData?.id, missionData?.mission_mode, tradingHubOpen, applyMadagascarOffersGeneration, applyElitePendingFromStatus]);

  // Fetch Trading Hub status when dialog opens
  useEffect(() => {
    if (!tradingHubOpen || !missionData?.id) return;
    const token = localStorage.getItem('token');
    const fetchStatus = async () => {
      setTradingHubLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/war-effort/trading-hub/status?missionId=${missionData.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const cooldownEndsAtMs = (data.remainingCooldownSeconds > 0) ? Date.now() + data.remainingCooldownSeconds * 1000 : null;
          setTradingHubStatus({ ...data, cooldownEndsAtMs });
          if (data.tradingBlocked) {
            setMadagascarTradingBlocked(true);
          } else {
            setMadagascarTradingBlocked(false);
            if (data.nextAutoRestockAtMs != null) {
              applyMadagascarOffersGeneration(data.nextAutoRestockAtMs);
            }
          }
          setEnergyCooldownDisplaySeconds(data.remainingCooldownSeconds > 0 ? data.remainingCooldownSeconds : null);
          setTradingHubProtectCellId(null);
          setTradingHubSubvertCellId(null);
          if (data.tradingBlocked) {
            playSoundEffect('/sounds/denied.mp3');
          } else {
            if (tradingHubAmbienceRef.current) {
              tradingHubAmbienceRef.current.pause();
              tradingHubAmbienceRef.current.currentTime = 0;
              tradingHubAmbienceRef.current = null;
            }
            const v = getEffectiveSoundOutputVolume(0.5);
            if (v !== null) {
              const ambient = new Audio('/sounds/trading_hub_ambience.mp3');
              ambient.loop = true;
              ambient.volume = v;
              tradingHubAmbienceRef.current = ambient;
              ambient.play().catch(() => {});
            }
          }
        } else {
          const errBody = await res.json().catch(() => ({}));
          setError(errBody.error || `Trading hub: ${res.status}`);
        }
      } catch (e) {
        console.error('Trading hub status:', e);
        setError(e.message || 'Trading hub failed to load');
      } finally {
        setTradingHubLoading(false);
      }
    };
    fetchStatus();
  }, [tradingHubOpen, missionData?.id, applyMadagascarOffersGeneration]);

  // Live countdown for free energy: update every second when Trading Hub is open and cooldown is active
  useEffect(() => {
    if (!tradingHubOpen || !tradingHubStatus?.cooldownEndsAtMs) {
      setEnergyCooldownDisplaySeconds(null);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((tradingHubStatus.cooldownEndsAtMs - Date.now()) / 1000));
      setEnergyCooldownDisplaySeconds(remaining);
      if (remaining <= 0) {
        setTradingHubStatus(prev => prev ? { ...prev, canClaimEnergy: true, remainingCooldownSeconds: 0, cooldownEndsAtMs: null } : null);
      }
    };
    tick(); // run once immediately
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [tradingHubOpen, tradingHubStatus?.cooldownEndsAtMs]);

  // Global free restock (5 min) countdown: tick every 1s and refetch when auto-restock time is reached (per-slot countdowns removed from UI)
  useEffect(() => {
    if (!tradingHubOpen || !missionData?.id || !tradingHubStatus) return;
    const hasAutoRestock = tradingHubStatus.nextAutoRestockAtMs != null;
    if (!hasAutoRestock) return;
    setShopCountdownTick(Date.now());
    const token = localStorage.getItem('token');
    const interval = setInterval(async () => {
      const now = Date.now();
      setShopCountdownTick(now);
      const autoRestockExpired = tradingHubStatus.nextAutoRestockAtMs != null && tradingHubStatus.nextAutoRestockAtMs <= now;
      if (autoRestockExpired) {
        try {
          const res = await fetch(`${API_URL}/war-effort/trading-hub/status?missionId=${missionData.id}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            const cooldownEndsAtMs = (data.remainingCooldownSeconds > 0) ? Date.now() + data.remainingCooldownSeconds * 1000 : null;
            setTradingHubStatus({ ...data, cooldownEndsAtMs });
            if (data.tradingBlocked) {
              setMadagascarTradingBlocked(true);
            } else if (data.nextAutoRestockAtMs != null) {
              setMadagascarTradingBlocked(false);
              applyMadagascarOffersGeneration(data.nextAutoRestockAtMs);
            }
          }
        } catch (_) {}
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [tradingHubOpen, missionData?.id, tradingHubStatus?.nextAutoRestockAtMs, applyMadagascarOffersGeneration]);

  // Fetch opponent username
  useEffect(() => {
    const fetchOpponentUsername = async () => {
      if (!missionData || !userId) {
        setOpponentUsername(null);
        return;
      }
      
      // Don't show opponent if mission is waiting for opponent
      if (missionData.status === 'waiting_opponent') {
        setOpponentUsername(null);
        setOpponentUsernames([]);
        return;
      }

      const mode = missionData.mission_mode;
      const uid = Number(userId);

      if (mode === 'ffa') {
        const opponentIds = [missionData.player1_id, missionData.player2_id, missionData.player3_id, missionData.player4_id]
          .filter(Boolean)
          .filter(id => Number(id) !== uid);
        if (opponentIds.length === 0) {
          setOpponentUsername(null);
          setOpponentUsernames([]);
          return;
        }
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/api/player-ranks`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ userIds: opponentIds })
          });
          if (response.ok) {
            const ranks = await response.json();
            const names = opponentIds.map(id => {
              const r = ranks.find(x => String(x.id) === String(id));
              return (r && r.username) ? r.username : `Player ${id}`;
            });
            setOpponentUsernames(names);
            setOpponentUsername(null);
          } else {
            setOpponentUsernames(opponentIds.map(id => `Player ${id}`));
            setOpponentUsername(null);
          }
        } catch (error) {
          console.error('Error fetching opponent usernames:', error);
          setOpponentUsernames(opponentIds.map(id => `Player ${id}`));
          setOpponentUsername(null);
        }
        return;
      }

      const opponentId = isPlayer1 ? missionData.player2_id : missionData.player1_id;
      if (!opponentId) {
        setOpponentUsername(null);
        setOpponentUsernames([]);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/player-ranks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ userIds: [opponentId] })
        });
        if (response.ok) {
          const ranks = await response.json();
          const opponentData = ranks.find(r => String(r.id) === String(opponentId));
          if (opponentData && opponentData.username) {
            setOpponentUsername(opponentData.username);
          } else {
            setOpponentUsername('Opponent');
          }
        } else {
          setOpponentUsername('Opponent');
        }
        setOpponentUsernames([]);
      } catch (error) {
        console.error('Error fetching opponent username:', error);
        setOpponentUsername('Opponent');
        setOpponentUsernames([]);
      }
    };
    fetchOpponentUsername();
  }, [missionData?.player1_id, missionData?.player2_id, missionData?.player3_id, missionData?.player4_id, missionData?.mission_mode, missionData?.status, isPlayer1, userId]);
  
  // Fetch user's strategies for their purpose
  useEffect(() => {
    // Safety check: ensure component is mounted and state setters are available
    if (typeof setUserStrategies !== 'function' || typeof setSelectedStrategyId !== 'function') {
      console.error('State setters not available');
      return;
    }
    
    const fetchUserStrategies = async () => {
      if (!missionData || !userId) return;
      
      let playerPurpose = null;
      if ((missionData.mission_mode === 'ffa' || missionData.mission_mode === '2v2') && viewerSlot >= 1 && viewerSlot <= 4) {
        playerPurpose = missionData[`player${viewerSlot}_purpose`] ?? null;
      }
      if (playerPurpose == null) playerPurpose = isPlayer1 ? missionData.player1_purpose : missionData.player2_purpose;
      if (!playerPurpose) return;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const currentLanguage = window.i18n?.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const response = await fetch(`${API_URL}/war-effort/strategies?lang=${encodeURIComponent(normalizedLanguage)}&purpose=${encodeURIComponent(playerPurpose)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const strategies = await response.json();
          if (Array.isArray(strategies)) {
            setUserStrategies(strategies);
            // Auto-select first strategy if available (use functional update to avoid stale closure)
            if (strategies.length > 0) {
              setSelectedStrategyId(prev => {
                // Only set if not already set
                if (prev) return prev;
                return strategies[0].id;
              });
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user strategies:', error);
      }
    };
    
    fetchUserStrategies();
  }, [missionData?.id, missionData?.mission_mode, missionData?.player1_purpose, missionData?.player2_purpose, missionData?.player3_purpose, missionData?.player4_purpose, isPlayer1, userId, viewerSlot]);

  // When owned country or HQ modal opens, fetch current defense strategy for that cell to show on button
  const ownedModalCellId = ownedCountryChoice?.cellId ?? ownedHQChoice?.cellId;
  useEffect(() => {
    if (!ownedModalCellId || !missionData?.id) {
      setOwnedCountryDefenseStrategyId(null);
      setOwnedCountryDefenseStrategyLoading(false);
      return;
    }
    if (isOwnedCellProtected(ownedCountryChoice?.cellId)) {
      setOwnedCountryDefenseStrategyId(null);
      setOwnedCountryDefenseStrategyLoading(false);
      return;
    }
    let cancelled = false;
    setOwnedCountryDefenseStrategyLoading(true);
    const token = localStorage.getItem('token');
    fetch(
      `${API_URL}/war-effort/defense-strategy?missionId=${missionData.id}&countryCellId=${ownedModalCellId}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    )
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (cancelled) return;
        const id = data?.countryStrategyId ?? data?.defaultStrategyId ?? null;
        setOwnedCountryDefenseStrategyId(id != null ? id : null);
      })
      .catch(() => {
        if (!cancelled) setOwnedCountryDefenseStrategyId(null);
      })
      .finally(() => {
        if (!cancelled) setOwnedCountryDefenseStrategyLoading(false);
      });
    return () => { cancelled = true; };
  }, [ownedModalCellId, missionData?.id, protectedCellId, ownedCountryChoice?.cellId]);

  const ALL_HQ_CELL_IDS = Object.values(PURPOSE_TO_HQ_CELL_ID);
  // Fetch Tectonic Boredrive cost when owned country modal opens (for non-HQ, non-tectonic cell)
  useEffect(() => {
    if (!ownedCountryChoice?.cellId || !missionData?.id) {
      setTectonicBoredriveCost(null);
      setTectonicBoredriveResult(null);
      return;
    }
    const cellId = ownedCountryChoice.cellId;
    const tectonicIds = (missionData?.tectonic_boredrive_cell_ids || []).map((id) => Number(id)).filter((n) => !isNaN(n));
    const isTectonicCell = tectonicIds.includes(Number(cellId));
    const isHQ = ALL_HQ_CELL_IDS.includes(Number(cellId));
    if (isTectonicCell || isHQ || isOwnedCellProtected(ownedCountryChoice.cellId)) {
      setTectonicBoredriveCost(null);
      setTectonicBoredriveResult(null);
      return;
    }
    setTectonicBoredriveResult(null);
    let cancelled = false;
    fetch(`${API_URL}/war-effort/tectonic-boredrive-cost?cellId=${cellId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (!cancelled && data && typeof data.engineerCost === 'number') {
          setTectonicBoredriveCost(data.engineerCost);
        } else {
          setTectonicBoredriveCost(null);
        }
      })
      .catch(() => {
        if (!cancelled) setTectonicBoredriveCost(null);
      });
    return () => { cancelled = true; };
  }, [ownedCountryChoice?.cellId, missionData?.id, missionData?.tectonic_boredrive_cell_ids, protectedCellId]);

  // Building page placement: track mouse on document so ghost size updates (avoids missed events).
  // ESC and right-click cancel the current placement without leaving the mission.
  useEffect(() => {
    if (!showBuildingPage || !buildingForPlacement) return;
    const onMove = (e) => setPlacementMouse({ x: e.clientX, y: e.clientY });
    const cancelPlacement = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setBuildingForPlacement(null);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
        cancelPlacement(e);
      }
    };
    const onContextMenu = (e) => {
      cancelPlacement(e);
    };
    document.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('contextmenu', onContextMenu);
    return () => {
      document.removeEventListener('mousemove', onMove);
      window.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('contextmenu', onContextMenu);
    };
  }, [showBuildingPage, buildingForPlacement]);

  // Attack mode: right-click cancels target selection (same as Cancel or clicking the attacking country again).
  useEffect(() => {
    if (!attackFromCountry || showBuildingPage) return;
    const cancelAttackMode = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setAttackFromCountry(null);
      setError(null);
    };
    document.addEventListener('contextmenu', cancelAttackMode, true);
    return () => {
      document.removeEventListener('contextmenu', cancelAttackMode, true);
    };
  }, [attackFromCountry, showBuildingPage]);

  // Trading Hub: load offers generation while on the world map so auto/player restock can notify even with the hub closed.
  useEffect(() => {
    if (showBuildingPage || !missionData?.id) return;
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return;
    const fetchOffers = async () => {
      try {
        const res = await fetch(`${API_URL}/war-effort/trading-hub/status?missionId=${missionData.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        if (data.tradingBlocked) {
          setMadagascarTradingBlocked(true);
          return;
        }
        setMadagascarTradingBlocked(false);
        if (data.nextAutoRestockAtMs == null) {
          throw new Error('Trading hub status did not return nextAutoRestockAtMs');
        }
        applyMadagascarOffersGeneration(data.nextAutoRestockAtMs);
      } catch (e) {
        console.error('Trading hub offers check:', e);
      }
    };
    fetchOffers();
    return () => { cancelled = true; };
  }, [showBuildingPage, missionData?.id, applyMadagascarOffersGeneration]);

  // Load last-seen Madagascar offers generation for this mission/player
  useEffect(() => {
    madagascarPopupGenerationRef.current = null;
    setMadagascarOffersGeneration(null);
    setMadagascarTradingBlocked(false);
    setShowMadagascarTradeHint(false);
    if (!missionData?.id || userId == null) {
      setSeenMadagascarOffersGeneration(null);
      return;
    }
    setSeenMadagascarOffersGeneration(readSeenMadagascarOffersGeneration(missionData.id, userId));
  }, [missionData?.id, userId]);

  // Opening the Trading Hub (Madagascar or trading button) marks current offers as seen
  useEffect(() => {
    if (!tradingHubOpen) return;
    if (madagascarOffersGeneration == null || !missionData?.id || userId == null) return;
    writeSeenMadagascarOffersGeneration(missionData.id, userId, madagascarOffersGeneration);
    setSeenMadagascarOffersGeneration(madagascarOffersGeneration);
    setShowMadagascarTradeHint(false);
    if (madagascarHintHideTimerRef.current) {
      clearTimeout(madagascarHintHideTimerRef.current);
      madagascarHintHideTimerRef.current = null;
    }
  }, [tradingHubOpen, madagascarOffersGeneration, missionData?.id, userId]);

  // Refetch shop when the free restock timer elapses so new offers appear even if the hub is closed
  useEffect(() => {
    if (showBuildingPage || tradingHubOpen || !missionData?.id || madagascarOffersGeneration == null || madagascarTradingBlocked) {
      if (madagascarAutoRestockTimerRef.current) {
        clearTimeout(madagascarAutoRestockTimerRef.current);
        madagascarAutoRestockTimerRef.current = null;
      }
      return;
    }
    const delayMs = madagascarOffersGeneration - Date.now();
    if (madagascarAutoRestockTimerRef.current) {
      clearTimeout(madagascarAutoRestockTimerRef.current);
      madagascarAutoRestockTimerRef.current = null;
    }
    madagascarAutoRestockTimerRef.current = setTimeout(async () => {
      madagascarAutoRestockTimerRef.current = null;
      const token = localStorage.getItem('token');
      if (!token || !missionData?.id) return;
      try {
        const res = await fetch(`${API_URL}/war-effort/trading-hub/status?missionId=${missionData.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.tradingBlocked) {
          setMadagascarTradingBlocked(true);
          return;
        }
        setMadagascarTradingBlocked(false);
        if (data.nextAutoRestockAtMs == null) {
          throw new Error('Trading hub status did not return nextAutoRestockAtMs after auto-restock');
        }
        applyMadagascarOffersGeneration(data.nextAutoRestockAtMs);
      } catch (e) {
        console.error('Trading hub auto-restock check:', e);
      }
    }, Math.max(0, delayMs) + 250);
    return () => {
      if (madagascarAutoRestockTimerRef.current) {
        clearTimeout(madagascarAutoRestockTimerRef.current);
        madagascarAutoRestockTimerRef.current = null;
      }
    };
  }, [showBuildingPage, tradingHubOpen, missionData?.id, madagascarOffersGeneration, madagascarTradingBlocked, applyMadagascarOffersGeneration]);

  // Popup "New trade options in Madagascar" when a restock creates unseen offers (not when an offer is sold)
  useEffect(() => {
    if (showBuildingPage || madagascarTradingBlocked || tradingHubOpen) {
      setShowMadagascarTradeHint(false);
      return;
    }
    if (madagascarOffersGeneration == null) return;
    const unseen = seenMadagascarOffersGeneration !== madagascarOffersGeneration;
    if (!unseen) {
      setShowMadagascarTradeHint(false);
      madagascarPopupGenerationRef.current = madagascarOffersGeneration;
      return;
    }
    if (madagascarPopupGenerationRef.current === madagascarOffersGeneration) return;
    madagascarPopupGenerationRef.current = madagascarOffersGeneration;
    setShowMadagascarTradeHint(true);
    if (madagascarHintHideTimerRef.current) {
      clearTimeout(madagascarHintHideTimerRef.current);
    }
    madagascarHintHideTimerRef.current = setTimeout(() => {
      madagascarHintHideTimerRef.current = null;
      setShowMadagascarTradeHint(false);
    }, 4500);
  }, [showBuildingPage, madagascarTradingBlocked, tradingHubOpen, madagascarOffersGeneration, seenMadagascarOffersGeneration]);

  // Fetch unlocked HQ buildings when building page is shown
  useEffect(() => {
    if (!showBuildingPage) return;
    const fetchUnlocked = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${API_URL}/war-effort/unlocked-buildings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.unlockedBuildingIds)) {
            setUnlockedBuildingIds(data.unlockedBuildingIds);
          }
        }
      } catch (err) {
        console.error('Error fetching unlocked buildings:', err);
      }
    };
    fetchUnlocked();
  }, [showBuildingPage]);

  // Load persisted HQ placements on mount (for map tooltips) and when building page opens (same mission)
  useEffect(() => {
    if (!missionData?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const loadPlacements = async () => {
      try {
        const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/hq-placements`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        const placements = data.placements || [];
        const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
        const h = typeof window !== 'undefined' ? window.innerHeight : 600;
        const converted = placements.map((p) => {
          const building = HQ_BUILDINGS.find((b) => b.id === p.building_id);
          if (!building) return null;
          const item = {
            building,
            x: (p.x_ratio ?? 0) * w,
            y: (p.y_ratio ?? 0) * h,
            size: Math.round((p.size_ratio ?? 0.1) * h),
            placementId: p.id
          };
          if (typeof p.produced_amount === 'number') {
            item.currentProduced = p.produced_amount;
            item.maxProduced = p.produced_max ?? 20;
            item.produced_per_minute = p.produced_per_minute ?? 1;
            item.last_ration_tick_at = p.last_ration_tick_at || null;
            if (typeof p.building_complete === 'boolean') item.building_complete = p.building_complete;
          }
          if (p.building_id === 'training_grounds') {
            item.trainingProgress = typeof p.training_progress === 'number' ? p.training_progress : 0;
            item.trainingReady = Boolean(p.training_ready);
            item.training_completed_at = p.training_completed_at || null;
          }
          if (p.building_id === 'barracks' || p.building_id === 'barracks_upgraded') {
            item.barracksProgress = typeof p.barracks_progress === 'number' ? p.barracks_progress : 0;
            item.barracksReady = Boolean(p.barracks_ready);
            item.barracks_completed_at = p.barracks_completed_at || null;
            item.barracks_pending_order_ids = Array.isArray(p.barracks_pending_order_ids) ? p.barracks_pending_order_ids : [];
            item.barracks_faction_id = p.barracks_faction_id != null ? parseInt(p.barracks_faction_id, 10) : null;
          }
          if (p.building_id === 'armory' || p.building_id === 'armory_upgraded') {
            item.armoryProgress = typeof p.armory_progress === 'number' ? p.armory_progress : 0;
            item.armoryReady = Boolean(p.armory_ready);
            item.armory_completed_at = p.armory_completed_at || null;
            item.armory_pending_order_ids = Array.isArray(p.armory_pending_order_ids) ? p.armory_pending_order_ids : [];
            item.armory_faction_id = p.armory_faction_id != null ? parseInt(p.armory_faction_id, 10) : null;
          }
          if (p.building_id === 'drone_hangar' || p.building_id === 'drone_hangar_upgraded') {
            item.droneHangarProgress = typeof p.drone_hangar_progress === 'number' ? p.drone_hangar_progress : 0;
            item.droneHangarReady = Boolean(p.drone_hangar_ready);
            item.drone_hangar_completed_at = p.drone_hangar_completed_at || null;
            item.drone_hangar_pending_order_ids = Array.isArray(p.drone_hangar_pending_order_ids) ? p.drone_hangar_pending_order_ids : [];
            item.drone_hangar_faction_id = p.drone_hangar_faction_id != null ? parseInt(p.drone_hangar_faction_id, 10) : null;
          }
          if (p.building_id === 'communications_hub' || p.building_id === 'communications_hub_upgraded') {
            item.comhubProgress = typeof p.comhub_progress === 'number' ? p.comhub_progress : 0;
            item.comhubReady = Boolean(p.comhub_ready);
            item.comhub_completed_at = p.comhub_completed_at || null;
            item.comhub_pending_order_ids = Array.isArray(p.comhub_pending_order_ids) ? p.comhub_pending_order_ids : [];
            item.communications_hub_faction_id = p.communications_hub_faction_id != null ? parseInt(p.communications_hub_faction_id, 10) : null;
          }
          return item;
        }).filter(Boolean);
        setPlacedBuildings(converted);
      } catch (err) {
        console.error('Error loading HQ placements:', err);
      }
    };
    loadPlacements();
    // Only poll for placement updates when building page is open
    if (!showBuildingPage) return;
    const interval = setInterval(loadPlacements, 60000); // 60s - was 20s, reduced network load
    return () => clearInterval(interval);
  }, [showBuildingPage, missionData?.id, placementRefreshTrigger]);

  const applyTradeForumState = useCallback((data) => {
    if (!data || data.level < 1) {
      throw new Error('Trade Forum: payload is missing level');
    }
    if (data.energy == null || data.energyCap == null) {
      throw new Error('Trade Forum: payload is missing energy');
    }
    setTradeForum(data);
    setPlayerResource((prev) => {
      if (!prev) {
        return { type: 'energy', displayName: 'Energy', amount: data.energy, cap: data.energyCap };
      }
      return { ...prev, amount: data.energy, cap: data.energyCap };
    });
    setTradeForumAmounts((prev) => {
      const next = { ...prev };
      for (const resource of TRADE_FORUM_RESOURCE_ORDER) {
        if (next[resource] == null) next[resource] = 1000;
      }
      return next;
    });
    if (data.level >= 2 && data.automation) {
      setTradeForumAutoDraft((prev) => prev || tradeForumAutoDraftFromAutomation(data.automation));
    }
  }, []);

  const fetchTradeForum = useCallback(() => {
    if (!missionData?.id || !hasTradeForum) return;
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Not logged in');
    }
    fetch(`${API_URL}/war-effort/mission/${missionData.id}/trade-forum`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) {
          if (body.error === 'Trade Forum is not placed in this mission') {
            setTradeForum(null);
            return;
          }
          throw new Error(body.error || 'Failed to load Trade Forum');
        }
        applyTradeForumState(body);
        setTradeForumError(null);
      })
      .catch((err) => {
        setTradeForumError(err.message || 'Failed to load Trade Forum');
      });
  }, [missionData?.id, hasTradeForum, applyTradeForumState]);

  useEffect(() => {
    if (showBuildingPage || !isUserInMission || !hasTradeForum) {
      setTradeForum(null);
      return undefined;
    }
    fetchTradeForum();
    const interval = setInterval(fetchTradeForum, 30000);
    const handler = () => fetchTradeForum();
    window.addEventListener('war-effort-mission-update', handler);
    return () => {
      clearInterval(interval);
      window.removeEventListener('war-effort-mission-update', handler);
    };
  }, [showBuildingPage, isUserInMission, hasTradeForum, fetchTradeForum]);

  const postTradeForumTrade = (resource, side) => {
    const token = localStorage.getItem('token');
    if (!token || !missionData?.id) {
      throw new Error('Not logged in');
    }
    if (tradeForum && tradeForum.trumpManeuverBlocked === true) {
      setTradeForumError('Trump Maneuver: opponents can only watch. Trading is locked.');
      return;
    }
    const amount = parseInt(tradeForumAmounts[resource], 10);
    if (!Number.isInteger(amount) || amount < 1) {
      setTradeForumError('Enter a positive amount');
      return;
    }
    setTradeForumBusyKey(`${resource}-${side}`);
    setTradeForumError(null);
    fetch(`${API_URL}/war-effort/mission/${missionData.id}/trade-forum/trade`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ resource, side, amount })
    })
      .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) {
          throw new Error(body.error || 'Failed to trade');
        }
        applyTradeForumState(body);
        playSoundEffect('/sounds/forum.mp3');
      })
      .catch((err) => {
        setTradeForumError(err.message || 'Failed to trade');
      })
      .finally(() => {
        setTradeForumBusyKey(null);
      });
  };

  const deactivateTradeForumAutomation = (fromEdit) => {
    const token = localStorage.getItem('token');
    if (!token || !missionData?.id) {
      throw new Error('Not logged in');
    }
    if (!fromEdit) {
      setTradeForumAutoSaving(true);
      setTradeForumError(null);
    }
    setTradeForum((prev) => {
      if (!prev || !prev.automation) return prev;
      return { ...prev, automation: { ...prev.automation, active: false } };
    });
    fetch(`${API_URL}/war-effort/mission/${missionData.id}/trade-forum/automation`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ active: false })
    })
      .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) {
          throw new Error(body.error || 'Failed to deactivate automation');
        }
        setTradeForum((prev) => (prev ? { ...prev, automation: body.automation } : prev));
        if (!fromEdit) {
          playSoundEffect('/sounds/click.mp3');
        }
      })
      .catch((err) => {
        setTradeForumError(err.message || 'Failed to deactivate automation');
      })
      .finally(() => {
        if (!fromEdit) {
          setTradeForumAutoSaving(false);
        }
      });
  };

  const activateTradeForumAutomation = () => {
    const token = localStorage.getItem('token');
    if (!token || !missionData?.id) {
      throw new Error('Not logged in');
    }
    if (!tradeForumAutoDraft) {
      throw new Error('Trade Forum: automation draft is missing');
    }
    const toIntOrNull = (value) => {
      if (value === '' || value == null) return null;
      const n = parseInt(value, 10);
      if (!Number.isInteger(n)) {
        throw new Error('Trade Forum: automation values must be integers');
      }
      return n;
    };
    let payload;
    try {
      payload = {
        active: true,
        sellEnergyBelow: toIntOrNull(tradeForumAutoDraft.sellEnergyBelow),
        sellResourceOver: toIntOrNull(tradeForumAutoDraft.sellResourceOver),
        sellStockAbove: toIntOrNull(tradeForumAutoDraft.sellStockAbove),
        sellAmount: toIntOrNull(tradeForumAutoDraft.sellAmount),
        sellResources: tradeForumAutoDraft.sellResources,
        buyEnergyAbove: toIntOrNull(tradeForumAutoDraft.buyEnergyAbove),
        buyResourceUnder: toIntOrNull(tradeForumAutoDraft.buyResourceUnder),
        buyStockBelow: toIntOrNull(tradeForumAutoDraft.buyStockBelow),
        buyAmount: toIntOrNull(tradeForumAutoDraft.buyAmount),
        buyResources: tradeForumAutoDraft.buyResources
      };
    } catch (err) {
      setTradeForumError(err.message || 'Failed to activate automation');
      return;
    }
    setTradeForumAutoSaving(true);
    setTradeForumError(null);
    fetch(`${API_URL}/war-effort/mission/${missionData.id}/trade-forum/automation`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    })
      .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) {
          throw new Error(body.error || 'Failed to activate automation');
        }
        setTradeForum((prev) => (prev ? { ...prev, automation: body.automation } : prev));
        setTradeForumAutoDraft({
          sellEnergyBelow: body.automation.sellEnergyBelow ?? '',
          sellResourceOver: body.automation.sellResourceOver ?? '',
          sellStockAbove: body.automation.sellStockAbove ?? '',
          sellAmount: body.automation.sellAmount ?? '',
          sellResources: Array.isArray(body.automation.sellResources) ? body.automation.sellResources : [],
          buyEnergyAbove: body.automation.buyEnergyAbove ?? '',
          buyResourceUnder: body.automation.buyResourceUnder ?? '',
          buyStockBelow: body.automation.buyStockBelow ?? '',
          buyAmount: body.automation.buyAmount ?? '',
          buyResources: Array.isArray(body.automation.buyResources) ? body.automation.buyResources : []
        });
        playSoundEffect('/sounds/click.mp3');
      })
      .catch((err) => {
        setTradeForumError(err.message || 'Failed to activate automation');
      })
      .finally(() => {
        setTradeForumAutoSaving(false);
      });
  };

  const handleTradeForumAmountChange = (resource, value) => {
    setTradeForumAmounts((prev) => ({ ...prev, [resource]: value }));
  };

  const handleTradeForumAutoFieldChange = (field, value) => {
    setTradeForumAutoDraft((prev) => ({ ...(prev || emptyTradeForumAutoDraft()), [field]: value }));
    if (tradeForum?.automation?.active === true) {
      deactivateTradeForumAutomation(true);
    }
  };

  const handleTradeForumToggleAutoResource = (field, resource) => {
    setTradeForumAutoDraft((prev) => {
      const draft = prev || emptyTradeForumAutoDraft();
      const current = Array.isArray(draft[field]) ? draft[field] : [];
      const next = current.includes(resource)
        ? current.filter((id) => id !== resource)
        : [...current, resource];
      return { ...draft, [field]: next };
    });
    if (tradeForum?.automation?.active === true) {
      deactivateTradeForumAutomation(true);
    }
  };

  const renderTradeForumMapPanel = () => (
    hasTradeForum ? (
      <TradeForumMapPanel
        tradeForum={tradeForum}
        tradeForumError={tradeForumError}
        tradeForumAmounts={tradeForumAmounts}
        tradeForumBusyKey={tradeForumBusyKey}
        tradeForumAutoDraft={tradeForumAutoDraft}
        tradeForumAutoSaving={tradeForumAutoSaving}
        onAmountChange={handleTradeForumAmountChange}
        onTrade={postTradeForumTrade}
        onAutoFieldChange={handleTradeForumAutoFieldChange}
        onToggleAutoResource={handleTradeForumToggleAutoResource}
        onActivateAutomation={activateTradeForumAutomation}
        onDeactivateAutomation={() => deactivateTradeForumAutomation(false)}
      />
    ) : null
  );

  const handleCountryClick = (cellId) => {
    playSoundEffect('/sounds/click.mp3');
    const cellIdNum = Number(cellId);
    
    // DEV MODE: If dev mode is active and a purpose is selected, set country ownership
    if (devModeActive && devSelectedPurpose !== null) {
      handleDevSetCountryPurpose(cellIdNum);
      return;
    }
    
    // If in attack mode, treat click as selecting target or cancel (must run before "direct click" message)
    if (attackFromCountry) {
      if (cellIdNum === attackFromCountry.cellId) {
        setAttackFromCountry(null);
        setError(null);
        return;
      }
      const oppHqCellId = opponentPurpose ? PURPOSE_TO_HQ_CELL_ID[opponentPurpose] : null;
      const isFfa = missionData?.mission_mode === 'ffa';
      const ffaHqEntry = isFfa && Array.isArray(enemyHqAttackInfo?.attackableHQs) && enemyHqAttackInfo.attackableHQs.find(h => h.hqCellId === cellIdNum);
      const isOpponentHQ = ffaHqEntry || (oppHqCellId != null && cellIdNum === oppHqCellId);
      if (isOpponentHQ) {
        if (isCeaseFireActive) {
          setError(t('warEffort.ceaseFireNoAttacksFor', { time: formatCeaseFireCountdown(ceaseFireRemainingSeconds) }));
          return;
        }
        if (isConquestAttackerCeaseFireActive) {
          setError(t('warEffort.cannotAttackHqAfterConquest', { time: formatCeaseFireCountdown(conquestAttackerCeaseFireRemainingSeconds) }));
          return;
        }
        if (ffaHqEntry) {
          setAttackEnemyHQDialog({
            show: true,
            countryName: ffaHqEntry.countryName || getCountryFromCellId(cellIdNum),
            hqCellId: ffaHqEntry.hqCellId,
            movementCost: ffaHqEntry.movementCost,
            defenderUserId: ffaHqEntry.defenderUserId
          });
        } else {
          const infoCost = enemyHqAttackInfo?.hqCellId != null && Number(enemyHqAttackInfo.hqCellId) === cellIdNum
            ? enemyHqAttackInfo.movementCost
            : enemyHqAttackInfo?.movementCost;
          setAttackEnemyHQDialog({
            show: true,
            countryName: getCountryFromCellId(cellIdNum),
            hqCellId: oppHqCellId,
            movementCost: infoCost ?? null,
            defenderUserId: null
          });
        }
        setError(null);
        return;
      }
      if (opponentFortifiedCountries.includes(cellIdNum) || opponentFortifiedCountries.includes(cellId)) {
        if (isCeaseFireActive) {
          setError(t('warEffort.ceaseFireNoCountriesFor', { time: formatCeaseFireCountdown(ceaseFireRemainingSeconds) }));
          return;
        }
        if (isConquestAttackerCeaseFireActive) {
          setError(t('warEffort.cannotAttackAnotherFortified', { time: formatCeaseFireCountdown(conquestAttackerCeaseFireRemainingSeconds) }));
          return;
        }
        if (isOpponentFortifiedCeaseFireActive) {
          setError(t('warEffort.opponentFortsProtectedFor', { time: formatCeaseFireCountdown(opponentFortifiedCeaseFireRemainingSeconds) }));
          return;
        }
        const reconquerEntry = reconquerCooldowns.find((r) => r.cell_id === cellIdNum);
        if (reconquerEntry && reconquerEntry.remaining_seconds > 0) {
          setError(t('warEffort.cannotReconquerFor', { time: formatCeaseFireCountdown(reconquerEntry.remaining_seconds) }));
          return;
        }
        let opponentFortifications = isPlayer1 ? missionData?.player2_fortifications : missionData?.player1_fortifications;
        if (missionData?.mission_mode === 'ffa' && missionData && viewerSlot >= 1 && viewerSlot <= 4) {
          for (let s = 1; s <= 4; s++) {
            if (s === viewerSlot) continue;
            const countries = missionData[`player${s}_fortified_countries`];
            const arr = Array.isArray(countries) ? countries : (countries ? [].concat(countries) : []);
            if (arr.includes(cellIdNum) || arr.includes(Number(cellIdNum))) {
              opponentFortifications = missionData[`player${s}_fortifications`] || {};
              break;
            }
          }
        }
        const fortificationData = opponentFortifications && (opponentFortifications[cellIdNum] || opponentFortifications[String(cellIdNum)]);
        const fortificationType = fortificationData?.type || 'unknown';
        const fortification = FORTIFICATION_TYPES.find(f => f.type === fortificationType);
        const fortificationName = fortificationType === 'eclipse_hidden' ? t('warEffort.fortificationTypes.eclipse_hidden') : (fortification ? t(`warEffort.fortificationTypes.${fortification.type}`) : fortificationType);
        setAttackDialog({
          show: true,
          countryCellId: cellIdNum,
          fortificationType: fortificationName,
          fortificationTypeHidden: fortificationType === 'eclipse_hidden',
          countryName: getCountryFromCellId(cellIdNum)
        });
        setError(null);
        return;
      }
      const cellOwnerInAttack = cellOwnersMap[String(cellIdNum)] || cellOwnersMap[String(cellId)];
      if (cellOwnerInAttack) {
        if (cellIdNum === SENTIENTS_HQ_CELL_ID) {
          setTradingHubOpen(true);
          setAttackFromCountry(null);
          setError(null);
          return;
        }
        const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
        const allowedFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
        const opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
        const opponentAllowedFactions = opponentPurpose ? PURPOSE_TO_FACTION_MAPPING[opponentPurpose] || [] : [];
        const allOpponentFactions = (missionData?.mission_mode === 'ffa' && missionData)
          ? [1, 2, 3, 4]
              .filter(s => (s !== viewerSlot && missionData[`player${s}_id`] != null))
              .flatMap(s => PURPOSE_TO_FACTION_MAPPING[missionData[`player${s}_purpose`]] || [])
          : opponentAllowedFactions;
        const isPlayerFaction = allowedFactions.includes(cellOwnerInAttack);
        const isOpponentFaction = (missionData?.mission_mode === 'ffa' ? allOpponentFactions : opponentAllowedFactions).includes(cellOwnerInAttack);
        if (isOpponentFaction && Object.values(PURPOSE_TO_HQ_CELL_ID).includes(cellIdNum)) {
          const defId = getDefenderUserIdForHQCell(missionData, cellIdNum);
          if (missionData?.mission_mode === 'ffa' && defId != null && Number(defId) !== Number(userId)) {
            const inAttackable = Array.isArray(enemyHqAttackInfo?.attackableHQs) && enemyHqAttackInfo.attackableHQs.some(h => h.hqCellId === cellIdNum);
            if (!inAttackable) {
              setError(t('warEffort.opponentStillHasForts'));
              return;
            }
          } else if (missionData?.mission_mode !== 'ffa' && oppHqCellId != null && cellIdNum !== oppHqCellId) {
            setError(t('warEffort.onlyOpponentHqAttackable'));
            return;
          }
        }
        if (isOpponentFaction && (oppHqCellId == null || cellIdNum !== oppHqCellId) && (missionData?.mission_mode === 'ffa' || missionData?.player2_id)) {
          if (isCeaseFireActive) {
            setError(t('warEffort.ceaseFireNoCountriesFor', { time: formatCeaseFireCountdown(ceaseFireRemainingSeconds) }));
            return;
          }
          const reconquerEntry = reconquerCooldowns.find((r) => r.cell_id === cellIdNum);
          if (reconquerEntry && reconquerEntry.remaining_seconds > 0) {
            setError(t('warEffort.cannotReconquerFor', { time: formatCeaseFireCountdown(reconquerEntry.remaining_seconds) }));
            return;
          }
          setAttackOtherCountryDialog({
            show: true,
            countryCellId: cellIdNum,
            countryName: getCountryFromCellId(cellIdNum),
            ownerFaction: cellOwnerInAttack,
            isOpponentUnfortified: true
          });
          setError(null);
          return;
        }
        if (!isPlayerFaction && !isOpponentFaction && (missionData?.mission_mode === 'ffa' || missionData?.player2_id)) {
          if (Object.values(PURPOSE_TO_HQ_CELL_ID).includes(cellIdNum)) {
            setError(t('warEffort.factionHqOnlyOpponent'));
            return;
          }
          if (isCeaseFireActive) {
            setError(t('warEffort.ceaseFireNoCountriesFor', { time: formatCeaseFireCountdown(ceaseFireRemainingSeconds) }));
            return;
          }
          setAttackOtherCountryDialog({
            show: true,
            countryCellId: cellIdNum,
            countryName: getCountryFromCellId(cellIdNum),
            ownerFaction: cellOwnerInAttack,
            isOpponentUnfortified: false
          });
          setError(null);
          return;
        }
      }
      setAttackFromCountry(null);
      setError(null);
      return;
    }
    
    // Opponent or other faction country clicked without attack mode: require "own country → Attack → then target"
    const isControlled = controlledCountries.includes(cellIdNum) || controlledCountries.includes(cellId);
    const cellOwner = cellOwnersMap[String(cellIdNum)] || cellOwnersMap[String(cellId)];
    if (cellOwner && !isControlled) {
      if (cellIdNum === SENTIENTS_HQ_CELL_ID) {
        setTradingHubOpen(true);
        setError(null);
        return;
      }
      const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
      const allowedFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
      const opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
      const opponentAllowedFactions = opponentPurpose ? PURPOSE_TO_FACTION_MAPPING[opponentPurpose] || [] : [];
      const isPlayerFaction = allowedFactions.includes(cellOwner);
      const isOpponentFaction = opponentAllowedFactions.includes(cellOwner);
      if (isOpponentFaction) {
        setError(t('warEffort.toAttackClickCountry'));
        return;
      }
      if (!isPlayerFaction && !isOpponentFaction) {
        setError(t('warEffort.toAttackClickCountry'));
        return;
      }
    }
    if (!isControlled && (opponentFortifiedCountries.includes(cellIdNum) || opponentFortifiedCountries.includes(cellId))) {
      setError(t('warEffort.toAttackClickCountry'));
      return;
    }
    
    // Debug: Log when click fails
    if (!isControlled && controlledCountries.length > 0) {
      console.warn('Country not controlled:', {
        cellId,
        cellIdNum,
        isInArray: controlledCountries.includes(cellIdNum),
        controlledCount: controlledCountries.length,
        sampleControlled: controlledCountries.slice(0, 5)
      });
    }
    
    // Check if country is controlled by player's faction (based on purpose)
    if (!isControlled) {
      setError(t('warEffort.doNotControlCountry', { cellId, controlled: controlledCountries.join(', ') }));
      return;
    }
    
    // Owned country: show Fortify vs Attack choice only when not headquarters
    const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
    const hqCellId = playerPurpose ? PURPOSE_TO_HQ_CELL_ID[playerPurpose] : null;
    if (hqCellId != null && cellIdNum !== hqCellId) {
      setOwnedCountryChoice({ cellId: cellIdNum, countryName: getCountryFromCellId(cellIdNum) });
      setSelectedCountry(null);
      setSelectedFortification(null);
      setError(null);
      return;
    }
    
    // Own HQ: show Attack Country / Show HQ choice dialog (do not instantly enter build mode)
    if (hqCellId != null && cellIdNum === hqCellId) {
      setOwnedHQChoice({ cellId: cellIdNum, countryName: getCountryFromCellId(cellIdNum) });
      setSelectedCountry(null);
      setSelectedFortification(null);
      setError(null);
      return;
    }
    
    // No HQ mapping: go straight to fortification selection for other owned cells
    if (isOwnedCellProtected(cellIdNum)) {
      setError(t('warEffort.tradingHub.countryProtectedNoFortify'));
      return;
    }
    if (playerFortifiedCountries.includes(cellId) || playerFortifiedCountries.includes(cellIdNum)) {
      setError(t('warEffort.alreadyFortified'));
      return;
    }
    if (playerFortificationSlotsUsed >= 3) {
      setError(t('warEffort.maxThreeFortified'));
      return;
    }
    setSelectedCountry(cellId);
    setSelectedFortification(null);
    setError(null);
  };

  const handleCountryClickRef = useRef(handleCountryClick);
  handleCountryClickRef.current = handleCountryClick;
  const onWorldMapCountryClick = useCallback((cellId) => {
    handleCountryClickRef.current(cellId);
  }, []);

  const worldMapDevHighlightCells = useMemo(
    () => (devShowFortifications ? devCommandCenterCells.allCells : EMPTY_CELL_IDS),
    [devShowFortifications, devCommandCenterCells.allCells]
  );

  const madagascarTradeHintMessage = t('warEffort.newTradeOptionsMadagascarHint', 'New trade options in Madagascar');
  const worldMapHintPopup = useMemo(() => {
    if (!showMadagascarTradeHint || madagascarTradingBlocked) return null;
    return { cellId: SENTIENTS_HQ_CELL_ID, message: madagascarTradeHintMessage };
  }, [showMadagascarTradeHint, madagascarTradingBlocked, madagascarTradeHintMessage]);

  const worldMapNewTradeOfferCells = useMemo(() => {
    const showOffers = !showBuildingPage && !tradingHubOpen && !madagascarTradingBlocked
      && madagascarOffersGeneration != null
      && seenMadagascarOffersGeneration !== madagascarOffersGeneration;
    return showOffers ? [SENTIENTS_HQ_CELL_ID] : EMPTY_CELL_IDS;
  }, [showBuildingPage, tradingHubOpen, madagascarTradingBlocked, madagascarOffersGeneration, seenMadagascarOffersGeneration]);
  
  const handleFortifyWithType = async (fortificationType) => {
    if (!selectedCountry || !fortificationType) {
      setError(t('warEffort.selectCountryAndType'));
      return;
    }

    const fortification = FORTIFICATION_TYPES.find(f => f.type === fortificationType);
    if (!fortification) {
      setError(t('warEffort.invalidFortificationType'));
      return;
    }

    if (playerResources < fortification.cost) {
      setError(t('warEffort.insufficientResourcesNeedHave', { need: fortification.cost, have: playerResources }));
      return;
    }

    if (playerFortificationSlotsUsed >= 3) {
      setError(t('warEffort.maxThreeFortified'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSelectedFortification(fortificationType);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/fortify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          countryCellId: selectedCountry,
          fortificationType: fortificationType
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Update mission data (use viewerSlot for FFA/2v2 so correct player slot is updated)
        const updatedMission = { ...missionData };
        const slot = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') ? viewerSlot : (isPlayer1 ? 1 : 2);
        if (slot >= 1 && slot <= 4) {
          const countriesKey = `player${slot}_fortified_countries`;
          const resourcesKey = `player${slot}_resources`;
          const slotsKey = `player${slot}_fortification_slots_used`;
          const fortificationsKey = `player${slot}_fortifications`;
          updatedMission[resourcesKey] = result.remainingResources;
          updatedMission[countriesKey] = [...playerFortifiedCountries, selectedCountry];
          updatedMission[slotsKey] = playerFortificationSlotsUsed + 1;
          // Merge new fortification into player*_fortifications so tooltip shows manual fortification immediately
          // (e.g. when country already had Command Center bonus, tooltip reads from this object)
          const cellIdNum = Number(selectedCountry);
          const existingForts = updatedMission[fortificationsKey] && typeof updatedMission[fortificationsKey] === 'object' ? updatedMission[fortificationsKey] : {};
          const newFortEntry = { type: fortificationType };
          updatedMission[fortificationsKey] = {
            ...existingForts,
            [cellIdNum]: newFortEntry,
            [String(cellIdNum)]: newFortEntry
          };
        }
        setMissionData(updatedMission);

        // Reset selection
        setSelectedCountry(null);
        setSelectedFortification(null);

        // Refresh controlled countries
        fetchControlledCountries();

        if (onFortificationComplete) {
          onFortificationComplete(updatedMission);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fortify country');
        setSelectedFortification(null);
      }
    } catch (error) {
      console.error('Error fortifying country:', error);
      setError(t('warEffort.failedToFortify'));
      setSelectedFortification(null);
    } finally {
      setLoading(false);
    }
  };
  
  const playerResources = playerResource ? playerResource.amount : 0;
  const availableFortifications = FORTIFICATION_TYPES.filter(f => playerResources >= f.cost);
  
  // Handle attack on opponent's fortified country
  const handleAttackOpponentFortified = async () => {
    if (!attackDialog.countryCellId) {
      setError(t('warEffort.missingCountryToAttack'));
      setAttackDialog({ show: false, countryCellId: null, fortificationType: null, countryName: null });
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(appendLangQueryToUrl(`${API_URL}/war-effort/attack-fortified`, i18nInstance.language), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          countryCellId: attackDialog.countryCellId,
          ...(attackFromCountry?.cellId != null && { fromCellId: attackFromCountry.cellId })
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAttackDialog({ show: false, countryCellId: null, fortificationType: null, countryName: null });
        setAttackFromCountry(null);
        if (data.pendingEliteDefense === true) {
          if (data.expiresAt == null || !data.countryName) {
            throw new Error('Attack fortified: pending elite defense is missing expiresAt or countryName');
          }
          setPendingEliteAttackWait({
            missionId: missionData.id,
            countryCellId: data.countryCellId,
            countryName: data.countryName,
            battleType: data.battleType,
            expiresAt: data.expiresAt
          });
          if (data.countryCellId != null) {
            const cellNum = Number(data.countryCellId);
            setCellsUnderAttack((prev) => {
              if (!Array.isArray(prev)) {
                throw new Error('cellsUnderAttack must be an array');
              }
              return prev.includes(cellNum) ? prev : [...prev, cellNum];
            });
          }
          return;
        }
        if (!data.gameId || !data.gameState) {
          throw new Error('Attack fortified: response is missing gameId or gameState');
        }
        navigate(`/battle/${data.gameId}`, { state: { gameState: data.gameState, gameId: data.gameId } });
      } else {
        const errorData = await response.json();
        setError(errorData.errorKey ? t(`warEffort.${errorData.errorKey}`) : (errorData.error || 'Failed to attack fortified country'));
        // Don't close dialog on error - let user try again
      }
    } catch (error) {
      console.error('Error attacking fortified country:', error);
      setError(t('warEffort.failedToAttackFortified'));
      // Don't close dialog on error - let user try again
    } finally {
      setLoading(false);
    }
  };
  
  // Handle attack on other faction's country
  const handleAttackOtherCountry = async () => {
    if (!attackOtherCountryDialog.countryCellId) {
      setError(t('warEffort.missingCountryToAttack'));
      setAttackOtherCountryDialog({ show: false, countryCellId: null, countryName: null, ownerFaction: null, isOpponentUnfortified: false });
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const endpoint = attackOtherCountryDialog.isOpponentUnfortified
        ? appendLangQueryToUrl(`${API_URL}/war-effort/attack-opponent-country`, i18nInstance.language)
        : appendLangQueryToUrl(`${API_URL}/war-effort/attack-other-country`, i18nInstance.language);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          countryCellId: attackOtherCountryDialog.countryCellId,
          ...(attackFromCountry?.cellId != null && { fromCellId: attackFromCountry.cellId })
        })
      });
      
      if (response.ok) {
        const { gameId, gameState } = await response.json();
        setAttackOtherCountryDialog({ show: false, countryCellId: null, countryName: null, ownerFaction: null, isOpponentUnfortified: false });
        setAttackFromCountry(null);
        navigate(`/battle/${gameId}`, { state: { gameState, gameId } });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to attack country');
        // Don't close dialog on error - let user try again
      }
    } catch (error) {
      console.error('Error attacking other country:', error);
      setError(t('warEffort.failedToAttackCountry'));
      // Don't close dialog on error - let user try again
    } finally {
      setLoading(false);
    }
  };

  // Handle attack on enemy HQ (when opponent has no fortified countries left; cost = movement + 400; winner wins mission)
  const handleAttackEnemyHQ = async () => {
    const resolvedCost = (() => {
      if (missionData?.mission_mode === 'ffa' && Array.isArray(enemyHqAttackInfo?.attackableHQs)) {
        const entry = enemyHqAttackInfo.attackableHQs.find((h) => Number(h.hqCellId) === Number(attackEnemyHQDialog.hqCellId));
        if (entry?.movementCost != null) return entry.movementCost;
      }
      if (enemyHqAttackInfo?.movementCost != null) return enemyHqAttackInfo.movementCost;
      return attackEnemyHQDialog.movementCost;
    })();
    if (!attackEnemyHQDialog.hqCellId || resolvedCost == null) {
      setError(t('warEffort.missingHqAttackInfo'));
      setAttackEnemyHQDialog({ show: false, countryName: null, hqCellId: null, movementCost: null, defenderUserId: null });
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(appendLangQueryToUrl(`${API_URL}/war-effort/attack-enemy-hq`, i18nInstance.language), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          ...(attackFromCountry?.cellId != null && { fromCellId: attackFromCountry.cellId }),
          ...(attackEnemyHQDialog?.hqCellId != null && { hqCellId: attackEnemyHQDialog.hqCellId }),
          ...(attackEnemyHQDialog?.defenderUserId != null && { defenderUserId: attackEnemyHQDialog.defenderUserId })
        })
      });
      if (response.ok) {
        const data = await response.json();
        setAttackEnemyHQDialog({ show: false, countryName: null, hqCellId: null, movementCost: null, defenderUserId: null });
        setAttackFromCountry(null);
        setEnemyHqAttackInfo(null);
        if (data.pendingEliteDefense === true) {
          if (data.expiresAt == null || !data.countryName) {
            throw new Error('Attack HQ: pending elite defense is missing expiresAt or countryName');
          }
          setPendingEliteAttackWait({
            missionId: missionData.id,
            countryCellId: data.countryCellId,
            countryName: data.countryName,
            battleType: data.battleType,
            expiresAt: data.expiresAt
          });
          if (data.countryCellId != null) {
            const cellNum = Number(data.countryCellId);
            setCellsUnderAttack((prev) => {
              if (!Array.isArray(prev)) {
                throw new Error('cellsUnderAttack must be an array');
              }
              return prev.includes(cellNum) ? prev : [...prev, cellNum];
            });
          }
          return;
        }
        if (!data.gameId || !data.gameState) {
          throw new Error('Attack HQ: response is missing gameId or gameState');
        }
        navigate(`/battle/${data.gameId}`, { state: { gameState: data.gameState, gameId: data.gameId } });
      } else {
        const errorData = await response.json();
        setError(errorData.errorKey ? t(`warEffort.${errorData.errorKey}`) : (errorData.error || 'Failed to attack enemy HQ'));
      }
    } catch (error) {
      console.error('Error attacking enemy HQ:', error);
      setError(t('warEffort.failedToAttackEnemyHq'));
    } finally {
      setLoading(false);
    }
  };
  
  const energyAmount = playerResource ? playerResource.amount : 0;
  const energyCap = (playerResource && typeof playerResource.cap === 'number') ? playerResource.cap : 1000;
  const energyFillPercent = energyCap > 0 ? Math.min(100, Math.max(0, (energyAmount / energyCap) * 100)) : 0;
  const energyProductionPerMinute = territoryBonuses
    ? computeEnergyProductionPerMinute(placedBuildings, territoryBonuses, activeShopEffects)
    : null;
  const getEffectiveBuildingCost = (baseCost) => Math.floor((baseCost || 0) * (buildingCostMultiplier ?? 1));
  const storedHqResources = getStoredHqResourcesFromPlacements(placedBuildings);

  const handleTransferEnergy = useCallback(async (amount) => {
    if (!missionData?.id || transferEnergyLoading) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setTransferEnergyLoading(true);
    try {
      const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/transfer-energy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setTransferEnergyModalOpen(false);
        const transferred = data.transferred ?? 0;
        const kept = data.kept ?? 0;
        if (playerResource && typeof playerResource.amount === 'number') {
          setPlayerResource((prev) => prev ? { ...prev, amount: prev.amount - transferred } : null);
        }
        if (transferred > 0) {
          if (kept > 0) {
            setError(t('warEffort.transferredEnergyKept', { transferred, kept }));
          } else {
            setError(t('warEffort.transferredEnergy', { transferred }));
          }
        } else if (kept > 0) {
          setError(t('warEffort.allyAtEnergyCap'));
        }
        fetchControlledCountries();
      } else {
        setError(data.error || 'Failed to transfer energy');
      }
    } catch (err) {
      console.error('Transfer energy:', err);
      setError(t('warEffort.failedToTransferEnergy'));
    } finally {
      setTransferEnergyLoading(false);
    }
  }, [missionData?.id, transferEnergyLoading, playerResource, fetchControlledCountries]);

  // Producer display: same formula as server so UI shows the amount that will be collected
  const [producerTickAt, setProducerTickAt] = useState(0);
  useEffect(() => {
    if (!showBuildingPage) return;
    const interval = setInterval(() => setProducerTickAt(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [showBuildingPage]);

  const powerplantPlacementsRef = useRef([]);
  powerplantPlacementsRef.current = showBuildingPage
    ? placedBuildings
        .filter((item) => item.building?.id === 'powerplant' || item.building?.id === 'powerplant_upgraded')
        .map((p) => ({ x: p.x, y: p.y, amount: p.building?.id === 'powerplant_upgraded' ? 16 : 8 }))
    : [];

  // Powerplant energy grant animation every 60s (server grants 8 energy per powerplant per minute, 16 for upgraded)
  // When the animation plays, optimistically update the energy bar so it stays in sync with the visual
  useEffect(() => {
    if (!showBuildingPage) return;
    const run = () => {
      const placements = powerplantPlacementsRef.current;
      if (placements.length === 0) return;
      const totalGrant = placements.reduce((sum, p) => sum + (p.amount ?? 0), 0);
      setPlayerResource((prev) => {
        if (!prev) return prev;
        const cap = typeof prev.cap === 'number' ? prev.cap : 1000;
        const current = typeof prev.amount === 'number' ? prev.amount : 0;
        return { ...prev, amount: Math.min(cap, current + totalGrant) };
      });
      setPowerplantFlashPlacements([...placements]);
      setTimeout(() => setPowerplantFlashPlacements([]), 900);
    };
    const interval = setInterval(run, 60000);
    return () => clearInterval(interval);
  }, [showBuildingPage]);

  // Fetch full order data for barracks "Choose an order" modal (OrderDesign cards)
  useEffect(() => {
    if (!barracksOrdersModal.show || !barracksOrdersModal.orders?.length) {
      setBarracksEnrichedOrders([]);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    const fetchAll = async () => {
      const enriched = await Promise.all(
        barracksOrdersModal.orders.map(async (ord) => {
          try {
            const res = await fetch(`${API_URL}/order/${ord.id}/full?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`Order ${ord.id} not found`);
            const full = await res.json();
            full.faction = full.faction || full.faction_name;
            full.rarity = ord.rarity || full.rarity;
            return full;
          } catch (err) {
            console.error(`Failed to fetch order ${ord.id}:`, err);
            return null;
          }
        })
      );
      setBarracksEnrichedOrders(enriched.filter(Boolean));
    };
    fetchAll();
  }, [barracksOrdersModal.show, barracksOrdersModal.orders, i18nInstance.language]);

  function getDisplayedProduced(item) {
    const stored = typeof item.currentProduced === 'number' ? item.currentProduced : 0;
    const max = item.maxProduced;
    if (max == null) {
      return stored;
    }
    return Math.min(max, stored);
  }

  // Mission over: show win/loss screen for winner or loser (including when they were offline). Both can dismiss.
  // Dismissed mission ids are persisted in sessionStorage so a stale fetch/websocket cannot reopen AfterBattle.
  const missionAlreadyDismissed = missionData?.id != null && isWarEffortMissionDismissed(missionData.id);
  const justDismissedMissionId = missionAlreadyDismissed
    || (missionData?.status === 'completed' && location.state?.dismissedMissionId != null && Number(location.state.dismissedMissionId) === Number(missionData.id));

  useEffect(() => {
    if (!justDismissedMissionId) return;
    onBack();
  }, [justDismissedMissionId, onBack]);

  if (justDismissedMissionId || (missionData?.status === 'completed' && missionAlreadyDismissed)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px', color: '#fff' }}>
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  // Winners with rewards: redirect to AfterBattle so they get the same look/feel as "Dev War Effort Rewards" (wareffort_success.jpg, message phase, flip cards, grant).
  if (missionData?.status === 'completed') {
    const playerWon = missionData.mission_mode === 'ffa'
      ? (userId != null && missionData.winner_id != null && Number(missionData.winner_id) === Number(userId))
      : ((isPlayer1 && missionData.player1_mission_complete) || (!isPlayer1 && missionData.player2_mission_complete));
    if (playerWon && missionData.mission_end_game_id) {
      return (
        <WarEffortRedirectToAfterBattle
          missionId={missionData.id}
          missionEndGameId={missionData.mission_end_game_id}
          result="win"
          navigate={navigate}
        />
      );
    }
    if (!playerWon && missionData.mission_end_game_id) {
      return (
        <WarEffortRedirectToAfterBattle
          missionId={missionData.id}
          missionEndGameId={missionData.mission_end_game_id}
          result="loss"
          navigate={navigate}
        />
      );
    }
    return (
      <WarEffortMissionOverScreen
        playerWon={playerWon}
        missionId={missionData.id}
        missionEndGameId={missionData.mission_end_game_id}
        onBack={onBack}
        API_URL={API_URL}
        t={t}
      />
    );
  }

  const fortifiedCountryWarningOverlay = fortifiedCountryUnderAttackWarning && fortifiedCountryUnderAttackWarning.countryNames?.length > 0 && typeof document !== 'undefined' && createPortal(
    <div
      className="fortified-country-under-attack-warning"
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 100001,
        padding: '24px 48px',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        border: '4px solid #cc0000',
        borderRadius: '12px',
        color: '#fff',
        fontSize: '22px',
        fontWeight: 'bold',
        textAlign: 'center',
        boxShadow: '0 0 40px rgba(204, 0, 0, 0.6)',
        pointerEvents: 'none'
      }}
    >
      {fortifiedCountryUnderAttackWarning.countryNames.length === 1
        ? t('warEffort.fortifiedCountryUnderAttackSingle', {
            country: localizeCountryName(fortifiedCountryUnderAttackWarning.countryNames[0], t)
          })
        : t('warEffort.fortifiedCountryUnderAttackMultiple', {
            countries: (() => {
              const names = fortifiedCountryUnderAttackWarning.countryNames.map((n) => localizeCountryName(n, t));
              return `${names.slice(0, -1).join(', ')} ${t('warEffort.listAnd')} ${names[names.length - 1]}`;
            })()
          })}
    </div>,
    document.body
  );

  // Building page overlay (map stays mounted so paint patterns are not re-decoded).
  // Friends, Settings, and username are already shown by parent WareEffort (top-left).
  let hqBuildingPage = null;
  if (showBuildingPage) {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const h = typeof window !== 'undefined' ? window.innerHeight : 600;
    const yNorm = Math.max(0, Math.min(1, placementMouse.y / h));
    const zoomT = Math.pow(yNorm, 1.6);
    const placementGhostMin = 40;
    const placementGhostMax = 720;
    const placementGhostSize = buildingForPlacement
      ? Math.round(placementGhostMin + zoomT * (placementGhostMax - placementGhostMin))
      : 0;
    hqBuildingPage = (
      <div
        className="war-effort-hq-building-page"
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 2,
          pointerEvents: 'auto',
          backgroundImage: 'url(/images/area.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          overflow: 'auto',
          cursor: buildingForPlacement ? 'none' : undefined
        }}
        onMouseDown={(e) => {
          if (e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
          e.preventDefault();
          if (typeof window !== 'undefined' && window.getSelection) {
            window.getSelection().removeAllRanges();
          }
        }}
        onSelectStart={(e) => {
          if (e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
          e.preventDefault();
        }}
        onMouseMove={(e) => {
          if (buildingForPlacement) setPlacementMouse({ x: e.clientX, y: e.clientY });
        }}
        onClick={(e) => {
          if (e.shiftKey && typeof window !== 'undefined' && window.getSelection) {
            window.getSelection().removeAllRanges();
          }
          if (buildingForPlacement && !e.target.closest('button')) {
            const h = typeof window !== 'undefined' ? window.innerHeight : 600;
            const yNorm = Math.max(0, Math.min(1, placementMouse.y / h));
            const zoomT = Math.pow(yNorm, 1.6);
            const placementGhostMin = 40;
            const placementGhostMax = 720;
            const sizeAtDrop = Math.round(placementGhostMin + zoomT * (placementGhostMax - placementGhostMin));
            const newX = placementMouse.x;
            const newY = placementMouse.y;
            const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
            const halfSize = sizeAtDrop / 2;
            const fullyVisibleOnScreen =
              newX - halfSize >= 0 &&
              newX + halfSize <= w &&
              newY - halfSize >= 0 &&
              newY + halfSize <= h;
            const inBuildableZone = newY >= h / 4;
            // Improved overlap check: use circular collision (Euclidean distance) with stricter threshold
            // This prevents diagonal corner-to-corner placements that looked bad with box collision
            const overlapsExisting = placedBuildings.some((item) => {
              const s1 = sizeAtDrop;
              const s2 = item.size ?? 432;
              const halfSum = (s1 + s2) / 2;
              // Use 0.65 multiplier (was 0.35) - allows ~35% overlap instead of ~65%
              // Add 20px gap for visual breathing room
              const threshold = Math.max(60, halfSum * 0.65 + 20);
              // Circular collision using Euclidean distance (not box-based)
              const dx = newX - item.x;
              const dy = newY - item.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              return distance < threshold;
            });
            if (fullyVisibleOnScreen && inBuildableZone && !overlapsExisting) {
              const building = buildingForPlacement;
              const cost = getEffectiveBuildingCost(building.cost);
              const { groupIds, cap } = getBuildingCapInfo(building.id);
              const builtOfType = placedBuildings.filter((p) => groupIds.includes(p.building.id)).length;
              if (builtOfType >= cap) {
                setBuildingForPlacement(null);
                setError(t('warEffort.maxBuildingsBuilt', { name: localizeBuildingName(building.id, t), cap }));
                return;
              }
              if (energyAmount < cost) {
                setBuildingForPlacement(null);
                setError(t('warEffort.insufficientEnergyNeedHave', { need: cost, have: energyAmount }));
                return;
              }
              if (!canAffordHqResourceCosts(building.resourceCosts, storedHqResources)) {
                setBuildingForPlacement(null);
                setError(t('warEffort.insufficientHqStorageToBuild', { name: localizeBuildingName(building.id, t) }));
                return;
              }
              const keepPlacingAfterThis = e.shiftKey === true && cap > 1 && (builtOfType + 1) < cap;
              if (missionData?.id) {
                const x_ratio = newX / w;
                const y_ratio = newY / h;
                const size_ratio = sizeAtDrop / h;
                fetch(`${API_URL}/war-effort/mission/${missionData.id}/hq-placements`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                  },
                  body: JSON.stringify({
                    building_id: building.id,
                    cost,
                    x_ratio,
                    y_ratio,
                    size_ratio
                  })
                })
                  .then((res) => {
                    if (!res.ok) return res.json().then((body) => Promise.reject(new Error(body.error || 'Failed to save')));
                    return res.json();
                  })
                  .then((data) => {
                    playSoundEffect('/sounds/build.mp3');
                    const isStorageBuilding = RESOURCE_STORAGE_BUILDING_IDS.includes(building.id);
                    const newItem = {
                      building,
                      x: newX,
                      y: newY,
                      size: sizeAtDrop,
                      placementId: data.id
                    };
                    if (isStorageBuilding) {
                      newItem.currentProduced = 0;
                      newItem.produced_per_minute = getStoragePerMinuteForBuildingId(building.id, viewerPurpose);
                      newItem.last_ration_tick_at = new Date().toISOString();
                    }
                    if (building.id === 'training_grounds') {
                      newItem.trainingProgress = 0;
                      newItem.trainingReady = false;
                      newItem.training_completed_at = null;
                    }
                    if (building.id === 'barracks') {
                      newItem.barracksProgress = 0;
                      newItem.barracksReady = false;
                      newItem.barracks_completed_at = null;
                      newItem.barracks_pending_order_ids = [];
                      if (data.barracks_faction_id != null) newItem.barracks_faction_id = data.barracks_faction_id;
                    }
                    if (building.id === 'armory') {
                      newItem.armoryProgress = 0;
                      newItem.armoryReady = false;
                      newItem.armory_completed_at = null;
                      newItem.armory_pending_order_ids = [];
                      if (data.armory_faction_id != null) newItem.armory_faction_id = data.armory_faction_id;
                    }
                    if (building.id === 'drone_hangar') {
                      newItem.droneHangarProgress = 0;
                      newItem.droneHangarReady = false;
                      newItem.drone_hangar_completed_at = null;
                      newItem.drone_hangar_pending_order_ids = [];
                      if (data.drone_hangar_faction_id != null) newItem.drone_hangar_faction_id = data.drone_hangar_faction_id;
                    }
                    if (building.id === 'communications_hub') {
                      newItem.comhubProgress = 0;
                      newItem.comhubReady = false;
                      newItem.comhub_completed_at = null;
                      newItem.comhub_pending_order_ids = [];
                      if (data.communications_hub_faction_id != null) newItem.communications_hub_faction_id = data.communications_hub_faction_id;
                    }
                    setPlacedBuildings((prev) => {
                      if (isStorageBuilding) {
                        newItem.maxProduced = getStorageMaxForBuildingId(building.id, countPlacedDepots(prev), viewerPurpose);
                      }
                      const next = [...prev, newItem];
                      if (building.id !== DEPOT_BUILDING_ID) return next;
                      const depotCount = countPlacedDepots(next);
                      return next.map((p) => {
                        if (!RESOURCE_STORAGE_BUILDING_IDS.includes(p.building?.id)) return p;
                        const newMax = getStorageMaxForBuildingId(p.building.id, depotCount, viewerPurpose);
                        const displayed = getDisplayedProduced(p);
                        return {
                          ...p,
                          currentProduced: Math.min(displayed, newMax),
                          maxProduced: newMax,
                          last_ration_tick_at: new Date().toISOString()
                        };
                      });
                    });
                    const remainingEnergy = data.remaining_energy;
                    const canAffordAnother = typeof remainingEnergy === 'number' && remainingEnergy >= cost;
                    if (!keepPlacingAfterThis || !canAffordAnother) {
                      setBuildingForPlacement(null);
                    }
                    if (typeof remainingEnergy === 'number' && playerResource) {
                      setPlayerResource((prev) => prev ? { ...prev, amount: remainingEnergy } : null);
                    }
                    if (building.resourceCosts) {
                      setPlacementRefreshTrigger((n) => n + 1);
                    }
                    refetchMission();
                    fetchControlledCountries(); // refresh buildingCostMultiplier / hasInfrastructureSubsidy after subsidy consumed
                  })
                  .catch((err) => {
                    console.error('Error saving HQ placement:', err);
                    setError(err.message || 'Failed to save building.');
                  });
              } else {
                setPlacedBuildings((prev) => [...prev, {
                  building,
                  x: newX,
                  y: newY,
                  size: sizeAtDrop
                }]);
                if (!keepPlacingAfterThis || (energyAmount - cost) < cost) {
                  setBuildingForPlacement(null);
                }
              }
            }
          } else if (buildingForPlacement && !fullyVisibleOnScreen) {
            setError(t('warEffort.buildingMustBeFullyVisible'));
          } else if (selectedPlacementId != null && !e.target.closest('[data-hq-placement]') && !e.target.closest('button')) {
            setSelectedPlacementId(null);
          }
        }}
      >
        {/* Energy: floating transparent cylinder, below header to avoid overlap with Friends/Settings/username */}
        <WarEffortEnergyBar
          energyCap={energyCap}
          energyFillPercent={energyFillPercent}
          energyAmount={energyAmount}
          productionPerMinute={energyProductionPerMinute}
          t={t}
        />
        {showTransferEnergy && (
          <div className="war-effort-map-chrome--transfer">
            <button
              type="button"
              className="war-effort-transfer-energy-btn"
              onClick={() => { playSoundEffect('/sounds/click.mp3'); setTransferEnergyModalOpen(true); }}
              style={{
                fontSize: '9px',
                padding: '4px 2px',
                cursor: 'pointer',
                backgroundColor: 'rgba(78, 205, 196, 0.25)',
                border: '1px solid rgba(78, 205, 196, 0.6)',
                borderRadius: '6px',
                color: '#4ECDC4',
                fontWeight: 'bold',
                whiteSpace: 'normal',
                lineHeight: 1.2,
                width: '100%'
              }}
            >
              {t('warEffort.transferEnergy', 'Transfer Energy')}
            </button>
          </div>
        )}
        {transferEnergyModalOpen && createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="transfer-energy-title"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.7)',
              pointerEvents: 'auto'
            }}
            onClick={(e) => { if (e.target === e.currentTarget) setTransferEnergyModalOpen(false); }}
          >
            <div
              style={{
                backgroundColor: '#2a2a2a',
                border: '2px solid rgba(78, 205, 196, 0.6)',
                borderRadius: 12,
                padding: 20,
                minWidth: 220,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 id="transfer-energy-title" style={{ margin: '0 0 16px', color: '#4ECDC4', fontSize: 16 }}>{t('warEffort.transferEnergy', 'Transfer Energy')}</h3>
              <p style={{ margin: '0 0 12px', color: '#aaa', fontSize: 12 }}>{t('warEffort.transferEnergyToAlly', 'Give energy to your ally:')}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {[50, 100, 200].map((n) => (
                  <button
                    key={n}
                    type="button"
                    disabled={transferEnergyLoading || energyAmount < n}
                    onClick={() => handleTransferEnergy(n)}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: (transferEnergyLoading || energyAmount < n) ? '#444' : 'rgba(78, 205, 196, 0.25)',
                      border: `1px solid ${energyAmount >= n ? 'rgba(78, 205, 196, 0.6)' : '#555'}`,
                      borderRadius: 8,
                      color: energyAmount >= n ? '#4ECDC4' : '#888',
                      cursor: (transferEnergyLoading || energyAmount < n) ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={transferEnergyLoading || energyAmount <= 0}
                  onClick={() => handleTransferEnergy('all')}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: (transferEnergyLoading || energyAmount <= 0) ? '#444' : 'rgba(78, 205, 196, 0.25)',
                    border: `1px solid ${energyAmount > 0 ? 'rgba(78, 205, 196, 0.6)' : '#555'}`,
                    borderRadius: 8,
                    color: energyAmount > 0 ? '#4ECDC4' : '#888',
                    cursor: (transferEnergyLoading || energyAmount <= 0) ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  All
                </button>
              </div>
              <MenuPaintedButton
                type="button"
                className="war-effort-ingame-cancel-btn"
                imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                onClick={() => setTransferEnergyModalOpen(false)}
              >
                Cancel
              </MenuPaintedButton>
            </div>
          </div>,
          document.body
        )}
        {/* Placement ghost: bigger when lower; far-away effect = more transparent + blur when higher */}
        {buildingForPlacement && (() => {
          const ghostYNorm = Math.max(0, Math.min(1, placementMouse.y / h));
          const ghostOpacity = 0.6 + 0.4 * ghostYNorm;
          const ghostBlur = (1 - ghostYNorm) * 1;
          return (
            <div
              style={{
                position: 'fixed',
                left: placementMouse.x,
                top: placementMouse.y,
                width: placementGhostSize,
                height: placementGhostSize,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 2500,
                opacity: ghostOpacity,
                filter: ghostBlur > 0 ? `blur(${ghostBlur}px)` : 'none'
              }}
            >
              <img
                src={buildingForPlacement.image}
                alt={buildingForPlacement.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  ...(buildingForPlacement.id === 'hospital' ? { transform: 'scale(0.9)' } : (buildingForPlacement.id === 'powerplant' || buildingForPlacement.id === 'powerplant_upgraded') ? { transform: 'scale(0.8)' } : {})
                }}
              />
            </div>
          );
        })()}
        {/* Placed buildings - far-away effect (opacity + blur by Y); Destroy in lower half */}
        {placedBuildings.map((item, idx) => {
          const itemYNorm = Math.max(0, Math.min(1, item.y / h));
          const itemOpacity = 0.6 + 0.4 * itemYNorm;
          const itemBlur = (1 - itemYNorm) * 1;
          return (
          <div
            data-hq-placement
            key={item.placementId != null ? item.placementId : `placement-${idx}`}
            role="button"
            tabIndex={buildingForPlacement ? -1 : 0}
            onClick={(e) => {
              e.stopPropagation();
              if (item.placementId != null) {
                const soundPath = item.building?.id && BUILDING_CLICK_SOUNDS[item.building.id];
                if (soundPath) playSoundEffect(soundPath);
                setSelectedPlacementId((prev) => (prev === item.placementId ? null : item.placementId));
              }
            }}
            style={{
              position: 'fixed',
              left: item.x,
              top: item.y,
              width: item.size ?? 432,
              height: item.size ?? 432,
              transform: 'translate(-50%, -50%)',
              pointerEvents: buildingForPlacement ? 'none' : 'auto',
              cursor: buildingForPlacement ? 'none' : 'pointer',
              zIndex: selectedPlacementId === item.placementId ? 150 : 100,
              borderRadius: 4,
              opacity: itemOpacity,
              filter: itemBlur > 0 ? `blur(${itemBlur}px)` : 'none',
              display: (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) ? 'flex' : 'block',
              flexDirection: (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) ? 'column' : undefined,
              overflow: (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) ? 'visible' : 'hidden'
            }}
          >
            <div
              style={{
                flex: (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) ? 0 : undefined,
                flexShrink: 0,
                width: '100%',
                height: (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) ? undefined : '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) ? 'flex-start' : 'center',
                pointerEvents: 'none'
              }}
            >
              <img
                src={item.building.image}
                alt={item.building.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                  display: 'block',
                  ...(item.building?.id === 'hospital' ? { transform: 'scale(0.9)' } : (item.building?.id === 'powerplant' || item.building?.id === 'powerplant_upgraded') ? { transform: 'scale(0.8)' } : {})
                }}
                onLoad={(e) => {
                  const img = e.target;
                  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                    const ratio = img.naturalWidth / img.naturalHeight;
                    setBuildingImageAspectRatio((prev) => (prev[item.building.image] === ratio ? prev : { ...prev, [item.building.image]: ratio }));
                  }
                }}
              />
            </div>
            {/* Vertical progress bar: right edge of image, inside, green cylinder style (like energy bar) */}
            {(typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) && (() => {
              const size = item.size ?? 432;
              const ratio = buildingImageAspectRatio[item.building.image] ?? 1;
              const imageHeight = size / ratio;
              const barHeight = Math.max(24, Math.min(120, Math.round(imageHeight * 0.75)));
              const rightInset = Math.max(4, Math.round(size * 0.02));
              const barWidth = Math.max(12, Math.min(20, Math.round(size * 0.06)));
              let fillPercent = 0;
              let label = '';
              const isBarracks = item.building?.id === 'barracks' || item.building?.id === 'barracks_upgraded';
              if (isBarracks) {
                fillPercent = Math.min(100, (item.barracksProgress ?? 0) * 100);
                label = `${Math.round(fillPercent)}%`;
              } else {
                const BARRACKS_STYLE_CFG = [
                  { ids: ['armory', 'armory_upgraded'], progressKey: 'armoryProgress' },
                  { ids: ['drone_hangar', 'drone_hangar_upgraded'], progressKey: 'droneHangarProgress' },
                  { ids: ['communications_hub', 'communications_hub_upgraded'], progressKey: 'comhubProgress' }
                ];
                const cfg = BARRACKS_STYLE_CFG.find((c) => c.ids.includes(item.building?.id));
                if (cfg) {
                  fillPercent = Math.min(100, (item[cfg.progressKey] ?? 0) * 100);
                  label = `${Math.round(fillPercent)}%`;
                } else {
                  const displayed = getDisplayedProduced(item);
                  const maxProduced = item.maxProduced ?? 20;
                  fillPercent = maxProduced > 0 ? (displayed / maxProduced) * 100 : 0;
                  label = `${displayed}/${maxProduced}`;
                }
              }
              return (
                <div
                  key="bar"
                  className="war-effort-producer-progress-bar"
                  style={{
                    position: 'absolute',
                    right: rightInset,
                    top: (imageHeight - barHeight) / 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    pointerEvents: 'none'
                  }}
                >
                  <span
                    style={{
                      fontSize: Math.max(8, Math.min(10, Math.round(size * 0.025))),
                      color: '#9acd32',
                      fontWeight: 'bold',
                      marginBottom: 2,
                      padding: '1px 4px',
                      borderRadius: 3,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap'
                    }}
                  >{label}</span>
                  <div
                    style={{
                      width: barWidth,
                      height: barHeight,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      border: '1px solid rgba(107, 142, 35, 0.5)',
                      borderRadius: Math.min(10, Math.round(barWidth / 2)),
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end'
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: `${fillPercent}%`,
                        minHeight: fillPercent > 0 ? 2 : 0,
                        backgroundColor: fillPercent >= 100 ? '#4a7c2c' : '#6b8e23',
                        borderRadius: `0 0 ${Math.min(10, Math.round(barWidth / 2))}px ${Math.min(10, Math.round(barWidth / 2))}px`,
                        transition: 'height 0.3s ease'
                      }}
                    />
                  </div>
                </div>
              );
            })()}
            {/* Upgrade/Destroy buttons rendered via portal - centered on building, full size, not clipped by building overflow */}
          </div>
          );
        })}
        {/* Upgrade/Destroy buttons rendered via portal - fixed size, centered on building, not clipped by building overflow */}
        {typeof document !== 'undefined' && (() => {
          const sel = placedBuildings.find((p) => selectedPlacementId === p.placementId && p.placementId != null);
          if (!sel) return null;
          const item = sel;
          const isStorageBuilding = RESOURCE_STORAGE_BUILDING_IDS.includes(item.building?.id);
          const hasProducerUI = !isStorageBuilding && (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id));
          const renderCollectOrComplete = () => {
            if (isStorageBuilding) {
              return null;
            }
            const isBarracks = item.building?.id === 'barracks' || item.building?.id === 'barracks_upgraded';
            if (isBarracks) {
              const hasPending = item.barracks_pending_order_ids && item.barracks_pending_order_ids.length > 0;
              const ready = Boolean(item.barracksReady) && !hasPending;
              return (
                <MenuPaintedButton
                  type="button"
                  className="war-effort-building-action-btn"
                  imagePath={WAR_EFFORT_ADD_BOT_BUTTON_PATH}
                  disabled={!ready}
                  title={ready ? t('warEffort.complete') : t('warEffort.waiting')}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (item.placementId == null || !missionData?.id) return;
                    try {
                      const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${item.placementId}/complete-barracks`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) { setError(data.error || 'Failed to complete barracks'); return; }
                      if (data.orders?.length > 0) {
                        setBarracksOrdersModal({ show: true, placementId: item.placementId, buildingId: 'barracks', orders: data.orders, loading: false });
                        setPlacedBuildings((prev) => prev.map((p) => p.placementId === item.placementId ? { ...p, barracks_pending_order_ids: data.orders.map((o) => o.id) } : p));
                        setError(null);
                      } else { setError(t('warEffort.noOrdersAvailable')); setPlacementRefreshTrigger((n) => n + 1); }
                    } catch (err) { setError(err.message || 'Failed to complete barracks'); }
                  }}
                >{t('warEffort.complete')}</MenuPaintedButton>
              );
            }
            const BARRACKS_STYLE_CFG = [
              { ids: ['armory', 'armory_upgraded'], pendingKey: 'armory_pending_order_ids', completeEndpoint: 'complete-armory', readyKey: 'armoryReady' },
              { ids: ['drone_hangar', 'drone_hangar_upgraded'], pendingKey: 'drone_hangar_pending_order_ids', completeEndpoint: 'complete-drone-hangar', readyKey: 'droneHangarReady' },
              { ids: ['communications_hub', 'communications_hub_upgraded'], pendingKey: 'comhub_pending_order_ids', completeEndpoint: 'complete-comhub', readyKey: 'comhubReady' }
            ];
            const cfg = BARRACKS_STYLE_CFG.find((c) => c.ids.includes(item.building?.id));
            if (cfg) {
              const hasPending = (item[cfg.pendingKey] && item[cfg.pendingKey].length > 0);
              const isReady = Boolean(item[cfg.readyKey]) && !hasPending;
              return (
                <MenuPaintedButton
                  type="button"
                  className="war-effort-building-action-btn"
                  imagePath={WAR_EFFORT_ADD_BOT_BUTTON_PATH}
                  disabled={!isReady}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (item.placementId == null || !missionData?.id) return;
                    try {
                      const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${item.placementId}/${cfg.completeEndpoint}`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) { setError(data.error || 'Failed'); return; }
                      if (data.orders?.length > 0) {
                        setBarracksOrdersModalSelectedIds([]);
                        setBarracksOrdersModal({ show: true, placementId: item.placementId, buildingId: item.building?.id || cfg.ids[0], orders: data.orders, loading: false });
                        const pu = {}; pu[cfg.pendingKey] = data.orders.map((o) => o.id);
                        setPlacedBuildings((prev) => prev.map((p) => p.placementId === item.placementId ? { ...p, ...pu } : p));
                        setError(null);
                      } else { setError(t('warEffort.noOrdersAvailable')); setPlacementRefreshTrigger((n) => n + 1); }
                    } catch (err) { setError(err.message); }
                  }}
                >{t('warEffort.complete')}</MenuPaintedButton>
              );
            }
            const displayed = getDisplayedProduced(item);
            return (
              <MenuPaintedButton
                type="button"
                className="war-effort-building-action-btn"
                imagePath={WAR_EFFORT_ADD_BOT_BUTTON_PATH}
                disabled={displayed <= 0}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (item.placementId == null || !missionData?.id) return;
                  try {
                    const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${item.placementId}/collect-resource`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                    if (!res.ok) { const errData = await res.json().catch(() => ({})); setError(errData.error || 'Failed'); return; }
                    const data = await res.json();
                    setPlacedBuildings((prev) => prev.map((p) => p.placementId === item.placementId ? { ...p, currentProduced: 0, maxProduced: data.produced_max ?? 20, last_ration_tick_at: new Date().toISOString() } : p));
                    setError(null);
                    setCollectSparkPlacementId(item.placementId);
                    setCollectSparkAmount(data.collected ?? 0);
                    setTimeout(() => { setCollectSparkPlacementId(null); setCollectSparkAmount(null); }, 900);
                  } catch (err) { setError(err.message); }
                }}
              >{t('warEffort.collect')}</MenuPaintedButton>
            );
          };
          return createPortal(
            <div
              role="group"
              aria-label="Building actions"
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                left: item.x,
                top: item.y,
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                pointerEvents: 'auto',
                zIndex: 1000,
                isolation: 'isolate'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {(item.building?.id === 'hall' || item.building?.id === 'hall_upgraded') && (
                  <MenuPaintedButton
                    type="button"
                    className="war-effort-building-action-btn"
                    imagePath={WAR_EFFORT_PRIMARY_BUTTON_PATH}
                    onClick={(e) => {
                      e.stopPropagation();
                      playSoundEffect('/sounds/click.mp3');
                      if (!missionData?.id) return;
                      const token = localStorage.getItem('token');
                      if (!token) {
                        setError(t('warEffort.notLoggedIn'));
                        return;
                      }
                      setHallAscensionModal({
                        show: true,
                        loading: true,
                        hallLevel: item.building?.id === 'hall_upgraded' ? 2 : 1,
                        storage: null,
                        units: [],
                        selectedOrderId: null,
                        selectedOccurrence: null,
                        result: null,
                        error: null
                      });
                      fetchLocalizedHallAscension()
                        .then((payload) => {
                          setHallAscensionModal({
                            show: true,
                            loading: false,
                            hallLevel: payload.hallLevel,
                            storage: payload.storage,
                            units: payload.units,
                            selectedOrderId: null,
                            selectedOccurrence: null,
                            result: null,
                            error: null
                          });
                        })
                        .catch((err) => {
                          setHallAscensionModal({
                            show: true,
                            loading: false,
                            hallLevel: 0,
                            storage: null,
                            units: [],
                            selectedOrderId: null,
                            selectedOccurrence: null,
                            result: null,
                            error: err.message || 'Failed to load Hall of Ascension'
                          });
                        });
                    }}
                  >
                    {t('warEffort.ascend')}
                  </MenuPaintedButton>
                )}
                {BUILDING_UPGRADES[item.building?.id] && (() => {
                  const upgrade = BUILDING_UPGRADES[item.building.id];
                  const energyOk = energyAmount >= upgrade.cost;
                  const resourcesOk = canAffordHqResourceCosts(upgrade.resourceCosts, storedHqResources);
                  const canAfford = energyOk && resourcesOk;
                  return (
                    <MenuPaintedButton
                      type="button"
                      className="war-effort-building-action-btn"
                      imagePath={WAR_EFFORT_PRIMARY_BUTTON_PATH}
                      disabled={!canAfford}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!canAfford || item.placementId == null || !missionData?.id) return;
                        try {
                          const url = item.building.id === 'powerplant'
                            ? `${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${item.placementId}/upgrade-powerplant`
                            : `${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${item.placementId}/upgrade-building`;
                          const res = await fetch(url, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                            body: item.building.id === 'powerplant' ? undefined : JSON.stringify({ building_id: item.building.id })
                          });
                          const data = await res.json().catch(() => ({}));
                          if (!res.ok) { setError(data.error || 'Upgrade failed'); return; }
                          playSoundEffect('/sounds/build.mp3');
                          const upgradedBuilding = HQ_BUILDINGS.find((b) => b.id === upgrade.upgradedId);
                          const storageSnap = RESOURCE_STORAGE_BUILDING_IDS.includes(item.building?.id)
                            ? {
                              currentProduced: getDisplayedProduced(item),
                              maxProduced: getStorageMaxForBuildingId(upgrade.upgradedId, countPlacedDepots(placedBuildings), viewerPurpose),
                              produced_per_minute: getStoragePerMinuteForBuildingId(upgrade.upgradedId, viewerPurpose),
                              last_ration_tick_at: new Date().toISOString()
                            }
                            : {};
                          setPlacedBuildings((prev) => prev.map((p) => p.placementId === item.placementId ? { ...p, building: upgradedBuilding, ...storageSnap } : p));
                          if (typeof data.remaining_energy === 'number' && playerResource) {
                            setPlayerResource((prev) => prev ? { ...prev, amount: data.remaining_energy } : null);
                          }
                          if (upgrade.resourceCosts) {
                            setPlacementRefreshTrigger((n) => n + 1);
                          }
                          setSelectedPlacementId(null);
                          refetchMission();
                        } catch (err) { setError(t('warEffort.upgradeFailed')); }
                      }}
                    >
                      {upgrade.resourceCosts ? (
                        <span className="war-effort-building-upgrade-cost">
                          {t('warEffort.upgrade')}
                          <BuildingHqCostLine
                            energyCost={upgrade.cost}
                            resourceCosts={upgrade.resourceCosts}
                            energyAffordable={energyOk}
                            storedResources={storedHqResources}
                          />
                        </span>
                      ) : (
                        t('warEffort.upgradeWithCost', { cost: upgrade.cost })
                      )}
                    </MenuPaintedButton>
                  );
                })()}
                <MenuPaintedButton
                  type="button"
                  className="war-effort-building-action-btn war-effort-building-action-btn--destroy"
                  imagePath={WAR_EFFORT_LEAVE_MISSION_BUTTON_PATH}
                  onClick={(e) => {
                    e.stopPropagation();
                    playSoundEffect('/sounds/demolish.mp3');
                    if (item.placementId != null && missionData?.id) {
                      fetch(`${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${item.placementId}`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                      })
                        .then(() => refetchMission())
                        .catch((err) => console.error('Error deleting HQ placement:', err));
                    }
                    setPlacedBuildings((prev) => {
                      const remaining = prev.filter((p) => p.placementId !== item.placementId);
                      if (item.building?.id !== DEPOT_BUILDING_ID) return remaining;
                      const depotCount = countPlacedDepots(remaining);
                      return remaining.map((p) => {
                        if (!RESOURCE_STORAGE_BUILDING_IDS.includes(p.building?.id)) return p;
                        const newMax = getStorageMaxForBuildingId(p.building.id, depotCount, viewerPurpose);
                        const displayed = getDisplayedProduced(p);
                        return {
                          ...p,
                          currentProduced: Math.min(displayed, newMax),
                          maxProduced: newMax,
                          last_ration_tick_at: new Date().toISOString()
                        };
                      });
                    });
                    setSelectedPlacementId(null);
                  }}
                >
                  {t('warEffort.destroy')}
                </MenuPaintedButton>
              </div>
              {hasProducerUI && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  {renderCollectOrComplete()}
                </div>
              )}
            </div>,
            document.body
          );
        })()}
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, pointerEvents: 'none' }}>
          {collectSparkPlacementId != null && collectSparkAmount != null && (() => {
            const item = placedBuildings.find((p) => p.placementId === collectSparkPlacementId);
            if (!item) return null;
            return (
              <div
                className="war-effort-collect-float"
                style={{
                  position: 'fixed',
                  left: item.x,
                  top: item.y,
                  transform: 'translate(-50%, -50%)'
                }}
                aria-hidden
              >
                +{collectSparkAmount}
              </div>
            );
          })()}
          {trainingResult != null && (() => {
            const item = placedBuildings.find((p) => p.placementId === trainingResult.placementId);
            if (!item) return null;
            const msg = trainingResult.outcome === 'nothing' || !trainingResult.order
              ? 'Nothing'
              : `${trainingResult.order.rarity}: ${trainingResult.order.name}`;
            return (
              <div
                className="war-effort-collect-float"
                style={{
                  position: 'fixed',
                  left: item.x,
                  top: item.y,
                  transform: 'translate(-50%, -50%)',
                  fontSize: 12,
                  color: trainingResult.order ? '#9acd32' : '#aaa'
                }}
                aria-hidden
              >
                {msg}
              </div>
            );
          })()}
          {powerplantFlashPlacements.map((p, idx) => (
            <div
              key={`powerplant-${idx}-${p.x}-${p.y}`}
              className="war-effort-collect-float war-effort-collect-float--energy"
              style={{
                position: 'fixed',
                left: p.x,
                top: p.y,
                transform: 'translate(-50%, -50%)'
              }}
              aria-hidden
            >
              +{p.amount}
            </div>
          ))}
          {barracksOrdersModal.show && barracksOrdersModal.orders.length > 0 && (() => {
            // Order-generating buildings (Barracks, Armory, Drone Hangar, ComHub) always allow 1 reward only; Training Grounds applies only to conquest/subversion
            const ordersToKeep = 1;
            const isMultiSelect = ordersToKeep > 1;
            const selectedIds = barracksOrdersModalSelectedIds;
            const toggleSelection = (id) => {
              if (selectedIds.includes(id)) {
                setBarracksOrdersModalSelectedIds((prev) => prev.filter((x) => x !== id));
              } else if (selectedIds.length < ordersToKeep) {
                setBarracksOrdersModalSelectedIds((prev) => [...prev, id]);
              }
            };
            const submitSelection = async (orderIdsToSubmit) => {
              if (barracksOrdersModal.loading || !missionData?.id || !barracksOrdersModal.placementId) return;
              const bid = barracksOrdersModal.buildingId;
              const selectEndpoint = (bid === 'barracks' || bid === 'barracks_upgraded') ? 'select-barracks-order'
                : (bid === 'armory' || bid === 'armory_upgraded') ? 'select-armory-order'
                : (bid === 'drone_hangar' || bid === 'drone_hangar_upgraded') ? 'select-drone-hangar-order'
                : (bid === 'communications_hub' || bid === 'communications_hub_upgraded') ? 'select-comhub-order'
                : 'select-barracks-order';
              setBarracksOrdersModal((prev) => ({ ...prev, loading: true }));
              try {
                const body = orderIdsToSubmit.length === 1 ? { orderId: orderIdsToSubmit[0] } : { orderIds: orderIdsToSubmit };
                const res = await fetch(
                  `${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${barracksOrdersModal.placementId}/${selectEndpoint}`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                    body: JSON.stringify(body)
                  }
                );
                if (!res.ok) {
                  const errData = await res.json().catch(() => ({}));
                  setError(errData.error || 'Failed to select order');
                  setBarracksOrdersModal({ show: false, placementId: null, buildingId: null, orders: [], loading: false });
                  return;
                }
                setBarracksOrdersModal({ show: false, placementId: null, buildingId: null, orders: [], loading: false });
                setBarracksOrdersModalSelectedIds([]);
                setError(null);
                setPlacementRefreshTrigger((t) => t + 1);
              } catch (err) {
                console.error('Error selecting order:', err);
                setError(err.message || 'Failed to select order');
                setBarracksOrdersModal({ show: false, placementId: null, buildingId: null, orders: [], loading: false });
                setBarracksOrdersModalSelectedIds([]);
              }
            };
            const handleCardClick = (ord) => {
              if (isMultiSelect) {
                toggleSelection(ord.id);
              } else {
                submitSelection([ord.id]);
              }
            };
            const canConfirm = selectedIds.length >= 1 && selectedIds.length <= ordersToKeep;
            return (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 6000,
                  background: 'rgba(0,0,0,0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'auto'
                }}
                onClick={(e) => { if (e.target === e.currentTarget) { setBarracksOrdersModal({ show: false, placementId: null, buildingId: null, orders: [], loading: false }); setBarracksOrdersModalSelectedIds([]); } }}
              >
                <div
                  style={{
                    background: 'transparent',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: 24
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 style={{ margin: '0 0 8px 0', color: '#fff' }}>
                    {isMultiSelect ? `Choose up to ${ordersToKeep} orders to add to your attack strategy` : 'Choose an order to add to your attack strategy'}
                  </h3>
                  <p style={{ fontSize: 12, color: '#aaa', marginBottom: 20 }}>These orders will be added for this mission only.</p>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'nowrap',
                      gap: 40,
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    {barracksOrdersModal.orders.map((ord) => {
                      const enriched = barracksEnrichedOrders.find((e) => e && e.id === ord.id);
                      const isSelected = selectedIds.includes(ord.id);
                      return (
                        <div
                          key={ord.id}
                          style={{
                            width: '390px',
                            height: '390px',
                            flexShrink: 0,
                            cursor: barracksOrdersModal.loading ? 'not-allowed' : 'pointer',
                            position: 'relative',
                            overflow: 'visible',
                            outline: isMultiSelect && isSelected ? '4px solid #4ECDC4' : 'none',
                            borderRadius: 8
                          }}
                          onClick={() => handleCardClick(ord)}
                        >
                          <div
                            className={`reward-order-container rarity-${(ord.rarity || 'common').toLowerCase()}`}
                            style={{
                              width: '390px',
                              height: '390px',
                              position: 'relative',
                              margin: 0,
                              padding: 0
                            }}
                          >
                            {enriched ? (
                              <OrderDesign
                                order={enriched}
                                minimal={false}
                                noMargin={true}
                                gameState={null}
                              />
                            ) : (
                              <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'transparent',
                                  color: '#aaa',
                                  fontSize: 14
                                }}
                              >
                                Loading…
                              </div>
                            )}
                            <OrderCardBubbles rarity={enriched?.rarity || ord.rarity || 'common'} />
                          </div>
                          {isMultiSelect && isSelected && (
                            <div style={{ position: 'absolute', top: 8, right: 8, background: '#4ECDC4', color: '#000', padding: '4px 8px', borderRadius: 4, fontWeight: 'bold' }}>
                              Selected
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {isMultiSelect && (
                    <div style={{ display: 'flex', gap: 16, marginTop: 20, alignItems: 'center' }}>
                      {ordersToKeep === 3 && (
                        <button
                          type="button"
                          onClick={() => submitSelection(barracksOrdersModal.orders.map((o) => o.id))}
                          disabled={barracksOrdersModal.loading}
                          style={{ padding: '10px 20px', background: '#4ECDC4', color: '#000', border: 'none', borderRadius: 8, cursor: barracksOrdersModal.loading ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}
                        >
                          Keep all 3
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => submitSelection(selectedIds)}
                        disabled={!canConfirm || barracksOrdersModal.loading}
                        style={{
                          padding: '10px 20px',
                          background: canConfirm ? '#4ECDC4' : '#555',
                          color: canConfirm ? '#000' : '#999',
                          border: 'none',
                          borderRadius: 8,
                          cursor: canConfirm && !barracksOrdersModal.loading ? 'pointer' : 'not-allowed',
                          fontWeight: 'bold'
                        }}
                      >
                        Confirm ({selectedIds.length}/{ordersToKeep})
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
        <div className="war-effort-bottom-buttons">
          <button
            type="button"
            className="war-effort-bottom-btn"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              onBuildingPageChange?.(false);
            }}
            aria-label="World map"
          >
            <img src="/images/world_button.png" alt="" className="war-effort-bottom-btn__image" />
          </button>
          <button
            type="button"
            className="war-effort-bottom-btn"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setShowBuildMenu(true);
            }}
            aria-label="Build"
          >
            <img src="/images/build_button.png" alt="" className="war-effort-bottom-btn__image" />
          </button>
          <button
            type="button"
            className="war-effort-bottom-btn"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              openTradingHubAfterMapRef.current = true;
              onBuildingPageChange?.(false);
            }}
            aria-label="Trading Hub"
          >
            <img src="/images/trading_button.png" alt="" className="war-effort-bottom-btn__image" />
          </button>
          <button
            type="button"
            className="war-effort-bottom-btn"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              openAttackStrategyModal();
            }}
            aria-label={t('warEffort.attackStrategy')}
          >
            <img src="/images/strategy_button.png" alt="" className="war-effort-bottom-btn__image" />
          </button>
        </div>
        {attackStrategyModal.show && typeof document !== 'undefined' && createPortal(
          <div
            className="war-effort-attack-strategy-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setAttackStrategyModal({ show: false, loading: false, strategy: null, deactivatedSlots: [] }); setAttackStrategyTooltipRect(null); }}
          >
            <div
              className="war-effort-attack-strategy-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="war-effort-attack-strategy-sidebar war-effort-attack-strategy-sidebar--hq"
              >
                <div style={{ flexShrink: 0, marginBottom: 12 }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#fff' }}>{t('warEffort.attackStrategy')}</h3>
                  {attackStrategyModal.loading ? (
                    <p style={{ color: '#aaa' }}>{t('warEffort.loadingEllipsis')}</p>
                  ) : attackStrategyModal.strategy?.orders?.length > 0 ? (
                    (() => {
                      const deactivatedSet = new Set(attackStrategyModal.deactivatedSlots || []);
                      const slots = attackStrategyModal.strategy.orders.flatMap((order, i) =>
                        Array.from({ length: order.count || 1 }, (_, j) => ({
                          order,
                          key: `${order.id ?? order.order_id ?? i}-${j}`,
                          orderId: order.id ?? order.order_id,
                          occurrence: j
                        }))
                      );
                      const total = slots.length;
                      const activeCount = slots.filter((s) => !deactivatedSet.has(`${s.orderId}:${s.occurrence}`)).length;
                      return (
                        <>
                          <div style={{ fontWeight: 'bold', color: '#aaa' }}>
                            {t('warEffort.activeOrdersNeed', { active: activeCount, total })}
                          </div>
                          {activeCount < 30 && (
                            <div style={{ marginTop: 8, padding: 8, background: 'rgba(255,100,0,0.2)', borderRadius: 4, color: '#ffa', fontSize: '0.85em' }}>
                              {t('warEffort.activateMoreOrders')}
                            </div>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    <p style={{ color: '#aaa' }}>{t('warEffort.noOrdersInAttackStrategy')}</p>
                  )}
                </div>
                {attackStrategyModal.loading ? null : attackStrategyModal.strategy?.orders?.length > 0 ? (
                  (() => {
                    const deactivatedSet = new Set(attackStrategyModal.deactivatedSlots || []);
                    const slots = attackStrategyModal.strategy.orders.flatMap((order, i) =>
                      Array.from({ length: order.count || 1 }, (_, j) => ({
                        order,
                        key: `${order.id ?? order.order_id ?? i}-${j}`,
                        orderId: order.id ?? order.order_id,
                        occurrence: j
                      }))
                    );
                    return (
                      <div className="war-effort-attack-strategy-list">
                        {slots.map(({ order, key, orderId, occurrence }) => (
                          <AttackStrategySlotRow
                            key={key}
                            order={order}
                            orderId={orderId}
                            occurrence={occurrence}
                            isDeactivated={deactivatedSet.has(`${orderId}:${occurrence}`)}
                            costLabel={t('strategy.cost')}
                            onToggle={handleAttackStrategyToggleSlot}
                            setTooltipRect={setAttackStrategyTooltipRect}
                            hallStars={hallAscensionLevelForOrder(attackStrategyModal.hallAscensions, orderId, occurrence)}
                            canSell={
                              Boolean(attackStrategyModal.canSellUnits)
                              && slots.length > 30
                              && getAttackStrategyUnitSellEnergy(order, attackStrategyModal.unitSellPrices) != null
                            }
                            sellEnergy={getAttackStrategyUnitSellEnergy(order, attackStrategyModal.unitSellPrices)}
                            sellBusy={attackStrategySellingKey === `${orderId}:${occurrence}`}
                            onSell={handleAttackStrategySellUnit}
                          />
                        ))}
                      </div>
                  );
                })()
              ) : null}
              <MenuPaintedButton
                type="button"
                className="war-effort-ingame-cancel-btn"
                imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                onClick={() => { setAttackStrategyModal({ show: false, loading: false, strategy: null, deactivatedSlots: [] }); setAttackStrategyTooltipRect(null); }}
              >
                {t('common.close')}
              </MenuPaintedButton>
            </div>
            {!attackStrategyModal.loading && attackStrategyModal.strategy?.orders?.length > 0 && (
              <AttackStrategyStatisticsPanel
                orders={getActiveAttackStrategyOrders(
                  attackStrategyModal.strategy.orders,
                  attackStrategyModal.deactivatedSlots
                )}
                layout="column"
              />
            )}
          </div>
          </div>
        , document.body)}
        {attackStrategyTooltipRect && attackStrategyModal.strategy && createPortal(
          <div
            style={{
              position: 'fixed',
              left: `${attackStrategyTooltipRect.left}px`,
              ...(attackStrategyTooltipRect.showAbove
                ? { bottom: `${typeof window !== 'undefined' ? window.innerHeight - attackStrategyTooltipRect.top + 5 : 0}px` }
                : { top: `${attackStrategyTooltipRect.bottom + 5}px` }),
              zIndex: 99999,
              pointerEvents: 'none',
              transform: 'scale(0.8)',
              transformOrigin: attackStrategyTooltipRect.showAbove ? 'bottom left' : 'top left',
              maxHeight: 400,
              overflow: 'auto'
            }}
          >
            <OrderDesign
              order={previewHallAscendedOrder(
                attackStrategyTooltipRect.order,
                hallRowsForOrder(
                  attackStrategyModal.hallAscensions,
                  attackStrategyTooltipRect.orderId,
                  attackStrategyTooltipRect.occurrence
                )
              ).order}
              count={0}
              minimal={false}
              noMargin={true}
              lazyLoad={false}
              isOOR={false}
              ascensionHighlights={
                previewHallAscendedOrder(
                  attackStrategyTooltipRect.order,
                  hallRowsForOrder(
                    attackStrategyModal.hallAscensions,
                    attackStrategyTooltipRect.orderId,
                    attackStrategyTooltipRect.occurrence
                  )
                ).highlights
              }
            />
          </div>,
          document.body
        )}
        {hallAscensionModal.show && typeof document !== 'undefined' && createPortal(
          <div
            className="war-effort-attack-strategy-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setHallAscensionTooltip(null);
                setHallAscensionModal({
                  show: false,
                  loading: false,
                  hallLevel: 0,
                  storage: null,
                  units: [],
                  selectedOrderId: null,
                  selectedOccurrence: null,
                  result: null,
                  error: null
                });
              }
            }}
          >
            <div
              className="war-effort-attack-strategy-modal war-effort-hall-ascension-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="war-effort-attack-strategy-sidebar war-effort-attack-strategy-sidebar--hq">
                <h3 style={{ margin: '0 0 8px 0', color: '#fff' }}>{t('warEffort.hallOfAscension')}</h3>
                <p style={{ color: '#aaa', marginTop: 0 }}>
                  {hallAscensionModal.hallLevel >= 2
                    ? t('warEffort.hallAscendTwice')
                    : t('warEffort.hallAscendOnce')}
                </p>
                {hallAscensionModal.storage && (
                  <div className="war-effort-hall-storage">
                    {Object.entries(hallAscensionModal.storage).map(([resource, amount]) => (
                      <span key={resource} className="war-effort-hall-cost-item">
                        <img src={HALL_RESOURCE_ICONS[resource]} alt={resource} />
                        {amount}
                      </span>
                    ))}
                  </div>
                )}
                {hallAscensionModal.loading ? (
                  <p style={{ color: '#aaa' }}>{t('warEffort.loadingEllipsis')}</p>
                ) : hallAscensionModal.error && !hallAscensionModal.units.length ? (
                  <p style={{ color: '#f88' }}>{hallAscensionModal.error}</p>
                ) : (
                  <div className="war-effort-attack-strategy-list">
                    {hallAscensionModal.units.map((unit) => {
                      if (unit.occurrence == null) {
                        throw new Error(`Hall of Ascension: occurrence is required for order ${unit.orderId}`);
                      }
                      const displayOrder = {
                        ...(unit.order || {}),
                        id: unit.orderId,
                        name: unit.name,
                        type: unit.type,
                        rarity: unit.rarity,
                        faction_name: unit.factionName,
                        attack_rating: unit.previewAttack,
                        health: unit.previewHealth,
                        abilities: unit.previewAbilities
                      };
                      const isFullyAscended = unit.ascensionLevel >= hallAscensionModal.hallLevel;
                      const isSelected = hallCopyIsSelected(
                        unit,
                        hallAscensionModal.selectedOrderId,
                        hallAscensionModal.selectedOccurrence
                      );
                      return (
                        <div
                          key={`${unit.orderId}:${unit.occurrence}`}
                          className={`war-effort-attack-strategy-entry${isSelected ? ' war-effort-hall-unit--selected' : ''}${isFullyAscended ? ' war-effort-hall-unit--fully-ascended' : (!unit.canAscend ? ' war-effort-attack-strategy-entry--inactive' : '')}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            playSoundEffect('/sounds/click.mp3');
                            setHallAscensionModal((prev) => ({
                              ...prev,
                              selectedOrderId: unit.orderId,
                              selectedOccurrence: unit.occurrence,
                              result: null,
                              error: null
                            }));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setHallAscensionModal((prev) => ({
                                ...prev,
                                selectedOrderId: unit.orderId,
                                selectedOccurrence: unit.occurrence,
                                result: null,
                                error: null
                              }));
                            }
                          }}
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const showAbove = typeof window !== 'undefined' && rect.bottom + 400 > window.innerHeight;
                            setHallAscensionTooltip({
                              orderId: unit.orderId,
                              occurrence: unit.occurrence,
                              top: rect.top,
                              bottom: rect.bottom,
                              left: rect.left,
                              showAbove
                            });
                          }}
                          onMouseLeave={() => setHallAscensionTooltip(null)}
                        >
                          <OrderSidebarStyleCard
                            order={displayOrder}
                            displayName={unit.name}
                            displayCost={displayOrder.cost ?? 0}
                            costLabel={t('strategy.cost')}
                            className="order-sidebar-style-card--battle-modal-list"
                            beforeCost={
                              unit.ascensionLevel > 0 ? (
                                <span className="war-effort-hall-stars" aria-label={`${unit.ascensionLevel} hall ascension`}>
                                  {'★'.repeat(unit.ascensionLevel)}
                                </span>
                              ) : null
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                <MenuPaintedButton
                  type="button"
                  className="war-effort-ingame-cancel-btn"
                  imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                  onClick={() => {
                    setHallAscensionTooltip(null);
                    setHallAscensionModal({
                      show: false,
                      loading: false,
                      hallLevel: 0,
                      storage: null,
                      units: [],
                      selectedOrderId: null,
                      selectedOccurrence: null,
                      result: null,
                      error: null
                    });
                  }}
                >
                  {t('common.close')}
                </MenuPaintedButton>
              </div>
              <div className="war-effort-hall-ascension-detail">
                {(() => {
                  const selected = hallAscensionModal.units.find((u) =>
                    hallCopyIsSelected(u, hallAscensionModal.selectedOrderId, hallAscensionModal.selectedOccurrence)
                  );
                  if (!selected) {
                    return <p style={{ color: '#aaa' }}>{t('warEffort.selectUnitForStats')}</p>;
                  }
                  const postKind = (kind) => {
                    const token = localStorage.getItem('token');
                    if (!token || !missionData?.id) {
                      setHallAscensionModal((prev) => ({ ...prev, error: t('warEffort.notLoggedIn') }));
                      return;
                    }
                    fetch(`${API_URL}/war-effort/mission/${missionData.id}/hall-ascension`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ orderId: selected.orderId, occurrence: selected.occurrence, kind })
                    })
                      .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
                      .then(({ ok, body }) => {
                        if (!ok) {
                          throw new Error(body.error || 'Failed to ascend');
                        }
                        return fetchLocalizedHallAscension().then((payload) => ({ payload, result: body.result }));
                      })
                      .then(({ payload, result }) => {
                        setHallAscensionModal((prev) => ({
                          ...prev,
                          hallLevel: payload.hallLevel,
                          storage: payload.storage,
                          units: payload.units,
                          result,
                          error: null
                        }));
                        setPlacementRefreshTrigger((t) => t + 1);
                      })
                      .catch((err) => {
                        setHallAscensionModal((prev) => ({ ...prev, error: err.message || 'Failed to ascend' }));
                      });
                  };
                  const isFullyAscended = selected.ascensionLevel >= hallAscensionModal.hallLevel;
                  if (isFullyAscended && !Array.isArray(selected.ascensions)) {
                    throw new Error(`Hall of Ascension: unit ${selected.orderId} is missing ascension bonuses`);
                  }
                  return (
                    <>
                      <h4 style={{ color: '#fff', marginTop: 0 }}>{selected.name}</h4>
                      <p style={{ color: '#ccc' }}>
                        {t('warEffort.hallAscensionProgress', {
                          current: selected.ascensionLevel,
                          max: hallAscensionModal.hallLevel
                        })}
                        {!isFullyAscended && selected.cannotAscendReason ? ` — ${selected.cannotAscendReason}` : ''}
                      </p>
                      {isFullyAscended ? (
                        <div className="war-effort-hall-bonuses">
                          {selected.ascensions.map((row) => (
                            <div key={row.level} className="war-effort-hall-bonus">
                              <div className="war-effort-hall-bonus-label">
                                {t('warEffort.hallAscensionBonusLabel', { level: row.level })}
                              </div>
                              <div className="war-effort-hall-bonus-value">{hallAscensionBonusText(row, t)}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          {hallAscensionModal.result && (
                            <p className="war-effort-hall-result">
                              {t('warEffort.hallGainedBonus', { bonus: hallAscensionBonusText(hallAscensionModal.result, t) })}
                            </p>
                          )}
                          {hallAscensionModal.error && <p style={{ color: '#f88' }}>{hallAscensionModal.error}</p>}
                          <button
                            type="button"
                            className="war-effort-hall-kind-btn"
                            disabled={!selected.stats?.available || !selected.stats?.affordable}
                            onClick={() => {
                              playSoundEffect('/sounds/ascend.mp3');
                              postKind('stats');
                            }}
                          >
                            <span className="war-effort-hall-kind-title">{t('warEffort.increaseStats')}</span>
                            <span className="war-effort-hall-kind-desc">+1 attack or +1 health (random)</span>
                            <HallResourceCostLine cost={selected.stats?.cost} />
                            {!selected.stats?.affordable && selected.stats?.available && <em>{t('warEffort.notEnoughStoredResources')}</em>}
                            {selected.stats?.reason && <em>{selected.stats.reason}</em>}
                          </button>
                          <button
                            type="button"
                            className="war-effort-hall-kind-btn"
                            disabled={!selected.ability?.available || !selected.ability?.affordable}
                            onClick={() => {
                              playSoundEffect('/sounds/ascend.mp3');
                              postKind('ability');
                            }}
                          >
                            <span className="war-effort-hall-kind-title">{t('warEffort.gainAbility')}</span>
                            <span className="war-effort-hall-kind-desc">{t('warEffort.randomAbilityUnitLacks')}</span>
                            <HallResourceCostLine cost={selected.ability?.cost} />
                            {!selected.ability?.affordable && selected.ability?.available && <em>{t('warEffort.notEnoughStoredResources')}</em>}
                            {selected.ability?.reason && <em>{selected.ability.reason}</em>}
                          </button>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>,
          document.body
        )}
        {hallAscensionModal.show && hallAscensionTooltip && typeof document !== 'undefined' && (() => {
          const unit = hallAscensionModal.units.find((u) =>
            hallCopyIsSelected(u, hallAscensionTooltip.orderId, hallAscensionTooltip.occurrence)
          );
          if (!unit) return null;
          const displayOrder = hallTooltipDisplayOrder(unit);
          return createPortal(
            <div
              style={{
                position: 'fixed',
                right: `${typeof window !== 'undefined' ? window.innerWidth - hallAscensionTooltip.left + 8 : 8}px`,
                ...(hallAscensionTooltip.showAbove
                  ? { bottom: `${typeof window !== 'undefined' ? window.innerHeight - hallAscensionTooltip.top + 5 : 0}px` }
                  : { top: `${hallAscensionTooltip.top}px` }),
                zIndex: 99999,
                pointerEvents: 'none',
                transform: 'scale(0.8)',
                transformOrigin: hallAscensionTooltip.showAbove ? 'bottom right' : 'top right',
                maxHeight: 400,
                overflow: 'auto'
              }}
            >
              <OrderDesign
                order={displayOrder}
                count={0}
                minimal={false}
                noMargin={true}
                lazyLoad={false}
                isOOR={false}
                ascensionHighlights={{
                  attack: !!(unit.highlights && unit.highlights.attack),
                  health: !!(unit.highlights && unit.highlights.health),
                  abilities: Array.isArray(unit.highlights && unit.highlights.abilities)
                    ? unit.highlights.abilities
                    : []
                }}
              />
            </div>,
            document.body
          );
        })()}
        {showBuildMenu && typeof document !== 'undefined' && createPortal(
          <div
            className="war-effort-build-menu-overlay"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100000,
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto'
            }}
            onClick={(e) => e.target === e.currentTarget && setShowBuildMenu(false)}
          >
            <div
              className="war-effort-build-menu-dialog"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label={t('warEffort.buildMenuTitle', 'Build headquarters')}
            >
              <MenuPaintedButton
                type="button"
                className="war-effort-build-menu-close"
                imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                aria-label={t('warEffort.tradingHub.close', 'Close')}
                onClick={() => { setShowBuildMenu(false); }}
              >
                ✕
              </MenuPaintedButton>
              <div className="war-effort-build-menu-dialog__tabs" role="tablist" aria-label={t('warEffort.buildMenuTabsLabel', 'Building categories')}>
                {BUILD_MENU_TAB_KEYS.map((tabKey) => (
                  <MenuPaintedButton
                    key={tabKey}
                    type="button"
                    role="tab"
                    id={`war-effort-build-tab-${tabKey}`}
                    aria-selected={buildMenuTab === tabKey}
                    aria-controls="war-effort-build-tab-panel"
                    className="war-effort-build-menu-tab"
                    active={buildMenuTab === tabKey}
                    imagePath={WAR_EFFORT_FACTION_BUTTON_PATH}
                    surfaceFilter={MENU_PAINTED_SURFACE_FILTER}
                    onClick={() => {
                      setBuildMenuTab(tabKey);
                    }}
                  >
                    <WarEffortBuildMenuTabIcon tabKey={tabKey} />
                    <span className="war-effort-build-menu-tab__label">{t(`warEffort.buildMenuTabs.${tabKey}`)}</span>
                  </MenuPaintedButton>
                ))}
              </div>
              <div
                id="war-effort-build-tab-panel"
                className="war-effort-build-menu-dialog__cards"
                role="tabpanel"
                aria-labelledby={`war-effort-build-tab-${buildMenuTab}`}
              >
                {buildMenuBuildingsForTab.map((building) => {
                  const isUnlocked = unlockedBuildingIds.includes(building.id);
                  const effectiveCost = getEffectiveBuildingCost(building.cost);
                  const energyOk = energyAmount >= effectiveCost;
                  const resourcesOk = canAffordHqResourceCosts(building.resourceCosts, storedHqResources);
                  const canAfford = energyOk && resourcesOk;
                  const { groupIds, cap } = getBuildingCapInfo(building.id);
                  const builtCount = placedBuildings.filter((p) => groupIds.includes(p.building.id)).length;
                  const atCapacity = builtCount >= cap;
                  const canSelect = isUnlocked && canAfford && !atCapacity;
                  const buildingName = localizeBuildingName(building.id, t);
                  const buildingDescription = localizeBuildingDescription(building.id, t, viewerPurpose);
                  const energyDiscountLabel = hasInfrastructureSubsidy ? t('warEffort.energyOff55') : buildingCostMultiplier < 1 ? t('warEffort.energyOff20') : '';
                  return (
                    <div
                      key={building.id}
                      className={`war-effort-build-menu-card ${canSelect ? 'war-effort-build-menu-card--selectable' : ''}`}
                      role={canSelect ? 'button' : undefined}
                      title={!isUnlocked ? t('warEffort.lockedTitle', { name: buildingName }) : atCapacity ? t('warEffort.maxReachedTitle', { name: buildingName, cap }) : !canAfford ? t('warEffort.notEnoughResourcesNeed', { cost: effectiveCost }) : buildingName}
                      onClick={canSelect ? (e) => {
                        e.stopPropagation();
                        playSoundEffect('/sounds/click.mp3');
                        setPlacementMouse({ x: e.clientX, y: e.clientY });
                        setBuildingForPlacement(building);
                        setShowBuildMenu(false);
                      } : undefined}
                    >
                      {!isUnlocked && (
                        <span className="war-effort-build-menu-card__lock" aria-hidden>
                          🔒
                        </span>
                      )}
                      <img
                        src={building.image}
                        alt={buildingName}
                        className={`war-effort-build-menu-card__image${building.id === 'hospital' ? ' war-effort-build-menu-card__image--hospital' : ''}`}
                      />
                      <h4 className="war-effort-build-menu-card__name">{buildingName}</h4>
                      {buildingDescription && (
                        <p className="war-effort-build-menu-card__description" title={buildingDescription}>{buildingDescription}</p>
                      )}
                      <div className="war-effort-build-menu-card__meta">
                        <BuildingHqCostLine
                          energyCost={effectiveCost}
                          resourceCosts={building.resourceCosts}
                          energyAffordable={energyOk}
                          storedResources={storedHqResources}
                          energyDiscountLabel={energyDiscountLabel}
                        />
                        <span className="war-effort-build-menu-card__count">
                          {t('warEffort.builtCount', { built: builtCount, cap })}
                        </span>
                      </div>
                      {isUnlocked && !canAfford && (
                        <span className="war-effort-build-menu-card__hint">{!energyOk ? t('warEffort.notEnoughEnergy') : t('warEffort.notEnoughResources')}</span>
                      )}
                      {isUnlocked && atCapacity && (
                        <span className="war-effort-build-menu-card__hint">{t('warEffort.maximumReached')}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  return (
    <CountryHoverProvider bindRef={countryHoverBindRef}>
    <div
      className={`war-effort-fortification${showBuildingPage ? ' war-effort-fortification--hq-open' : ''}`}
      style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}
    >
      {achievementUnlocked && (
        <AchievementUnlockedToast
          titleId={achievementUnlocked.titleId}
          titleName={achievementUnlocked.titleName}
          color={achievementUnlocked.color}
          achievementKey={achievementUnlocked.achievementKey}
          onClose={() => setAchievementUnlocked(null)}
        />
      )}
      {pendingToast && (
        <AchievementUnlockedToast
          titleId={pendingToast.titleId}
          titleName={pendingToast.titleName}
          color={pendingToast.color}
          achievementKey={pendingToast.achievementKey}
          onClose={dismissPendingToast}
        />
      )}
      {fortifiedCountryWarningOverlay}
      {/* Unified bottom buttons - world map: Build, Trading, Strategy */}
      <div className="war-effort-bottom-buttons">
        <button
          type="button"
          className="war-effort-bottom-btn"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setWorldMapResetTrigger((t) => t + 1);
          }}
          aria-label="World map"
        >
          <img src="/images/world_button.png" alt="" className="war-effort-bottom-btn__image" />
        </button>
        <button
          type="button"
          className="war-effort-bottom-btn"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            openBuildMenuAfterBuildingPageRef.current = true;
            onBuildingPageChange?.(true);
          }}
          aria-label="Build"
        >
          <img src="/images/build_button.png" alt="" className="war-effort-bottom-btn__image" />
        </button>
        <button
          type="button"
          className="war-effort-bottom-btn"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setTradingHubOpen(true);
          }}
          aria-label="Trading Hub"
        >
          <img src="/images/trading_button.png" alt="" className="war-effort-bottom-btn__image" />
        </button>
        <button
          type="button"
          className="war-effort-bottom-btn"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            openAttackStrategyModal();
          }}
          aria-label={t('warEffort.attackStrategy')}
        >
          <img src="/images/strategy_button.png" alt="" className="war-effort-bottom-btn__image" />
        </button>
      </div>
      {attackStrategyModal.show && !showBuildingPage && (
        <div
          className="war-effort-attack-strategy-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setAttackStrategyModal({ show: false, loading: false, strategy: null, deactivatedSlots: [] }); setAttackStrategyTooltipRect(null); }}
        >
          <div
            className="war-effort-attack-strategy-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="war-effort-attack-strategy-sidebar strategy-sidebar"
            >
              <h3 style={{ margin: '0 0 16px 0', color: '#fff' }}>{t('warEffort.attackStrategy')}</h3>
              {attackStrategyModal.loading ? (
                <p style={{ color: '#aaa' }}>{t('warEffort.loadingEllipsis')}</p>
              ) : attackStrategyModal.strategy?.orders?.length > 0 ? (
                (() => {
                  const deactivatedSet = new Set(attackStrategyModal.deactivatedSlots || []);
                  const slots = attackStrategyModal.strategy.orders.flatMap((order, i) =>
                    Array.from({ length: order.count || 1 }, (_, j) => ({
                      order,
                      key: `${order.id ?? order.order_id ?? i}-${j}`,
                      orderId: order.id ?? order.order_id,
                      occurrence: j
                    }))
                  );
                  const total = slots.length;
                  const activeCount = slots.filter((s) => !deactivatedSet.has(`${s.orderId}:${s.occurrence}`)).length;
                  return (
                    <>
                      <div style={{ marginBottom: 12, fontWeight: 'bold', color: '#aaa' }}>
                        {t('warEffort.activeOrdersNeed', { active: activeCount, total })}
                      </div>
                      {activeCount < 30 && (
                        <div style={{ marginBottom: 8, padding: 8, background: 'rgba(255,100,0,0.2)', borderRadius: 4, color: '#ffa', fontSize: '0.85em' }}>
                          {t('warEffort.activateMoreOrders')}
                        </div>
                      )}
                      <div className="war-effort-attack-strategy-list">
                        {slots.map(({ order, key, orderId, occurrence }) => (
                          <AttackStrategySlotRow
                            key={key}
                            order={order}
                            orderId={orderId}
                            occurrence={occurrence}
                            isDeactivated={deactivatedSet.has(`${orderId}:${occurrence}`)}
                            costLabel={t('strategy.cost')}
                            onToggle={handleAttackStrategyToggleSlot}
                            setTooltipRect={setAttackStrategyTooltipRect}
                            hallStars={hallAscensionLevelForOrder(attackStrategyModal.hallAscensions, orderId, occurrence)}
                            canSell={
                              Boolean(attackStrategyModal.canSellUnits)
                              && total > 30
                              && getAttackStrategyUnitSellEnergy(order, attackStrategyModal.unitSellPrices) != null
                            }
                            sellEnergy={getAttackStrategyUnitSellEnergy(order, attackStrategyModal.unitSellPrices)}
                            sellBusy={attackStrategySellingKey === `${orderId}:${occurrence}`}
                            onSell={handleAttackStrategySellUnit}
                          />
                        ))}
                      </div>
                  </>
                );
              })()
            ) : (
              <p style={{ color: '#aaa' }}>{t('warEffort.noOrdersInAttackStrategy')}</p>
            )}
              <MenuPaintedButton
                type="button"
                className="war-effort-ingame-cancel-btn"
                imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                onClick={() => { setAttackStrategyModal({ show: false, loading: false, strategy: null, deactivatedSlots: [] }); setAttackStrategyTooltipRect(null); }}
              >
                {t('common.close')}
              </MenuPaintedButton>
            </div>
            {!attackStrategyModal.loading && attackStrategyModal.strategy?.orders?.length > 0 && (
              <AttackStrategyStatisticsPanel
                orders={getActiveAttackStrategyOrders(
                  attackStrategyModal.strategy.orders,
                  attackStrategyModal.deactivatedSlots
                )}
                layout="column"
              />
            )}
          </div>
        </div>
      )}
      {attackStrategyTooltipRect && attackStrategyModal.strategy && createPortal(
        <div
          style={{
            position: 'fixed',
            left: `${attackStrategyTooltipRect.left}px`,
            ...(attackStrategyTooltipRect.showAbove
              ? { bottom: `${typeof window !== 'undefined' ? window.innerHeight - attackStrategyTooltipRect.top + 5 : 0}px` }
              : { top: `${attackStrategyTooltipRect.bottom + 5}px` }),
            zIndex: 99999,
            pointerEvents: 'none',
            transform: 'scale(0.8)',
            transformOrigin: attackStrategyTooltipRect.showAbove ? 'bottom left' : 'top left',
            maxHeight: 400,
            overflow: 'auto'
          }}
        >
          <OrderDesign
            order={previewHallAscendedOrder(
              attackStrategyTooltipRect.order,
              hallRowsForOrder(
                attackStrategyModal.hallAscensions,
                attackStrategyTooltipRect.orderId,
                attackStrategyTooltipRect.occurrence
              )
            ).order}
            count={0}
            minimal={false}
            noMargin={true}
            lazyLoad={false}
            isOOR={false}
            ascensionHighlights={
              previewHallAscendedOrder(
                attackStrategyTooltipRect.order,
                hallRowsForOrder(
                  attackStrategyModal.hallAscensions,
                  attackStrategyTooltipRect.orderId,
                  attackStrategyTooltipRect.occurrence
                )
              ).highlights
            }
          />
        </div>,
        document.body
      )}
      {/* Energy: floating transparent cylinder, below header to avoid overlap with Friends/Settings/username */}
      <WarEffortEnergyBar
        energyCap={energyCap}
        energyFillPercent={energyFillPercent}
        energyAmount={energyAmount}
        productionPerMinute={energyProductionPerMinute}
        t={t}
      />
      {showTransferEnergy && (
        <div className="war-effort-map-chrome--transfer">
          <button
            type="button"
            className="war-effort-transfer-energy-btn"
            onClick={() => { playSoundEffect('/sounds/click.mp3'); setTransferEnergyModalOpen(true); }}
            style={{
              fontSize: '9px',
              padding: '4px 2px',
              cursor: 'pointer',
              backgroundColor: 'rgba(78, 205, 196, 0.25)',
              border: '1px solid rgba(78, 205, 196, 0.6)',
              borderRadius: '6px',
              color: '#4ECDC4',
              fontWeight: 'bold',
              whiteSpace: 'normal',
              lineHeight: 1.2,
              width: '100%'
            }}
          >
            {t('warEffort.transferEnergy', 'Transfer Energy')}
          </button>
        </div>
      )}
      {transferEnergyModalOpen && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="transfer-energy-title-map"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.7)',
            pointerEvents: 'auto'
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setTransferEnergyModalOpen(false); }}
        >
          <div
            style={{
              backgroundColor: '#2a2a2a',
              border: '2px solid rgba(78, 205, 196, 0.6)',
              borderRadius: 12,
              padding: 20,
              minWidth: 220,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="transfer-energy-title-map" style={{ margin: '0 0 16px', color: '#4ECDC4', fontSize: 16 }}>{t('warEffort.transferEnergy', 'Transfer Energy')}</h3>
            <p style={{ margin: '0 0 12px', color: '#aaa', fontSize: 12 }}>{t('warEffort.transferEnergyToAlly', 'Give energy to your ally:')}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {[50, 100, 200].map((n) => (
                <button
                  key={n}
                  type="button"
                  disabled={transferEnergyLoading || energyAmount < n}
                  onClick={() => handleTransferEnergy(n)}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: (transferEnergyLoading || energyAmount < n) ? '#444' : 'rgba(78, 205, 196, 0.25)',
                    border: `1px solid ${energyAmount >= n ? 'rgba(78, 205, 196, 0.6)' : '#555'}`,
                    borderRadius: 8,
                    color: energyAmount >= n ? '#4ECDC4' : '#888',
                    cursor: (transferEnergyLoading || energyAmount < n) ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                disabled={transferEnergyLoading || energyAmount <= 0}
                onClick={() => handleTransferEnergy('all')}
                style={{
                  padding: '8px 14px',
                  backgroundColor: (transferEnergyLoading || energyAmount <= 0) ? '#444' : 'rgba(78, 205, 196, 0.25)',
                  border: `1px solid ${energyAmount > 0 ? 'rgba(78, 205, 196, 0.6)' : '#555'}`,
                  borderRadius: 8,
                  color: energyAmount > 0 ? '#4ECDC4' : '#888',
                  cursor: (transferEnergyLoading || energyAmount <= 0) ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                All
              </button>
            </div>
            <MenuPaintedButton
              type="button"
              className="war-effort-ingame-cancel-btn"
              imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
              onClick={() => setTransferEnergyModalOpen(false)}
            >
              Cancel
            </MenuPaintedButton>
          </div>
        </div>,
        document.body
      )}

      {/* World map - full screen, no borders or background content */}
      <div
        className="world-map-container"
        aria-hidden={showBuildingPage ? 'true' : undefined}
        style={{ border: 'none', margin: 0, padding: 0, position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1, pointerEvents: showBuildingPage ? 'none' : undefined }}
      >
        {/* Engineers count: below back button (upper right), format Engineers: X/10 */}
        {missionData?.id != null && (
          <div
            className="war-effort-map-chrome--engineers"
            style={{ '--war-effort-painted-chrome-image': resolvePublicAssetBackgroundUrl(WAR_EFFORT_REFRESH_BUTTON_PATH) }}
          >
            <span className="war-effort-map-chrome--engineers__label">
              {t('warEffort.engineersCount', { current: engineersForDisplay != null ? engineersForDisplay : '—' })}
            </span>
          </div>
        )}
        <WorldMap
          resetViewTrigger={worldMapResetTrigger}
          interactive={!showBuildingPage}
          cellOwnersMap={cellOwnersMap}
          getFactionBackground={getFactionBackground}
          factionsInGame={factionsInGame}
          onCountryClick={onWorldMapCountryClick}
          onCountryHover={handleCountryHover}
          onCountryLeave={handleCountryLeave}
          highlightedCells={controlledCountries}
          fortifiedCells={fortifiedCellsForMap}
          commandCenterFortifiedCells={commandCenterFortifiedCellsForMap}
          viewerCommandCenterStripeBaseColor={viewerCommandCenterStripeColors?.base}
          viewerCommandCenterStripeDarkerColor={viewerCommandCenterStripeColors?.darker}
          opponentFortifiedCells={opponentFortifiedCellsForMap}
          opponentCommandCenterFortifiedCells={opponentCommandCenterFortifiedCellsForMap}
          opponentCommandCenterStripeBaseColor={opponentCommandCenterStripeColors?.base}
          opponentCommandCenterStripeDarkerColor={opponentCommandCenterStripeColors?.darker}
          opponentCommandCenterStripeByCellId={opponentCommandCenterStripeByCellId}
          selectedCell={selectedCountry}
          missionSubmissionHoverCellId={missionSubmissionHoverCellId}
          attackArrowFromCellId={attackFromCountry?.cellId ?? null}
          isAttackArrowTargetCell={isAttackArrowTargetCell}
          devModeHighlightCells={worldMapDevHighlightCells}
          neutralFortificationCountMap={neutralFortificationCountMap}
          hintPopup={worldMapHintPopup}
          cellsUnderAttack={cellsUnderAttack}
          newTradeOfferCells={worldMapNewTradeOfferCells}
          tectonicBoredriveCells={tectonicBoredriveCellsForMap}
        />

        {/* Dev-only: Bot log when player2 is simulated */}
        {devModeActive && isPlayer1 && missionData?.player2_simulated_at && missionData?.id && (
          <WarEffortBotLog missionId={missionData.id} />
        )}
        
        {/* Mission and Opponent Info Overlay */}
        {(() => {
          // First, verify that the current user is part of this mission
          if (!isUserInMission) {
            // User is not part of this mission - don't show anything
            return null;
          }
          
          // Only show mission info when opponent exists. 1v1: mission must be active and other slot filled. FFA/2v2: any other slot filled (including bot), even in waiting_crew.
          const hasOpponent = missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2'
            ? [1, 2, 3, 4].some(s => s !== viewerSlot && missionData?.[`player${s}_id`])
            : (missionData?.status === 'active' && (isPlayer1 ? missionData?.player2_id : missionData?.player1_id));
          
          // Don't show mission until opponent exists - mission target depends on opponent's headquarters
          if (!hasOpponent) {
            const playerPurpose = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') && viewerSlot >= 1 && viewerSlot <= 4
              ? (missionData?.[`player${viewerSlot}_purpose`] ?? null)
              : (isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose);
            const viewerName = (typeof user === 'string' ? user : user?.username) || (isPlayer1 ? t('warEffort.player1') : t('warEffort.player2'));
            const hqCellId = playerPurpose ? PURPOSE_TO_HQ_CELL_ID[playerPurpose] : null;
            const hqCountry = hqCellId != null ? displayCountryForCell(hqCellId) : null;
            const playerFactionName = playerPurpose ? DEV_PURPOSE_TO_FACTION[playerPurpose] : null;
            const hqBg = playerFactionName ? getHeadquartersImage(playerFactionName) : null;
            return playerPurpose && (
              <div className="war-effort-map-chrome--info-stack">
              <div className="war-effort-map-chrome--quest-logs">
              <div
                className="war-effort-info-box war-effort-info-box--on-map"
                style={{
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  background: hqBg
                    ? `linear-gradient(135deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.72) 100%), url(${hqBg}) center/cover`
                    : 'rgba(12, 14, 18, 0.88)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.04)'
                }}
              >
                <div style={{
                  padding: '10px 14px',
                  color: 'rgba(255, 255, 255, 0.92)',
                  fontSize: '13px',
                  fontFamily: "'Exo 2', 'Segoe UI', system-ui, sans-serif"
                }}>
                  <div className="war-effort-info-box-header">
                    <span><span style={{ fontWeight: '600', marginRight: '6px' }}>{t('warEffort.hqLabel', { name: viewerName })}</span>{hqCountry || '—'}</span>
                    <span style={{ color: PURPOSE_TO_COLOR[playerPurpose] || '#4ECDC4', fontWeight: '600', fontSize: '12px' }}>{localizePurpose(playerPurpose, t)}</span>
                  </div>
                  <div className="war-effort-info-box-value war-effort-info-box-value--muted" style={{ marginTop: '8px', fontSize: '12px' }}>
                    {t('warEffort.waitingForOpponentToJoin')}
                  </div>
                </div>
              </div>
              </div>
              {renderTradeForumMapPanel()}
              </div>
            );
          }
          
          // Calculate the correct mission name for this player (only when opponent exists). FFA/2v2: use viewerSlot.
          const playerPurpose = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') && viewerSlot >= 1 && viewerSlot <= 4
            ? (missionData?.[`player${viewerSlot}_purpose`] ?? null)
            : (isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose);
          const playerTargetCountry = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') && viewerSlot >= 1 && viewerSlot <= 4
            ? (missionData?.[`player${viewerSlot}_mission_target_country`] || missionData?.mission_target_country)
            : (isPlayer1 ? (missionData?.player1_mission_target_country || missionData?.mission_target_country) : (missionData?.player2_mission_target_country || missionData?.mission_target_country));
          const playerMissionType = playerPurpose ? PURPOSE_TO_MISSION_TYPE[playerPurpose] : null;
          const playerMissionName = (playerMissionType && playerTargetCountry)
            ? `${playerMissionType} in ${playerTargetCountry}`
            : missionData?.mission_name;
          const playerMissionDisplay = (playerPurpose && playerTargetCountry)
            ? t('warEffort.missionInCountry', {
                missionType: localizeMissionType(playerPurpose, t),
                country: displayCountry(playerTargetCountry)
              })
            : playerMissionName;
          const lastPlayerDisplay = getLastPlayerActionDisplay(lastPlayerAction, lastPlayerActionCellId, t);
          // Country for hover: use explicit target, or parse from "MissionType in Country" when from mission_name fallback
          const missionCountryForHover = playerTargetCountry || ((playerMissionName && / in /.test(playerMissionName))
            ? playerMissionName.replace(/^.*\s+in\s+/, '').trim()
            : null);
          const playerTargetCellId = missionCountryForHover ? getCellIdFromCountry(missionCountryForHover) : null;

          const lastOpponentDisplay = getLastOpponentActionDisplay(lastOpponentAction, lastOpponentActionCellId, t);
          
          // Single-opponent (1v1) mission name for legacy display
          const opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
          const opponentTargetCountry = isPlayer1
            ? (missionData?.player2_mission_target_country || missionData?.mission_target_country)
            : (missionData?.player1_mission_target_country || missionData?.mission_target_country);
          const opponentMissionType = opponentPurpose ? PURPOSE_TO_MISSION_TYPE[opponentPurpose] : null;
          const opponentMissionName = (opponentMissionType && opponentTargetCountry)
            ? `${opponentMissionType} in ${opponentTargetCountry}`
            : null;
          const opponentMissionDisplay = (opponentPurpose && opponentTargetCountry)
            ? t('warEffort.missionInCountry', {
                missionType: localizeMissionType(opponentPurpose, t),
                country: displayCountry(opponentTargetCountry)
              })
            : opponentMissionName;
          
          const showMultiOpponent = opponentsForBox.length > 0;
          const playerHqCellId = playerPurpose ? PURPOSE_TO_HQ_CELL_ID[playerPurpose] : null;
          const playerHqCountry = playerHqCellId != null ? getCountryFromCellId(playerHqCellId) : null;
          const viewerName = (typeof user === 'string' ? user : user?.username) || (isPlayer1 ? t('warEffort.player1') : t('warEffort.player2'));
          const playerFactionName = playerPurpose ? DEV_PURPOSE_TO_FACTION[playerPurpose] : null;
          const playerHqBg = playerFactionName ? getHeadquartersImage(playerFactionName) : null;
          const opponentHqCellId = opponentPurpose ? PURPOSE_TO_HQ_CELL_ID[opponentPurpose] : null;
          const opponentHqCountry = opponentHqCellId != null ? getCountryFromCellId(opponentHqCellId) : null;
          const opponentName = opponentUsername || (isPlayer1 ? t('warEffort.player2') : t('warEffort.player1'));
          const opponentFactionName = opponentPurpose ? DEV_PURPOSE_TO_FACTION[opponentPurpose] : null;
          const opponentHqBg = opponentFactionName ? getHeadquartersImage(opponentFactionName) : null;
          const hqBoxStyle = (bgImage) => ({
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '10px',
            overflow: 'hidden',
            position: 'relative',
            background: bgImage
              ? `linear-gradient(135deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.72) 100%), url(${bgImage}) center/cover`
              : 'rgba(12, 14, 18, 0.88)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.04)',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column'
          });
          const hqOverlayStyle = {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            padding: '10px 14px',
            color: 'rgba(255, 255, 255, 0.92)',
            fontSize: '13px',
            fontFamily: "'Exo 2', 'Segoe UI', system-ui, sans-serif",
            minHeight: '44px',
            flex: '1 1 auto'
          };
          const sectionLabel = 'war-effort-info-box-label';
          const sectionValue = 'war-effort-info-box-value';
          const sectionValueMuted = 'war-effort-info-box-value war-effort-info-box-value--muted';
          const toConquerItems = (cellIds) => {
            const allowedFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
            return (Array.isArray(cellIds) ? cellIds : []).map((cellId) => {
              const countryName = getCountryFromCellId(cellId);
              const owner = cellOwnersMap[String(cellId)];
              const conquered = !!(owner && allowedFactions.includes(owner));
              return { cellId, countryName, conquered };
            });
          };
          const viewerConquerCellIds = showMultiOpponent
            ? opponentsForBox.flatMap((o) => o.fortifiedCountries || [])
            : opponentFortifiedCountries.slice(0, 3);
          const renderConquerList = (items) => (
            <div className="war-effort-info-box-conquer-list">
              {items.map(({ cellId, countryName, conquered }) => (
                <div
                  key={cellId}
                  className="war-effort-info-box-conquer-row"
                  onMouseEnter={() => setMissionSubmissionHoverCellId(cellId)}
                  onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                  role="listitem"
                  aria-label={t('warEffort.conquerCountry', { country: displayCountry(countryName) }) + (conquered ? `, ${t('warEffort.conquered')}` : '')}
                >
                  {conquered && <span className="war-effort-info-box-conquer-done" aria-label={t('warEffort.conquered')}>✓</span>}
                  <span>{t('warEffort.conquerCountry', { country: displayCountry(countryName) })}</span>
                </div>
              ))}
            </div>
          );
          const hasAnyBox = playerHqCountry || (showMultiOpponent ? opponentsForBox.some(o => o.hqCountry) : opponentHqCountry);
          return (playerMissionName || opponentUsername || (Array.isArray(opponentUsernames) && opponentUsernames.length > 0) || playerPurpose) && hasAnyBox && (
            <div className={`war-effort-map-chrome--info-stack${expandedInfoSlots.length > 0 ? ' war-effort-map-chrome--info-stack--logs-open' : ''}`}>
              <div className="war-effort-map-chrome--quest-logs">
              {/* Viewer's single info box: header + purpose in top-right; expand on click for mission, submission, last action */}
              {playerHqCountry && (
                <div
                  className="war-effort-info-box war-effort-info-box--on-map"
                  style={hqBoxStyle(playerHqBg)}
                  onClick={() => setExpandedInfoSlots(prev => prev.includes(viewerSlot) ? prev.filter(s => s !== viewerSlot) : [...prev, viewerSlot])}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedInfoSlots(prev => prev.includes(viewerSlot) ? prev.filter(s => s !== viewerSlot) : [...prev, viewerSlot]); } }}
                  aria-expanded={expandedInfoSlots.includes(viewerSlot)}
                  aria-label={`${t('warEffort.hqLabel', { name: viewerName })} ${displayCountry(playerHqCountry)}. ${expandedInfoSlots.includes(viewerSlot) ? t('warEffort.collapseDetails') : t('warEffort.expandDetails')}`}
                >
                  <div style={hqOverlayStyle}>
                    <div className="war-effort-info-box-header">
                      <span
                        className="war-effort-info-box-hq-link"
                        onMouseEnter={() => setMissionSubmissionHoverCellId(playerHqCellId)}
                        onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                        role="img"
                        aria-label={t('warEffort.highlightOnMap', { country: displayCountry(playerHqCountry) })}
                      >
                        <span style={{ fontWeight: '600', marginRight: '6px' }}>{t('warEffort.hqLabel', { name: viewerName })}</span>
                        {displayCountry(playerHqCountry)}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {playerPurpose && <span style={{ color: PURPOSE_TO_COLOR[playerPurpose] || '#4ECDC4', fontWeight: '600', fontSize: '12px' }}>{localizePurpose(playerPurpose, t)}</span>}
                        <span className="war-effort-info-box-chevron" title={expandedInfoSlots.includes(viewerSlot) ? t('warEffort.clickToCollapse') : t('warEffort.clickToExpand')}>
                          {expandedInfoSlots.includes(viewerSlot) ? '▼' : '▶'}
                        </span>
                      </span>
                    </div>
                    {expandedInfoSlots.includes(viewerSlot) && (
                      <div className="war-effort-info-box-expanded">
                        {(playerMissionName || viewerConquerCellIds.length > 0) && (
                          <div className="war-effort-info-box-section">
                            {playerMissionName && (
                              <>
                                <div className={sectionLabel}>{t('warEffort.mission.missionTitle')}</div>
                                <div className={sectionValue}>
                                  {playerTargetCellId != null ? (
                                    <span
                                      className="war-effort-info-box-hq-link"
                                      onMouseEnter={() => setMissionSubmissionHoverCellId(playerTargetCellId)}
                                      onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                                      role="img"
                                      aria-label={t('warEffort.highlightOnMap', { country: displayCountry(missionCountryForHover || getCountryFromCellId(playerTargetCellId)) })}
                                    >
                                      {playerMissionDisplay ?? playerMissionType}
                                    </span>
                                  ) : (
                                    (playerMissionDisplay ?? playerMissionType)
                                  )}
                                </div>
                              </>
                            )}
                            {viewerConquerCellIds.length > 0 && renderConquerList(toConquerItems(viewerConquerCellIds))}
                          </div>
                        )}
                        {lastPlayerDisplay && (() => {
                          const lastActionCellIdForHover = lastPlayerDisplay.cellId;
                          return (
                            <div className="war-effort-info-box-section">
                              <div className={sectionLabel}>{t('warEffort.lastAction')}</div>
                              <div className={sectionValueMuted} style={{ color: '#fbbf24' }}>
                                {lastActionCellIdForHover != null ? (
                                  <span
                                    className="war-effort-info-box-hq-link"
                                    onMouseEnter={() => setMissionSubmissionHoverCellId(lastActionCellIdForHover)}
                                    onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                                    role="img"
                                    aria-label={t('warEffort.highlightOnMap', { country: displayCountryForCell(lastActionCellIdForHover) })}
                                  >
                                    {lastPlayerDisplay.message}
                                  </span>
                                ) : (
                                  lastPlayerDisplay.message
                                )}
                              </div>
                            </div>
                          );
                        })()}
                        {opponentAttackTargets && opponentAttackTargets.length > 0 && (
                          <div className="war-effort-info-box-section">
                            <div className={sectionLabel}>{t('warEffort.opponentsAttacking')}</div>
                            <div className="war-effort-info-box-conquer-list">
                              {opponentAttackTargets.map((at) => (
                                <div
                                  key={at.userId}
                                  className="war-effort-info-box-conquer-row"
                                  onMouseEnter={() => setMissionSubmissionHoverCellId(at.countryCellId)}
                                  onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                                  role="listitem"
                                  aria-label={t('warEffort.attackingUsernameCountry', { username: at.username, country: displayCountry(at.countryName) })}
                                >
                                  {at.username} → {displayCountry(at.countryName)}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {(isCeaseFireActive || isOpponentFortifiedCeaseFireActive || isConquestAttackerCeaseFireActive) && (
                          <div className="war-effort-info-box-section">
                            <div className={sectionLabel}>{t('warEffort.statusLabel')}</div>
                            <div className="war-effort-info-box-status war-effort-info-box-status--warning">
                              {isCeaseFireActive && (
                                <span>{t('warEffort.ceaseFireNoAttacks', { time: formatCeaseFireCountdown(ceaseFireRemainingSeconds) })}</span>
                              )}
                              {!isCeaseFireActive && isOpponentFortifiedCeaseFireActive && (
                                <span>{t('warEffort.opponentFortifiedProtected', { time: formatCeaseFireCountdown(opponentFortifiedCeaseFireRemainingSeconds) })}</span>
                              )}
                              {!isCeaseFireActive && !isOpponentFortifiedCeaseFireActive && isConquestAttackerCeaseFireActive && (
                                <span>{t('warEffort.conquestCooldownStatus', { time: formatCeaseFireCountdown(conquestAttackerCeaseFireRemainingSeconds) })}</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Opponent info box(es): one per opponent, same pattern */}
              {showMultiOpponent && opponentsForBox.filter(o => o.hqCountry).map((opp) => (
                <div
                  key={opp.slot}
                  className="war-effort-info-box war-effort-info-box--on-map"
                  style={hqBoxStyle(opp.factionName ? getHeadquartersImage(opp.factionName) : null)}
                  onClick={() => setExpandedInfoSlots(prev => prev.includes(opp.slot) ? prev.filter(s => s !== opp.slot) : [...prev, opp.slot])}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedInfoSlots(prev => prev.includes(opp.slot) ? prev.filter(s => s !== opp.slot) : [...prev, opp.slot]); } }}
                  aria-expanded={expandedInfoSlots.includes(opp.slot)}
                  aria-label={`${t('warEffort.hqLabel', { name: opp.username })} ${displayCountry(opp.hqCountry)}. ${expandedInfoSlots.includes(opp.slot) ? t('warEffort.collapseDetails') : t('warEffort.expandDetails')}`}
                >
                  <div style={hqOverlayStyle}>
                    <div className="war-effort-info-box-header">
                      <span
                        className="war-effort-info-box-hq-link"
                        onMouseEnter={() => setMissionSubmissionHoverCellId(opp.hqCellId)}
                        onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                        role="img"
                        aria-label={t('warEffort.highlightOnMap', { country: displayCountry(opp.hqCountry) })}
                      >
                        <span style={{ fontWeight: '600', marginRight: '6px' }}>{t('warEffort.hqLabel', { name: opp.username })}</span>
                        {displayCountry(opp.hqCountry)}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {opp.purpose && <span style={{ color: PURPOSE_TO_COLOR[opp.purpose] || '#4ECDC4', fontWeight: '600', fontSize: '12px' }}>{localizePurpose(opp.purpose, t)}</span>}
                        <span className="war-effort-info-box-chevron" title={expandedInfoSlots.includes(opp.slot) ? t('warEffort.clickToCollapse') : t('warEffort.clickToExpand')}>
                          {expandedInfoSlots.includes(opp.slot) ? '▼' : '▶'}
                        </span>
                      </span>
                    </div>
                    {expandedInfoSlots.includes(opp.slot) && (
                      <div className="war-effort-info-box-expanded">
                        {(opp.missionName || (opp.fortifiedCountries && opp.fortifiedCountries.length > 0)) && (
                          <div className="war-effort-info-box-section">
                            {opp.missionName && (
                              <>
                                <div className={sectionLabel}>{t('warEffort.mission.missionTitle')}</div>
                                <div className={sectionValue}>
                                  {opp.purpose && opp.targetCountry
                                    ? t('warEffort.missionInCountry', {
                                        missionType: localizeMissionType(opp.purpose, t),
                                        country: displayCountry(opp.targetCountry)
                                      })
                                    : opp.missionName}
                                </div>
                              </>
                            )}
                            {opp.fortifiedCountries && opp.fortifiedCountries.length > 0 && renderConquerList(toConquerItems(opp.fortifiedCountries))}
                          </div>
                        )}
                        {viewerHasIntelligenceAgency && opponentsForBox[0]?.slot === opp.slot && lastOpponentDisplay?.message && (
                          <div className="war-effort-info-box-section">
                            <div className={sectionLabel}>{t('warEffort.lastAction')}</div>
                            <div
                              className={sectionValueMuted}
                              {...(lastOpponentDisplay.cellId != null
                                ? {
                                    style: { cursor: 'pointer' },
                                    onMouseEnter: () => setMissionSubmissionHoverCellId(lastOpponentDisplay.cellId),
                                    onMouseLeave: () => setMissionSubmissionHoverCellId(null)
                                  }
                                : {})}
                              role={lastOpponentDisplay.cellId != null ? 'listitem' : undefined}
                              aria-label={lastOpponentDisplay.cellId != null ? lastOpponentDisplay.message : undefined}
                            >
                              {lastOpponentDisplay.message}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {!showMultiOpponent && opponentHqCountry && (
                <div
                  className="war-effort-info-box war-effort-info-box--on-map"
                  style={hqBoxStyle(opponentHqBg)}
                  onClick={() => { const slot = isPlayer1 ? 2 : 1; setExpandedInfoSlots(prev => prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot]); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const slot = isPlayer1 ? 2 : 1; setExpandedInfoSlots(prev => prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot]); } }}
                  aria-expanded={expandedInfoSlots.includes(isPlayer1 ? 2 : 1)}
                  aria-label={`${t('warEffort.hqLabel', { name: opponentName })} ${displayCountry(opponentHqCountry)}. ${expandedInfoSlots.includes(isPlayer1 ? 2 : 1) ? t('warEffort.collapseDetails') : t('warEffort.expandDetails')}`}
                >
                  <div style={hqOverlayStyle}>
                    <div className="war-effort-info-box-header">
                      <span
                        className="war-effort-info-box-hq-link"
                        onMouseEnter={() => setMissionSubmissionHoverCellId(opponentHqCellId)}
                        onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                        role="img"
                        aria-label={t('warEffort.highlightOnMap', { country: displayCountry(opponentHqCountry) })}
                      >
                        <span style={{ fontWeight: '600', marginRight: '6px' }}>{t('warEffort.hqLabel', { name: opponentName })}</span>
                        {displayCountry(opponentHqCountry)}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {opponentPurpose && <span style={{ color: PURPOSE_TO_COLOR[opponentPurpose] || '#4ECDC4', fontWeight: '600', fontSize: '12px' }}>{localizePurpose(opponentPurpose, t)}</span>}
                        <span className="war-effort-info-box-chevron" title={expandedInfoSlots.includes(isPlayer1 ? 2 : 1) ? t('warEffort.clickToCollapse') : t('warEffort.clickToExpand')}>
                          {expandedInfoSlots.includes(isPlayer1 ? 2 : 1) ? '▼' : '▶'}
                        </span>
                      </span>
                    </div>
                    {expandedInfoSlots.includes(isPlayer1 ? 2 : 1) && (
                      <div className="war-effort-info-box-expanded">
                        {opponentMissionName && (
                          <div className="war-effort-info-box-section">
                            <div className={sectionLabel}>{t('warEffort.mission.missionTitle')}</div>
                            <div className={sectionValue}>{opponentMissionDisplay}</div>
                          </div>
                        )}
                        {viewerHasIntelligenceAgency && lastOpponentDisplay?.message && (
                          <div className="war-effort-info-box-section">
                            <div className={sectionLabel}>{t('warEffort.lastAction')}</div>
                            <div
                              className={sectionValueMuted}
                              {...(lastOpponentDisplay.cellId != null
                                ? {
                                    style: { cursor: 'pointer' },
                                    onMouseEnter: () => setMissionSubmissionHoverCellId(lastOpponentDisplay.cellId),
                                    onMouseLeave: () => setMissionSubmissionHoverCellId(null)
                                  }
                                : {})}
                              role={lastOpponentDisplay.cellId != null ? 'listitem' : undefined}
                              aria-label={lastOpponentDisplay.cellId != null ? lastOpponentDisplay.message : undefined}
                            >
                              {lastOpponentDisplay.message}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
              {renderTradeForumMapPanel()}
            </div>
          );
        })()}

        {/* Active bonuses (right side) - territory production + shop effect thumbnails */}
        {isUserInMission && (territoryBonusEffects.length > 0 || activeShopEffects.length > 0) && (
          <ActiveShopEffectsThumbnails
            effects={[...territoryBonusEffects, ...activeShopEffects]}
            effectTypeToKey={SHOP_EFFECT_TYPE_TO_KEY}
            effectTypeToImage={SHOP_EFFECT_TYPE_TO_IMAGE}
            t={t}
            title={territoryBonusEffects.length > 0
              ? t('warEffort.territoryBonuses.activeBonuses', 'Active bonuses')
              : t('warEffort.tradingHub.activeEffects', 'Active shop effects')}
          />
        )}

        {/* Attack mode: distance/cost bar and cancel - below header to avoid overlap with Friends/Settings/username */}
        {attackFromCountry && (
          <div className="war-effort-map-chrome--attack-bar">
            <span style={{ color: '#FFD700' }}>{t('warEffort.attackingFrom', { country: displayCountry(attackFromCountry.countryName) })}</span>
            <WarEffortAttackHoverStatus
              attackFromCountry={attackFromCountry}
              controlledCountries={controlledCountries}
              enemyHqAttackInfo={enemyHqAttackInfo}
              missionMode={missionData?.mission_mode}
              missionId={missionData?.id}
              opponentHqCellId={opponentHqCellId}
              userId={userId}
              playerResources={playerResources}
              isCeaseFireActive={isCeaseFireActive}
              displayCountryForCell={displayCountryForCell}
              t={t}
            />
            {isCeaseFireActive && (
              <span style={{ color: '#FFB347' }}>{t('warEffort.ceaseFireAttacksDisabled', { time: formatCeaseFireCountdown(ceaseFireRemainingSeconds) })}</span>
            )}
            {!isCeaseFireActive && isOpponentFortifiedCeaseFireActive && (
              <span style={{ color: '#FFB347' }}>{t('warEffort.opponentFortifiedProtectedShort', { time: formatCeaseFireCountdown(opponentFortifiedCeaseFireRemainingSeconds) })}</span>
            )}
            {!isCeaseFireActive && !isOpponentFortifiedCeaseFireActive && isConquestAttackerCeaseFireActive && (
              <span style={{ color: '#FFB347' }}>{t('warEffort.conquestCooldownShort', { time: formatCeaseFireCountdown(conquestAttackerCeaseFireRemainingSeconds) })}</span>
            )}
            <MenuPaintedButton
              type="button"
              className="war-effort-ingame-cancel-btn"
              imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
              onClick={() => { setAttackFromCountry(null); setError(null); }}
            >
              {t('warEffort.cancel')}
            </MenuPaintedButton>
          </div>
        )}
      </div>
      
      {/* Owned country choice: Fortifications vs Attack */}
      {ownedCountryChoice && (
        <div
          className="war-effort-owned-choice-overlay"
          onClick={() => setOwnedCountryChoice(null)}
        >
          <div
            className="war-effort-owned-choice-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="war-effort-owned-choice-title">
              {isOwnedCellProtected(ownedCountryChoice.cellId)
                ? t('warEffort.fortifyProtectedNeedsNone', { country: displayCountry(ownedCountryChoice.countryName) })
                : t('warEffort.ordersForForcesIn', { country: displayCountry(ownedCountryChoice.countryName) })}
            </h3>
            <div className="war-effort-owned-choice-actions">
              {isOwnedCellProtected(ownedCountryChoice.cellId) ? (
                <p className="war-effort-owned-choice-note">
                  {t('warEffort.tradingHub.countryProtectedNoFortify')}
                </p>
              ) : (
                <>
                  {(() => {
                    const tectonicIds = (missionData?.tectonic_boredrive_cell_ids || []).map((id) => Number(id)).filter((n) => !isNaN(n));
                    const isTectonicCell = tectonicIds.includes(Number(ownedCountryChoice.cellId));
                    const isHQCell = ALL_HQ_CELL_IDS.includes(Number(ownedCountryChoice.cellId));
                    const fortifyDisabled = playerFortificationSlotsUsed >= 3 || isTectonicCell;
                    return (
                      <button
                        type="button"
                        className="war-effort-owned-option-btn"
                        disabled={fortifyDisabled}
                        onClick={() => {
                          if (fortifyDisabled) return;
                          setSelectedCountry(ownedCountryChoice.cellId);
                          setSelectedFortification(null);
                          setOwnedCountryChoice(null);
                          setError(null);
                        }}
                        title={isTectonicCell ? t('warEffort.cannotFortifyTectonicBoredrive') : (playerFortificationSlotsUsed >= 3 ? t('warEffort.mission.maxFortifyHint') : undefined)}
                        style={{
                          padding: '20px 24px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          backgroundColor: fortifyDisabled ? '#555' : '#333',
                          color: fortifyDisabled ? '#999' : 'white',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderRadius: '10px',
                          cursor: fortifyDisabled ? 'not-allowed' : 'pointer',
                          opacity: fortifyDisabled ? 0.8 : 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(/images/fortify_button.jpg)`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      >
                        <WarEffortOwnedOptionLabel action="fortify">
                          {t('warEffort.fortifyCountry', { country: displayCountry(ownedCountryChoice.countryName) })}
                        </WarEffortOwnedOptionLabel>
                        {playerFortificationSlotsUsed >= 3 && !isTectonicCell && (
                          <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                            {t('warEffort.alreadyFortifiedThree')}
                          </span>
                        )}
                        {isTectonicCell && (
                          <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                            {t('warEffort.tectonicBoredriveWasUsedHere')}
                          </span>
                        )}
                      </button>
                    );
                  })()}
                  {!ALL_HQ_CELL_IDS.includes(Number(ownedCountryChoice.cellId)) && !isOwnedCellProtected(ownedCountryChoice.cellId) && !(missionData?.tectonic_boredrive_cell_ids || []).map((id) => Number(id)).includes(Number(ownedCountryChoice.cellId)) && (
                    <button
                      type="button"
                      className="war-effort-owned-option-btn"
                      disabled={tectonicBoredriveLoading || tectonicBoredriveCost == null || (engineersForDisplay ?? 0) < (tectonicBoredriveCost ?? 0)}
                      onClick={async () => {
                        if (tectonicBoredriveLoading || tectonicBoredriveCost == null || (engineersForDisplay ?? 0) < tectonicBoredriveCost) return;
                        setTectonicBoredriveLoading(true);
                        setTectonicBoredriveResult(null);
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${API_URL}/war-effort/tectonic-boredrive`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            body: JSON.stringify({ missionId: missionData?.id, cellId: ownedCountryChoice.cellId })
                          });
                          const data = await res.json().catch(() => ({}));
                          if (!res.ok) throw new Error(data.error || t('warEffort.tectonicBoredriveFailed'));
                          setTectonicBoredriveResult(data.energyGranted != null ? t('warEffort.tectonicBoredriveEnergyGained', { amount: data.energyGranted }) : null);
                          fetchControlledCountries();
                        } catch (err) {
                          setError(err.message || t('warEffort.tectonicBoredriveFailed'));
                        } finally {
                          setTectonicBoredriveLoading(false);
                        }
                      }}
                      title={tectonicBoredriveCost != null && (engineersForDisplay ?? 0) < tectonicBoredriveCost ? t('warEffort.tectonicBoredriveNeedEngineers', { need: tectonicBoredriveCost, have: engineersForDisplay ?? 0 }) : undefined}
                      style={{
                        padding: '20px 24px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        backgroundColor: (tectonicBoredriveLoading || tectonicBoredriveCost == null || (engineersForDisplay ?? 0) < (tectonicBoredriveCost ?? 0)) ? '#555' : '#333',
                        color: 'white',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderRadius: '10px',
                        cursor: (tectonicBoredriveLoading || tectonicBoredriveCost == null || (engineersForDisplay ?? 0) < (tectonicBoredriveCost ?? 0)) ? 'not-allowed' : 'pointer',
                        opacity: (tectonicBoredriveLoading || tectonicBoredriveCost == null || (engineersForDisplay ?? 0) < (tectonicBoredriveCost ?? 0)) ? 0.8 : 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(/images/tectonic_boredrive.jpg)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      <WarEffortOwnedOptionLabel action="boredrive">
                        {tectonicBoredriveCost != null ? t('warEffort.tectonicBoredriveWithCost', { count: tectonicBoredriveCost }) : t('warEffort.tectonicBoredriveCostLoading')}
                      </WarEffortOwnedOptionLabel>
                      {tectonicBoredriveResult && (
                        <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95, color: '#7fdb7f' }}>
                          {tectonicBoredriveResult}
                        </span>
                      )}
                    </button>
                  )}
                  {(() => {
                    const noOpponentYet = missionData?.mission_mode === 'ffa'
                      ? [missionData.player1_id, missionData.player2_id, missionData.player3_id, missionData.player4_id].filter(Boolean).filter(id => Number(id) !== Number(userId)).length === 0
                      : !missionData?.player2_id;
                    const attackDisabled = noOpponentYet || isCeaseFireActive;
                    const reasonText = attackDisabled
                      ? (noOpponentYet ? 'Opponent not revealed yet' : `Cease fire in effect (${formatCeaseFireCountdown(ceaseFireRemainingSeconds)})`)
                      : null;
                    return (
                      <button
                        type="button"
                        className="war-effort-owned-option-btn"
                        disabled={attackDisabled}
                        onClick={() => {
                          if (attackDisabled) return;
                          setAttackFromCountry({ cellId: ownedCountryChoice.cellId, countryName: ownedCountryChoice.countryName });
                          setOwnedCountryChoice(null);
                          setError(null);
                        }}
                        title={attackDisabled ? (noOpponentYet ? 'Wait for a second player to join' : 'Cease fire: no attacks allowed') : undefined}
                        style={{
                          padding: '20px 24px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          backgroundColor: attackDisabled ? '#555' : '#333',
                          color: 'white',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderRadius: '10px',
                          cursor: attackDisabled ? 'not-allowed' : 'pointer',
                          opacity: attackDisabled ? 0.8 : 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(/images/attack_country_button.jpg)`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      >
                        <WarEffortOwnedOptionLabel action="attack">
                          {t('warEffort.attackCountry')}
                        </WarEffortOwnedOptionLabel>
                        {reasonText && (
                          <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                            {reasonText}
                          </span>
                        )}
                      </button>
                    );
                  })()}
                  <button
                    type="button"
                    className="war-effort-owned-option-btn"
                    onClick={async () => {
                      const cellId = ownedCountryChoice.cellId;
                      const countryName = ownedCountryChoice.countryName;
                      setDefenseStrategyDialog(prev => ({
                        ...prev,
                        show: true,
                        cellId,
                        countryName,
                        selectedStrategyId: userStrategies.length > 0 ? (userStrategies[0].id) : null,
                        useEverywhere: false,
                        loading: true,
                        saving: false
                      }));
                      setOwnedCountryChoice(null);
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(
                          `${API_URL}/war-effort/defense-strategy?missionId=${missionData?.id}&countryCellId=${cellId}`,
                          { headers: { 'Authorization': `Bearer ${token}` } }
                        );
                        if (res.ok) {
                          const data = await res.json();
                          const prefill = data.countryStrategyId ?? data.defaultStrategyId ?? (userStrategies.length > 0 ? userStrategies[0].id : null);
                          const stillValid = prefill && userStrategies.some(s => String(s.id) === String(prefill));
                          setDefenseStrategyDialog(prev => ({
                            ...prev,
                            selectedStrategyId: stillValid ? prefill : (userStrategies.length > 0 ? userStrategies[0].id : null),
                            useEverywhere: !!data.defaultStrategyId,
                            loading: false
                          }));
                        } else {
                          setDefenseStrategyDialog(prev => ({ ...prev, loading: false }));
                        }
                      } catch (e) {
                        setDefenseStrategyDialog(prev => ({ ...prev, loading: false }));
                      }
                    }}
                    style={{
                      padding: '20px 24px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      backgroundColor: '#333',
                      color: 'white',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundImage: 'linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(/images/defense_strategy_button.jpg)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <WarEffortOwnedOptionLabel action="defenseStrategy">
                      {t('warEffort.setDefenseStrategy')}
                    </WarEffortOwnedOptionLabel>
                    <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                      {ownedCountryDefenseStrategyLoading
                        ? t('warEffort.loading')
                        : ownedCountryDefenseStrategyId != null && userStrategies.length > 0
                          ? t('warEffort.currentStrategy', { name: userStrategies.find(s => String(s.id) === String(ownedCountryDefenseStrategyId))?.name ?? t('warEffort.unknownStrategy') })
                          : t('warEffort.usedWhenCountryAttacked')}
                    </span>
                  </button>
                </>
              )}
              <MenuPaintedButton
                type="button"
                className="war-effort-owned-choice-cancel"
                imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                onClick={() => setOwnedCountryChoice(null)}
              >
                {isOwnedCellProtected(ownedCountryChoice.cellId) ? t('common.close') : t('warEffort.cancel')}
              </MenuPaintedButton>
            </div>
          </div>
        </div>
      )}
      
      {/* Own HQ choice: Attack Country vs Show HQ */}
      {ownedHQChoice && (
        <div
          className="war-effort-owned-choice-overlay"
          onClick={() => setOwnedHQChoice(null)}
        >
          <div
            className="war-effort-owned-choice-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="war-effort-owned-choice-title">
              {t('warEffort.whatWouldYouLikeAtHq', { country: displayCountry(ownedHQChoice.countryName) })}
            </h3>
            <div className="war-effort-owned-choice-actions">
              {(() => {
                const noOpponentYet = missionData?.mission_mode === 'ffa'
                  ? [missionData.player1_id, missionData.player2_id, missionData.player3_id, missionData.player4_id].filter(Boolean).filter(id => Number(id) !== Number(userId)).length === 0
                  : !missionData?.player2_id;
                const attackDisabled = noOpponentYet || isCeaseFireActive;
                const reasonText = attackDisabled
                  ? (noOpponentYet ? t('warEffort.opponentNotRevealed') : t('warEffort.ceaseFireInEffectTime', { time: formatCeaseFireCountdown(ceaseFireRemainingSeconds) }))
                  : null;
                return (
                  <button
                    type="button"
                    className="war-effort-owned-option-btn"
                    disabled={attackDisabled}
                    onClick={() => {
                      if (attackDisabled) return;
                      setAttackFromCountry({ cellId: ownedHQChoice.cellId, countryName: ownedHQChoice.countryName });
                      setOwnedHQChoice(null);
                      setError(null);
                    }}
                    title={attackDisabled ? (noOpponentYet ? t('warEffort.waitForSecondPlayer') : t('warEffort.ceaseFireNoAttacksAllowed')) : undefined}
                    style={{
                      padding: '20px 24px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      backgroundColor: attackDisabled ? '#555' : '#333',
                      color: 'white',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderRadius: '10px',
                      cursor: attackDisabled ? 'not-allowed' : 'pointer',
                      opacity: attackDisabled ? 0.8 : 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundImage: 'linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(/images/attack_country_button.jpg)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <WarEffortOwnedOptionLabel action="attack">
                      {t('warEffort.attackCountry')}
                    </WarEffortOwnedOptionLabel>
                    {reasonText && (
                      <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                        {reasonText}
                      </span>
                    )}
                  </button>
                );
              })()}
              {(() => {
                const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
                const playerFactionName = playerPurpose ? DEV_PURPOSE_TO_FACTION[playerPurpose] : null;
                const hqImage = playerFactionName ? getHeadquartersImage(playerFactionName) : null;
                return (
                  <button
                    type="button"
                    className="war-effort-owned-option-btn"
                    onClick={() => {
                      setHqBuildingCountryName(ownedHQChoice.countryName);
                      onBuildingPageChange?.(true);
                      setOwnedHQChoice(null);
                      setError(null);
                    }}
                    style={{
                      padding: '20px 24px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      backgroundColor: '#333',
                      color: 'white',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundImage: hqImage
                        ? `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(${hqImage})`
                        : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <WarEffortOwnedOptionLabel action="showHq">
                      {t('warEffort.showHq')}
                    </WarEffortOwnedOptionLabel>
                    <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                      {t('warEffort.enterBuildingMode')}
                    </span>
                  </button>
                );
              })()}
              <button
                type="button"
                className="war-effort-owned-option-btn"
                onClick={async () => {
                  const cellId = ownedHQChoice.cellId;
                  const countryName = ownedHQChoice.countryName;
                  setDefenseStrategyDialog(prev => ({
                    ...prev,
                    show: true,
                    cellId,
                    countryName,
                    selectedStrategyId: userStrategies.length > 0 ? (userStrategies[0].id) : null,
                    useEverywhere: false,
                    loading: true,
                    saving: false
                  }));
                  setOwnedHQChoice(null);
                  try {
                    const token = localStorage.getItem('token');
                    const res = await fetch(
                      `${API_URL}/war-effort/defense-strategy?missionId=${missionData?.id}&countryCellId=${cellId}`,
                      { headers: { 'Authorization': `Bearer ${token}` } }
                    );
                    if (res.ok) {
                      const data = await res.json();
                      const prefill = data.countryStrategyId ?? data.defaultStrategyId ?? (userStrategies.length > 0 ? userStrategies[0].id : null);
                      const stillValid = prefill && userStrategies.some(s => String(s.id) === String(prefill));
                      setDefenseStrategyDialog(prev => ({
                        ...prev,
                        selectedStrategyId: stillValid ? prefill : (userStrategies.length > 0 ? userStrategies[0].id : null),
                        useEverywhere: !!data.defaultStrategyId,
                        loading: false
                      }));
                    } else {
                      setDefenseStrategyDialog(prev => ({ ...prev, loading: false }));
                    }
                  } catch (e) {
                    setDefenseStrategyDialog(prev => ({ ...prev, loading: false }));
                  }
                }}
                style={{
                  padding: '20px 24px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  backgroundColor: '#333',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  backgroundImage: 'linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(/images/defense_strategy_button.jpg)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <WarEffortOwnedOptionLabel action="defenseStrategy">
                  {t('warEffort.setDefenseStrategy')}
                </WarEffortOwnedOptionLabel>
                <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                  {ownedCountryDefenseStrategyLoading
                    ? t('warEffort.loading')
                    : ownedCountryDefenseStrategyId != null && userStrategies.length > 0
                      ? t('warEffort.currentStrategy', { name: userStrategies.find(s => String(s.id) === String(ownedCountryDefenseStrategyId))?.name ?? t('warEffort.unknownStrategy') })
                      : t('warEffort.usedWhenHqAttacked')}
                </span>
              </button>
              <MenuPaintedButton
                type="button"
                className="war-effort-owned-choice-cancel"
                imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                onClick={() => setOwnedHQChoice(null)}
              >
                {t('warEffort.cancel')}
              </MenuPaintedButton>
            </div>
          </div>
        </div>
      )}
      
      {/* Country Tooltip */}
      <WarEffortCountryHoverTooltip bindRef={countryHoverBindRef}>
        {(hoveredCell, setPositionEl) => (
        <div
          ref={setPositionEl}
          className="country-tooltip"
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            padding: '15px',
            zIndex: 2000,
            minWidth: '200px',
            maxHeight: 'min(calc(100vh - 24px), 600px)',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)',
            pointerEvents: 'none'
          }}
        >
          <div style={{ 
            color: 'white', 
            fontSize: '14px', 
            fontWeight: 'bold', 
            marginBottom: '10px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            paddingBottom: '8px'
          }}>
            {displayCountryForCell(hoveredCell)}
          </div>
          {!isHeadquarters(hoveredCell) && (() => {
            const cellForWarlord = getCellData(hoveredCell);
            const warlordName = cellForWarlord?.warlord_username ?? t('warEffort.warlordNone');
            return (
              <div style={{ color: '#c9a227', fontSize: '12px', marginBottom: '6px' }}>
                {t('warEffort.warlordLabel')}: {warlordName}
              </div>
            );
          })()}
          {tectonicBoredriveCellsForMap.includes(Number(hoveredCell)) && (
            <div style={{ color: '#b8860b', fontSize: '12px', marginBottom: '8px' }}>
              {t('warEffort.tectonicBoredriveDamaged')}
            </div>
          )}
          {(() => {
            const cellIdNum = Number(hoveredCell);
            const cell = getCellData(hoveredCell);

            // When cell data is missing (e.g. slow load, or type mismatch), still show country bonus for Cuba/Iceland/Sri Lanka/Taiwan – same section style as Fortification
            if (!cell) {
              const bonusText = localizeCountryBonus(cellIdNum, t);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <span style={{ color: '#aaa', fontSize: '13px' }}>{t('warEffort.loadingShort')}</span>
                  {bonusText && (
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <div style={{ color: '#4ECDC4', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>{t('warEffort.countryBonus')}</div>
                      <div style={{ color: '#aaa', fontSize: '11px' }}>{bonusText}</div>
                    </div>
                  )}
                </div>
              );
            }

            // For headquarters, show image, owner, and fortifications (Hospital, Command Center) when viewer owns HQ
            if (isHeadquarters(hoveredCell) && cell.owner_name) {
              const headquartersImage = getHeadquartersImage(cell.owner_name);
              const playerPurpose = viewerPurpose ?? (isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose);
              const viewerFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
              const isViewerHq = viewerFactions.includes(cell.owner_name);
              const hospitalPlacement = placedBuildings.find((p) => p.building?.id === 'hospital' || p.building?.id === 'hospital_upgraded');
              const hasHospital = !!hospitalPlacement;
              const hospitalBuilding = hospitalPlacement ? HQ_BUILDINGS.find((b) => b.id === hospitalPlacement.building?.id) : HQ_BUILDINGS.find((b) => b.id === 'hospital');
              const commandCenterBuilding = HQ_BUILDINGS.find((b) => b.id === 'command_center');
              const isHqCommandCenterFortified = isViewerHq && (viewerCommandCenterBoostCellIdsOwned.includes(cellIdNum) || viewerCommandCenterBoostCellIdsOwned.includes(hoveredCell));
              const isOpponentHq = !viewerFactions.includes(cell.owner_name);
              const isOpponentHqCommandCenterFortified = isOpponentHq && (opponentCommandCenterBoostCellIdsOwned.includes(cellIdNum) || opponentCommandCenterBoostCellIdsOwned.includes(hoveredCell));
              const hasAnyHqFortification = isViewerHq && (hasHospital || isHqCommandCenterFortified);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {headquartersImage && (
                    <img
                      src={headquartersImage}
                      alt={displayFaction(cell.owner_name)}
                      style={{
                        width: '250px',
                        height: '150px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        marginBottom: '6px'
                      }}
                    />
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: getFactionBackgroundCssColor(cell.owner_name),
                        flexShrink: 0
                      }}
                    />
                    <span style={{ color: 'white', fontSize: '13px', flex: 1 }}>
                      {displayFaction(cell.owner_name)}
                    </span>
                    <span style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>
                      {t('warEffort.headquartersShort')}
                    </span>
                  </div>
                  {/* Buildings list: small building images (viewer's placedBuildings or opponent's buildings; FFA/2v2: per-opponent from opponentHqBuildingIdsByUser) */}
                  {(() => {
                    const hoveredNum = Number(hoveredCell);
                    const isFfaOr2v2 = missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2';
                    let buildingIds = [];
                    if (isViewerHq) {
                      buildingIds = placedBuildings.map((p) => p.building?.id).filter(Boolean);
                    } else if (isFfaOr2v2) {
                      const oppForCell = opponentsForBox.find((o) => o.hqCellId != null && o.hqCellId === hoveredNum);
                      buildingIds = oppForCell ? (opponentHqBuildingIdsByUser[String(oppForCell.userId)] || []) : [];
                    } else {
                      const oppPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
                      const oppHqCellId = oppPurpose ? PURPOSE_TO_HQ_CELL_ID[oppPurpose] : null;
                      const isOpponentPurposeHq = oppHqCellId != null && (hoveredNum === oppHqCellId);
                      buildingIds = isOpponentPurposeHq ? opponentHqBuildingIds : [];
                    }
                    if (buildingIds.length === 0) return null;
                    // 10 buildings per row: 10 * 28px + 9 * 4px gap = 316px
                    const buildingSize = 28;
                    const buildingGap = 4;
                    const buildingsPerRow = 10;
                    const rowWidth = buildingsPerRow * buildingSize + (buildingsPerRow - 1) * buildingGap;
                    return (
                      <div style={{
                        marginTop: '6px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: `${buildingGap}px`,
                        alignItems: 'center',
                        width: `${rowWidth}px`
                      }}>
                        {buildingIds.map((bid) => {
                          const b = HQ_BUILDINGS.find((x) => x.id === bid);
                          if (!b?.image) return null;
                          return (
                            <img
                              key={bid}
                              src={b.image}
                              alt={b.name}
                              title={b.name}
                              style={{
                                width: `${buildingSize}px`,
                                height: `${buildingSize}px`,
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid rgba(255,255,255,0.2)'
                              }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          );
                        })}
                      </div>
                    );
                  })()}
                  {/* HQ fortifications: Hospital and/or Command Center (same detailed format as non-HQ) */}
                  {hasHospital && hospitalBuilding && (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{ color: '#4ECDC4', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>
                        {t('warEffort.fortificationLabel')}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {hospitalBuilding.image && (
                          <img
                            src={hospitalBuilding.image}
                            alt={localizeBuildingName('hospital', t)}
                            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>{localizeBuildingName(hospitalBuilding.id, t)}</div>
                          <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>{localizeBuildingDescription(hospitalBuilding.id, t)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  {isHqCommandCenterFortified && commandCenterBuilding && (() => {
                    const hqCountryName = getCountryFromCellId(hoveredCell);
                    const allNeighbourCountries = hqCountryName ? getNeighbourCountries(hqCountryName) : [];
                    const playerPurpose = viewerPurpose ?? (isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose);
                    const allowedFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
                    const playerControlledCellIds = new Set(
                      Object.entries(cellOwnersMap)
                        .filter(([, owner]) => owner && allowedFactions.includes(owner))
                        .map(([cellIdStr]) => Number(cellIdStr))
                    );
                    const neighbourCountries = allNeighbourCountries.filter((name) => {
                      const cellId = getCellIdFromCountry(name);
                      return cellId != null && playerControlledCellIds.has(cellId);
                    });
                    return (
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        <div style={{ color: '#4ECDC4', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>
                        {t('warEffort.fortifiedByCommandCenter')}
                      </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {commandCenterBuilding.image && (
                            <img
                              src={commandCenterBuilding.image}
                              alt={localizeBuildingName('command_center', t)}
                              style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>{localizeBuildingName(commandCenterBuilding.id, t)}</div>
                            <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>{localizeBuildingDescription(commandCenterBuilding.id, t)}</div>
                            {neighbourCountries.length > 0 && (
                              <div style={{ color: '#aaa', fontSize: '11px', marginTop: '8px' }}>
                                <div style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>{t('warEffort.neighbouringCountries')}</div>
                                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                  {neighbourCountries.map((name) => (
                                    <li key={name} style={{ marginBottom: '2px' }}>{displayCountry(name)}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Opponent HQ: Command Center boost (opponent owns 3+ neighbours of their HQ) */}
                  {isOpponentHqCommandCenterFortified && commandCenterBuilding && (() => {
                    const hqCountryName = getCountryFromCellId(hoveredCell);
                    const allNeighbourCountries = hqCountryName ? getNeighbourCountries(hqCountryName) : [];
                    let opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
                    if ((missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') && viewerSlot >= 1 && viewerSlot <= 4) {
                      const hoveredNum = Number(hoveredCell);
                      for (let s = 1; s <= 4; s++) {
                        if (s === viewerSlot) continue;
                        const p = missionData?.[`player${s}_purpose`];
                        if (p && PURPOSE_TO_HQ_CELL_ID[p] === hoveredNum) {
                          opponentPurpose = p;
                          break;
                        }
                      }
                    }
                    const opponentAllowedFactions = opponentPurpose ? PURPOSE_TO_FACTION_MAPPING[opponentPurpose] || [] : [];
                    const opponentControlledCellIds = new Set(
                      Object.entries(cellOwnersMap)
                        .filter(([, owner]) => owner && opponentAllowedFactions.includes(owner))
                        .map(([cellIdStr]) => Number(cellIdStr))
                    );
                    const neighbourCountries = allNeighbourCountries.filter((name) => {
                      const cellId = getCellIdFromCountry(name);
                      return cellId != null && opponentControlledCellIds.has(cellId);
                    });
                    return (
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        <div style={{ color: '#4ECDC4', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>
                          {t('warEffort.opponentCommandCenter')}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {commandCenterBuilding.image && (
                            <img
                              src={commandCenterBuilding.image}
                              alt={localizeBuildingName('command_center', t)}
                              style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>{localizeBuildingName('command_center', t)}</div>
                            <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>
                              +1 health to defender&apos;s units when you attack this HQ.
                            </div>
                            {neighbourCountries.length > 0 && (
                              <div style={{ color: '#aaa', fontSize: '11px', marginTop: '8px' }}>
                                <div style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>Opponent&apos;s neighbouring countries:</div>
                                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                  {neighbourCountries.map((name) => (
                                    <li key={name} style={{ marginBottom: '2px' }}>{displayCountry(name)}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Trading Hub protection – show when this country has been protected via Trading Hub */}
                  {protectedCellId != null && cellIdNum === Number(protectedCellId) && (
                    <div style={{
                      marginTop: '6px',
                      padding: '6px 8px',
                      background: 'rgba(76, 175, 80, 0.2)',
                      borderRadius: '4px',
                      borderLeft: '3px solid #4CAF50'
                    }}>
                      <span style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 'bold' }}>{t('warEffort.tradingHub.protectedByTradingHub', 'Protected by Trading Hub')}</span>
                      <span style={{ color: 'white', fontSize: '12px' }}>{t('warEffort.tradingHub.cannotBeAttacked', ' — cannot be attacked.')}</span>
                    </div>
                  )}
                  {/* Iceland bonus on HQ: when the owner of this HQ owns Iceland, defending countries get +1 intel (same as other owned countries) */}
                  {(() => {
                    const icelandCell = warEffortData?.cells?.find(c => Number(c.cell_id) === ICELAND_CELL_ID);
                    const icelandOwnerName = icelandCell?.owner_name;
                    const ownerHasIceland = cell.owner_name && icelandOwnerName && cell.owner_name === icelandOwnerName;
                    if (!ownerHasIceland) return null;
                    return (
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        <div style={{
                          color: '#4ECDC4',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          marginBottom: '6px'
                        }}>
                          {t('warEffort.countryBonus')}
                        </div>
                        <div style={{ color: '#aaa', fontSize: '11px' }}>
                          {t('warEffort.icelandOwnerOwns', { bonus: t('warEffort.countryBonusIceland') })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            }
            
            // For regular countries, show owner and fortification if applicable (cellIdNum already set above)
            const isPlayerFortified = playerFortifiedCountries.includes(cellIdNum) || playerFortifiedCountries.includes(hoveredCell);
            const isOpponentFortified = opponentFortifiedCountries.includes(cellIdNum) || opponentFortifiedCountries.includes(hoveredCell);
            const isCommandCenterFortified = viewerCommandCenterBoostCellIdsOwned.includes(cellIdNum) || viewerCommandCenterBoostCellIdsOwned.includes(hoveredCell);
            const isOpponentCommandCenterFortified = opponentCommandCenterBoostCellIdsOwned.includes(cellIdNum) || opponentCommandCenterBoostCellIdsOwned.includes(hoveredCell);
            
            // Get fortification data (same pattern as attack dialog)
            let fortificationData = null;
            let fortification = null;
            
            if (isPlayerFortified || isOpponentFortified) {
              let fortifications = null;
              if (isPlayerFortified) {
                const slot = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') ? viewerSlot : null;
                fortifications = (slot >= 1 && slot <= 4) ? (missionData?.[`player${slot}_fortifications`] ?? null) : (isPlayer1 ? missionData?.player1_fortifications : missionData?.player2_fortifications);
              } else {
                // Opponent: find which slot owns this cell (FFA/2v2 can have multiple opponents)
                const mode = missionData?.mission_mode;
                const mySlot = (mode === 'ffa' || mode === '2v2') ? viewerSlot : null;
                if ((mode === 'ffa' || mode === '2v2') && mySlot >= 1 && mySlot <= 4) {
                  for (let s = 1; s <= 4; s++) {
                    if (s === mySlot) continue;
                    const arr = getFortifiedCountriesArray(missionData?.[`player${s}_fortified_countries`]);
                    if (arr.includes(cellIdNum) || arr.includes(Number(hoveredCell))) {
                      fortifications = missionData?.[`player${s}_fortifications`] ?? null;
                      break;
                    }
                  }
                }
                if (!fortifications) fortifications = isPlayer1 ? missionData?.player2_fortifications : missionData?.player1_fortifications;
              }
              if (fortifications) {
                // Try both string and number keys (JSONB keys might be strings) - same pattern as attack dialog
                fortificationData = (
                  fortifications[cellIdNum] || 
                  fortifications[String(cellIdNum)] ||
                  fortifications[hoveredCell] || 
                  fortifications[String(hoveredCell)]
                ) ? (fortifications[cellIdNum] || fortifications[String(cellIdNum)] || fortifications[hoveredCell] || fortifications[String(hoveredCell)]) : null;
                
                if (fortificationData) {
                  const fortificationType = fortificationData.type || 'unknown';
                  fortification = fortificationType === 'eclipse_hidden'
                    ? { type: 'eclipse_hidden', name: 'Fortified' }
                    : FORTIFICATION_TYPES.find(f => f.type === fortificationType);
                  // Eclipse Array: show opponent fortification bonuses only when viewer has upgraded
                  if (isOpponentFortified && !viewerHasEclipseArrayUpgraded) {
                    fortification = { type: 'eclipse_hidden', name: 'Fortified' };
                  }
                }
              }
            }
            
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* Owner info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {cell.owner_name ? (
                    <>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: getFactionBackgroundCssColor(cell.owner_name),
                          flexShrink: 0
                        }}
                      />
                      <span style={{ color: 'white', fontSize: '13px', flex: 1 }}>
                        {displayFaction(cell.owner_name)}
                      </span>
                    </>
                  ) : (
                    <span style={{ color: '#aaa', fontSize: '13px' }}>{t('warEffort.unclaimed')}</span>
                  )}
                </div>
                {/* Country ownership bonus (Cuba / Iceland / Sri Lanka / Taiwan) – same style as Fortification section; shown even when not fortified */}
                {COUNTRY_BONUS_CELL_IDS.includes(Number(cellIdNum)) && (() => {
                  const bonusText = localizeCountryBonus(cellIdNum, t);
                  if (!bonusText) return null;
                  return (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{
                        color: '#4ECDC4',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginBottom: '6px'
                      }}>
                        {t('warEffort.countryBonus')}
                      </div>
                      <div style={{ color: '#aaa', fontSize: '11px' }}>
                        {bonusText}
                      </div>
                    </div>
                  );
                })()}
                {/* Iceland bonus on any country: when the owner of this country owns Iceland, all their defending countries get +1 intel – show in tooltip (e.g. Norway when you own Iceland) */}
                {(() => {
                  const icelandCell = warEffortData?.cells?.find(c => Number(c.cell_id) === ICELAND_CELL_ID);
                  const icelandOwnerName = icelandCell?.owner_name;
                  const ownerHasIceland = cell.owner_name && icelandOwnerName && cell.owner_name === icelandOwnerName;
                  if (!ownerHasIceland) return null;
                  if (cellIdNum === ICELAND_CELL_ID) return null; // already shown in Country bonus block above
                  return (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{
                        color: '#4ECDC4',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginBottom: '6px'
                      }}>
                        {t('warEffort.countryBonus')}
                      </div>
                      <div style={{ color: '#aaa', fontSize: '11px' }}>
                        {t('warEffort.icelandOwnerOwns', { bonus: t('warEffort.countryBonusIceland') })}
                      </div>
                    </div>
                  );
                })()}
                {/* Trading Hub protection – show when this country has been protected via Trading Hub */}
                {protectedCellId != null && cellIdNum === Number(protectedCellId) && (
                  <div style={{
                    marginTop: '6px',
                    padding: '6px 8px',
                    background: 'rgba(76, 175, 80, 0.2)',
                    borderRadius: '4px',
                    borderLeft: '3px solid #4CAF50'
                  }}>
                    <span style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 'bold' }}>{t('warEffort.tradingHub.protectedByTradingHub', 'Protected by Trading Hub')}</span>
                    <span style={{ color: 'white', fontSize: '12px' }}>{t('warEffort.tradingHub.cannotBeAttacked', ' — cannot be attacked.')}</span>
                  </div>
                )}
                {/* Neutral fortifications (random fortifications on neutral countries) */}
                {(() => {
                  const nf = missionData?.neutral_fortifications;
                  const neutralData = nf && (nf[cellIdNum] || nf[String(cellIdNum)] || nf[hoveredCell] || nf[String(hoveredCell)]);
                  if (!neutralData || !neutralData.count || neutralData.count < 1) return null;
                  const hasTypes = neutralData.types && neutralData.types.length > 0;
                  const shineColor = neutralData.count === 1 ? '#87CEEB' : neutralData.count === 2 ? '#9370DB' : '#FFD700';
                  return (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{
                        color: shineColor,
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginBottom: hasTypes ? '6px' : 0
                      }}>
                        Neutral fortifications ({neutralData.count}){!hasTypes ? ' — types unknown' : ''}
                      </div>
                      {hasTypes && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {neutralData.types.map((ft) => {
                          const f = FORTIFICATION_TYPES.find(x => x.type === ft);
                          if (!f) return <span key={ft} style={{ color: '#aaa', fontSize: '11px' }}>{ft}</span>;
                          return (
                            <div key={ft} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                              {f.unitId && (
                                <img
                                  src={`/images/${f.unitId}.jpg`}
                                  alt={t(`warEffort.fortificationTypes.${f.type}`)}
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    flexShrink: 0
                                  }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <div>
                                <div style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>{t(`warEffort.fortificationTypes.${f.type}`)}</div>
                                <div style={{ color: '#aaa', fontSize: '11px' }}>{t(`warEffort.fortificationTypes.${f.type}_desc`)}</div>
                                {f.type === 'defend_twice' && (() => {
                                  const wins = Number(neutralData.defend_twice_consecutive_wins) || 0;
                                  const statusKey = wins > 0 ? 'warEffort.defendTwice.statusAttackerWonOne' : null;
                                  return (
                                    <div style={{ color: '#4ECDC4', fontSize: '12px', marginTop: '4px', fontWeight: 'bold' }}>
                                      {wins}/2{statusKey != null ? ` (${t(statusKey)})` : ''}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      )}
                    </div>
                  );
                })()}
                {/* Fortification info (Fortify country) */}
                {fortification && (
                  <div style={{ 
                    marginTop: '8px', 
                    paddingTop: '8px', 
                    borderTop: '1px solid rgba(255, 255, 255, 0.2)' 
                  }}>
                    <div style={{ 
                      color: '#4ECDC4', 
                      fontSize: '12px', 
                      fontWeight: 'bold', 
                      marginBottom: '6px' 
                    }}>
                        Fortification
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {fortification.unitId && (
                        <img
                          src={`/images/${fortification.unitId}.jpg`}
                          alt={t(`warEffort.fortificationTypes.${fortification.type}`)}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid rgba(255, 255, 255, 0.3)'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>
                          {fortification.type === 'eclipse_hidden'
                            ? t('warEffort.fortificationTypes.eclipse_hidden')
                            : t(`warEffort.fortificationTypes.${fortification.type}`)}
                        </div>
                        {fortification.type !== 'eclipse_hidden' && (
                        <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>
                          {t(`warEffort.fortificationTypes.${fortification.type}_desc`)}
                        </div>
                        )}
                        {fortification.type === 'defend_twice' && (() => {
                          const wins = fortificationData?.defend_twice_consecutive_wins ?? 0;
                          const statusKey = wins > 0
                            ? (isOpponentFortified
                              ? 'warEffort.defendTwice.statusYouWonOne'
                              : 'warEffort.defendTwice.statusAttackerWonOne')
                            : null;
                          return (
                            <div style={{ color: '#4ECDC4', fontSize: '12px', marginTop: '6px', fontWeight: 'bold' }}>
                              {wins}/2{statusKey != null ? ` (${t(statusKey)})` : ''}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
                {/* Command Center boost (own 3+ neighbours) — same logic and tooltip as 1v1; viewer slot resolved for FFA/2v2 */}
                {isCommandCenterFortified && (() => {
                  const commandCenter = HQ_BUILDINGS.find(b => b.id === 'command_center');
                  const countryName = getCountryFromCellId(hoveredCell);
                  const allNeighbourCountries = countryName ? getNeighbourCountries(countryName) : [];
                  // Show only neighbouring countries the viewer controls (viewerPurpose for FFA/2v2 so slot is correct)
                  const playerPurpose = viewerPurpose ?? (isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose);
                  const allowedFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
                  const playerControlledCellIds = new Set(
                    Object.entries(cellOwnersMap)
                      .filter(([, owner]) => owner && allowedFactions.includes(owner))
                      .map(([cellIdStr]) => Number(cellIdStr))
                  );
                  const neighbourCountries = allNeighbourCountries.filter((name) => {
                    const cellId = getCellIdFromCountry(name);
                    return cellId != null && playerControlledCellIds.has(cellId);
                  });
                  return (
                    <div style={{ 
                      marginTop: '8px', 
                      paddingTop: '8px', 
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)' 
                    }}>
                      <div style={{ 
                        color: '#4ECDC4', 
                        fontSize: '12px', 
                        fontWeight: 'bold', 
                        marginBottom: '6px' 
                      }}>
                        {t('warEffort.fortifiedByCommandCenter')}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {commandCenter?.image && (
                          <img
                            src={commandCenter.image}
                            alt={localizeBuildingName('command_center', t)}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '4px'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>
                            {localizeBuildingName('command_center', t)}
                          </div>
                          <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>
                            {localizeBuildingDescription('command_center', t)}
                          </div>
                          {neighbourCountries.length > 0 && (
                            <div style={{ color: '#aaa', fontSize: '11px', marginTop: '8px' }}>
                              <div style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>{t('warEffort.neighbouringCountries')}</div>
                              <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                {neighbourCountries.map((name) => (
                                  <li key={name} style={{ marginBottom: '2px' }}>{name}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {/* Opponent's Command Center boost (opponent owns 3+ neighbours) — same tooltip as 1v1; in FFA use cell owner so correct opponent's neighbours shown */}
                {isOpponentCommandCenterFortified && !isCommandCenterFortified && (() => {
                  const commandCenter = HQ_BUILDINGS.find(b => b.id === 'command_center');
                  const countryName = getCountryFromCellId(hoveredCell);
                  const allNeighbourCountries = countryName ? getNeighbourCountries(countryName) : [];
                  // 1v1: single opponent purpose; FFA/2v2: this cell is owned by one opponent — use cell.owner_name so we show that opponent's neighbouring countries
                  const opponentAllowedFactions = cell?.owner_name
                    ? [cell.owner_name]
                    : (isPlayer1 ? PURPOSE_TO_FACTION_MAPPING[missionData?.player2_purpose] : PURPOSE_TO_FACTION_MAPPING[missionData?.player1_purpose]) || [];
                  const opponentControlledCellIds = new Set(
                    Object.entries(cellOwnersMap)
                      .filter(([, owner]) => owner && opponentAllowedFactions.includes(owner))
                      .map(([cellIdStr]) => Number(cellIdStr))
                  );
                  const neighbourCountries = allNeighbourCountries.filter((name) => {
                    const cellId = getCellIdFromCountry(name);
                    return cellId != null && opponentControlledCellIds.has(cellId);
                  });
                  return (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{
                        color: '#4ECDC4',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginBottom: '6px'
                      }}>
                        {t('warEffort.opponentCommandCenter')}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {commandCenter?.image && (
                          <img
                            src={commandCenter.image}
                            alt={localizeBuildingName('command_center', t)}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '4px'
                            }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>
                            {localizeBuildingName('command_center', t)}
                          </div>
                          <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>
                            {localizeBuildingDescription('command_center', t)}
                          </div>
                          {neighbourCountries.length > 0 && (
                            <div style={{ color: '#aaa', fontSize: '11px', marginTop: '8px' }}>
                              <div style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>{t('warEffort.opponentNeighbouringCountries')}</div>
                              <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                {neighbourCountries.map((name) => (
                                  <li key={name} style={{ marginBottom: '2px' }}>{displayCountry(name)}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </div>
        )}
      </WarEffortCountryHoverTooltip>
      
      {/* Fortification selection - brought to front with high z-index */}
      {selectedCountry && (
        <div className="fortification-selection" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 3000, backgroundColor: 'rgba(0, 0, 0, 0.95)', padding: '30px', borderRadius: '12px', border: '2px solid rgba(255, 255, 255, 0.3)', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto', pointerEvents: 'auto' }}>
          <h3 style={{ color: 'white', marginBottom: '20px' }}>{t('warEffort.selectFortificationFor', { country: displayCountryForCell(selectedCountry) })}</h3>
          
          {availableFortifications.length === 0 ? (
            <div className="error" style={{ color: '#ff4444', textAlign: 'center' }}>{t('warEffort.insufficientResourcesFortification')}</div>
          ) : (
            <div className="fortification-options">
              {FORTIFICATION_TYPES.map(fort => (
                <div
                  key={fort.type}
                  className={`fortification-option ${selectedFortification === fort.type ? 'selected' : ''} ${playerResources < fort.cost ? 'disabled' : ''} ${loading ? 'loading' : ''}`}
                  onClick={() => {
                    if (playerResources >= fort.cost && !loading && playerFortifiedCountries.length < 3) {
                      setSelectedFortification(fort.type);
                      // Fortify immediately
                      handleFortifyWithType(fort.type);
                    }
                  }}
                  style={{ 
                    cursor: (playerResources >= fort.cost && !loading && playerFortifiedCountries.length < 3) ? 'pointer' : 'not-allowed', 
                    pointerEvents: 'auto',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '170px'
                  }}
                >
                  {fort.unitId && (
                    <img 
                      src={`/images/${fort.unitId}.jpg`} 
                      alt={t(`warEffort.fortificationTypes.${fort.type}`)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 0
                      }}
                      onError={(e) => {
                        // Fallback if image doesn't exist
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div style={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    padding: '12px',
                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 50%, transparent 100%)'
                  }}>
                    <div className="fortification-name" style={{ 
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                      marginBottom: '5px'
                    }}>{t(`warEffort.fortificationTypes.${fort.type}`)}</div>
                    <div className="fortification-cost" style={{ 
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                      marginBottom: '5px'
                    }}>Cost: {fort.cost}</div>
                    <div className="fortification-description" style={{ 
                      textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)',
                      fontSize: '0.8em'
                    }}>{t(`warEffort.fortificationTypes.${fort.type}_desc`)}</div>
                    {loading && selectedFortification === fort.type && (
                      <div style={{ 
                        color: '#4a9eff', 
                        marginTop: '10px', 
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)'
                      }}>{t('warEffort.fortifying')}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <MenuPaintedButton
            className="war-effort-ingame-cancel-btn"
            imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
            onClick={() => {
              setSelectedCountry(null);
              setSelectedFortification(null);
              setError(null);
            }}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              pointerEvents: 'auto'
            }}
          >
            {t('warEffort.closeX')}
          </MenuPaintedButton>
        </div>
      )}
      
      {error && (
        <div className="error-message" style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 3001, backgroundColor: 'rgba(255, 68, 68, 0.95)', color: 'white', padding: '15px 30px', borderRadius: '8px', border: '2px solid rgba(255, 255, 255, 0.3)', pointerEvents: 'auto' }}>
          {error}
        </div>
      )}
      
      {pendingEliteAttackWait && (
        <WarEffortEliteDefenseWaitingOverlay wait={pendingEliteAttackWait} t={t} />
      )}
      {pendingEliteDefense && !pendingEliteAttackWait && (
        <WarEffortEliteDefenseDialog
          offer={pendingEliteDefense}
          onOk={handleEliteDefenseAccept}
          onRefuse={handleEliteDefenseRefuse}
          busy={eliteDefenseBusy}
          t={t}
          countryDisplayName={displayCountry(pendingEliteDefense.countryName)}
          actionError={eliteDefenseActionError}
        />
      )}

      {/* Attack Dialog */}
      {attackDialog.show && (
        <div className="attack-dialog-overlay" style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh', 
          backgroundColor: 'rgba(0, 0, 0, 0.8)', 
          zIndex: 4000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          pointerEvents: 'auto'
        }}>
          <div className="attack-dialog" style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.95)', 
            border: '2px solid rgba(255, 255, 255, 0.3)', 
            borderRadius: '12px', 
            padding: '30px', 
            maxWidth: '500px', 
            width: '90vw',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#FFD700' }}>
              {t('warEffort.attackFortifiedCountry')}
            </h3>
            <p style={{ marginBottom: '30px', fontSize: '16px', lineHeight: '1.6' }}>
              {attackDialog.fortificationTypeHidden
                ? t('warEffort.countryFortifiedHidden', { country: displayCountry(attackDialog.countryName) })
                : t('warEffort.countryFortifiedWith', { country: displayCountry(attackDialog.countryName), fortification: attackDialog.fortificationType })}
            </p>
            
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <MenuPaintedButton
                className="war-effort-building-action-btn war-effort-building-action-btn--destroy"
                imagePath={WAR_EFFORT_LEAVE_MISSION_BUTTON_PATH}
                onClick={() => setAttackDialog({ show: false, countryCellId: null, fortificationType: null, countryName: null, selectedStrategyId: null })}
                disabled={loading}
              >
                {t('warEffort.no')}
              </MenuPaintedButton>
              <MenuPaintedButton
                className="war-effort-building-action-btn"
                imagePath={WAR_EFFORT_PRIMARY_BUTTON_PATH}
                onClick={handleAttackOpponentFortified}
                disabled={loading}
              >
                {loading ? t('warEffort.launching') : t('warEffort.yes')}
              </MenuPaintedButton>
            </div>
          </div>
        </div>
      )}
      
      {/* Attack Other Country Dialog */}
      {attackOtherCountryDialog.show && (
        <div className="attack-dialog-overlay" style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh', 
          backgroundColor: 'rgba(0, 0, 0, 0.8)', 
          zIndex: 4000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          pointerEvents: 'auto'
        }}>
          <div className="attack-dialog" style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.95)', 
            border: '2px solid rgba(255, 255, 255, 0.3)', 
            borderRadius: '12px', 
            padding: '30px', 
            maxWidth: '500px', 
            width: '90vw',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#FFD700' }}>
              {attackOtherCountryDialog.isOpponentUnfortified ? t('warEffort.attackOpponentCountry') : t('warEffort.attackOtherCountry')}
            </h3>
            <p style={{ marginBottom: '30px', fontSize: '16px', lineHeight: '1.6' }}>
              {t('warEffort.countryControlledBy', {
                country: displayCountry(attackOtherCountryDialog.countryName),
                faction: localizeFactionName(attackOtherCountryDialog.ownerFaction, t)
              })}
              {attackOtherCountryDialog.isOpponentUnfortified ? ` ${t('warEffort.notFortified')}` : ''}
              {' '}{t('warEffort.winTakeControl')}
            </p>
            
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <MenuPaintedButton
                className="war-effort-ingame-cancel-btn"
                imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                onClick={() => setAttackOtherCountryDialog({ show: false, countryCellId: null, countryName: null, ownerFaction: null, selectedStrategyId: null, isOpponentUnfortified: false })}
                disabled={loading}
              >
                {t('warEffort.cancel')}
              </MenuPaintedButton>
              <MenuPaintedButton
                className="war-effort-building-action-btn war-effort-building-action-btn--destroy"
                imagePath={WAR_EFFORT_LEAVE_MISSION_BUTTON_PATH}
                onClick={handleAttackOtherCountry}
                disabled={loading}
              >
                {loading ? t('warEffort.launching') : t('warEffort.mission.attack')}
              </MenuPaintedButton>
            </div>
          </div>
        </div>
      )}

      {/* Attack Enemy HQ Dialog (when opponent has no fortified countries left; winner wins mission) */}
      {attackEnemyHQDialog.show && (
        <div className="attack-dialog-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 4000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto'
        }}>
          <div className="attack-dialog" style={{
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90vw',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#FFD700' }}>
              {t('warEffort.attackEnemyHeadquarters')}
            </h3>
            <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.6' }}>
              {t('warEffort.attackEnemyHqBody', { country: displayCountry(attackEnemyHQDialog.countryName) })}
            </p>
            {(() => {
              const liveCost = (() => {
                if (missionData?.mission_mode === 'ffa' && Array.isArray(enemyHqAttackInfo?.attackableHQs)) {
                  const entry = enemyHqAttackInfo.attackableHQs.find((h) => Number(h.hqCellId) === Number(attackEnemyHQDialog.hqCellId));
                  if (entry?.movementCost != null) return entry.movementCost;
                }
                if (enemyHqAttackInfo?.movementCost != null) return enemyHqAttackInfo.movementCost;
                return attackEnemyHQDialog.movementCost;
              })();
              return liveCost != null ? (
              <p style={{ marginBottom: '20px', fontSize: '14px', color: '#4ECDC4' }}>
                {t('warEffort.costEnergyMovementPlus', { cost: liveCost })}
              </p>
            ) : (
              <p style={{ marginBottom: '20px', fontSize: '14px', color: '#aaa' }}>
                {enemyHqAttackInfo ? t('warEffort.enemyHqCostUnavailable') : t('warEffort.costLoading')}
              </p>
            );
            })()}
            {(() => {
              const dialogHqNum = attackEnemyHQDialog.hqCellId != null ? Number(attackEnemyHQDialog.hqCellId) : null;
              const canAttackThisHQ = missionData?.mission_mode === 'ffa'
                ? (Array.isArray(enemyHqAttackInfo?.attackableHQs) && enemyHqAttackInfo.attackableHQs.some(h => Number(h.hqCellId) === dialogHqNum))
                : !!(enemyHqAttackInfo?.canAttack && (
                  enemyHqAttackInfo?.hqCellId == null
                    || Number(enemyHqAttackInfo.hqCellId) === dialogHqNum
                ));
              if (canAttackThisHQ) return null;
              const blockReason = enemyHqAttackInfo?.blockReason;
              const conquestSecs = enemyHqAttackInfo?.conquestCooldownRemainingSeconds > 0
                ? enemyHqAttackInfo.conquestCooldownRemainingSeconds
                : conquestAttackerCeaseFireRemainingSeconds;
              return (
                <div style={{ marginBottom: '20px', fontSize: '14px', color: '#ff6b6b' }}>
                  {blockReason === 'conquest_cooldown' || (isConquestAttackerCeaseFireActive && (!enemyHqAttackInfo?.opponentFortifiedCellIds?.length)) ? (
                    <p style={{ marginBottom: '8px' }}>
                      {t('warEffort.cannotAttackHqAfterConquest', { time: formatCeaseFireCountdown(conquestSecs || 0) })}
                    </p>
                  ) : (
                    <>
                      <p style={{ marginBottom: '8px' }}>{t('warEffort.enemyHqOnlyWhenNoForts')}</p>
                      {enemyHqAttackInfo?.opponentFortifiedCountryNames?.length > 0 ? (
                        <p style={{ margin: 0 }}>
                          {t('warEffort.stillConsideredFortified', { countries: enemyHqAttackInfo.opponentFortifiedCountryNames.map((name) => displayCountry(name)).join(', ') })}
                        </p>
                      ) : enemyHqAttackInfo?.opponentFortifiedCellIds?.length > 0 ? (
                        <p style={{ margin: 0 }}>
                          {t('warEffort.stillConsideredFortified', { countries: enemyHqAttackInfo.opponentFortifiedCellIds.join(', ') })}
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
              );
            })()}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <MenuPaintedButton
                className="war-effort-ingame-cancel-btn"
                imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                onClick={() => setAttackEnemyHQDialog({ show: false, countryName: null, hqCellId: null, movementCost: null, defenderUserId: null })}
                disabled={loading}
              >
                {t('warEffort.cancel')}
              </MenuPaintedButton>
              <button
                onClick={handleAttackEnemyHQ}
                disabled={loading || !(missionData?.mission_mode === 'ffa'
                  ? (Array.isArray(enemyHqAttackInfo?.attackableHQs) && enemyHqAttackInfo.attackableHQs.some(h => Number(h.hqCellId) === Number(attackEnemyHQDialog.hqCellId)))
                  : !!(enemyHqAttackInfo?.canAttack && (
                    enemyHqAttackInfo?.hqCellId == null
                      || Number(enemyHqAttackInfo.hqCellId) === Number(attackEnemyHQDialog.hqCellId)
                  )))}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4ECDC4',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.5 : 1
                }}
              >
                {loading ? t('warEffort.launching') : (missionData?.mission_mode === 'ffa'
                  ? (!(Array.isArray(enemyHqAttackInfo?.attackableHQs) && enemyHqAttackInfo.attackableHQs.some(h => Number(h.hqCellId) === Number(attackEnemyHQDialog.hqCellId))) ? t('warEffort.notAttackableYet') : t('warEffort.attackHq'))
                  : (!enemyHqAttackInfo?.canAttack ? t('warEffort.notAttackableYet') : t('warEffort.attackHq')))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Defense Strategy Dialog: set strategy used when this country is attacked */}
      {defenseStrategyDialog.show && (
        <div className="attack-dialog-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 4000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto'
        }}>
          <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90vw',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#3498db' }}>
              {t('warEffort.defenseStrategyTitle')}{defenseStrategyDialog.countryName ? ` – ${displayCountry(defenseStrategyDialog.countryName)}` : ''}
            </h3>
            <p style={{ marginBottom: '20px', fontSize: '14px', color: '#aaa' }}>
              {t('warEffort.chooseDefendingBotBody')}
            </p>
            {defenseStrategyDialog.loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#aaa' }}>{t('warEffort.loading')}</div>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#CCCCCC' }}>
                    {t('warEffort.strategyColon')}
                  </label>
                  {userStrategies.length === 0 ? (
                    <div style={{ color: '#ff4444', fontSize: '14px' }}>{t('warEffort.noStrategiesForPurpose')}</div>
                  ) : (
                    <CustomDropdown
                      options={userStrategies.map(s => ({
                        value: s.id,
                        label: s.name,
                        faction_name: s.faction_name,
                        faction_name_translated: s.faction_name_translated,
                      }))}
                      value={defenseStrategyDialog.selectedStrategyId}
                      onChange={(value) => setDefenseStrategyDialog(prev => ({ ...prev, selectedStrategyId: value }))}
                      placeholder={t('warEffort.selectStrategyPlaceholderShort')}
                      renderOption={(option) => {
                        const strategy = userStrategies.find(
                          (s) => String(s.id) === String(option.value)
                        );
                        const factionName = strategy?.faction_name || option.faction_name;
                        if (!factionName) {
                          return <span>{option.label}</span>;
                        }
                        const translatedFactionName =
                          strategy?.faction_name_translated ||
                          option.faction_name_translated ||
                          factionName;
                        const factionIconPath = getFactionIconPath(factionName);
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div
                              className="faction-orb"
                              style={{ background: getFactionBoxBackground(factionName) }}
                              title={translatedFactionName}
                            >
                              {factionIconPath ? (
                                <img
                                  src={factionIconPath}
                                  alt=""
                                  className="faction-orb-icon"
                                  draggable={false}
                                  aria-hidden="true"
                                />
                              ) : null}
                            </div>
                            <span>{option.label}</span>
                          </div>
                        );
                      }}
                    />
                  )}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={defenseStrategyDialog.useEverywhere}
                    onChange={(e) => setDefenseStrategyDialog(prev => ({ ...prev, useEverywhere: e.target.checked }))}
                  />
                  <span style={{ fontSize: '14px' }}>{t('warEffort.useStrategyEverywhere')}</span>
                </label>
                <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                  <MenuPaintedButton
                    className="war-effort-ingame-cancel-btn"
                    imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                    onClick={() => setDefenseStrategyDialog(prev => ({ ...prev, show: false }))}
                    disabled={defenseStrategyDialog.saving}
                  >
                    {t('warEffort.cancel')}
                  </MenuPaintedButton>
                  <MenuPaintedButton
                    className="war-effort-building-action-btn"
                    imagePath={WAR_EFFORT_PRIMARY_BUTTON_PATH}
                    onClick={async () => {
                      if (!defenseStrategyDialog.selectedStrategyId || !defenseStrategyDialog.cellId || !missionData?.id) return;
                      setDefenseStrategyDialog(prev => ({ ...prev, saving: true }));
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${API_URL}/war-effort/defense-strategy`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            missionId: missionData.id,
                            countryCellId: defenseStrategyDialog.cellId,
                            strategyId: defenseStrategyDialog.selectedStrategyId,
                            useEverywhere: defenseStrategyDialog.useEverywhere
                          })
                        });
                        if (res.ok) {
                          setDefenseStrategyDialog(prev => ({ ...prev, show: false, saving: false }));
                          setError(null);
                        } else {
                          const data = await res.json().catch(() => ({}));
                          setError(data.error || t('warEffort.failedToSetDefenseStrategy'));
                          setDefenseStrategyDialog(prev => ({ ...prev, saving: false }));
                        }
                      } catch (e) {
                        setError(t('warEffort.failedToSetDefenseStrategy'));
                        setDefenseStrategyDialog(prev => ({ ...prev, saving: false }));
                      }
                    }}
                    disabled={defenseStrategyDialog.saving || !defenseStrategyDialog.selectedStrategyId || userStrategies.length === 0}
                  >
                    {defenseStrategyDialog.saving ? t('warEffort.saving') : t('common.save')}
                  </MenuPaintedButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Trading Hub (Madagascar / The Sentients) – card-based offers with images */}
      {tradingHubOpen && (
        <div className="trading-hub-overlay">
          <div className={`trading-hub-dialog${tradingHubStatus?.tradingBlocked && !tradingHubLoading ? ' trading-hub-dialog--blocked' : ''}`}>
            {!(tradingHubStatus?.tradingBlocked && !tradingHubLoading) && (
              <div className="trading-hub-header">
                <h3>{t('warEffort.tradingHub.title', 'Trading Hub – Madagascar')}</h3>
                <p className="trading-hub-subtitle">{t('warEffort.tradingHub.subtitle', 'The Sentients offer deals for energy.')}</p>
              </div>
            )}
            {tradingHubLoading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>{t('warEffort.tradingHub.loading', 'Loading...')}</div>
            ) : tradingHubStatus?.tradingBlocked ? (
              <div
                className="trading-hub-blocked"
                style={{ backgroundImage: resolvePublicAssetBackgroundUrl('/images/shop_closed.jpg') }}
              >
                <p className="trading-hub-blocked__message">
                  {t('warEffort.tradingHub.tradingBlockedDefault')}
                </p>
                <MenuPaintedButton
                  className="trading-hub-blocked__close"
                  imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                  onClick={() => setTradingHubOpen(false)}
                >
                  {t('warEffort.tradingHub.close', 'Close')}
                </MenuPaintedButton>
              </div>
            ) : tradingHubStatus ? (
              <>
                <div className="trading-hub-energy">
                  <strong>{t('warEffort.tradingHub.yourEnergy', 'Your energy:')}</strong> {tradingHubStatus.energyBalance}
                  {typeof tradingHubStatus.billyBucksBalance === 'number' && (
                    <span style={{ marginLeft: '16px' }}><strong>{t('warEffort.tradingHub.billyBucks', 'Billy Bucks:')}</strong> {tradingHubStatus.billyBucksBalance}</span>
                  )}
                </div>
                <div className="trading-hub-offers">
                  {/* Restock button: free restock countdown + Restock now (n Energy) inside same button */}
                  <div className="trading-hub-restock-wrap" style={{ marginBottom: '16px' }}>
                    <button
                      type="button"
                      className="trading-hub-restock-btn"
                      disabled={(tradingHubStatus.energyBalance ?? 0) < (tradingHubStatus.restockCost ?? 100)}
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API_URL}/war-effort/trading-hub/restock`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                            body: JSON.stringify({ missionId: missionData.id })
                          });
                          const data = await res.json();
                          if (res.ok) {
                            if (data.nextAutoRestockAtMs == null) {
                              throw new Error('Trading hub restock did not return nextAutoRestockAtMs');
                            }
                            setTradingHubStatus(prev => prev ? { ...prev, energyBalance: data.energyBalance, shopSlots: data.shopSlots, restockCount: data.restockCount, restockCost: data.restockCost, nextAutoRestockAtMs: data.nextAutoRestockAtMs } : null);
                            applyMadagascarOffersGeneration(data.nextAutoRestockAtMs);
                            if (playerResource) setPlayerResource(prev => prev ? { ...prev, amount: data.energyBalance } : null);
                            setShopSlotCellId({});
                            playSoundEffect('/sounds/restock.mp3');
                          } else {
                            setError(data.errorKey ? t(`warEffort.tradingHub.${data.errorKey}`) : t('warEffort.tradingHub.restockFailed'));
                          }
                        } catch (e) {
                          setError(e.message || t('warEffort.tradingHub.restockFailed', 'Restock failed'));
                        }
                      }}
                    >
                      <img src={resolvePublicAssetUrl('/images/restock_button.png')} alt="" aria-hidden />
                      <span className="trading-hub-restock-btn__labels" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        {tradingHubStatus.nextAutoRestockAtMs != null && (
                          <span className="trading-hub-auto-restock-countdown" style={{ fontSize: '14px', color: '#FFD700' }}>
                            {t('warEffort.tradingHub.freeRestockIn', {
                              mm: Math.floor(Math.max(0, (tradingHubStatus.nextAutoRestockAtMs - (shopCountdownTick ?? Date.now())) / 1000) / 60),
                              ss: String(Math.max(0, Math.floor((tradingHubStatus.nextAutoRestockAtMs - (shopCountdownTick ?? Date.now())) / 1000) % 60)).padStart(2, '0'),
                              defaultValue: 'Free restock in {{mm}}:{{ss}}'
                            })}
                          </span>
                        )}
                        <span className="trading-hub-restock-btn__label">{t('warEffort.tradingHub.restockNow', { cost: tradingHubStatus.restockCost ?? 100, defaultValue: 'Restock now ({{cost}} Energy)' })}</span>
                      </span>
                    </button>
                  </div>
                  {/* Unified 3-slot shop (catalog 0–17). Sold slots show "Sold" until next global restock; no per-slot countdown. */}
                  {(Array.isArray(tradingHubStatus.shopSlots) ? tradingHubStatus.shopSlots : []).map((item, slotIndex) => {
                    if (item.countdownEndsAt != null) {
                      return (
                        <div key={`shop-slot-${slotIndex}-sold`} className="trading-hub-offer-card trading-hub-offer-card--sold">
                          <div className="trading-hub-sold-strike" aria-hidden />
                          <div className="trading-hub-offer-card__body trading-hub-offer-card__body--sold">
                            <span className="trading-hub-sold-word">{t('warEffort.tradingHub.shop.sold', 'Sold')}</span>
                            <p className="trading-hub-offer-card__description trading-hub-offer-card__description--sold">
                              {t('warEffort.tradingHub.shop.newItemAtNextRestock', 'New item at next restock')}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    const needsCellId = item.id === 12 || item.id === 13 || item.id === 2 || item.id === 6 || item.id === 19;
                    const selectedCellId = (shopSlotCellId || {})[slotIndex] ?? null;
                    const costLabel = item.costCurrency === 'free'
                      ? t('warEffort.tradingHub.energyFreeOncePerHour', 'Free')
                      : item.costCurrency === 'billyBucks'
                        ? t('warEffort.tradingHub.billyBucksCost', { cost: item.cost, defaultValue: '{{cost}} Billy Bucks' })
                        : t('warEffort.tradingHub.energyCost', { cost: item.cost, defaultValue: '{{cost}} energy' });
                    const canAfford = item.costCurrency === 'free'
                      ? (tradingHubStatus.remainingCooldownSeconds ?? 0) === 0
                      : item.costCurrency === 'billyBucks'
                        ? (tradingHubStatus.billyBucksBalance ?? 0) >= (item.cost || 0)
                        : (tradingHubStatus.energyBalance ?? 0) >= (item.cost || 0);
                    const isClaimEnergy = item.id === 11;
                    const isProtect = item.id === 12;
                    const isSubvert = item.id === 13;
                    const subvertDisabled = isSubvert && !tradingHubStatus.canSubvert;
                    const isDisabled = !canAfford || (needsCellId && !selectedCellId) || (item.id === 14 && tradingHubStatus.hasBoughtBoosterBundleForMission) || subvertDisabled;
                    const targetOptions = item.id === 12
                      ? (tradingHubStatus.protectableCells || []).filter((c) => !playerFortifiedCountries.includes(Number(c.cellId)))
                      : item.id === 13
                        ? (tradingHubStatus.subvertableCells || [])
                        : item.id === 2
                          ? (tradingHubStatus.structuralExposureTargetCells || [])
                          : item.id === 6
                            ? (tradingHubStatus.reconquerCooldownCells || [])
                            : item.id === 19
                              ? (tradingHubStatus.tectonicReconcilementCells || [])
                              : [];
                    const showDropdown = (isProtect || isSubvert) || (needsCellId && targetOptions.length > 0);
                    return (
                      <div key={`shop-slot-${slotIndex}-${item.id}`} className="trading-hub-offer-card">
                        <div className="trading-hub-offer-card__image-wrap">
                          <img src={`/images/${item.image || 'shop_energy.jpg'}`} alt="" className="trading-hub-offer-card__image" />
                          <div className="trading-hub-offer-card__price-overlay">{costLabel}</div>
                        </div>
                        <div className="trading-hub-offer-card__body">
                          <p className="trading-hub-offer-card__title">{item.id === 11 ? t('warEffort.tradingHub.askForEnergy', { amount: 200 }) : t(`warEffort.${item.nameKey}`)}</p>
                          <p className="trading-hub-offer-card__description">{t(`warEffort.${item.descriptionKey || item.nameKey}`)}</p>
                          {isClaimEnergy && (energyCooldownDisplaySeconds ?? tradingHubStatus.remainingCooldownSeconds ?? 0) > 0 && (
                            <p style={{ fontSize: '12px', color: '#FFD700', margin: 0 }}>
                              {t('warEffort.tradingHub.nextClaimIn', {
                                mm: Math.floor((energyCooldownDisplaySeconds ?? tradingHubStatus.remainingCooldownSeconds) / 60),
                                ss: String((energyCooldownDisplaySeconds ?? tradingHubStatus.remainingCooldownSeconds) % 60).padStart(2, '0'),
                                defaultValue: 'Next claim in: {{mm}}:{{ss}}'
                              })}
                            </p>
                          )}
                          {isProtect && tradingHubStatus.protectedCellId != null && (
                            <p style={{ fontSize: '12px', color: '#4ECDC4', margin: 0 }}>{t('warEffort.tradingHub.currentlyProtected', { country: getCountryFromCellId(tradingHubStatus.protectedCellId), defaultValue: 'Currently protected: {{country}}' })}</p>
                          )}
                          {isSubvert && tradingHubStatus.subvertBlockedReason && (
                            <p style={{ fontSize: '12px', color: '#FFD700', margin: 0 }}>{tradingHubStatus.subvertBlockedReason}</p>
                          )}
                          {item.id === 14 && (
                            <div style={{ marginBottom: 6 }}>
                              <MenuPaintedButton
                                type="button"
                                className="trading-hub-buy-btn trading-hub-buy-btn--lemonade"
                                imagePath={WAR_EFFORT_PRIMARY_BUTTON_PATH}
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${API_URL}/bank/buy-lemonade`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }
                                    });
                                    const text = await res.text();
                                    let data = {};
                                    try { data = text ? JSON.parse(text) : {}; } catch {
                                      setError(t('warEffort.tradingHub.serverInvalidResponse', 'Server returned an invalid response. Is the backend running?'));
                                      return;
                                    }
                                    if (res.ok) {
                                      setTradingHubStatus(prev => prev ? { ...prev, billyBucksBalance: data.newBalance } : null);
                                      playSoundEffect('/sounds/drink.mp3');
                                      if (data.achievementUnlocked) setAchievementUnlocked(data.achievementUnlocked);
                                    } else {
                                      setError(data.errorKey ? t(`warEffort.tradingHub.${data.errorKey}`) : t('warEffort.tradingHub.purchaseFailed'));
                                    }
                                  } catch (e) {
                                    setError(e.message || t('warEffort.tradingHub.purchaseFailed', 'Purchase failed'));
                                  }
                                }}
                              >
                                {t('warEffort.tradingHub.buyLemonade', 'Buy Lemonade for 10 Billy Bucks')}
                              </MenuPaintedButton>
                            </div>
                          )}
                          {showDropdown && (
                            <div
                              className="trading-hub-offer-card__country-select"
                              style={{ opacity: subvertDisabled ? 0.6 : 1, pointerEvents: subvertDisabled ? 'none' : 'auto' }}
                            >
                              <CustomDropdown
                                placeholder={
                                  item.id === 12
                                    ? t('warEffort.tradingHub.selectCountryProtect', 'Select country to protect')
                                    : item.id === 13
                                      ? t('warEffort.tradingHub.selectCountrySubvert', 'Select country to subvert')
                                      : item.id === 2
                                        ? t('warEffort.tradingHub.shop.selectCountryReveal', 'Select country to reveal')
                                        : item.id === 6
                                          ? t('warEffort.tradingHub.shop.selectCountrySpeedpass', 'Select country (reconquer cooldown)')
                                          : t('warEffort.tradingHub.shop.selectCountryReconcile', 'Select country to restore')
                                }
                                options={[{ value: '', label: '—' }, ...targetOptions.map(c => ({ value: String(c.cellId), label: c.countryName || `Cell ${c.cellId}` }))]}
                                value={selectedCellId != null ? String(selectedCellId) : ''}
                                onChange={(val) => setShopSlotCellId(prev => ({ ...prev, [slotIndex]: val ? parseInt(val, 10) : null }))}
                              />
                            </div>
                          )}
                          <div className="trading-hub-offer-card__actions">
                            <MenuPaintedButton
                              className="trading-hub-buy-btn"
                              imagePath={WAR_EFFORT_PRIMARY_BUTTON_PATH}
                              disabled={isDisabled}
                              onClick={async () => {
                                try {
                                  const body = { missionId: missionData.id, itemId: item.id };
                                  if (needsCellId && selectedCellId != null) body.cellId = selectedCellId;
                                  const res = await fetch(`${API_URL}/war-effort/trading-hub/buy-shop-item`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                                    body: JSON.stringify(body)
                                  });
                                  const data = await res.json();
                                  if (res.ok) {
                                    setTradingHubStatus(prev => prev ? {
                                      ...prev,
                                      energyBalance: data.energyBalance ?? prev.energyBalance,
                                      billyBucksBalance: data.billyBucksBalance ?? prev.billyBucksBalance,
                                      remainingCooldownSeconds: data.remainingCooldownSeconds ?? prev.remainingCooldownSeconds,
                                      protectedCellId: data.protectedCellId ?? prev.protectedCellId,
                                      hasBoughtBoosterBundleForMission: data.hasBoughtBoosterBundleForMission ?? prev.hasBoughtBoosterBundleForMission,
                                      shopSlots: Array.isArray(data.shopSlots) ? data.shopSlots : prev.shopSlots
                                    } : null);
                                    if (data.energyBalance != null && playerResource) setPlayerResource(prev => prev ? { ...prev, amount: data.energyBalance } : null);
                                    if (data.protectedCellId != null) setProtectedCellId(data.protectedCellId);
                                    setShopSlotCellId(prev => ({ ...prev, [slotIndex]: null }));
                                    if (data.orders) setTradingHubBoosterOrders(data.orders);
                                    if (Array.isArray(data.activeEffects)) setActiveShopEffects(data.activeEffects);
                                    else if (missionData?.id) {
                                      fetch(`${API_URL}/war-effort/mission-status/${missionData.id}?mapView=1`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
                                        .then(r => r.ok ? r.json() : null)
                                        .then(status => {
                                          if (!status) return;
                                          if (Array.isArray(status.activeEffects)) setActiveShopEffects(status.activeEffects);
                                          if (Object.prototype.hasOwnProperty.call(status, 'territoryBonuses')) {
                                            setTerritoryBonuses(readTerritoryBonuses(status.territoryBonuses));
                                          }
                                        })
                                        .catch(() => {});
                                    }
                                    if (data.gameId) {
                                      setTradingHubOpen(false);
                                      fetchControlledCountries();
                                      const statusRes = await fetch(`${API_URL}/war-effort/status`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                                      if (statusRes.ok) setWarEffortData(await statusRes.json());
                                      navigate('/after-battle', {
                                        replace: true,
                                        state: {
                                          gameId: data.gameId,
                                          result: 'win',
                                          isWarEffort: true,
                                          gameState: { warEffortMissionId: missionData.id },
                                          missionId: missionData.id
                                        }
                                      });
                                    }
                                    if (data.granted != null) setEnergyCooldownDisplaySeconds(3600);
                                    if (data.gambleWonItem) setGambleWonItem(data.gambleWonItem);
                                    playSoundEffect('/sounds/buy.mp3');
                                  } else {
                                    setError(data.errorKey ? t(`warEffort.tradingHub.${data.errorKey}`) : t('warEffort.tradingHub.purchaseFailed'));
                                  }
                                } catch (e) {
                                  setError(e.message || t('warEffort.tradingHub.purchaseFailed', 'Purchase failed'));
                                }
                              }}
                            >
                              {isClaimEnergy ? t('warEffort.tradingHub.claimUpToEnergy', { amount: tradingHubStatus.claimAmount ?? 200, defaultValue: 'Claim up to {{amount}} energy' }) : item.id === 14 && tradingHubStatus.hasBoughtBoosterBundleForMission ? t('warEffort.tradingHub.alreadyPurchased', 'Already purchased') : t('warEffort.tradingHub.buy', 'Buy')}
                            </MenuPaintedButton>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="trading-hub-close-wrap">
                  <MenuPaintedButton
                    className="trading-hub-close-btn"
                    imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                    onClick={() => { setTradingHubOpen(false); setTradingHubStatus(null); setTradingHubBoosterOrders(null); setGambleWonItem(null); }}
                  >
                    {t('warEffort.tradingHub.close', 'Close')}
                  </MenuPaintedButton>
                </div>
              </>
            ) : (
              <div style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>
                <p>{t('warEffort.tradingHub.unableToLoad', 'Unable to load trading hub.')}</p>
                {error && <p style={{ marginTop: 12, color: '#e74c3c', fontSize: '14px' }}>{error}</p>}
                <div className="trading-hub-close-wrap" style={{ marginTop: 16 }}>
                  <MenuPaintedButton
                    className="trading-hub-close-btn"
                    imagePath={WAR_EFFORT_REFRESH_BUTTON_PATH}
                    onClick={() => { setTradingHubOpen(false); setTradingHubStatus(null); setError(null); }}
                  >
                    {t('warEffort.tradingHub.close', 'Close')}
                  </MenuPaintedButton>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gamble result overlay: show what the player won when they buy Gamble */}
      {gambleWonItem && typeof document !== 'undefined' && createPortal(
        <div
          className="trading-hub-gamble-result-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.75)',
            zIndex: 10001
          }}
        >
          <div
            className="trading-hub-gamble-result-panel"
            style={{
              background: 'linear-gradient(180deg, #1a2332 0%, #0d1520 100%)',
              borderRadius: 12,
              padding: '24px 32px',
              maxWidth: 360,
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              border: '1px solid rgba(78, 205, 196, 0.3)'
            }}
          >
            <p style={{ margin: '0 0 16px', fontSize: '1.25rem', color: '#4ECDC4' }}>
              {t('warEffort.tradingHub.gambleYouWon', 'You won!')}
            </p>
            <div style={{ marginBottom: 16 }}>
              <img
                src={`/images/${gambleWonItem.image || 'shop_energy.jpg'}`}
                alt=""
                style={{ width: 120, height: 120, objectFit: 'contain', borderRadius: 8 }}
              />
            </div>
            <p style={{ margin: 0, fontSize: '1rem', color: '#eee', fontWeight: 600 }}>
              {t(`warEffort.${gambleWonItem.nameKey}`)}
            </p>
            {gambleWonItem.descriptionKey && (
              <p style={{ margin: '12px 0 0', fontSize: '0.9rem', color: '#aaa', lineHeight: 1.4 }}>
                {t(`warEffort.${gambleWonItem.descriptionKey}`)}
              </p>
            )}
            <button
              onClick={() => setGambleWonItem(null)}
              style={{
                marginTop: 20,
                padding: '10px 24px',
                fontSize: '1rem',
                cursor: 'pointer',
                background: '#4ECDC4',
                color: '#0d1520',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600
              }}
            >
              {t('warEffort.tradingHub.close', 'Close')}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Booster bundle reward overlay – on top of existing page (backdrop + centered panel) */}
      {tradingHubBoosterOrders && tradingHubBoosterOrders.length > 0 && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.7)',
            zIndex: 10000
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              color: '#fff',
              padding: 24,
              background: 'linear-gradient(180deg, #1a1a2e 0%, #0a0a12 100%)',
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.15)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
            }}
          >
            <h2 style={{ marginBottom: 8, fontSize: '1.5rem' }}>{t('warEffort.tradingHub.boosterOverlayTitle', 'Booster bundle')}</h2>
            <p style={{ marginBottom: 16, color: '#aaa', fontSize: '0.9rem' }}>{t('warEffort.tradingHub.boosterOverlaySubtitle', 'Orders added to your attack strategy')}</p>
            <div
              className="war-effort-mission-over-rewards-grid"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 40,
                justifyContent: 'center',
                marginBottom: 20
              }}
            >
              {tradingHubBoosterOrders.map((order, index) => (
                <WarEffortMissionOverRewardCard key={order.id || index} reward={{ type: 'order', data: order }} index={index} t={t} />
              ))}
            </div>
            <button
              type="button"
              onClick={() => setTradingHubBoosterOrders(null)}
              style={{
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: 'bold',
                color: '#fff',
                backgroundColor: '#4ECDC4',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                marginTop: 5
              }}
            >
              {t('warEffort.continue', 'Continue')}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Dev: Neutral fortified countries popup */}
      {process.env.NODE_ENV === 'development' && showNeutralFortifiedPopup && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="neutral-fortified-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.7)',
            padding: 24
          }}
          onClick={(e) => e.target === e.currentTarget && setShowNeutralFortifiedPopup(false)}
        >
          <div
            style={{
              background: 'linear-gradient(180deg, #1a1a2e 0%, #0a0a12 100%)',
              border: '2px solid #4ECDC4',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              maxWidth: 420,
              width: '100%',
              maxHeight: '80vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="neutral-fortified-title" style={{ margin: 0, padding: '16px 20px', color: '#4ECDC4', fontSize: '1.1rem', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
              Neutral fortified countries
            </h2>
            <div style={{ padding: '12px 20px', color: '#ccc', fontSize: '14px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              Total: {Object.keys(missionData?.neutral_fortifications || {}).length} countries
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '12px 20px 20px' }}>
              {(() => {
                const raw = missionData?.neutral_fortifications;
                if (!raw || typeof raw !== 'object') {
                  return <div style={{ color: '#888', fontSize: '13px' }}>No neutral fortifications data.</div>;
                }
                const entries = Object.entries(raw)
                  .map(([cellIdStr, data]) => ({
                    cellId: Number(cellIdStr),
                    count: data?.count ?? 0,
                    types: data?.types
                  }))
                  .filter((e) => !isNaN(e.cellId) && e.count >= 1)
                  .sort((a, b) => getCountryFromCellId(a.cellId).localeCompare(getCountryFromCellId(b.cellId)));
                return (
                  <ul style={{ margin: 0, paddingLeft: 20, color: '#ddd', fontSize: '13px', lineHeight: 1.6 }}>
                    {entries.map(({ cellId, count }) => (
                      <li key={cellId}>
                        {getCountryFromCellId(cellId)} — {count} fortification{count !== 1 ? 's' : ''}
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <button
                type="button"
                onClick={() => setShowNeutralFortifiedPopup(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4ECDC4',
                  color: '#1a1a2e',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Dev Mode Panel (only for player1) */}
      {isDevUser && (
        <div className="dev-mode-panel" style={{
          position: 'fixed',
          bottom: '20px',
          left: '44px',
          zIndex: 5000,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          {/* Toggle Dev Mode Button */}
          <button
            onClick={() => {
              setDevModeActive(!devModeActive);
              if (devModeActive) {
                setDevSelectedPurpose(null);
                setDevShowFortifications(false);
              }
            }}
            className="dev-mode-toggle-btn"
            style={{
              padding: '10px 16px',
              backgroundColor: devModeActive ? '#ff6b35' : '#444',
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
            }}
          >
            {devModeActive ? 'DEV MODE ON' : 'DEV MODE'}
          </button>
          
          {/* Dev Mode Controls */}
          {devModeActive && (
            <div className="dev-mode-controls" style={{
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              border: '2px solid #ff6b35',
              borderRadius: '8px',
              padding: '15px',
              minWidth: '250px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)'
            }}>
              <div style={{ marginBottom: '12px', color: '#ff6b35', fontWeight: 'bold', fontSize: '14px' }}>
                World Map Editor
              </div>

              {/* Bot status (1v1 simulated or FFA bot) */}
              {devHasBot && (
                <div style={{
                  marginBottom: '12px',
                  padding: '8px 10px',
                  backgroundColor: 'rgba(78, 205, 196, 0.15)',
                  border: '1px solid rgba(78, 205, 196, 0.5)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#ccc'
                }}>
                  <div style={{ color: '#4ECDC4', fontWeight: 'bold', marginBottom: '4px' }}>Bot status</div>
                  <div>Energy: {devBotEnergy != null ? devBotEnergy : '—'}</div>
                  <div>Attacking: {devBotCurrentAttack?.countryName ?? '—'}</div>
                </div>
              )}
              
              {/* Pending Changes Indicator */}
              {Object.keys(devPendingOwners).length > 0 && (
                <div style={{ 
                  marginBottom: '12px', 
                  padding: '6px 10px', 
                  backgroundColor: 'rgba(231, 76, 60, 0.2)', 
                  border: '1px solid #e74c3c',
                  borderRadius: '4px',
                  fontSize: '11px',
                  color: '#e74c3c'
                }}>
                  {Object.keys(devPendingOwners).length} unsaved changes
                </div>
              )}

              {/* Purpose Selector */}
              <div style={{ marginBottom: '15px' }}>
                <div style={{ color: '#aaa', fontSize: '11px', marginBottom: '6px' }}>Click country to assign purpose:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {['Consume', 'Eradicate', 'Colonize', 'Protect', 'Survive'].map(purpose => {
                    // Use actual faction colors from factionColors mapping
                    const purposeColors = {
                      'Consume': '#8B0000',      // Asrah - dark red
                      'Eradicate': '#4B0082',    // Exolot Dominion - indigo/purple
                      'Colonize': '#3A3A3A',     // League of Shadows - dark gray (brightened for visibility)
                      'Protect': '#F4A460',      // Ra's Children - sandy brown
                      'Survive': '#556B2F'       // Remnants of Humanity - olive green
                    };
                    return (
                      <button
                        key={purpose}
                        onClick={() => setDevSelectedPurpose(devSelectedPurpose === purpose ? null : purpose)}
                        disabled={devLoading}
                        style={{
                          padding: '6px 10px',
                          backgroundColor: devSelectedPurpose === purpose ? purposeColors[purpose] : 'rgba(255,255,255,0.1)',
                          color: 'white',
                          border: `2px solid ${purposeColors[purpose]}`,
                          borderRadius: '4px',
                          cursor: devLoading ? 'not-allowed' : 'pointer',
                          fontSize: '11px',
                          fontWeight: devSelectedPurpose === purpose ? 'bold' : 'normal',
                          opacity: devLoading ? 0.5 : 1
                        }}
                      >
                        {purpose}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setDevSelectedPurpose('clear')}
                    disabled={devLoading}
                    style={{
                      padding: '6px 10px',
                      backgroundColor: devSelectedPurpose === 'clear' ? '#555' : 'rgba(255,255,255,0.1)',
                      color: 'white',
                      border: '2px solid #555',
                      borderRadius: '4px',
                      cursor: devLoading ? 'not-allowed' : 'pointer',
                      fontSize: '11px',
                      fontWeight: devSelectedPurpose === 'clear' ? 'bold' : 'normal',
                      opacity: devLoading ? 0.5 : 1
                    }}
                  >
                    Clear
                  </button>
                </div>
                {devSelectedPurpose && (
                  <div style={{ color: '#4ECDC4', fontSize: '11px', marginTop: '6px' }}>
                    Mode: {devSelectedPurpose === 'clear' ? 'Clear ownership' : `Assign to ${devSelectedPurpose}`}
                  </div>
                )}
              </div>
              
              {/* Dev Statistics */}
              {devStatistics && (
                <div style={{ 
                  marginBottom: '15px', 
                  padding: '10px', 
                  backgroundColor: 'rgba(255,255,255,0.05)', 
                  borderRadius: '6px',
                  fontSize: '11px'
                }}>
                  <div style={{ color: '#aaa', marginBottom: '8px', fontWeight: 'bold' }}>
                    Statistics ({devStatistics.total} countries)
                  </div>
                  {['Consume', 'Eradicate', 'Colonize', 'Protect', 'Survive'].map(purpose => {
                    const stats = devStatistics.purposes[purpose];
                    // Use actual faction colors from factionColors mapping
                    const purposeColors = {
                      'Consume': '#8B0000',      // Asrah - dark red
                      'Eradicate': '#4B0082',    // Exolot Dominion - indigo/purple
                      'Colonize': '#6A6A6A',     // League of Shadows - gray (brightened for text visibility)
                      'Protect': '#F4A460',      // Ra's Children - sandy brown
                      'Survive': '#7A9B4F'       // Remnants of Humanity - olive green (brightened for text)
                    };
                    return (
                      <div key={purpose} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '4px',
                        padding: '3px 6px',
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        borderRadius: '3px',
                        borderLeft: `3px solid ${purposeColors[purpose]}`
                      }}>
                        <span style={{ color: purposeColors[purpose], fontWeight: 'bold' }}>
                          {purpose}
                        </span>
                        <span style={{ color: '#ccc' }}>
                          {stats.count} 
                          <span style={{ color: '#777', marginLeft: '6px', fontSize: '10px' }}>
                            HQ: {stats.hqCountryName || '?'}
                            {stats.ownsHq ? ' ✓' : ' ✗'}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Show Fortifications Toggle */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#ccc', fontSize: '12px' }}>
                  <input
                    type="checkbox"
                    checked={devShowFortifications}
                    onChange={(e) => setDevShowFortifications(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Show Command Center Fortifications
                </label>
                {devShowFortifications && devCommandCenterCells.allCells.length > 0 && (
                  <div style={{ color: '#FFD700', fontSize: '10px', marginTop: '4px' }}>
                    {devCommandCenterCells.allCells.length} countries eligible
                  </div>
                )}
              </div>

              {/* Show neutral fortified countries (dev) */}
              {process.env.NODE_ENV === 'development' && (
                <div style={{ marginBottom: '15px' }}>
                  <button
                    type="button"
                    onClick={() => setShowNeutralFortifiedPopup(true)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: 'rgba(78, 205, 196, 0.2)',
                      color: '#4ECDC4',
                      border: '1px solid #4ECDC4',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    Show neutral fortified countries
                  </button>
                </div>
              )}
              
              {/* Simulate opponent (only when mission has no player2) */}
              {!missionData?.player2_id && (
                <div style={{ marginBottom: '12px' }}>
                  <button
                    onClick={handleSimulateOpponent}
                    disabled={devSimulateOpponentLoading}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: devSimulateOpponentLoading ? '#555' : '#27ae60',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: devSimulateOpponentLoading ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      opacity: devSimulateOpponentLoading ? 0.5 : 1,
                      width: '100%'
                    }}
                  >
                    {devSimulateOpponentLoading ? 'Simulating…' : 'Simulate opponent'}
                  </button>
                  <div style={{ color: '#888', fontSize: '10px', marginTop: '4px' }}>
                    Joins as player2 and fortifies 3 random owned countries
                  </div>
                </div>
              )}
              {/* End cease fire (dev only, when full or opponent-fortified cease fire is active) */}
              {(isCeaseFireActive || isOpponentFortifiedCeaseFireActive || isConquestAttackerCeaseFireActive) && (
                <div style={{ marginBottom: '12px' }}>
                  <button
                    onClick={handleEndCeaseFire}
                    disabled={devEndCeaseFireLoading}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: devEndCeaseFireLoading ? '#555' : '#e67e22',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: devEndCeaseFireLoading ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      opacity: devEndCeaseFireLoading ? 0.5 : 1,
                      width: '100%'
                    }}
                  >
                    {devEndCeaseFireLoading ? 'Ending…' : 'End cease fire'}
                  </button>
                </div>
              )}

              {/* Reset energy to 1000 (dev only) */}
              {missionData?.id && (
                <div style={{ marginBottom: '12px' }}>
                  <button
                    onClick={handleDevResetEnergy}
                    disabled={devResetEnergyLoading}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: devResetEnergyLoading ? '#555' : '#4ECDC4',
                      color: '#000',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: devResetEnergyLoading ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      opacity: devResetEnergyLoading ? 0.5 : 1,
                      width: '100%'
                    }}
                  >
                    {devResetEnergyLoading ? 'Resetting…' : 'Reset energy to 1000'}
                  </button>
                </div>
              )}

              {/* Simulate "fortified under attack" warning (dev only, player1) */}
              <div style={{ marginBottom: '12px' }}>
                <button
                  onClick={handleDevSimulateUnderAttackWarning}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#cc0000',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    width: '100%'
                  }}
                >
                  Simulate under attack warning
                </button>
                <div style={{ color: '#888', fontSize: '10px', marginTop: '4px' }}>
                  Shows red &quot;WARNING: Austria is being attacked!&quot; for 5s
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  onClick={handleDevRestoreDefault}
                  disabled={devLoading || Object.keys(devPendingOwners).length === 0}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: Object.keys(devPendingOwners).length > 0 ? '#e74c3c' : '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (devLoading || Object.keys(devPendingOwners).length === 0) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    opacity: (devLoading || Object.keys(devPendingOwners).length === 0) ? 0.5 : 1
                  }}
                >
                  {Object.keys(devPendingOwners).length > 0 ? 'Discard Changes' : 'No Changes'}
                </button>
                <button
                  onClick={handleDevStoreAsDefault}
                  disabled={devLoading || Object.keys(devPendingOwners).length === 0}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#e67e22',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (devLoading || Object.keys(devPendingOwners).length === 0) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    opacity: (devLoading || Object.keys(devPendingOwners).length === 0) ? 0.5 : 1
                  }}
                >
                  Store as Default
                </button>
                <button
                  onClick={handleDevRandomize}
                  disabled={devLoading}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#9b59b6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: devLoading ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    opacity: devLoading ? 0.5 : 1
                  }}
                >
                  Randomize
                </button>
              </div>
              
              {devLoading && (
                <div style={{ color: '#4ECDC4', fontSize: '11px', marginTop: '10px', textAlign: 'center' }}>
                  Processing...
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {hqBuildingPage}
    </div>
    </CountryHoverProvider>
  );
}

export default WarEffortFortification;
